import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { normalizarTextoPtBr } from "../utils/textoPtBr";
import { TEXTOS_INTEGRACOES_ERP } from "./textosIntegracoesErp";
import { TEXTOS_MONITOR_TEF_FISCAL } from "./textosMonitorTefFiscal";
import { TEXTOS_MONITOR_TEF_FISCAL_COMPLEMENTO } from "./textosMonitorTefFiscalComplemento";
import { TEXTOS_EMISSAO_FISCAL } from "./textosEmissaoFiscal";
import { TEXTOS_OPERACAO_ERP } from "./textosOperacaoErp";
import { TEXTOS_OPERACAO_COMPLETA } from "./textosOperacaoCompleta";
import { TEXTOS_WHATSAPP_QR } from "./textosWhatsappQr";
import { TEXTOS_CHAT_ATENDIMENTO as TEXTOS_CONFIG_CHAT_ATENDIMENTO } from "./textosChatAtendimento";
import { TEXTOS_ETAPAS } from "./textosEtapas";
import { TEXTOS_SEGURANCA } from "./textosSeguranca";
import { TEXTOS_DASHBOARD_VENDAS } from "./textosDashboardVendas";
import { TEXTOS_ACESSO_PERMISSAO } from "./textosAcessoPermissao";
import { TEXTOS_ESTOQUE_TRANSFERENCIA } from "./textosEstoqueTransferencia";
import { TEXTOS_SUPORTE_TAREFAS } from "./textosSuporteTarefas";
import { TEXTOS_CONFIRMACOES_EMPRESA_VENDA } from "./textosConfirmacoesEmpresaVenda";
import { TEXTOS_COMPRAS_FORNECEDORES } from "./textosComprasFornecedores";
import { TEXTOS_REVENDA, traduzirFraseRevenda } from "./textosRevenda";
import { TEXTOS_RELATORIOS, traduzirFraseRelatorio } from "./textosRelatorios";
import { TEXTOS_PRE_VENDA_PRECOS } from "./textosPreVendaPrecos";
import { TEXTOS_AJUDA_CADASTROS_BASE } from "./textosAjudaCadastrosBase";
import { TEXTOS_AJUDA_CADASTROS_PRINCIPAIS } from "./textosAjudaCadastrosPrincipais";
import { TEXTOS_AJUDA_OPERACIONAL_1 } from "./textosAjudaOperacional1";
import { TEXTOS_AJUDA_OPERACIONAL_2 } from "./textosAjudaOperacional2";
import { TEXTOS_FINANCEIRO_DETALHES } from "./textosFinanceiroDetalhes";
import { TEXTOS_MARCA_EMPRESA } from "./textosMarcaEmpresa";
import { TEXTOS_AJUDA_CONFIGURACOES } from "./textosAjudaConfiguracoes";
import { TEXTOS_EMAIL_EMPRESAS } from "./textosEmailEmpresas";
import { TEXTOS_CHECKLIST } from "./textosChecklist";
import { TEXTOS_CREDITOS_OPENAI } from "./textosCreditosOpenAi";
import { TEXTOS_EMAIL_EMPRESAS_SIMPLIFICADO } from "./textosEmailEmpresasSimplificado";
import { TEXTOS_CHECKLIST_EXECUCAO } from "./textosChecklistExecucao";
import { TEXTOS_DASHBOARD_CHECKLIST } from "./textosDashboardChecklist";
import { TEXTOS_DASHBOARDS_HOME } from "./textosDashboardsHome";
import { TEXTOS_AREA_CLIENTE } from "./textosAreaCliente";
import { TEXTOS_COMPONENTES_UI } from "./textosComponentesUi";
import { TEXTOS_BADGES } from "./textosBadges";
import { FRASES_COMUNS } from './frasesComuns';

export type Idioma = "pt" | "en" | "es" | "zh-CN";

type ChaveTexto = string;

type Dicionario = Record<string, string>;

const TEXTOS_PDV: Record<Idioma, Dicionario> = {
  pt: {
    "PDV Caixa": "PDV Caixa", CNPJ: "CNPJ", "Caixa Aberto": "Caixa Aberto", "Caixa Fechado": "Caixa Fechado",
    Operador: "Operador", "Leitura de Código de Barras": "Leitura de Código de Barras",
    "Passe o produto no leitor ou digite o código": "Passe o produto no leitor ou digite o código",
    "Busca de Produtos": "Busca de Produtos", "Digite a descrição do produto": "Digite a descrição do produto",
    Acréscimos: "Acréscimos", Dinheiro: "Dinheiro", "Cartão Crédito": "Cartão Crédito",
    "Cartão Débito": "Cartão Débito", Pix: "Pix", Vale: "Vale", Misto: "Misto",
    "Valor recebido": "Valor recebido", "Falta receber": "Falta receber", Troco: "Troco",
    "Mais Opções": "Mais Opções", "Reimprimir última venda": "Reimprimir última venda",
    "Fechar Caixa": "Fechar Caixa", Cliente: "Cliente", Desconto: "Desconto", Quantidade: "Quantidade",
    Preço: "Preço", "Cancelar Item": "Cancelar Item", Sangria: "Sangria", Suprimento: "Suprimento",
    Pagamento: "Pagamento", Finalizar: "Finalizar", "Informe o desconto geral": "Informe o desconto geral",
    "Informe a quantidade": "Informe a quantidade", "Informe o preço unitário": "Informe o preço unitário",
    "Saldo insuficiente para a quantidade informada.": "Saldo insuficiente para a quantidade informada.",
    "Parte em dinheiro": "Parte em dinheiro", "Parte em Pix": "Parte em Pix",
    "No pagamento misto, informe uma parte em dinheiro menor que o total.": "No pagamento misto, informe uma parte em dinheiro menor que o total.",
    Versão: "Versão", Licença: "Licença", Online: "Online", Offline: "Offline",
  },
  en: {
    "PDV Caixa": "POS Register", CNPJ: "Tax ID", "Caixa Aberto": "Register Open", "Caixa Fechado": "Register Closed",
    Operador: "Operator", "Leitura de Código de Barras": "Barcode Scanning",
    "Passe o produto no leitor ou digite o código": "Scan the product or enter its code",
    "Busca de Produtos": "Product Search", "Digite a descrição do produto": "Enter the product description",
    Acréscimos: "Surcharges", Dinheiro: "Cash", "Cartão Crédito": "Credit Card", "Cartão Débito": "Debit Card",
    Pix: "Pix", Vale: "Voucher", Misto: "Split", "Valor recebido": "Amount received",
    "Falta receber": "Amount due", Troco: "Change", "Mais Opções": "More Options",
    "Reimprimir última venda": "Reprint last sale", "Fechar Caixa": "Close Register", Cliente: "Customer",
    Desconto: "Discount", Quantidade: "Quantity", Preço: "Price", "Cancelar Item": "Cancel Item",
    Sangria: "Cash Withdrawal", Suprimento: "Cash Deposit", Pagamento: "Payment", Finalizar: "Complete",
    "Informe o desconto geral": "Enter the overall discount", "Informe a quantidade": "Enter the quantity",
    "Informe o preço unitário": "Enter the unit price",
    "Saldo insuficiente para a quantidade informada.": "Insufficient stock for the entered quantity.",
    "Parte em dinheiro": "Cash portion", "Parte em Pix": "Pix portion",
    "No pagamento misto, informe uma parte em dinheiro menor que o total.": "For split payment, enter a cash portion lower than the total.",
    Versão: "Version", Licença: "License", Online: "Online", Offline: "Offline",
  },
  es: {
    "PDV Caixa": "Caja PDV", CNPJ: "CNPJ", "Caixa Aberto": "Caja Abierta", "Caixa Fechado": "Caja Cerrada",
    Operador: "Operador", "Leitura de Código de Barras": "Lectura de Código de Barras",
    "Passe o produto no leitor ou digite o código": "Escanee el producto o ingrese el código",
    "Busca de Produtos": "Búsqueda de Productos", "Digite a descrição do produto": "Ingrese la descripción del producto",
    Acréscimos: "Recargos", Dinheiro: "Efectivo", "Cartão Crédito": "Tarjeta de Crédito",
    "Cartão Débito": "Tarjeta de Débito", Pix: "Pix", Vale: "Vale", Misto: "Mixto",
    "Valor recebido": "Valor recibido", "Falta receber": "Falta recibir", Troco: "Cambio",
    "Mais Opções": "Más Opciones", "Reimprimir última venda": "Reimprimir última venta",
    "Fechar Caixa": "Cerrar Caja", Cliente: "Cliente", Desconto: "Descuento", Quantidade: "Cantidad",
    Preço: "Precio", "Cancelar Item": "Cancelar Ítem", Sangria: "Retiro", Suprimento: "Ingreso",
    Pagamento: "Pago", Finalizar: "Finalizar", "Informe o desconto geral": "Ingrese el descuento general",
    "Informe a quantidade": "Ingrese la cantidad", "Informe o preço unitário": "Ingrese el precio unitario",
    "Saldo insuficiente para a quantidade informada.": "Stock insuficiente para la cantidad ingresada.",
    "Parte em dinheiro": "Parte en efectivo", "Parte em Pix": "Parte en Pix",
    "No pagamento misto, informe uma parte em dinheiro menor que o total.": "En el pago mixto, ingrese una parte en efectivo menor que el total.",
    Versão: "Versión", Licença: "Licencia", Online: "En línea", Offline: "Sin conexión",
  },
  "zh-CN": {
    "PDV Caixa": "POS 收银台", CNPJ: "税号", "Caixa Aberto": "收银台已开启", "Caixa Fechado": "收银台已关闭",
    Operador: "操作员", "Leitura de Código de Barras": "条码扫描",
    "Passe o produto no leitor ou digite o código": "扫描商品或输入代码",
    "Busca de Produtos": "商品搜索", "Digite a descrição do produto": "输入商品描述",
    Acréscimos: "附加费", Dinheiro: "现金", "Cartão Crédito": "信用卡", "Cartão Débito": "借记卡",
    Pix: "Pix", Vale: "代金券", Misto: "组合支付", "Valor recebido": "已收金额", "Falta receber": "待收金额",
    Troco: "找零", "Mais Opções": "更多选项", "Reimprimir última venda": "重印上一笔销售",
    "Fechar Caixa": "关闭收银台", Cliente: "客户", Desconto: "折扣", Quantidade: "数量", Preço: "价格",
    "Cancelar Item": "取消商品", Sangria: "现金支出", Suprimento: "现金补充", Pagamento: "付款",
    Finalizar: "完成", "Informe o desconto geral": "请输入总折扣", "Informe a quantidade": "请输入数量",
    "Informe o preço unitário": "请输入单价", "Saldo insuficiente para a quantidade informada.": "库存不足，无法满足输入的数量。",
    "Parte em dinheiro": "现金部分", "Parte em Pix": "Pix 部分",
    "No pagamento misto, informe uma parte em dinheiro menor que o total.": "组合支付时，请输入小于总额的现金部分。",
    Versão: "版本", Licença: "许可证", Online: "在线", Offline: "离线",
  },
};

const TEXTOS_IMPRESSAO_COLUNAS: Record<Idioma, Dicionario> = {
  pt: {
    "Selecionar colunas do relatório": "Selecionar colunas do relatório",
    "Escolha quais colunas devem aparecer no relatório impresso.": "Escolha quais colunas devem aparecer no relatório impresso.",
    "Marcar todas": "Marcar todas",
    "Desmarcar todas": "Desmarcar todas",
    "Imprimir normal": "Imprimir normal",
    "Imprimir selecionadas": "Imprimir selecionadas",
    "Selecione ao menos uma coluna.": "Selecione ao menos uma coluna.",
  },
  en: {
    "Selecionar colunas do relatório": "Select report columns",
    "Escolha quais colunas devem aparecer no relatório impresso.": "Choose which columns should appear in the printed report.",
    "Marcar todas": "Select all",
    "Desmarcar todas": "Deselect all",
    "Imprimir normal": "Print normal",
    "Imprimir selecionadas": "Print selected",
    "Selecione ao menos uma coluna.": "Select at least one column.",
  },
  es: {
    "Selecionar colunas do relatório": "Seleccionar columnas del informe",
    "Escolha quais colunas devem aparecer no relatório impresso.": "Elija qué columnas deben aparecer en el informe impreso.",
    "Marcar todas": "Marcar todas",
    "Desmarcar todas": "Desmarcar todas",
    "Imprimir normal": "Imprimir normal",
    "Imprimir selecionadas": "Imprimir seleccionadas",
    "Selecione ao menos uma coluna.": "Seleccione al menos una columna.",
  },
  "zh-CN": {
    "Selecionar colunas do relatório": "选择报表列",
    "Escolha quais colunas devem aparecer no relatório impresso.": "选择打印报表中应显示的列。",
    "Marcar todas": "全选",
    "Desmarcar todas": "取消全选",
    "Imprimir normal": "正常打印",
    "Imprimir selecionadas": "打印所选列",
    "Selecione ao menos uma coluna.": "请至少选择一列。",
  },
};

const TEXTOS_COBRANCA: Record<Idioma, Dicionario> = {
  pt: {
    billingNetworkError: "Não foi possível conectar ao serviço de cobrança.",
    billingRequestError: "Não foi possível concluir a solicitação de cobrança.",
    billingCheckoutUrlMissing: "O servidor de cobrança não retornou a URL do checkout.",
    billingCheckoutUrlInvalid: "A URL retornada para o checkout não é válida ou segura.",
    "Minha conta e cobrança": "Minha conta e cobrança",
    "Gerencie o plano, os adicionais, o pagamento e as moedas de IA da organização.": "Gerencie o plano, os adicionais, o pagamento e as moedas de IA da organização.",
    "Pagamento processado no checkout seguro.": "Pagamento processado no checkout seguro.",
    "Não foi possível carregar os dados da sua conta.": "Não foi possível carregar os dados da sua conta.",
    "Não foi possível iniciar o checkout seguro.": "Não foi possível iniciar o checkout seguro.",
    "Não foi possível iniciar a compra de moedas de IA.": "Não foi possível iniciar a compra de moedas de IA.",
    "Carregando dados da conta...": "Carregando dados da conta...",
    "A cobrança da plataforma está desativada para esta organização.": "A cobrança da plataforma está desativada para esta organização.",
    "Resumo da assinatura": "Resumo da assinatura",
    "As informações abaixo são atualizadas pelo servidor de cobrança.": "As informações abaixo são atualizadas pelo servidor de cobrança.",
    Atualizar: "Atualizar",
    Fechar: "Fechar",
    "Plano atual": "Plano atual",
    "Usuários ativos": "Usuários ativos",
    "usuários no plano": "usuários no plano",
    "Fim do período de teste": "Fim do período de teste",
    "Fim do período gratuito": "Fim do período gratuito",
    "Próximo vencimento": "Próximo vencimento",
    "dia restante": "dia restante",
    "dias restantes": "dias restantes",
    "Saldo de moedas de IA": "Saldo de moedas de IA",
    "moedas disponíveis": "moedas disponíveis",
    "Plano e adicionais": "Plano e adicionais",
    "Escolha o plano adequado ao número de usuários da organização.": "Escolha o plano adequado ao número de usuários da organização.",
    "Plano contratado": "Plano contratado",
    "Plano selecionado": "Plano selecionado",
    "Limite de usuários": "Limite de usuários",
    "Venda assistida": "Venda assistida",
    "Valor do plano": "Valor do plano",
    "Consulte o canal de vendas": "Consulte o canal de vendas",
    "Nenhum plano está disponível no momento.": "Nenhum plano está disponível no momento.",
    "Pix ou cartão de crédito são escolhidos no checkout.": "Pix ou cartão de crédito são escolhidos no checkout.",
    "Falar com o canal de vendas": "Falar com o canal de vendas",
    "Ir para o checkout seguro": "Ir para o checkout seguro",
    "Comprar moedas de IA": "Comprar moedas de IA",
    "As moedas são adicionadas à carteira da organização após a confirmação do pagamento.": "As moedas são adicionadas à carteira da organização após a confirmação do pagamento.",
    "Saldo atual": "Saldo atual",
    "moedas de IA": "moedas de IA",
    "Comprar pacote": "Comprar pacote",
    "Nenhum pacote de moedas está disponível no momento.": "Nenhum pacote de moedas está disponível no momento.",
    "Consumo de moedas de IA": "Consumo de moedas de IA",
    "Defina quantas moedas serão descontadas da organização a cada solicitação enviada ao agente.": "Defina quantas moedas serão descontadas da organização a cada solicitação enviada ao agente.",
    "Moedas consumidas por solicitação": "Moedas consumidas por solicitação",
    "Configuração Asaas": "Configuração Asaas",
    "Gerencie o período gratuito, os planos e a cobrança das organizações pela integração Asaas.": "Gerencie o período gratuito, os planos e a cobrança das organizações pela integração Asaas.",
    "Ativar cobrança da plataforma": "Ativar cobrança da plataforma",
    "As organizações terão sete dias gratuitos antes do início da cobrança.": "As organizações terão sete dias gratuitos antes do início da cobrança.",
    "Com a cobrança desativada, a plataforma permanece gratuita e o menu Minha conta fica oculto.": "Com a cobrança desativada, a plataforma permanece gratuita e o menu Minha conta fica oculto.",
    "Cobrança ativa": "Cobrança ativa",
    "Plataforma gratuita": "Plataforma gratuita",
    "Ambiente Asaas": "Ambiente Asaas",
    "Sandbox (testes)": "Sandbox (testes)",
    Produção: "Produção",
    "Dias do período gratuito": "Dias do período gratuito",
    "Expiração do checkout em minutos": "Expiração do checkout em minutos",
    "Nova chave de API Asaas": "Nova chave de API Asaas",
    "Chave configurada; digite somente para substituir": "Chave configurada; digite somente para substituir",
    "Cole a chave da API Asaas": "Cole a chave da API Asaas",
    "Chave protegida e configurada no backend": "Chave protegida e configurada no backend",
    "Chave ainda não configurada": "Chave ainda não configurada",
    "Novo token do webhook Asaas": "Novo token do webhook Asaas",
    "Token configurado; digite somente para substituir": "Token configurado; digite somente para substituir",
    "Informe um token seguro para o webhook": "Informe um token seguro para o webhook",
    "Token protegido e configurado no backend": "Token protegido e configurado no backend",
    "Token do webhook ainda não configurado": "Token do webhook ainda não configurado",
    "URL pública do webhook Asaas": "URL pública do webhook Asaas",
    "A URL será gerada pelo backend": "A URL será gerada pelo backend",
    "No painel do Asaas, cadastre esta URL e use exatamente o mesmo token informado acima.": "No painel do Asaas, cadastre esta URL e use exatamente o mesmo token informado acima.",
    "Copiar URL do webhook Asaas": "Copiar URL do webhook Asaas",
    "URL do webhook Asaas copiada com sucesso.": "URL do webhook Asaas copiada com sucesso.",
    "Não foi possível copiar a URL do webhook Asaas.": "Não foi possível copiar a URL do webhook Asaas.",
    "A expiração do checkout deve ficar entre 10 e 1440 minutos.": "A expiração do checkout deve ficar entre 10 e 1440 minutos.",
    "Canal de vendas": "Canal de vendas",
    "E-mail do canal de vendas": "E-mail do canal de vendas",
    "Telefone do canal de vendas": "Telefone do canal de vendas",
    "URL do canal de vendas": "URL do canal de vendas",
    "Planos da plataforma": "Planos da plataforma",
    "Os valores não incluem TEF nem Fiscal, que são contratados separadamente.": "Os valores não incluem TEF nem Fiscal, que são contratados separadamente.",
    Ativo: "Ativo",
    Inativo: "Inativo",
    "Consultar canal de vendas": "Consultar canal de vendas",
    "por mês": "por mês",
    "Acima de 12 usuários": "Acima de 12 usuários",
    Até: "Até",
    usuários: "usuários",
    "Nenhum plano configurado.": "Nenhum plano configurado.",
    "Adicionais TEF e Fiscal": "Adicionais TEF e Fiscal",
    "Disponível para contratação": "Disponível para contratação",
    "Valor mensal": "Valor mensal",
    "Pacotes de moedas de IA": "Pacotes de moedas de IA",
    "Disponível para compra": "Disponível para compra",
    "Quantidade de moedas": "Quantidade de moedas",
    "Valor do pacote": "Valor do pacote",
    "Cobrança por organização": "Cobrança por organização",
    "Acompanhe plano, usuários, vencimentos e saldo de moedas de IA.": "Acompanhe plano, usuários, vencimentos e saldo de moedas de IA.",
    "Organização e Banco": "Organização e Banco",
    "Lista segura das organizações cadastradas na base.": "Lista segura das organizações cadastradas na base.",
    Organização: "Organização",
    Plano: "Plano",
    Usuários: "Usuários",
    Status: "Status",
    "Período e vencimento": "Período e vencimento",
    "Saldo IA": "Saldo IA",
    "Sem plano": "Sem plano",
    "Nenhuma organização disponível para cobrança.": "Nenhuma organização disponível para cobrança.",
    Ativa: "Ativa",
    "Período gratuito": "Período gratuito",
    Pendente: "Pendente",
    "Em atraso": "Em atraso",
    Suspensa: "Suspensa",
    Cancelada: "Cancelada",
    Gratuita: "Gratuita",
    "Não configurada": "Não configurada",
    "Configuração indisponível": "Configuração indisponível",
    "Tentar novamente": "Tentar novamente",
    "Não foi possível carregar a configuração de cobrança.": "Não foi possível carregar a configuração de cobrança.",
    "Não foi possível carregar as organizações da cobrança.": "Não foi possível carregar as organizações da cobrança.",
    "As configurações gerais foram salvas, mas a cobrança não foi alterada porque não pôde ser carregada.": "As configurações gerais foram salvas, mas a cobrança não foi alterada porque não pôde ser carregada.",
    "Checkout concluído. A confirmação do pagamento será atualizada pelo Asaas.": "Checkout concluído. A confirmação do pagamento será atualizada pelo Asaas.",
    "Checkout cancelado. Nenhuma alteração foi aplicada.": "Checkout cancelado. Nenhuma alteração foi aplicada.",
    "O checkout expirou. Gere um novo quando desejar.": "O checkout expirou. Gere um novo quando desejar.",
    "Não foi possível consultar a cobrança. Tente novamente.": "Não foi possível consultar a cobrança. Tente novamente.",
    "Consulte o saldo e compre moedas de IA para a organização.": "Consulte o saldo e compre moedas de IA para a organização.",
    "A plataforma está gratuita. Minha conta permanece disponível para comprar moedas de IA.": "A plataforma está gratuita. Minha conta permanece disponível para comprar moedas de IA.",
    "Seu período gratuito termina em": "Seu período gratuito termina em",
    dia: "dia",
    dias: "dias",
    "Escolher plano": "Escolher plano",
    "Seu período gratuito terminou.": "Seu período gratuito terminou.",
    "Para continuar usando a plataforma, regularize o plano em Minha conta.": "Para continuar usando a plataforma, regularize o plano em Minha conta.",
    "Abrir Minha conta": "Abrir Minha conta",
    "A cobrança automática está desativada, mas o plano contratado e o histórico continuam disponíveis.": "A cobrança automática está desativada, mas o plano contratado e o histórico continuam disponíveis.",
    "Histórico de pagamentos e recibos": "Histórico de pagamentos e recibos",
    "Consulte pagamentos confirmados, pendentes e vencidos do plano e das moedas de IA.": "Consulte pagamentos confirmados, pendentes e vencidos do plano e das moedas de IA.",
    "Compra de moedas de IA": "Compra de moedas de IA",
    "Pagamento do plano": "Pagamento do plano",
    "Baixar recibo": "Baixar recibo",
    "Efetivar pagamento": "Efetivar pagamento",
    "Nenhum pagamento registrado até o momento.": "Nenhum pagamento registrado até o momento.",
    "Data do pagamento": "Data do pagamento",
    "Recibo emitido com base na confirmação registrada pelo provedor de pagamento.": "Recibo emitido com base na confirmação registrada pelo provedor de pagamento.",
    Pago: "Pago",
    Recusado: "Recusado",
    Estornado: "Estornado",
    "Estorno parcial": "Estorno parcial",
    Contestado: "Contestado",
    Cancelado: "Cancelado",
    Excluído: "Excluído",
    "Salvar configurações": "Salvar configurações",
  },
  en: {
    billingNetworkError: "Could not connect to the billing service.",
    billingRequestError: "Could not complete the billing request.",
    billingCheckoutUrlMissing: "The billing server did not return a checkout URL.",
    billingCheckoutUrlInvalid: "The checkout URL returned is invalid or unsafe.",
    "Minha conta e cobrança": "My account and billing",
    "Gerencie o plano, os adicionais, o pagamento e as moedas de IA da organização.": "Manage the organization's plan, add-ons, payment, and AI coins.",
    "Pagamento processado no checkout seguro.": "Payment is processed through the secure checkout.",
    "Não foi possível carregar os dados da sua conta.": "Could not load your account details.",
    "Não foi possível iniciar o checkout seguro.": "Could not start the secure checkout.",
    "Não foi possível iniciar a compra de moedas de IA.": "Could not start the AI coin purchase.",
    "Carregando dados da conta...": "Loading account details...",
    "A cobrança da plataforma está desativada para esta organização.": "Platform billing is disabled for this organization.",
    "Resumo da assinatura": "Subscription summary",
    "As informações abaixo são atualizadas pelo servidor de cobrança.": "The information below is updated by the billing server.",
    Atualizar: "Refresh",
    Fechar: "Close",
    "Plano atual": "Current plan",
    "Usuários ativos": "Active users",
    "usuários no plano": "users in the plan",
    "Fim do período de teste": "Trial end",
    "Fim do período gratuito": "Free trial end",
    "Próximo vencimento": "Next due date",
    "dia restante": "day remaining",
    "dias restantes": "days remaining",
    "Saldo de moedas de IA": "AI coin balance",
    "moedas disponíveis": "coins available",
    "Plano e adicionais": "Plan and add-ons",
    "Escolha o plano adequado ao número de usuários da organização.": "Choose the plan that fits the organization's number of users.",
    "Plano contratado": "Selected plan",
    "Plano selecionado": "Selected plan",
    "Limite de usuários": "User limit",
    "Venda assistida": "Assisted sales",
    "Valor do plano": "Plan price",
    "Consulte o canal de vendas": "Contact the sales channel",
    "Nenhum plano está disponível no momento.": "No plan is currently available.",
    "Pix ou cartão de crédito são escolhidos no checkout.": "Choose Pix or credit card at checkout.",
    "Falar com o canal de vendas": "Contact sales",
    "Ir para o checkout seguro": "Go to secure checkout",
    "Comprar moedas de IA": "Buy AI coins",
    "As moedas são adicionadas à carteira da organização após a confirmação do pagamento.": "Coins are added to the organization's wallet after payment confirmation.",
    "Saldo atual": "Current balance",
    "moedas de IA": "AI coins",
    "Comprar pacote": "Buy package",
    "Nenhum pacote de moedas está disponível no momento.": "No coin package is currently available.",
    "Consumo de moedas de IA": "AI coin usage",
    "Defina quantas moedas serão descontadas da organização a cada solicitação enviada ao agente.": "Set how many coins are deducted from the organization for each request sent to the agent.",
    "Moedas consumidas por solicitação": "Coins used per request",
    "Configuração Asaas": "Asaas settings",
    "Gerencie o período gratuito, os planos e a cobrança das organizações pela integração Asaas.": "Manage the free trial, plans, and organization billing through the Asaas integration.",
    "Ativar cobrança da plataforma": "Enable platform billing",
    "As organizações terão sete dias gratuitos antes do início da cobrança.": "Organizations will have seven free days before billing starts.",
    "Com a cobrança desativada, a plataforma permanece gratuita e o menu Minha conta fica oculto.": "When billing is disabled, the platform remains free and the My account menu is hidden.",
    "Cobrança ativa": "Billing enabled",
    "Plataforma gratuita": "Free platform",
    "Ambiente Asaas": "Asaas environment",
    "Sandbox (testes)": "Sandbox (testing)",
    Produção: "Production",
    "Dias do período gratuito": "Free trial days",
    "Expiração do checkout em minutos": "Checkout expiration in minutes",
    "Nova chave de API Asaas": "New Asaas API key",
    "Chave configurada; digite somente para substituir": "Key configured; enter a value only to replace it",
    "Cole a chave da API Asaas": "Paste the Asaas API key",
    "Chave protegida e configurada no backend": "Key securely stored and configured on the backend",
    "Chave ainda não configurada": "Key not configured yet",
    "Novo token do webhook Asaas": "New Asaas webhook token",
    "Token configurado; digite somente para substituir": "Token configured; enter a value only to replace it",
    "Informe um token seguro para o webhook": "Enter a secure webhook token",
    "Token protegido e configurado no backend": "Token securely stored and configured on the backend",
    "Token do webhook ainda não configurado": "Webhook token not configured yet",
    "URL pública do webhook Asaas": "Public Asaas webhook URL",
    "A URL será gerada pelo backend": "The URL will be generated by the backend",
    "No painel do Asaas, cadastre esta URL e use exatamente o mesmo token informado acima.": "In the Asaas dashboard, register this URL and use exactly the same token entered above.",
    "Copiar URL do webhook Asaas": "Copy Asaas webhook URL",
    "URL do webhook Asaas copiada com sucesso.": "Asaas webhook URL copied successfully.",
    "Não foi possível copiar a URL do webhook Asaas.": "Could not copy the Asaas webhook URL.",
    "A expiração do checkout deve ficar entre 10 e 1440 minutos.": "Checkout expiration must be between 10 and 1440 minutes.",
    "Canal de vendas": "Sales channel",
    "E-mail do canal de vendas": "Sales channel email",
    "Telefone do canal de vendas": "Sales channel phone",
    "URL do canal de vendas": "Sales channel URL",
    "Planos da plataforma": "Platform plans",
    "Os valores não incluem TEF nem Fiscal, que são contratados separadamente.": "Prices do not include TEF or Fiscal, which are purchased separately.",
    Ativo: "Active",
    Inativo: "Inactive",
    "Consultar canal de vendas": "Contact sales",
    "por mês": "per month",
    "Acima de 12 usuários": "More than 12 users",
    Até: "Up to",
    usuários: "users",
    "Nenhum plano configurado.": "No plan configured.",
    "Adicionais TEF e Fiscal": "TEF and Fiscal add-ons",
    "Disponível para contratação": "Available for purchase",
    "Valor mensal": "Monthly price",
    "Pacotes de moedas de IA": "AI coin packages",
    "Disponível para compra": "Available for purchase",
    "Quantidade de moedas": "Number of coins",
    "Valor do pacote": "Package price",
    "Cobrança por organização": "Billing by organization",
    "Acompanhe plano, usuários, vencimentos e saldo de moedas de IA.": "Track plans, users, due dates, and AI coin balances.",
    "Organização e Banco": "Organization and Database",
    "Lista segura das organizações cadastradas na base.": "Secure list of organizations registered in the database.",
    Organização: "Organization",
    Plano: "Plan",
    Usuários: "Users",
    Status: "Status",
    "Período e vencimento": "Period and due date",
    "Saldo IA": "AI balance",
    "Sem plano": "No plan",
    "Nenhuma organização disponível para cobrança.": "No organization is available for billing.",
    Ativa: "Active",
    "Período gratuito": "Free trial",
    Pendente: "Pending",
    "Em atraso": "Overdue",
    Suspensa: "Suspended",
    Cancelada: "Canceled",
    Gratuita: "Free",
    "Não configurada": "Not configured",
    "Configuração indisponível": "Configuration unavailable",
    "Tentar novamente": "Try again",
    "Não foi possível carregar a configuração de cobrança.": "Could not load the billing settings.",
    "Não foi possível carregar as organizações da cobrança.": "Could not load the billing organizations.",
    "As configurações gerais foram salvas, mas a cobrança não foi alterada porque não pôde ser carregada.": "General settings were saved, but billing was not changed because it could not be loaded.",
    "Checkout concluído. A confirmação do pagamento será atualizada pelo Asaas.": "Checkout completed. Payment confirmation will be updated by Asaas.",
    "Checkout cancelado. Nenhuma alteração foi aplicada.": "Checkout canceled. No changes were applied.",
    "O checkout expirou. Gere um novo quando desejar.": "The checkout expired. Create a new one whenever you are ready.",
    "Não foi possível consultar a cobrança. Tente novamente.": "Could not check billing. Try again.",
    "Consulte o saldo e compre moedas de IA para a organização.": "Check the balance and buy AI coins for the organization.",
    "A plataforma está gratuita. Minha conta permanece disponível para comprar moedas de IA.": "The platform is free. My account remains available for purchasing AI coins.",
    "Seu período gratuito termina em": "Your free trial ends in",
    dia: "day",
    dias: "days",
    "Escolher plano": "Choose plan",
    "Seu período gratuito terminou.": "Your free trial has ended.",
    "Para continuar usando a plataforma, regularize o plano em Minha conta.": "To continue using the platform, select and pay for a plan in My account.",
    "Abrir Minha conta": "Open My account",
    "A cobrança automática está desativada, mas o plano contratado e o histórico continuam disponíveis.": "Automatic billing is disabled, but the contracted plan and payment history remain available.",
    "Histórico de pagamentos e recibos": "Payment and receipt history",
    "Consulte pagamentos confirmados, pendentes e vencidos do plano e das moedas de IA.": "Review confirmed, pending, and overdue payments for the plan and AI coins.",
    "Compra de moedas de IA": "AI coin purchase",
    "Pagamento do plano": "Plan payment",
    "Baixar recibo": "Download receipt",
    "Efetivar pagamento": "Make payment",
    "Nenhum pagamento registrado até o momento.": "No payments have been recorded yet.",
    "Data do pagamento": "Payment date",
    "Recibo emitido com base na confirmação registrada pelo provedor de pagamento.": "Receipt issued based on the confirmation recorded by the payment provider.",
    Pago: "Paid",
    Recusado: "Declined",
    Estornado: "Refunded",
    "Estorno parcial": "Partially refunded",
    Contestado: "Disputed",
    Cancelado: "Canceled",
    Excluído: "Deleted",
    "Salvar configurações": "Save settings",
  },
  es: {
    billingNetworkError: "No se pudo conectar con el servicio de facturación.",
    billingRequestError: "No se pudo completar la solicitud de facturación.",
    billingCheckoutUrlMissing: "El servidor de facturación no devolvió la URL del checkout.",
    billingCheckoutUrlInvalid: "La URL devuelta para el checkout no es válida o segura.",
    "Minha conta e cobrança": "Mi cuenta y facturación",
    "Gerencie o plano, os adicionais, o pagamento e as moedas de IA da organização.": "Gestione el plan, los adicionales, el pago y las monedas de IA de la organización.",
    "Pagamento processado no checkout seguro.": "El pago se procesa en el checkout seguro.",
    "Não foi possível carregar os dados da sua conta.": "No se pudieron cargar los datos de su cuenta.",
    "Não foi possível iniciar o checkout seguro.": "No se pudo iniciar el checkout seguro.",
    "Não foi possível iniciar a compra de moedas de IA.": "No se pudo iniciar la compra de monedas de IA.",
    "Carregando dados da conta...": "Cargando datos de la cuenta...",
    "A cobrança da plataforma está desativada para esta organização.": "La facturación de la plataforma está desactivada para esta organización.",
    "Resumo da assinatura": "Resumen de la suscripción",
    "As informações abaixo são atualizadas pelo servidor de cobrança.": "La información siguiente se actualiza desde el servidor de facturación.",
    Atualizar: "Actualizar",
    Fechar: "Cerrar",
    "Plano atual": "Plan actual",
    "Usuários ativos": "Usuarios activos",
    "usuários no plano": "usuarios del plan",
    "Fim do período de teste": "Fin del período de prueba",
    "Fim do período gratuito": "Fin del período gratuito",
    "Próximo vencimento": "Próximo vencimiento",
    "dia restante": "día restante",
    "dias restantes": "días restantes",
    "Saldo de moedas de IA": "Saldo de monedas de IA",
    "moedas disponíveis": "monedas disponibles",
    "Plano e adicionais": "Plan y adicionales",
    "Escolha o plano adequado ao número de usuários da organização.": "Elija el plan adecuado para la cantidad de usuarios de la organización.",
    "Plano contratado": "Plan contratado",
    "Plano selecionado": "Plan seleccionado",
    "Limite de usuários": "Límite de usuarios",
    "Venda assistida": "Venta asistida",
    "Valor do plano": "Precio del plan",
    "Consulte o canal de vendas": "Consulte al canal de ventas",
    "Nenhum plano está disponível no momento.": "No hay planes disponibles en este momento.",
    "Pix ou cartão de crédito são escolhidos no checkout.": "Pix o tarjeta de crédito se eligen en el checkout.",
    "Falar com o canal de vendas": "Hablar con el canal de ventas",
    "Ir para o checkout seguro": "Ir al checkout seguro",
    "Comprar moedas de IA": "Comprar monedas de IA",
    "As moedas são adicionadas à carteira da organização após a confirmação do pagamento.": "Las monedas se agregan a la cartera de la organización después de confirmar el pago.",
    "Saldo atual": "Saldo actual",
    "moedas de IA": "monedas de IA",
    "Comprar pacote": "Comprar paquete",
    "Nenhum pacote de moedas está disponível no momento.": "No hay paquetes de monedas disponibles en este momento.",
    "Consumo de moedas de IA": "Consumo de monedas de IA",
    "Defina quantas moedas serão descontadas da organização a cada solicitação enviada ao agente.": "Defina cuántas monedas se descontarán de la organización por cada solicitud enviada al agente.",
    "Moedas consumidas por solicitação": "Monedas consumidas por solicitud",
    "Configuração Asaas": "Configuración de Asaas",
    "Gerencie o período gratuito, os planos e a cobrança das organizações pela integração Asaas.": "Gestione el período gratuito, los planes y la facturación de las organizaciones mediante la integración con Asaas.",
    "Ativar cobrança da plataforma": "Activar facturación de la plataforma",
    "As organizações terão sete dias gratuitos antes do início da cobrança.": "Las organizaciones tendrán siete días gratis antes de que comience la facturación.",
    "Com a cobrança desativada, a plataforma permanece gratuita e o menu Minha conta fica oculto.": "Cuando la facturación está desactivada, la plataforma sigue siendo gratuita y el menú Mi cuenta permanece oculto.",
    "Cobrança ativa": "Facturación activa",
    "Plataforma gratuita": "Plataforma gratuita",
    "Ambiente Asaas": "Entorno de Asaas",
    "Sandbox (testes)": "Sandbox (pruebas)",
    Produção: "Producción",
    "Dias do período gratuito": "Días del período gratuito",
    "Expiração do checkout em minutos": "Vencimiento del checkout en minutos",
    "Nova chave de API Asaas": "Nueva clave API de Asaas",
    "Chave configurada; digite somente para substituir": "Clave configurada; ingrese un valor solo para reemplazarla",
    "Cole a chave da API Asaas": "Pegue la clave API de Asaas",
    "Chave protegida e configurada no backend": "Clave protegida y configurada en el backend",
    "Chave ainda não configurada": "Clave aún no configurada",
    "Novo token do webhook Asaas": "Nuevo token del webhook de Asaas",
    "Token configurado; digite somente para substituir": "Token configurado; ingrese un valor solo para reemplazarlo",
    "Informe um token seguro para o webhook": "Ingrese un token seguro para el webhook",
    "Token protegido e configurado no backend": "Token protegido y configurado en el backend",
    "Token do webhook ainda não configurado": "Token del webhook aún no configurado",
    "URL pública do webhook Asaas": "URL pública del webhook de Asaas",
    "A URL será gerada pelo backend": "La URL será generada por el backend",
    "No painel do Asaas, cadastre esta URL e use exatamente o mesmo token informado acima.": "En el panel de Asaas, registre esta URL y use exactamente el mismo token ingresado arriba.",
    "Copiar URL do webhook Asaas": "Copiar URL del webhook de Asaas",
    "URL do webhook Asaas copiada com sucesso.": "La URL del webhook de Asaas se copió correctamente.",
    "Não foi possível copiar a URL do webhook Asaas.": "No se pudo copiar la URL del webhook de Asaas.",
    "A expiração do checkout deve ficar entre 10 e 1440 minutos.": "El vencimiento del checkout debe estar entre 10 y 1440 minutos.",
    "Canal de vendas": "Canal de ventas",
    "E-mail do canal de vendas": "Correo electrónico del canal de ventas",
    "Telefone do canal de vendas": "Teléfono del canal de ventas",
    "URL do canal de vendas": "URL del canal de ventas",
    "Planos da plataforma": "Planes de la plataforma",
    "Os valores não incluem TEF nem Fiscal, que são contratados separadamente.": "Los precios no incluyen TEF ni Fiscal, que se contratan por separado.",
    Ativo: "Activo",
    Inativo: "Inactivo",
    "Consultar canal de vendas": "Consultar al canal de ventas",
    "por mês": "por mes",
    "Acima de 12 usuários": "Más de 12 usuarios",
    Até: "Hasta",
    usuários: "usuarios",
    "Nenhum plano configurado.": "No hay planes configurados.",
    "Adicionais TEF e Fiscal": "Adicionales TEF y Fiscal",
    "Disponível para contratação": "Disponible para contratar",
    "Valor mensal": "Precio mensual",
    "Pacotes de moedas de IA": "Paquetes de monedas de IA",
    "Disponível para compra": "Disponible para comprar",
    "Quantidade de moedas": "Cantidad de monedas",
    "Valor do pacote": "Precio del paquete",
    "Cobrança por organização": "Facturación por organización",
    "Acompanhe plano, usuários, vencimentos e saldo de moedas de IA.": "Consulte el plan, los usuarios, los vencimientos y el saldo de monedas de IA.",
    "Organização e Banco": "Organización y Base de Datos",
    "Lista segura das organizações cadastradas na base.": "Lista segura de las organizaciones registradas en la base de datos.",
    Organização: "Organización",
    Plano: "Plan",
    Usuários: "Usuarios",
    Status: "Estado",
    "Período e vencimento": "Período y vencimiento",
    "Saldo IA": "Saldo de IA",
    "Sem plano": "Sin plan",
    "Nenhuma organização disponível para cobrança.": "No hay organizaciones disponibles para facturación.",
    Ativa: "Activa",
    "Período gratuito": "Período gratuito",
    Pendente: "Pendiente",
    "Em atraso": "Atrasada",
    Suspensa: "Suspendida",
    Cancelada: "Cancelada",
    Gratuita: "Gratuita",
    "Não configurada": "No configurada",
    "Configuração indisponível": "Configuración no disponible",
    "Tentar novamente": "Intentar de nuevo",
    "Não foi possível carregar a configuração de cobrança.": "No se pudo cargar la configuración de facturación.",
    "Não foi possível carregar as organizações da cobrança.": "No se pudieron cargar las organizaciones de facturación.",
    "As configurações gerais foram salvas, mas a cobrança não foi alterada porque não pôde ser carregada.": "La configuración general se guardó, pero la facturación no se modificó porque no pudo cargarse.",
    "Checkout concluído. A confirmação do pagamento será atualizada pelo Asaas.": "Checkout completado. Asaas actualizará la confirmación del pago.",
    "Checkout cancelado. Nenhuma alteração foi aplicada.": "Checkout cancelado. No se aplicó ningún cambio.",
    "O checkout expirou. Gere um novo quando desejar.": "El checkout venció. Genere uno nuevo cuando lo desee.",
    "Não foi possível consultar a cobrança. Tente novamente.": "No se pudo consultar la facturación. Inténtelo de nuevo.",
    "Consulte o saldo e compre moedas de IA para a organização.": "Consulte el saldo y compre monedas de IA para la organización.",
    "A plataforma está gratuita. Minha conta permanece disponível para comprar moedas de IA.": "La plataforma es gratuita. Mi cuenta sigue disponible para comprar monedas de IA.",
    "Seu período gratuito termina em": "Su período gratuito termina en",
    dia: "día",
    dias: "días",
    "Escolher plano": "Elegir plan",
    "Seu período gratuito terminou.": "Su período gratuito terminó.",
    "Para continuar usando a plataforma, regularize o plano em Minha conta.": "Para continuar usando la plataforma, regularice el plan en Mi cuenta.",
    "Abrir Minha conta": "Abrir Mi cuenta",
    "A cobrança automática está desativada, mas o plano contratado e o histórico continuam disponíveis.": "La facturación automática está desactivada, pero el plan contratado y el historial siguen disponibles.",
    "Histórico de pagamentos e recibos": "Historial de pagos y recibos",
    "Consulte pagamentos confirmados, pendentes e vencidos do plano e das moedas de IA.": "Consulte los pagos confirmados, pendientes y vencidos del plan y de las monedas de IA.",
    "Compra de moedas de IA": "Compra de monedas de IA",
    "Pagamento do plano": "Pago del plan",
    "Baixar recibo": "Descargar recibo",
    "Efetivar pagamento": "Realizar pago",
    "Nenhum pagamento registrado até o momento.": "Aún no hay pagos registrados.",
    "Data do pagamento": "Fecha de pago",
    "Recibo emitido com base na confirmação registrada pelo provedor de pagamento.": "Recibo emitido según la confirmación registrada por el proveedor de pagos.",
    Pago: "Pagado",
    Recusado: "Rechazado",
    Estornado: "Reembolsado",
    "Estorno parcial": "Reembolso parcial",
    Contestado: "Disputado",
    Cancelado: "Cancelado",
    Excluído: "Eliminado",
    "Salvar configurações": "Guardar configuración",
  },
  "zh-CN": {
    billingNetworkError: "无法连接到计费服务。",
    billingRequestError: "无法完成计费请求。",
    billingCheckoutUrlMissing: "计费服务器未返回结账链接。",
    billingCheckoutUrlInvalid: "返回的结账链接无效或不安全。",
    "Minha conta e cobrança": "我的账户与计费",
    "Gerencie o plano, os adicionais, o pagamento e as moedas de IA da organização.": "管理组织的套餐、附加服务、付款和 AI 币。",
    "Pagamento processado no checkout seguro.": "付款将通过安全结账页面处理。",
    "Não foi possível carregar os dados da sua conta.": "无法加载您的账户信息。",
    "Não foi possível iniciar o checkout seguro.": "无法启动安全结账。",
    "Não foi possível iniciar a compra de moedas de IA.": "无法开始购买 AI 币。",
    "Carregando dados da conta...": "正在加载账户信息...",
    "A cobrança da plataforma está desativada para esta organização.": "此组织的平台计费已停用。",
    "Resumo da assinatura": "订阅摘要",
    "As informações abaixo são atualizadas pelo servidor de cobrança.": "以下信息由计费服务器更新。",
    Atualizar: "刷新",
    Fechar: "关闭",
    "Plano atual": "当前套餐",
    "Usuários ativos": "活跃用户",
    "usuários no plano": "套餐内用户",
    "Fim do período de teste": "试用期结束日期",
    "Fim do período gratuito": "免费试用期结束日期",
    "Próximo vencimento": "下次到期日",
    "dia restante": "天剩余",
    "dias restantes": "天剩余",
    "Saldo de moedas de IA": "AI 币余额",
    "moedas disponíveis": "可用 AI 币",
    "Plano e adicionais": "套餐和附加服务",
    "Escolha o plano adequado ao número de usuários da organização.": "请选择适合组织用户数量的套餐。",
    "Plano contratado": "已选套餐",
    "Plano selecionado": "已选套餐",
    "Limite de usuários": "用户上限",
    "Venda assistida": "销售协助",
    "Valor do plano": "套餐价格",
    "Consulte o canal de vendas": "请联系销售渠道",
    "Nenhum plano está disponível no momento.": "目前没有可用套餐。",
    "Pix ou cartão de crédito são escolhidos no checkout.": "请在结账页面选择 Pix 或信用卡。",
    "Falar com o canal de vendas": "联系销售",
    "Ir para o checkout seguro": "前往安全结账",
    "Comprar moedas de IA": "购买 AI 币",
    "As moedas são adicionadas à carteira da organização após a confirmação do pagamento.": "付款确认后，AI 币将添加到组织钱包。",
    "Saldo atual": "当前余额",
    "moedas de IA": "AI 币",
    "Comprar pacote": "购买套餐",
    "Nenhum pacote de moedas está disponível no momento.": "目前没有可购买的 AI 币套餐。",
    "Consumo de moedas de IA": "AI 币消耗",
    "Defina quantas moedas serão descontadas da organização a cada solicitação enviada ao agente.": "设置每次向助手发送请求时从组织扣除的 AI 币数量。",
    "Moedas consumidas por solicitação": "每次请求消耗的 AI 币",
    "Configuração Asaas": "Asaas 设置",
    "Gerencie o período gratuito, os planos e a cobrança das organizações pela integração Asaas.": "通过 Asaas 集成管理免费试用期、套餐和组织计费。",
    "Ativar cobrança da plataforma": "启用平台计费",
    "As organizações terão sete dias gratuitos antes do início da cobrança.": "组织将在计费开始前获得七天免费试用。",
    "Com a cobrança desativada, a plataforma permanece gratuita e o menu Minha conta fica oculto.": "停用计费后，平台保持免费，“我的账户”菜单将隐藏。",
    "Cobrança ativa": "计费已启用",
    "Plataforma gratuita": "免费平台",
    "Ambiente Asaas": "Asaas 环境",
    "Sandbox (testes)": "沙盒（测试）",
    Produção: "生产环境",
    "Dias do período gratuito": "免费试用天数",
    "Expiração do checkout em minutos": "结账过期时间（分钟）",
    "Nova chave de API Asaas": "新的 Asaas API 密钥",
    "Chave configurada; digite somente para substituir": "密钥已配置；仅在需要替换时输入",
    "Cole a chave da API Asaas": "粘贴 Asaas API 密钥",
    "Chave protegida e configurada no backend": "密钥已安全存储并在后端配置",
    "Chave ainda não configurada": "密钥尚未配置",
    "Novo token do webhook Asaas": "新的 Asaas Webhook 令牌",
    "Token configurado; digite somente para substituir": "令牌已配置；仅在需要替换时输入",
    "Informe um token seguro para o webhook": "请输入安全的 Webhook 令牌",
    "Token protegido e configurado no backend": "令牌已安全存储并在后端配置",
    "Token do webhook ainda não configurado": "Webhook 令牌尚未配置",
    "URL pública do webhook Asaas": "Asaas Webhook 公共 URL",
    "A URL será gerada pelo backend": "此 URL 将由后端生成",
    "No painel do Asaas, cadastre esta URL e use exatamente o mesmo token informado acima.": "请在 Asaas 控制面板中登记此 URL，并使用与上方输入内容完全相同的令牌。",
    "Copiar URL do webhook Asaas": "复制 Asaas Webhook URL",
    "URL do webhook Asaas copiada com sucesso.": "Asaas Webhook URL 已成功复制。",
    "Não foi possível copiar a URL do webhook Asaas.": "无法复制 Asaas Webhook URL。",
    "A expiração do checkout deve ficar entre 10 e 1440 minutos.": "结账过期时间必须介于 10 到 1440 分钟之间。",
    "Canal de vendas": "销售渠道",
    "E-mail do canal de vendas": "销售渠道电子邮箱",
    "Telefone do canal de vendas": "销售渠道电话",
    "URL do canal de vendas": "销售渠道 URL",
    "Planos da plataforma": "平台套餐",
    "Os valores não incluem TEF nem Fiscal, que são contratados separadamente.": "价格不包括需单独购买的 TEF 和 Fiscal 服务。",
    Ativo: "启用",
    Inativo: "停用",
    "Consultar canal de vendas": "联系销售渠道",
    "por mês": "每月",
    "Acima de 12 usuários": "超过 12 名用户",
    Até: "最多",
    usuários: "用户",
    "Nenhum plano configurado.": "尚未配置套餐。",
    "Adicionais TEF e Fiscal": "TEF 和 Fiscal 附加服务",
    "Disponível para contratação": "可购买",
    "Valor mensal": "月费",
    "Pacotes de moedas de IA": "AI 币套餐",
    "Disponível para compra": "可购买",
    "Quantidade de moedas": "AI 币数量",
    "Valor do pacote": "套餐价格",
    "Cobrança por organização": "按组织计费",
    "Acompanhe plano, usuários, vencimentos e saldo de moedas de IA.": "查看套餐、用户、到期日期和 AI 币余额。",
    "Organização e Banco": "组织与数据库",
    "Lista segura das organizações cadastradas na base.": "数据库中已注册组织的安全列表。",
    Organização: "组织",
    Plano: "套餐",
    Usuários: "用户",
    Status: "状态",
    "Período e vencimento": "周期与到期日",
    "Saldo IA": "AI 余额",
    "Sem plano": "无套餐",
    "Nenhuma organização disponível para cobrança.": "没有可用于计费的组织。",
    Ativa: "有效",
    "Período gratuito": "免费试用期",
    Pendente: "待处理",
    "Em atraso": "逾期",
    Suspensa: "已暂停",
    Cancelada: "已取消",
    Gratuita: "免费",
    "Não configurada": "未配置",
    "Configuração indisponível": "配置不可用",
    "Tentar novamente": "重试",
    "Não foi possível carregar a configuração de cobrança.": "无法加载计费配置。",
    "Não foi possível carregar as organizações da cobrança.": "无法加载计费组织。",
    "As configurações gerais foram salvas, mas a cobrança não foi alterada porque não pôde ser carregada.": "常规设置已保存，但由于无法加载计费配置，计费设置未更改。",
    "Checkout concluído. A confirmação do pagamento será atualizada pelo Asaas.": "结账已完成，付款确认将由 Asaas 更新。",
    "Checkout cancelado. Nenhuma alteração foi aplicada.": "结账已取消，未应用任何更改。",
    "O checkout expirou. Gere um novo quando desejar.": "结账已过期，您可以随时重新创建。",
    "Não foi possível consultar a cobrança. Tente novamente.": "无法查询计费信息，请重试。",
    "Consulte o saldo e compre moedas de IA para a organização.": "查看余额并为组织购买 AI 币。",
    "A plataforma está gratuita. Minha conta permanece disponível para comprar moedas de IA.": "平台当前免费。“我的账户”仍可用于购买 AI 币。",
    "Seu período gratuito termina em": "您的免费试用将在以下天数后结束：",
    dia: "天",
    dias: "天",
    "Escolher plano": "选择套餐",
    "Seu período gratuito terminou.": "您的免费试用已结束。",
    "Para continuar usando a plataforma, regularize o plano em Minha conta.": "要继续使用平台，请在“我的账户”中选择并支付套餐。",
    "Abrir Minha conta": "打开我的账户",
    "A cobrança automática está desativada, mas o plano contratado e o histórico continuam disponíveis.": "自动计费已停用，但已签约套餐和付款记录仍可查看。",
    "Histórico de pagamentos e recibos": "付款和收据记录",
    "Consulte pagamentos confirmados, pendentes e vencidos do plano e das moedas de IA.": "查看套餐和 AI 币的已确认、待处理及逾期付款。",
    "Compra de moedas de IA": "购买 AI 币",
    "Pagamento do plano": "套餐付款",
    "Baixar recibo": "下载收据",
    "Efetivar pagamento": "完成付款",
    "Nenhum pagamento registrado até o momento.": "目前没有付款记录。",
    "Data do pagamento": "付款日期",
    "Recibo emitido com base na confirmação registrada pelo provedor de pagamento.": "本收据依据付款服务商记录的确认信息开具。",
    Pago: "已付款",
    Recusado: "已拒绝",
    Estornado: "已退款",
    "Estorno parcial": "部分退款",
    Contestado: "有争议",
    Cancelado: "已取消",
    Excluído: "已删除",
    "Salvar configurações": "保存设置",
  },
};

const TEXTOS_CHAT_ATENDIMENTO: Record<Idioma, Dicionario> = {
  pt: {
    "Com IA": "Com IA", "Aguardando Humano": "Aguardando Humano", "Chat Interno": "Chat Interno",
    "Transferir para Humano": "Transferir para Humano", "Responder com sugestão da IA": "Responder com sugestão da IA",
    "Ver detalhes da análise": "Ver detalhes da análise", "Sugestão de resposta": "Sugestão de resposta", Prioridade: "Prioridade", Média: "Média",
    "IA atendendo": "IA atendendo", "Aguardando transferência": "Aguardando transferência", "Em atendimento humano": "Em atendimento humano", Finalizado: "Finalizado",
    "Atendimento iniciado pela IA": "Atendimento iniciado pela IA", Online: "Online", "Tempo médio de resposta": "Tempo médio de resposta", "Atendimentos hoje": "Atendimentos hoje",
    "Validar os dados e responder ao cliente com clareza.": "Validar os dados e responder ao cliente com clareza.", Transferir: "Transferir",
    "Aguardando atendente": "Aguardando atendente", "Aguardando aceite": "Aguardando aceite", Atender: "Atender",
    "Atendimento iniciado.": "Atendimento iniciado.", "Não foi possível iniciar o atendimento.": "Não foi possível iniciar o atendimento.",
    "Documento enviado.": "Documento enviado.", "Não foi possível enviar o documento.": "Não foi possível enviar o documento.",
    "Não foi possível adicionar a tag.": "Não foi possível adicionar a tag.", "Não foi possível remover a tag.": "Não foi possível remover a tag.",
    "Não foi possível alterar a cor da tag.": "Não foi possível alterar a cor da tag.", "Cor da tag": "Cor da tag", "Alterar cor da tag": "Alterar cor da tag", "Remover tag": "Remover tag",
    "Informe uma cor válida para a tag.": "Informe uma cor válida para a tag.", "Tag inválida.": "Tag inválida.", "Tag não encontrada.": "Tag não encontrada.",
    "Não foi possível carregar os clientes.": "Não foi possível carregar os clientes.", "Conversa iniciada.": "Conversa iniciada.",
    "Mais opções": "Mais opções", "Dados do contato": "Dados do contato", "Pesquisar na conversa": "Pesquisar na conversa",
    Emojis: "Emojis", "Anexar documento": "Anexar documento", "Clique em Atender para responder": "Clique em Atender para responder",
    "Dados da conversa": "Dados da conversa", "Pesquisar mensagens": "Pesquisar mensagens", Documento: "Documento",
    "Conversa aberta em": "Conversa aberta em", Atendente: "Atendente", "Tags do atendimento": "Tags do atendimento",
    "Adicionar tag": "Adicionar tag", "Resultados encontrados": "Resultados encontrados", Cancelar: "Cancelar",
    "Escolha um cliente para iniciar o atendimento.": "Escolha um cliente para iniciar o atendimento.",
    "Iniciar conversa": "Iniciar conversa", "Pesquisar cliente": "Pesquisar cliente", "Nome, e-mail ou telefone": "Nome, e-mail ou telefone",
    "Mensagem inicial (opcional)": "Mensagem inicial (opcional)", "Digite a primeira mensagem...": "Digite a primeira mensagem...",
    "Não foi possível alterar a disponibilidade.": "Não foi possível alterar a disponibilidade.",
    "Transferir para agente": "Transferir para agente", "Conversa transferida para o agente de IA.": "Conversa transferida para o agente de IA.",
    "Não foi possível transferir a conversa para o agente de IA.": "Não foi possível transferir a conversa para o agente de IA.",
    "A conversa não está em atendimento humano.": "A conversa não está em atendimento humano.", "A conversa não está mais em atendimento humano.": "A conversa não está mais em atendimento humano.", "O agente de IA está desativado.": "O agente de IA está desativado.",
    "Abrir chamado": "Abrir chamado", "Não foi possível preparar a abertura do chamado.": "Não foi possível preparar a abertura do chamado.",
    "Áudio enviado.": "Áudio enviado.", "Não foi possível enviar o áudio.": "Não foi possível enviar o áudio.",
    "Gravar áudio": "Gravar áudio", "Parar e enviar áudio": "Parar e enviar áudio",
    "Seu navegador não permite gravar áudio.": "Seu navegador não permite gravar áudio.", "Não foi possível acessar o microfone.": "Não foi possível acessar o microfone.",
    "Seu navegador não permite reproduzir áudio.": "Seu navegador não permite reproduzir áudio.", "Seu navegador não permite reproduzir vídeo.": "Seu navegador não permite reproduzir vídeo.",
    "Você não tem acesso a este chamado ou tarefa.": "Você não tem acesso a este chamado ou tarefa.",
    "Assumir chamado": "Assumir chamado", "Chamado assumido com sucesso.": "Chamado assumido com sucesso.",
    "Não foi possível assumir o chamado.": "Não foi possível assumir o chamado.", "Este chamado já foi assumido por outro usuário.": "Este chamado já foi assumido por outro usuário.",
    "Você não tem permissão para assumir este chamado.": "Você não tem permissão para assumir este chamado.",
    IA: "IA", Humano: "Humano", Finalizadas: "Finalizadas", "Indicadores do atendimento": "Indicadores do atendimento",
    "Conversas Ativas": "Conversas Ativas", "Visão atual": "Visão atual", "Atendidas pela IA": "Atendidas pela IA",
    "Transferidas para Humano": "Transferidas para Humano", "Conversa selecionada": "Conversa selecionada",
    Satisfação: "Satisfação", "Sem avaliações": "Sem avaliações", "Novo Atendimento": "Novo Atendimento",
    "Informações do atendimento": "Informações do atendimento", "Selecione uma conversa para visualizar os detalhes": "Selecione uma conversa para visualizar os detalhes",
    "Dados do Cliente": "Dados do Cliente", "Canal preferencial": "Canal preferencial", "Resumo da IA": "Resumo da IA", Resumo: "Resumo",
    "Intenção detectada": "Intenção detectada", "Atendimento humano solicitado": "Atendimento humano solicitado", "Atendimento em andamento": "Atendimento em andamento",
    Sentimento: "Sentimento", "Não identificado": "Não identificado", "Ver detalhes da conversa": "Ver detalhes da conversa", Roteamento: "Roteamento",
    "IA iniciada": "IA iniciada", Triagem: "Triagem", "Transferido para humano": "Transferido para humano", "Atendente Responsável": "Atendente Responsável",
    "Sem atendente": "Sem atendente", "Responsável pela conversa": "Responsável pela conversa", "Ações rápidas": "Ações rápidas",
    "Ativar IA": "Ativar IA", "Assumir atendimento": "Assumir atendimento", "Criar ticket": "Criar ticket", "Finalizar atendimento": "Finalizar atendimento",
    "Sem resumo disponível.": "Sem resumo disponível.",
  },
  en: {
    "Com IA": "With AI", "Aguardando Humano": "Waiting for Human", "Chat Interno": "Internal Chat",
    "Transferir para Humano": "Transfer to Human", "Responder com sugestão da IA": "Reply with AI suggestion",
    "Ver detalhes da análise": "View analysis details", "Sugestão de resposta": "Suggested response", Prioridade: "Priority", Média: "Medium",
    "IA atendendo": "AI handling", "Aguardando transferência": "Waiting for transfer", "Em atendimento humano": "Human support", Finalizado: "Completed",
    "Atendimento iniciado pela IA": "Support started by AI", Online: "Online", "Tempo médio de resposta": "Average response time", "Atendimentos hoje": "Services today",
    "Validar os dados e responder ao cliente com clareza.": "Validate the data and reply to the customer clearly.", Transferir: "Transfer",
    "Aguardando atendente": "Waiting for an agent", "Aguardando aceite": "Waiting for acceptance", Atender: "Accept",
    "Atendimento iniciado.": "Support started.", "Não foi possível iniciar o atendimento.": "Could not start support.",
    "Documento enviado.": "Document sent.", "Não foi possível enviar o documento.": "Could not send the document.",
    "Não foi possível adicionar a tag.": "Could not add the tag.", "Não foi possível remover a tag.": "Could not remove the tag.",
    "Não foi possível alterar a cor da tag.": "Could not change the tag color.", "Cor da tag": "Tag color", "Alterar cor da tag": "Change tag color", "Remover tag": "Remove tag",
    "Informe uma cor válida para a tag.": "Enter a valid tag color.", "Tag inválida.": "Invalid tag.", "Tag não encontrada.": "Tag not found.",
    "Não foi possível carregar os clientes.": "Could not load customers.", "Conversa iniciada.": "Conversation started.",
    "Mais opções": "More options", "Dados do contato": "Contact details", "Pesquisar na conversa": "Search conversation",
    Emojis: "Emojis", "Anexar documento": "Attach document", "Clique em Atender para responder": "Click Accept to reply",
    "Dados da conversa": "Conversation details", "Pesquisar mensagens": "Search messages", Documento: "Document",
    "Conversa aberta em": "Conversation opened at", Atendente: "Agent", "Tags do atendimento": "Conversation tags",
    "Adicionar tag": "Add tag", "Resultados encontrados": "Results found", Cancelar: "Cancel",
    "Escolha um cliente para iniciar o atendimento.": "Choose a customer to start support.",
    "Iniciar conversa": "Start conversation", "Pesquisar cliente": "Search customer", "Nome, e-mail ou telefone": "Name, email, or phone",
    "Mensagem inicial (opcional)": "Initial message (optional)", "Digite a primeira mensagem...": "Type the first message...",
    "Não foi possível alterar a disponibilidade.": "Could not change availability.",
    "Transferir para agente": "Transfer to AI agent", "Conversa transferida para o agente de IA.": "Conversation transferred to the AI agent.",
    "Não foi possível transferir a conversa para o agente de IA.": "Could not transfer the conversation to the AI agent.",
    "A conversa não está em atendimento humano.": "The conversation is not under human support.", "A conversa não está mais em atendimento humano.": "The conversation is no longer under human support.", "O agente de IA está desativado.": "The AI agent is disabled.",
    "Abrir chamado": "Open ticket", "Não foi possível preparar a abertura do chamado.": "Could not prepare the ticket form.",
    "Áudio enviado.": "Audio sent.", "Não foi possível enviar o áudio.": "Could not send the audio.",
    "Gravar áudio": "Record audio", "Parar e enviar áudio": "Stop and send audio",
    "Seu navegador não permite gravar áudio.": "Your browser does not support audio recording.", "Não foi possível acessar o microfone.": "Could not access the microphone.",
    "Seu navegador não permite reproduzir áudio.": "Your browser cannot play audio.", "Seu navegador não permite reproduzir vídeo.": "Your browser cannot play video.",
    "Você não tem acesso a este chamado ou tarefa.": "You do not have access to this ticket or task.",
    "Assumir chamado": "Take ticket", "Chamado assumido com sucesso.": "Ticket assigned to you successfully.",
    "Não foi possível assumir o chamado.": "Could not take the ticket.", "Este chamado já foi assumido por outro usuário.": "This ticket has already been taken by another user.",
    "Você não tem permissão para assumir este chamado.": "You do not have permission to take this ticket.",
    IA: "AI", Humano: "Human", Finalizadas: "Completed", "Indicadores do atendimento": "Service indicators",
    "Conversas Ativas": "Active Conversations", "Visão atual": "Current view", "Atendidas pela IA": "Handled by AI",
    "Transferidas para Humano": "Transferred to Human", "Conversa selecionada": "Selected conversation",
    Satisfação: "Satisfaction", "Sem avaliações": "No ratings", "Novo Atendimento": "New Service",
    "Informações do atendimento": "Service information", "Selecione uma conversa para visualizar os detalhes": "Select a conversation to view details",
    "Dados do Cliente": "Customer Details", "Canal preferencial": "Preferred channel", "Resumo da IA": "AI Summary", Resumo: "Summary",
    "Intenção detectada": "Detected intent", "Atendimento humano solicitado": "Human support requested", "Atendimento em andamento": "Service in progress",
    Sentimento: "Sentiment", "Não identificado": "Not identified", "Ver detalhes da conversa": "View conversation details", Roteamento: "Routing",
    "IA iniciada": "AI started", Triagem: "Triage", "Transferido para humano": "Transferred to human", "Atendente Responsável": "Assigned Agent",
    "Sem atendente": "No agent", "Responsável pela conversa": "Responsible for the conversation", "Ações rápidas": "Quick actions",
    "Ativar IA": "Activate AI", "Assumir atendimento": "Take service", "Criar ticket": "Create ticket", "Finalizar atendimento": "Finish service",
    "Sem resumo disponível.": "No summary available.",
  },
  es: {
    "Com IA": "Con IA", "Aguardando Humano": "Esperando humano", "Chat Interno": "Chat interno",
    "Transferir para Humano": "Transferir a humano", "Responder com sugestão da IA": "Responder con sugerencia de IA",
    "Ver detalhes da análise": "Ver detalles del análisis", "Sugestão de resposta": "Sugerencia de respuesta", Prioridade: "Prioridad", Média: "Media",
    "IA atendendo": "IA atendiendo", "Aguardando transferência": "Esperando transferencia", "Em atendimento humano": "Atención humana", Finalizado: "Finalizado",
    "Atendimento iniciado pela IA": "Atención iniciada por IA", Online: "En línea", "Tempo médio de resposta": "Tiempo medio de respuesta", "Atendimentos hoje": "Atenciones hoy",
    "Validar os dados e responder ao cliente com clareza.": "Validar los datos y responder al cliente con claridad.", Transferir: "Transferir",
    "Aguardando atendente": "Esperando agente", "Aguardando aceite": "Esperando aceptación", Atender: "Atender",
    "Atendimento iniciado.": "Atención iniciada.", "Não foi possível iniciar o atendimento.": "No se pudo iniciar la atención.",
    "Documento enviado.": "Documento enviado.", "Não foi possível enviar o documento.": "No se pudo enviar el documento.",
    "Não foi possível adicionar a tag.": "No se pudo agregar la etiqueta.", "Não foi possível remover a tag.": "No se pudo eliminar la etiqueta.",
    "Não foi possível alterar a cor da tag.": "No se pudo cambiar el color de la etiqueta.", "Cor da tag": "Color de la etiqueta", "Alterar cor da tag": "Cambiar color de la etiqueta", "Remover tag": "Eliminar etiqueta",
    "Informe uma cor válida para a tag.": "Ingrese un color válido para la etiqueta.", "Tag inválida.": "Etiqueta no válida.", "Tag não encontrada.": "Etiqueta no encontrada.",
    "Não foi possível carregar os clientes.": "No se pudieron cargar los clientes.", "Conversa iniciada.": "Conversación iniciada.",
    "Mais opções": "Más opciones", "Dados do contato": "Datos del contacto", "Pesquisar na conversa": "Buscar en la conversación",
    Emojis: "Emojis", "Anexar documento": "Adjuntar documento", "Clique em Atender para responder": "Haga clic en Atender para responder",
    "Dados da conversa": "Datos de la conversación", "Pesquisar mensagens": "Buscar mensajes", Documento: "Documento",
    "Conversa aberta em": "Conversación abierta el", Atendente: "Agente", "Tags do atendimento": "Etiquetas de atención",
    "Adicionar tag": "Agregar etiqueta", "Resultados encontrados": "Resultados encontrados", Cancelar: "Cancelar",
    "Escolha um cliente para iniciar o atendimento.": "Elija un cliente para iniciar la atención.",
    "Iniciar conversa": "Iniciar conversación", "Pesquisar cliente": "Buscar cliente", "Nome, e-mail ou telefone": "Nombre, correo o teléfono",
    "Mensagem inicial (opcional)": "Mensaje inicial (opcional)", "Digite a primeira mensagem...": "Escriba el primer mensaje...",
    "Não foi possível alterar a disponibilidade.": "No se pudo cambiar la disponibilidad.",
    "Transferir para agente": "Transferir al agente de IA", "Conversa transferida para o agente de IA.": "Conversación transferida al agente de IA.",
    "Não foi possível transferir a conversa para o agente de IA.": "No se pudo transferir la conversación al agente de IA.",
    "A conversa não está em atendimento humano.": "La conversación no está bajo atención humana.", "A conversa não está mais em atendimento humano.": "La conversación ya no está bajo atención humana.", "O agente de IA está desativado.": "El agente de IA está desactivado.",
    "Abrir chamado": "Abrir ticket", "Não foi possível preparar a abertura do chamado.": "No se pudo preparar el formulario del ticket.",
    "Áudio enviado.": "Audio enviado.", "Não foi possível enviar o áudio.": "No se pudo enviar el audio.",
    "Gravar áudio": "Grabar audio", "Parar e enviar áudio": "Detener y enviar audio",
    "Seu navegador não permite gravar áudio.": "Su navegador no permite grabar audio.", "Não foi possível acessar o microfone.": "No se pudo acceder al micrófono.",
    "Seu navegador não permite reproduzir áudio.": "Su navegador no puede reproducir audio.", "Seu navegador não permite reproduzir vídeo.": "Su navegador no puede reproducir video.",
    "Você não tem acesso a este chamado ou tarefa.": "No tiene acceso a este ticket o tarea.",
    "Assumir chamado": "Asumir ticket", "Chamado assumido com sucesso.": "Ticket asumido correctamente.",
    "Não foi possível assumir o chamado.": "No se pudo asumir el ticket.", "Este chamado já foi assumido por outro usuário.": "Este ticket ya fue asumido por otro usuario.",
    "Você não tem permissão para assumir este chamado.": "No tiene permiso para asumir este ticket.",
    IA: "IA", Humano: "Humano", Finalizadas: "Finalizadas", "Indicadores do atendimento": "Indicadores de atención",
    "Conversas Ativas": "Conversaciones activas", "Visão atual": "Vista actual", "Atendidas pela IA": "Atendidas por IA",
    "Transferidas para Humano": "Transferidas a humano", "Conversa selecionada": "Conversación seleccionada",
    Satisfação: "Satisfacción", "Sem avaliações": "Sin valoraciones", "Novo Atendimento": "Nueva atención",
    "Informações do atendimento": "Información de atención", "Selecione uma conversa para visualizar os detalhes": "Seleccione una conversación para ver los detalles",
    "Dados do Cliente": "Datos del cliente", "Canal preferencial": "Canal preferido", "Resumo da IA": "Resumen de IA", Resumo: "Resumen",
    "Intenção detectada": "Intención detectada", "Atendimento humano solicitado": "Atención humana solicitada", "Atendimento em andamento": "Atención en curso",
    Sentimento: "Sentimiento", "Não identificado": "No identificado", "Ver detalhes da conversa": "Ver detalles de la conversación", Roteamento: "Enrutamiento",
    "IA iniciada": "IA iniciada", Triagem: "Clasificación", "Transferido para humano": "Transferido a humano", "Atendente Responsável": "Agente responsable",
    "Sem atendente": "Sin agente", "Responsável pela conversa": "Responsable de la conversación", "Ações rápidas": "Acciones rápidas",
    "Ativar IA": "Activar IA", "Assumir atendimento": "Asumir atención", "Criar ticket": "Crear ticket", "Finalizar atendimento": "Finalizar atención",
    "Sem resumo disponível.": "Sin resumen disponible.",
  },
  "zh-CN": {
    "Com IA": "由 AI 处理", "Aguardando Humano": "等待人工", "Chat Interno": "内部聊天",
    "Transferir para Humano": "转人工", "Responder com sugestão da IA": "使用 AI 建议回复",
    "Ver detalhes da análise": "查看分析详情", "Sugestão de resposta": "回复建议", Prioridade: "优先级", Média: "中",
    "IA atendendo": "AI 处理中", "Aguardando transferência": "等待转接", "Em atendimento humano": "人工服务中", Finalizado: "已完成",
    "Atendimento iniciado pela IA": "由 AI 发起服务", Online: "在线", "Tempo médio de resposta": "平均响应时间", "Atendimentos hoje": "今日服务数",
    "Validar os dados e responder ao cliente com clareza.": "核对数据并清晰回复客户。", Transferir: "转接",
    "Aguardando atendente": "等待客服", "Aguardando aceite": "等待接单", Atender: "接单",
    "Atendimento iniciado.": "人工服务已开始。", "Não foi possível iniciar o atendimento.": "无法开始人工服务。",
    "Documento enviado.": "文档已发送。", "Não foi possível enviar o documento.": "无法发送文档。",
    "Não foi possível adicionar a tag.": "无法添加标签。", "Não foi possível remover a tag.": "无法删除标签。",
    "Não foi possível alterar a cor da tag.": "无法更改标签颜色。", "Cor da tag": "标签颜色", "Alterar cor da tag": "更改标签颜色", "Remover tag": "删除标签",
    "Informe uma cor válida para a tag.": "请输入有效的标签颜色。", "Tag inválida.": "标签无效。", "Tag não encontrada.": "未找到标签。",
    "Não foi possível carregar os clientes.": "无法加载客户。", "Conversa iniciada.": "对话已开始。",
    "Mais opções": "更多选项", "Dados do contato": "联系人信息", "Pesquisar na conversa": "搜索对话",
    Emojis: "表情", "Anexar documento": "附加文档", "Clique em Atender para responder": "点击接单后回复",
    "Dados da conversa": "对话详情", "Pesquisar mensagens": "搜索消息", Documento: "证件/文档",
    "Conversa aberta em": "对话开始时间", Atendente: "客服", "Tags do atendimento": "服务标签",
    "Adicionar tag": "添加标签", "Resultados encontrados": "找到的结果", Cancelar: "取消",
    "Escolha um cliente para iniciar o atendimento.": "选择客户以开始服务。",
    "Iniciar conversa": "开始对话", "Pesquisar cliente": "搜索客户", "Nome, e-mail ou telefone": "姓名、邮箱或电话",
    "Mensagem inicial (opcional)": "初始消息（可选）", "Digite a primeira mensagem...": "输入第一条消息...",
    "Não foi possível alterar a disponibilidade.": "无法更改在线状态。",
    "Transferir para agente": "转交给 AI 助手", "Conversa transferida para o agente de IA.": "对话已转交给 AI 助手。",
    "Não foi possível transferir a conversa para o agente de IA.": "无法将对话转交给 AI 助手。",
    "A conversa não está em atendimento humano.": "该对话当前不在人工服务中。", "A conversa não está mais em atendimento humano.": "该对话已不再处于人工服务中。", "O agente de IA está desativado.": "AI 助手已停用。",
    "Abrir chamado": "创建工单", "Não foi possível preparar a abertura do chamado.": "无法准备工单表单。",
    "Áudio enviado.": "音频已发送。", "Não foi possível enviar o áudio.": "无法发送音频。",
    "Gravar áudio": "录制音频", "Parar e enviar áudio": "停止并发送音频",
    "Seu navegador não permite gravar áudio.": "您的浏览器不支持录制音频。", "Não foi possível acessar o microfone.": "无法访问麦克风。",
    "Seu navegador não permite reproduzir áudio.": "您的浏览器无法播放音频。", "Seu navegador não permite reproduzir vídeo.": "您的浏览器无法播放视频。",
    "Você não tem acesso a este chamado ou tarefa.": "您无权访问此工单或任务。",
    "Assumir chamado": "接手工单", "Chamado assumido com sucesso.": "工单已成功分配给您。",
    "Não foi possível assumir o chamado.": "无法接手工单。", "Este chamado já foi assumido por outro usuário.": "该工单已被其他用户接手。",
    "Você não tem permissão para assumir este chamado.": "您无权接手此工单。",
    IA: "AI", Humano: "人工", Finalizadas: "已完成", "Indicadores do atendimento": "服务指标",
    "Conversas Ativas": "活跃对话", "Visão atual": "当前视图", "Atendidas pela IA": "AI 已处理",
    "Transferidas para Humano": "转人工", "Conversa selecionada": "所选对话",
    Satisfação: "满意度", "Sem avaliações": "暂无评价", "Novo Atendimento": "新建服务",
    "Informações do atendimento": "服务信息", "Selecione uma conversa para visualizar os detalhes": "选择对话以查看详情",
    "Dados do Cliente": "客户信息", "Canal preferencial": "首选渠道", "Resumo da IA": "AI 摘要", Resumo: "摘要",
    "Intenção detectada": "识别意图", "Atendimento humano solicitado": "已请求人工服务", "Atendimento em andamento": "服务进行中",
    Sentimento: "情绪", "Não identificado": "未识别", "Ver detalhes da conversa": "查看对话详情", Roteamento: "路由",
    "IA iniciada": "AI 已启动", Triagem: "分流", "Transferido para humano": "已转人工", "Atendente Responsável": "负责客服",
    "Sem atendente": "暂无客服", "Responsável pela conversa": "对话负责人", "Ações rápidas": "快捷操作",
    "Ativar IA": "启用 AI", "Assumir atendimento": "接手服务", "Criar ticket": "创建工单", "Finalizar atendimento": "结束服务",
    "Sem resumo disponível.": "暂无摘要。",
  },
};

const TEXTOS_AGENTE_IA: Record<Idioma, Dicionario> = {
  pt: {
    ...TEXTOS_CHAT_ATENDIMENTO.pt,
    Chat: "Chat", "Conversas entre clientes, equipe de suporte e agente de IA.": "Conversas entre clientes, equipe de suporte e agente de IA.",
    "Atenda as conversas iniciadas por clientes no aplicativo.": "Atenda as conversas iniciadas por clientes no aplicativo.", "Selecione uma conversa para atender": "Selecione uma conversa para atender", "Conversa iniciada no aplicativo": "Conversa iniciada no aplicativo",
    "Nova conversa": "Nova conversa", Conversas: "Conversas", "Carregando conversas...": "Carregando conversas...",
    "Nenhuma conversa encontrada.": "Nenhuma conversa encontrada.", "Conversa sem mensagens": "Conversa sem mensagens",
    "Selecione ou inicie uma conversa": "Selecione ou inicie uma conversa", "Com a IA": "Com a IA",
    "Aguardando atendimento": "Aguardando atendimento", "Em atendimento": "Em atendimento", Encerrada: "Encerrada",
    "Falar com atendente": "Falar com atendente", Encerrar: "Encerrar", "Agente de IA": "Agente de IA", Sistema: "Sistema", Você: "Você",
    "Digite sua mensagem...": "Digite sua mensagem...", "Enviar mensagem": "Enviar mensagem", "Atendimento humano solicitado.": "Atendimento humano solicitado.",
    "Conversa encerrada.": "Conversa encerrada.", "Não foi possível carregar as conversas.": "Não foi possível carregar as conversas.",
    "Não foi possível abrir a conversa.": "Não foi possível abrir a conversa.", "Não foi possível iniciar a conversa.": "Não foi possível iniciar a conversa.",
    "Não foi possível enviar a mensagem.": "Não foi possível enviar a mensagem.", "Não foi possível solicitar atendimento humano.": "Não foi possível solicitar atendimento humano.",
    "Não foi possível encerrar a conversa.": "Não foi possível encerrar a conversa.",
    "Configuração do agente de IA": "Configuração do agente de IA", "Defina a identidade, o comportamento e as regras de transferência do agente.": "Defina a identidade, o comportamento e as regras de transferência do agente.",
    "Gerenciar base de conhecimento": "Gerenciar base de conhecimento", "Nome do agente": "Nome do agente", Descrição: "Descrição", "Mensagem inicial": "Mensagem inicial",
    "Mensagem de fallback": "Mensagem de fallback", "Permitir atendimento humano": "Permitir atendimento humano", "Agente ativo": "Agente ativo",
    "Configuração do agente salva.": "Configuração do agente salva.", "Não foi possível salvar a configuração.": "Não foi possível salvar a configuração.",
    "Defina o tom de voz, as regras de atendimento e a forma como o agente deve responder. Informações sobre produtos e funcionalidades devem ser cadastradas na Base de conhecimento.": "Defina o tom de voz, as regras de atendimento e a forma como o agente deve responder. Informações sobre produtos e funcionalidades devem ser cadastradas na Base de conhecimento.",
    "Base de conhecimento": "Base de conhecimento", "Cadastre conteúdos confiáveis que o agente pode usar nas respostas.": "Cadastre conteúdos confiáveis que o agente pode usar nas respostas.",
    "Configurar agente": "Configurar agente", "Novo conteúdo": "Novo conteúdo", Pesquisar: "Pesquisar", "Busque por título, categoria ou palavra-chave": "Busque por título, categoria ou palavra-chave",
    Título: "Título", Categoria: "Categoria", Produto: "Produto", Ações: "Ações", "Todos os produtos": "Todos os produtos", Ativo: "Ativo", Inativo: "Inativo",
    "Nenhum conteúdo cadastrado.": "Nenhum conteúdo cadastrado.", "Editar conteúdo": "Editar conteúdo", "Informações factuais usadas pelo agente de IA.": "Informações factuais usadas pelo agente de IA.",
    "Produto relacionado": "Produto relacionado", Fonte: "Fonte", "Palavras-chave": "Palavras-chave", "Separe as palavras-chave por vírgulas": "Separe as palavras-chave por vírgulas", Conteúdo: "Conteúdo", "Conteúdo ativo": "Conteúdo ativo",
    "Excluir conteúdo": "Excluir conteúdo", "O item será removido logicamente e não poderá mais ser usado pelo agente.": "O item será removido logicamente e não poderá mais ser usado pelo agente.",
    "Base de conhecimento salva.": "Base de conhecimento salva.", "Item removido da base de conhecimento.": "Item removido da base de conhecimento.",
    "Não foi possível carregar a base de conhecimento.": "Não foi possível carregar a base de conhecimento.", "Não foi possível salvar a base de conhecimento.": "Não foi possível salvar a base de conhecimento.", "Não foi possível remover o item.": "Não foi possível remover o item.",
    "Informe o título e o conteúdo da base de conhecimento.": "Informe o título e o conteúdo da base de conhecimento.",
    "Agentes por organização": "Agentes por organização", "Configure a identidade e o comportamento do agente de cada organização cadastrada.": "Configure a identidade e o comportamento do agente de cada organização cadastrada.",
    "Agentes por empresa": "Agentes por empresa", "Configure separadamente os agentes de Vendas, Tickets e Produção de cada empresa da organização.": "Configure separadamente os agentes de Vendas, Tickets e Produção de cada empresa da organização.",
    "Configuração do agente da empresa": "Configuração do agente da empresa", "Edite a identidade e o comportamento deste canal de atendimento.": "Edite a identidade e o comportamento deste canal de atendimento.", "Nenhuma empresa encontrada.": "Nenhuma empresa encontrada.",
    "Configuração do agente salva para a empresa.": "Configuração do agente salva para a empresa.", "Não foi possível carregar os agentes das empresas.": "Não foi possível carregar os agentes das empresas.", "Não foi possível carregar o agente da empresa.": "Não foi possível carregar o agente da empresa.", "Não foi possível salvar o agente da empresa.": "Não foi possível salvar o agente da empresa.",
    Organização: "Organização", Agente: "Agente", "Atendimento humano": "Atendimento humano", Configuração: "Configuração", Habilitado: "Habilitado", Desabilitado: "Desabilitado", Configurado: "Configurado", "Não configurado": "Não configurado", "Editar agente": "Editar agente",
    "Configuração do agente da organização": "Configuração do agente da organização", "Edite os dados que serão usados nas conversas desta organização.": "Edite os dados que serão usados nas conversas desta organização.", "Organização selecionada": "Organização selecionada",
    "Configuração do agente salva para a organização.": "Configuração do agente salva para a organização.", "Não foi possível carregar os agentes das organizações.": "Não foi possível carregar os agentes das organizações.", "Não foi possível carregar o agente da organização.": "Não foi possível carregar o agente da organização.", "Não foi possível salvar o agente da organização.": "Não foi possível salvar o agente da organização.",
    "Preencha o nome, a mensagem inicial e a mensagem de fallback.": "Preencha o nome, a mensagem inicial e a mensagem de fallback.", "Nenhuma organização encontrada.": "Nenhuma organização encontrada.", "Carregando...": "Carregando...",
    assistantLoadError: "Não foi possível carregar o agente de IA.", assistantSendError: "Não foi possível enviar a mensagem.", humanSupportRequested: "Atendimento humano solicitado.", humanSupportRequestError: "Não foi possível solicitar atendimento humano.", sendMessage: "Enviar mensagem", talkToHuman: "Falar com atendente", waitingHumanSupport: "Aguardando atendimento humano.",
  },
  en: {
    ...TEXTOS_CHAT_ATENDIMENTO.en,
    Chat: "Chat", "Conversas entre clientes, equipe de suporte e agente de IA.": "Conversations between customers, support staff, and the AI agent.",
    "Atenda as conversas iniciadas por clientes no aplicativo.": "Handle conversations started by customers in the app.", "Selecione uma conversa para atender": "Select a conversation to handle", "Conversa iniciada no aplicativo": "Conversation started in the app",
    "Nova conversa": "New conversation", Conversas: "Conversations", "Carregando conversas...": "Loading conversations...", "Nenhuma conversa encontrada.": "No conversations found.", "Conversa sem mensagens": "Conversation without messages", "Selecione ou inicie uma conversa": "Select or start a conversation",
    "Com a IA": "With AI", "Aguardando atendimento": "Waiting for support", "Em atendimento": "In progress", Encerrada: "Closed", "Falar com atendente": "Talk to support", Encerrar: "Close", "Agente de IA": "AI agent", Sistema: "System", Você: "You", "Digite sua mensagem...": "Type your message...", "Enviar mensagem": "Send message",
    "Atendimento humano solicitado.": "Human support requested.", "Conversa encerrada.": "Conversation closed.", "Não foi possível carregar as conversas.": "Could not load conversations.", "Não foi possível abrir a conversa.": "Could not open the conversation.", "Não foi possível iniciar a conversa.": "Could not start the conversation.", "Não foi possível enviar a mensagem.": "Could not send the message.", "Não foi possível solicitar atendimento humano.": "Could not request human support.", "Não foi possível encerrar a conversa.": "Could not close the conversation.",
    "Configuração do agente de IA": "AI agent configuration", "Defina a identidade, o comportamento e as regras de transferência do agente.": "Define the agent identity, behavior, and handoff rules.", "Gerenciar base de conhecimento": "Manage knowledge base", "Nome do agente": "Agent name", Descrição: "Description", "Mensagem inicial": "Initial message", "Mensagem de fallback": "Fallback message", "Permitir atendimento humano": "Allow human support", "Agente ativo": "Agent enabled", "Configuração do agente salva.": "Agent configuration saved.", "Não foi possível salvar a configuração.": "Could not save the configuration.",
    "Defina o tom de voz, as regras de atendimento e a forma como o agente deve responder. Informações sobre produtos e funcionalidades devem ser cadastradas na Base de conhecimento.": "Define the tone of voice, service rules, and how the agent should respond. Product and feature information must be registered in the Knowledge Base.",
    "Base de conhecimento": "Knowledge base", "Cadastre conteúdos confiáveis que o agente pode usar nas respostas.": "Register reliable content the agent can use in its answers.", "Configurar agente": "Configure agent", "Novo conteúdo": "New content", Pesquisar: "Search", "Busque por título, categoria ou palavra-chave": "Search by title, category, or keyword", Título: "Title", Categoria: "Category", Produto: "Product", Ações: "Actions", "Todos os produtos": "All products", Ativo: "Active", Inativo: "Inactive", "Nenhum conteúdo cadastrado.": "No content registered.", "Editar conteúdo": "Edit content", "Informações factuais usadas pelo agente de IA.": "Factual information used by the AI agent.", "Produto relacionado": "Related product", Fonte: "Source", "Palavras-chave": "Keywords", "Separe as palavras-chave por vírgulas": "Separate keywords with commas", Conteúdo: "Content", "Conteúdo ativo": "Content enabled", "Excluir conteúdo": "Delete content", "O item será removido logicamente e não poderá mais ser usado pelo agente.": "The item will be logically deleted and can no longer be used by the agent.", "Base de conhecimento salva.": "Knowledge base saved.", "Item removido da base de conhecimento.": "Item removed from the knowledge base.", "Não foi possível carregar a base de conhecimento.": "Could not load the knowledge base.", "Não foi possível salvar a base de conhecimento.": "Could not save the knowledge base.", "Não foi possível remover o item.": "Could not remove the item.", "Informe o título e o conteúdo da base de conhecimento.": "Enter the knowledge base title and content.",
    "Agentes por organização": "Agents by organization", "Configure a identidade e o comportamento do agente de cada organização cadastrada.": "Configure the identity and behavior of the agent for each registered organization.",
    "Agentes por empresa": "Agents by company", "Configure separadamente os agentes de Vendas, Tickets e Produção de cada empresa da organização.": "Configure separate Sales, Ticket, and Production agents for each company in the organization.",
    "Configuração do agente da empresa": "Company agent configuration", "Edite a identidade e o comportamento deste canal de atendimento.": "Edit the identity and behavior for this service channel.", "Nenhuma empresa encontrada.": "No company found.",
    "Configuração do agente salva para a empresa.": "Company agent configuration saved.", "Não foi possível carregar os agentes das empresas.": "Could not load company agents.", "Não foi possível carregar o agente da empresa.": "Could not load the company agent.", "Não foi possível salvar o agente da empresa.": "Could not save the company agent.",
    Organização: "Organization", Agente: "Agent", "Atendimento humano": "Human support", Configuração: "Configuration", Habilitado: "Enabled", Desabilitado: "Disabled", Configurado: "Configured", "Não configurado": "Not configured", "Editar agente": "Edit agent",
    "Configuração do agente da organização": "Organization agent configuration", "Edite os dados que serão usados nas conversas desta organização.": "Edit the data used in this organization's conversations.", "Organização selecionada": "Selected organization",
    "Configuração do agente salva para a organização.": "Agent configuration saved for the organization.", "Não foi possível carregar os agentes das organizações.": "Could not load organization agents.", "Não foi possível carregar o agente da organização.": "Could not load the organization's agent.", "Não foi possível salvar o agente da organização.": "Could not save the organization's agent.",
    "Preencha o nome, a mensagem inicial e a mensagem de fallback.": "Enter the name, initial message, and fallback message.", "Nenhuma organização encontrada.": "No organization found.", "Carregando...": "Loading...",
    assistantLoadError: "Could not load the AI agent.", assistantSendError: "Could not send the message.", humanSupportRequested: "Human support requested.", humanSupportRequestError: "Could not request human support.", sendMessage: "Send message", talkToHuman: "Talk to support", waitingHumanSupport: "Waiting for human support.",
  },
  es: {
    ...TEXTOS_CHAT_ATENDIMENTO.es,
    Chat: "Chat", "Conversas entre clientes, equipe de suporte e agente de IA.": "Conversaciones entre clientes, soporte y el agente de IA.", "Atenda as conversas iniciadas por clientes no aplicativo.": "Atienda las conversaciones iniciadas por clientes en la aplicación.", "Selecione uma conversa para atender": "Seleccione una conversación para atender", "Conversa iniciada no aplicativo": "Conversación iniciada en la aplicación", "Nova conversa": "Nueva conversación", Conversas: "Conversaciones", "Carregando conversas...": "Cargando conversaciones...", "Nenhuma conversa encontrada.": "No se encontraron conversaciones.", "Conversa sem mensagens": "Conversación sin mensajes", "Selecione ou inicie uma conversa": "Seleccione o inicie una conversación", "Com a IA": "Con la IA", "Aguardando atendimento": "Esperando atención", "Em atendimento": "En atención", Encerrada: "Cerrada", "Falar com atendente": "Hablar con un agente", Encerrar: "Cerrar", "Agente de IA": "Agente de IA", Sistema: "Sistema", Você: "Usted", "Digite sua mensagem...": "Escriba su mensaje...", "Enviar mensagem": "Enviar mensaje", "Atendimento humano solicitado.": "Atención humana solicitada.", "Conversa encerrada.": "Conversación cerrada.", "Não foi possível carregar as conversas.": "No se pudieron cargar las conversaciones.", "Não foi possível abrir a conversa.": "No se pudo abrir la conversación.", "Não foi possível iniciar a conversa.": "No se pudo iniciar la conversación.", "Não foi possível enviar a mensagem.": "No se pudo enviar el mensaje.", "Não foi possível solicitar atendimento humano.": "No se pudo solicitar atención humana.", "Não foi possível encerrar a conversa.": "No se pudo cerrar la conversación.",
    "Configuração do agente de IA": "Configuración del agente de IA", "Defina a identidade, o comportamento e as regras de transferência do agente.": "Defina la identidad, el comportamiento y las reglas de transferencia del agente.", "Gerenciar base de conhecimento": "Gestionar base de conocimiento", "Nome do agente": "Nombre del agente", Descrição: "Descripción", "Mensagem inicial": "Mensaje inicial", "Mensagem de fallback": "Mensaje de respaldo", "Permitir atendimento humano": "Permitir atención humana", "Agente ativo": "Agente activo", "Configuração do agente salva.": "Configuración del agente guardada.", "Não foi possível salvar a configuração.": "No se pudo guardar la configuración.", "Defina o tom de voz, as regras de atendimento e a forma como o agente deve responder. Informações sobre produtos e funcionalidades devem ser cadastradas na Base de conhecimento.": "Defina el tono de voz, las reglas de atención y cómo debe responder el agente. La información sobre productos y funciones debe registrarse en la Base de conocimiento.",
    "Base de conhecimento": "Base de conocimiento", "Cadastre conteúdos confiáveis que o agente pode usar nas respostas.": "Registre contenidos confiables que el agente pueda usar en sus respuestas.", "Configurar agente": "Configurar agente", "Novo conteúdo": "Nuevo contenido", Pesquisar: "Buscar", "Busque por título, categoria ou palavra-chave": "Busque por título, categoría o palabra clave", Título: "Título", Categoria: "Categoría", Produto: "Producto", Ações: "Acciones", "Todos os produtos": "Todos los productos", Ativo: "Activo", Inativo: "Inactivo", "Nenhum conteúdo cadastrado.": "No hay contenido registrado.", "Editar conteúdo": "Editar contenido", "Informações factuais usadas pelo agente de IA.": "Información factual utilizada por el agente de IA.", "Produto relacionado": "Producto relacionado", Fonte: "Fuente", "Palavras-chave": "Palabras clave", "Separe as palavras-chave por vírgulas": "Separe las palabras clave con comas", Conteúdo: "Contenido", "Conteúdo ativo": "Contenido activo", "Excluir conteúdo": "Eliminar contenido", "O item será removido logicamente e não poderá mais ser usado pelo agente.": "El elemento se eliminará de forma lógica y el agente ya no podrá usarlo.", "Base de conhecimento salva.": "Base de conocimiento guardada.", "Item removido da base de conhecimento.": "Elemento eliminado de la base de conocimiento.", "Não foi possível carregar a base de conhecimento.": "No se pudo cargar la base de conocimiento.", "Não foi possível salvar a base de conhecimento.": "No se pudo guardar la base de conocimiento.", "Não foi possível remover o item.": "No se pudo eliminar el elemento.", "Informe o título e o conteúdo da base de conhecimento.": "Ingrese el título y el contenido de la base de conocimiento.",
    "Agentes por organização": "Agentes por organización", "Configure a identidade e o comportamento do agente de cada organização cadastrada.": "Configure la identidad y el comportamiento del agente de cada organización registrada.",
    "Agentes por empresa": "Agentes por empresa", "Configure separadamente os agentes de Vendas, Tickets e Produção de cada empresa da organização.": "Configure por separado los agentes de Ventas, Tickets y Producción de cada empresa de la organización.",
    "Configuração do agente da empresa": "Configuración del agente de la empresa", "Edite a identidade e o comportamento deste canal de atendimento.": "Edite la identidad y el comportamiento de este canal de atención.", "Nenhuma empresa encontrada.": "No se encontró ninguna empresa.",
    "Configuração do agente salva para a empresa.": "Configuración del agente de la empresa guardada.", "Não foi possível carregar os agentes das empresas.": "No se pudieron cargar los agentes de las empresas.", "Não foi possível carregar o agente da empresa.": "No se pudo cargar el agente de la empresa.", "Não foi possível salvar o agente da empresa.": "No se pudo guardar el agente de la empresa.",
    Organização: "Organización", Agente: "Agente", "Atendimento humano": "Atención humana", Configuração: "Configuración", Habilitado: "Habilitado", Desabilitado: "Deshabilitado", Configurado: "Configurado", "Não configurado": "No configurado", "Editar agente": "Editar agente",
    "Configuração do agente da organização": "Configuración del agente de la organización", "Edite os dados que serão usados nas conversas desta organização.": "Edite los datos utilizados en las conversaciones de esta organización.", "Organização selecionada": "Organización seleccionada",
    "Configuração do agente salva para a organização.": "Configuración del agente guardada para la organización.", "Não foi possível carregar os agentes das organizações.": "No se pudieron cargar los agentes de las organizaciones.", "Não foi possível carregar o agente da organização.": "No se pudo cargar el agente de la organización.", "Não foi possível salvar o agente da organização.": "No se pudo guardar el agente de la organización.",
    "Preencha o nome, a mensagem inicial e a mensagem de fallback.": "Complete el nombre, el mensaje inicial y el mensaje de respaldo.", "Nenhuma organização encontrada.": "No se encontró ninguna organización.", "Carregando...": "Cargando...",
    assistantLoadError: "No se pudo cargar el agente de IA.", assistantSendError: "No se pudo enviar el mensaje.", humanSupportRequested: "Atención humana solicitada.", humanSupportRequestError: "No se pudo solicitar atención humana.", sendMessage: "Enviar mensaje", talkToHuman: "Hablar con un agente", waitingHumanSupport: "Esperando atención humana.",
  },
  "zh-CN": {
    ...TEXTOS_CHAT_ATENDIMENTO["zh-CN"],
    Chat: "聊天", "Conversas entre clientes, equipe de suporte e agente de IA.": "客户、支持团队与 AI 助手之间的对话。", "Atenda as conversas iniciadas por clientes no aplicativo.": "处理客户在应用中发起的对话。", "Selecione uma conversa para atender": "选择要处理的对话", "Conversa iniciada no aplicativo": "在应用中发起的对话", "Nova conversa": "新对话", Conversas: "对话", "Carregando conversas...": "正在加载对话…", "Nenhuma conversa encontrada.": "未找到对话。", "Conversa sem mensagens": "暂无消息", "Selecione ou inicie uma conversa": "选择或开始一个对话", "Com a IA": "AI 对话", "Aguardando atendimento": "等待人工服务", "Em atendimento": "处理中", Encerrada: "已结束", "Falar com atendente": "联系人工客服", Encerrar: "结束", "Agente de IA": "AI 助手", Sistema: "系统", Você: "您", "Digite sua mensagem...": "输入消息…", "Enviar mensagem": "发送消息", "Atendimento humano solicitado.": "已请求人工服务。", "Conversa encerrada.": "对话已结束。", "Não foi possível carregar as conversas.": "无法加载对话。", "Não foi possível abrir a conversa.": "无法打开对话。", "Não foi possível iniciar a conversa.": "无法开始对话。", "Não foi possível enviar a mensagem.": "无法发送消息。", "Não foi possível solicitar atendimento humano.": "无法请求人工服务。", "Não foi possível encerrar a conversa.": "无法结束对话。",
    "Configuração do agente de IA": "AI 助手配置", "Defina a identidade, o comportamento e as regras de transferência do agente.": "定义助手身份、行为和人工转接规则。", "Gerenciar base de conhecimento": "管理知识库", "Nome do agente": "助手名称", Descrição: "描述", "Mensagem inicial": "欢迎消息", "Mensagem de fallback": "回退消息", "Permitir atendimento humano": "允许人工服务", "Agente ativo": "启用助手", "Configuração do agente salva.": "助手配置已保存。", "Não foi possível salvar a configuração.": "无法保存配置。", "Defina o tom de voz, as regras de atendimento e a forma como o agente deve responder. Informações sobre produtos e funcionalidades devem ser cadastradas na Base de conhecimento.": "定义语气、服务规则和助手的回答方式。产品及功能信息应录入知识库。",
    "Base de conhecimento": "知识库", "Cadastre conteúdos confiáveis que o agente pode usar nas respostas.": "录入可供助手回答时使用的可信内容。", "Configurar agente": "配置助手", "Novo conteúdo": "新建内容", Pesquisar: "搜索", "Busque por título, categoria ou palavra-chave": "按标题、类别或关键词搜索", Título: "标题", Categoria: "类别", Produto: "产品", Ações: "操作", "Todos os produtos": "所有产品", Ativo: "启用", Inativo: "停用", "Nenhum conteúdo cadastrado.": "暂无内容。", "Editar conteúdo": "编辑内容", "Informações factuais usadas pelo agente de IA.": "AI 助手使用的事实信息。", "Produto relacionado": "相关产品", Fonte: "来源", "Palavras-chave": "关键词", "Separe as palavras-chave por vírgulas": "请用逗号分隔关键词", Conteúdo: "内容", "Conteúdo ativo": "启用内容", "Excluir conteúdo": "删除内容", "O item será removido logicamente e não poderá mais ser usado pelo agente.": "该内容将被逻辑删除，助手将无法继续使用。", "Base de conhecimento salva.": "知识库已保存。", "Item removido da base de conhecimento.": "内容已从知识库移除。", "Não foi possível carregar a base de conhecimento.": "无法加载知识库。", "Não foi possível salvar a base de conhecimento.": "无法保存知识库。", "Não foi possível remover o item.": "无法移除该内容。", "Informe o título e o conteúdo da base de conhecimento.": "请输入知识库标题和内容。",
    "Agentes por organização": "按组织配置助手", "Configure a identidade e o comportamento do agente de cada organização cadastrada.": "为每个已注册组织配置助手的身份和行为。",
    "Agentes por empresa": "按公司配置助手", "Configure separadamente os agentes de Vendas, Tickets e Produção de cada empresa da organização.": "为组织内每家公司分别配置销售、工单和生产助手。",
    "Configuração do agente da empresa": "公司助手配置", "Edite a identidade e o comportamento deste canal de atendimento.": "编辑此服务渠道的助手身份和行为。", "Nenhuma empresa encontrada.": "未找到公司。",
    "Configuração do agente salva para a empresa.": "已保存公司助手配置。", "Não foi possível carregar os agentes das empresas.": "无法加载公司助手。", "Não foi possível carregar o agente da empresa.": "无法加载公司助手。", "Não foi possível salvar o agente da empresa.": "无法保存公司助手。",
    Organização: "组织", Agente: "助手", "Atendimento humano": "人工服务", Configuração: "配置", Habilitado: "已启用", Desabilitado: "已禁用", Configurado: "已配置", "Não configurado": "未配置", "Editar agente": "编辑助手",
    "Configuração do agente da organização": "组织助手配置", "Edite os dados que serão usados nas conversas desta organização.": "编辑该组织对话中使用的数据。", "Organização selecionada": "已选组织",
    "Configuração do agente salva para a organização.": "已保存该组织的助手配置。", "Não foi possível carregar os agentes das organizações.": "无法加载各组织的助手。", "Não foi possível carregar o agente da organização.": "无法加载该组织的助手。", "Não foi possível salvar o agente da organização.": "无法保存该组织的助手。",
    "Preencha o nome, a mensagem inicial e a mensagem de fallback.": "请填写名称、欢迎消息和回退消息。", "Nenhuma organização encontrada.": "未找到组织。", "Carregando...": "正在加载…",
    assistantLoadError: "无法加载 AI 助手。", assistantSendError: "无法发送消息。", humanSupportRequested: "已请求人工服务。", humanSupportRequestError: "无法请求人工服务。", sendMessage: "发送消息", talkToHuman: "联系人工客服", waitingHumanSupport: "正在等待人工服务。",
  },
};

const TEXTOS_CADASTRO_LOGIN: Record<Idioma, Dicionario> = {
  pt: {
    "E-mail de boas-vindas": "E-mail de boas-vindas",
    "Enviar e-mail de boas-vindas": "Enviar e-mail de boas-vindas",
    "Envie as credenciais de acesso para o e-mail cadastrado.": "Envie as credenciais de acesso para o e-mail cadastrado.",
    "Selecione apenas um login para enviar o e-mail de boas-vindas.": "Selecione apenas um login para enviar o e-mail de boas-vindas.",
    "A senha atual será substituída por uma senha temporária e enviada para": "A senha atual será substituída por uma senha temporária e enviada para",
    "O usuário deverá usar a nova senha no próximo acesso.": "O usuário deverá usar a nova senha no próximo acesso.",
    "Gerar senha e enviar": "Gerar senha e enviar",
    "Gerar senha automática": "Gerar senha automática",
    "E-mail de boas-vindas enviado com sucesso.": "E-mail de boas-vindas enviado com sucesso.",
    "Não foi possível enviar o e-mail de boas-vindas.": "Não foi possível enviar o e-mail de boas-vindas.",
    "Seu login não tem permissão para enviar credenciais de acesso.": "Seu login não tem permissão para enviar credenciais de acesso.",
    "O login selecionado não possui e-mail cadastrado.": "O login selecionado não possui e-mail cadastrado.",
    "Apenas o login Master pode enviar credenciais de suporte.": "Apenas o login Master pode enviar credenciais de suporte.",
    "O login de suporte selecionado não possui e-mail cadastrado.": "O login de suporte selecionado não possui e-mail cadastrado.",
  },
  en: {
    "E-mail de boas-vindas": "Welcome email",
    "Enviar e-mail de boas-vindas": "Send welcome email",
    "Envie as credenciais de acesso para o e-mail cadastrado.": "Send the access credentials to the registered email address.",
    "Selecione apenas um login para enviar o e-mail de boas-vindas.": "Select only one login to send the welcome email.",
    "A senha atual será substituída por uma senha temporária e enviada para": "The current password will be replaced with a temporary password and sent to",
    "O usuário deverá usar a nova senha no próximo acesso.": "The user must use the new password on the next sign-in.",
    "Gerar senha e enviar": "Generate password and send",
    "Gerar senha automática": "Generate password automatically",
    "E-mail de boas-vindas enviado com sucesso.": "Welcome email sent successfully.",
    "Não foi possível enviar o e-mail de boas-vindas.": "Could not send the welcome email.",
    "Seu login não tem permissão para enviar credenciais de acesso.": "Your login does not have permission to send access credentials.",
    "O login selecionado não possui e-mail cadastrado.": "The selected login does not have a registered email address.",
    "Apenas o login Master pode enviar credenciais de suporte.": "Only the Master login can send support access credentials.",
    "O login de suporte selecionado não possui e-mail cadastrado.": "The selected support login does not have a registered email address.",
  },
  es: {
    "E-mail de boas-vindas": "Correo de bienvenida",
    "Enviar e-mail de boas-vindas": "Enviar correo de bienvenida",
    "Envie as credenciais de acesso para o e-mail cadastrado.": "Envíe las credenciales de acceso al correo registrado.",
    "Selecione apenas um login para enviar o e-mail de boas-vindas.": "Seleccione un solo usuario para enviar el correo de bienvenida.",
    "A senha atual será substituída por uma senha temporária e enviada para": "La contraseña actual será reemplazada por una contraseña temporal y enviada a",
    "O usuário deverá usar a nova senha no próximo acesso.": "El usuario deberá usar la nueva contraseña en el próximo acceso.",
    "Gerar senha e enviar": "Generar contraseña y enviar",
    "Gerar senha automática": "Generar contraseña automáticamente",
    "E-mail de boas-vindas enviado com sucesso.": "Correo de bienvenida enviado correctamente.",
    "Não foi possível enviar o e-mail de boas-vindas.": "No se pudo enviar el correo de bienvenida.",
    "Seu login não tem permissão para enviar credenciais de acesso.": "Su usuario no tiene permiso para enviar credenciales de acceso.",
    "O login selecionado não possui e-mail cadastrado.": "El usuario seleccionado no tiene un correo registrado.",
    "Apenas o login Master pode enviar credenciais de suporte.": "Solo el usuario Master puede enviar credenciales de soporte.",
    "O login de suporte selecionado não possui e-mail cadastrado.": "El usuario de soporte seleccionado no tiene un correo registrado.",
  },
  "zh-CN": {
    "E-mail de boas-vindas": "欢迎邮件",
    "Enviar e-mail de boas-vindas": "发送欢迎邮件",
    "Envie as credenciais de acesso para o e-mail cadastrado.": "将访问凭据发送到已登记的电子邮箱。",
    "Selecione apenas um login para enviar o e-mail de boas-vindas.": "请选择一个登录账号发送欢迎邮件。",
    "A senha atual será substituída por uma senha temporária e enviada para": "当前密码将替换为临时密码并发送至",
    "O usuário deverá usar a nova senha no próximo acesso.": "用户下次登录时必须使用新密码。",
    "Gerar senha e enviar": "生成密码并发送",
    "Gerar senha automática": "自动生成密码",
    "E-mail de boas-vindas enviado com sucesso.": "欢迎邮件已成功发送。",
    "Não foi possível enviar o e-mail de boas-vindas.": "无法发送欢迎邮件。",
    "Seu login não tem permissão para enviar credenciais de acesso.": "您的登录账号无权发送访问凭据。",
    "O login selecionado não possui e-mail cadastrado.": "所选登录账号未登记电子邮箱。",
    "Apenas o login Master pode enviar credenciais de suporte.": "只有 Master 登录账号可以发送支持访问凭据。",
    "O login de suporte selecionado não possui e-mail cadastrado.": "所选支持登录账号未登记电子邮箱。",
  },
};

const TEXTOS_ERROS_CADASTRO: Record<Idioma, Dicionario> = {
  pt: {
    "Erro interno do servidor.": "Não foi possível concluir a operação. Revise os dados informados e tente novamente.",
    "Imagem selecionada. Salve as alterações para confirmar.": "Imagem selecionada. Salve as alterações para confirmar.",
    "Um dos campos excede o tamanho permitido.": "Um dos campos excede o tamanho permitido.",
    "Um dos vínculos selecionados não existe mais. Atualize a página e selecione novamente.": "Um dos vínculos selecionados não existe mais. Atualize a página e selecione novamente.",
    "Este cadastro está sendo usado em outros registros e não pode ser excluído.": "Este cadastro está sendo usado em outros registros e não pode ser excluído.",
    "Um dos valores informados não atende às regras do cadastro.": "Um dos valores informados não atende às regras do cadastro.",
    "O cadastro está sendo atualizado por outra operação. Aguarde um instante e tente novamente.": "O cadastro está sendo atualizado por outra operação. Aguarde um instante e tente novamente.",
    "O arquivo ou os dados enviados excedem o tamanho permitido.": "O arquivo ou os dados enviados excedem o tamanho permitido.",
    "Não foi possível acessar os dados neste momento. Aguarde alguns instantes e tente novamente.": "Não foi possível acessar os dados neste momento. Aguarde alguns instantes e tente novamente.",
    "Não foi possível salvar as alterações. Revise os dados informados e tente novamente.": "Não foi possível salvar as alterações. Revise os dados informados e tente novamente.",
    "Não foi possível excluir o cadastro. Verifique se ele está sendo usado e tente novamente.": "Não foi possível excluir o cadastro. Verifique se ele está sendo usado e tente novamente.",
    "Não foi possível importar os dados. Revise o arquivo e os campos obrigatórios.": "Não foi possível importar os dados. Revise o arquivo e os campos obrigatórios.",
    "Não foi possível enviar os dados. Revise as informações e tente novamente.": "Não foi possível enviar os dados. Revise as informações e tente novamente.",
    "Não foi possível concluir a operação. Revise os dados informados e tente novamente.": "Não foi possível concluir a operação. Revise os dados informados e tente novamente.",
  },
  en: {
    "Erro interno do servidor.": "The operation could not be completed. Review the entered data and try again.",
    "Imagem selecionada. Salve as alterações para confirmar.": "Image selected. Save the changes to confirm.",
    "Um dos campos excede o tamanho permitido.": "One of the fields exceeds the allowed length.",
    "Um dos vínculos selecionados não existe mais. Atualize a página e selecione novamente.": "One of the selected links no longer exists. Refresh the page and select it again.",
    "Este cadastro está sendo usado em outros registros e não pode ser excluído.": "This record is used by other records and cannot be deleted.",
    "Um dos valores informados não atende às regras do cadastro.": "One of the values does not meet the record rules.",
    "O cadastro está sendo atualizado por outra operação. Aguarde um instante e tente novamente.": "This record is being updated by another operation. Wait a moment and try again.",
    "O arquivo ou os dados enviados excedem o tamanho permitido.": "The uploaded file or data exceeds the allowed size.",
    "Não foi possível acessar os dados neste momento. Aguarde alguns instantes e tente novamente.": "The data is temporarily unavailable. Wait a moment and try again.",
    "Não foi possível salvar as alterações. Revise os dados informados e tente novamente.": "The changes could not be saved. Review the entered data and try again.",
    "Não foi possível excluir o cadastro. Verifique se ele está sendo usado e tente novamente.": "The record could not be deleted. Check whether it is in use and try again.",
    "Não foi possível importar os dados. Revise o arquivo e os campos obrigatórios.": "The data could not be imported. Review the file and required fields.",
    "Não foi possível enviar os dados. Revise as informações e tente novamente.": "The data could not be sent. Review the information and try again.",
    "Não foi possível concluir a operação. Revise os dados informados e tente novamente.": "The operation could not be completed. Review the entered data and try again.",
  },
  es: {
    "Erro interno do servidor.": "No se pudo completar la operación. Revise los datos e inténtelo de nuevo.",
    "Imagem selecionada. Salve as alterações para confirmar.": "Imagen seleccionada. Guarde los cambios para confirmar.",
    "Um dos campos excede o tamanho permitido.": "Uno de los campos supera la longitud permitida.",
    "Um dos vínculos selecionados não existe mais. Atualize a página e selecione novamente.": "Uno de los vínculos seleccionados ya no existe. Actualice la página y selecciónelo nuevamente.",
    "Este cadastro está sendo usado em outros registros e não pode ser excluído.": "Este registro está siendo utilizado por otros registros y no puede eliminarse.",
    "Um dos valores informados não atende às regras do cadastro.": "Uno de los valores no cumple las reglas del registro.",
    "O cadastro está sendo atualizado por outra operação. Aguarde um instante e tente novamente.": "Otra operación está actualizando este registro. Espere un momento e inténtelo de nuevo.",
    "O arquivo ou os dados enviados excedem o tamanho permitido.": "El archivo o los datos enviados superan el tamaño permitido.",
    "Não foi possível acessar os dados neste momento. Aguarde alguns instantes e tente novamente.": "Los datos no están disponibles en este momento. Espere unos instantes e inténtelo de nuevo.",
    "Não foi possível salvar as alterações. Revise os dados informados e tente novamente.": "No se pudieron guardar los cambios. Revise los datos e inténtelo de nuevo.",
    "Não foi possível excluir o cadastro. Verifique se ele está sendo usado e tente novamente.": "No se pudo eliminar el registro. Verifique si está en uso e inténtelo de nuevo.",
    "Não foi possível importar os dados. Revise o arquivo e os campos obrigatórios.": "No se pudieron importar los datos. Revise el archivo y los campos obligatorios.",
    "Não foi possível enviar os dados. Revise as informações e tente novamente.": "No se pudieron enviar los datos. Revise la información e inténtelo de nuevo.",
    "Não foi possível concluir a operação. Revise os dados informados e tente novamente.": "No se pudo completar la operación. Revise los datos e inténtelo de nuevo.",
  },
  "zh-CN": {
    "Erro interno do servidor.": "无法完成操作。请检查输入的数据后重试。",
    "Imagem selecionada. Salve as alterações para confirmar.": "已选择图片。请保存更改以确认。",
    "Um dos campos excede o tamanho permitido.": "某个字段超过允许的长度。",
    "Um dos vínculos selecionados não existe mais. Atualize a página e selecione novamente.": "所选关联项已不存在。请刷新页面后重新选择。",
    "Este cadastro está sendo usado em outros registros e não pode ser excluído.": "该记录正被其他记录使用，无法删除。",
    "Um dos valores informados não atende às regras do cadastro.": "某个输入值不符合记录规则。",
    "O cadastro está sendo atualizado por outra operação. Aguarde um instante e tente novamente.": "另一项操作正在更新此记录。请稍后重试。",
    "O arquivo ou os dados enviados excedem o tamanho permitido.": "上传的文件或数据超过允许的大小。",
    "Não foi possível acessar os dados neste momento. Aguarde alguns instantes e tente novamente.": "暂时无法访问数据。请稍后重试。",
    "Não foi possível salvar as alterações. Revise os dados informados e tente novamente.": "无法保存更改。请检查输入的数据后重试。",
    "Não foi possível excluir o cadastro. Verifique se ele está sendo usado e tente novamente.": "无法删除该记录。请检查它是否正在使用后重试。",
    "Não foi possível importar os dados. Revise o arquivo e os campos obrigatórios.": "无法导入数据。请检查文件和必填字段。",
    "Não foi possível enviar os dados. Revise as informações e tente novamente.": "无法发送数据。请检查信息后重试。",
    "Não foi possível concluir a operação. Revise os dados informados e tente novamente.": "无法完成操作。请检查输入的数据后重试。",
  },
};

const TEXTOS_VENDAS_CREDIARIO: Record<Idioma, Dicionario> = {
  pt: {
    "Crediário": "Crediário",
    "Cartão": "Cartão",
    "Transferência": "Transferência",
    "Forma de pagamento": "Forma de pagamento",
    "As parcelas do crediário ficarão pendentes até a baixa de cada pagamento.": "As parcelas do crediário ficarão pendentes até a baixa de cada pagamento.",
    "As parcelas do cartão de crédito serão baixadas automaticamente.": "As parcelas do cartão de crédito serão baixadas automaticamente.",
    "Selecione um cliente com e-mail cadastrado para usar o crediário.": "Selecione um cliente com e-mail cadastrado para usar o crediário.",
    "O cliente selecionado precisa ter um e-mail válido para receber as cobranças do crediário.": "O cliente selecionado precisa ter um e-mail válido para receber as cobranças do crediário.",
    "Selecione o tipo de ativação e o serial antes de enviar.": "Selecione o tipo de ativação e o serial antes de enviar.",
    "O cliente selecionado não possui e-mail cadastrado.": "O cliente selecionado não possui e-mail cadastrado.",
    "Não foi possível enviar o serial por e-mail.": "Não foi possível enviar o serial por e-mail.",
    "O serial selecionado não está disponível para envio.": "O serial selecionado não está disponível para envio.",
    "Serial enviado por e-mail com sucesso.": "Serial enviado por e-mail com sucesso.",
    "Enviar serial por e-mail": "Enviar serial por e-mail",
    "Disponível apenas para vendas no crediário": "Disponível apenas para vendas no crediário",
    "Acompanhe as parcelas do crediário e do cartão de crédito.": "Acompanhe as parcelas do crediário e do cartão de crédito.",
    "As parcelas do crediário e do cartão de crédito aparecerão aqui automaticamente.": "As parcelas do crediário e do cartão de crédito aparecerão aqui automaticamente.",
  },
  en: {
    "Crediário": "Store credit",
    "Cartão": "Card",
    "Transferência": "Bank transfer",
    "Forma de pagamento": "Payment method",
    "As parcelas do crediário ficarão pendentes até a baixa de cada pagamento.": "Store-credit installments remain pending until each payment is recorded.",
    "As parcelas do cartão de crédito serão baixadas automaticamente.": "Credit-card installments are settled automatically.",
    "Selecione um cliente com e-mail cadastrado para usar o crediário.": "Select a customer with a registered email to use store credit.",
    "O cliente selecionado precisa ter um e-mail válido para receber as cobranças do crediário.": "The selected customer needs a valid email to receive store-credit reminders.",
    "Selecione o tipo de ativação e o serial antes de enviar.": "Select the activation type and serial before sending.",
    "O cliente selecionado não possui e-mail cadastrado.": "The selected customer does not have a registered email.",
    "Não foi possível enviar o serial por e-mail.": "The serial could not be sent by email.",
    "O serial selecionado não está disponível para envio.": "The selected serial is not available to send.",
    "Serial enviado por e-mail com sucesso.": "Serial sent by email successfully.",
    "Enviar serial por e-mail": "Send serial by email",
    "Disponível apenas para vendas no crediário": "Available only for store-credit sales",
    "Acompanhe as parcelas do crediário e do cartão de crédito.": "Track store-credit and credit-card installments.",
    "As parcelas do crediário e do cartão de crédito aparecerão aqui automaticamente.": "Store-credit and credit-card installments will appear here automatically.",
  },
  es: {
    "Crediário": "Crédito de tienda",
    "Cartão": "Tarjeta",
    "Transferência": "Transferencia",
    "Forma de pagamento": "Forma de pago",
    "As parcelas do crediário ficarão pendentes até a baixa de cada pagamento.": "Las cuotas del crédito de tienda permanecerán pendientes hasta registrar cada pago.",
    "As parcelas do cartão de crédito serão baixadas automaticamente.": "Las cuotas de la tarjeta de crédito se liquidarán automáticamente.",
    "Selecione um cliente com e-mail cadastrado para usar o crediário.": "Seleccione un cliente con correo registrado para usar el crédito de tienda.",
    "O cliente selecionado precisa ter um e-mail válido para receber as cobranças do crediário.": "El cliente seleccionado necesita un correo válido para recibir los avisos del crédito de tienda.",
    "Selecione o tipo de ativação e o serial antes de enviar.": "Seleccione el tipo de activación y el serial antes de enviarlo.",
    "O cliente selecionado não possui e-mail cadastrado.": "El cliente seleccionado no tiene un correo registrado.",
    "Não foi possível enviar o serial por e-mail.": "No se pudo enviar el serial por correo.",
    "O serial selecionado não está disponível para envio.": "El serial seleccionado no está disponible para enviar.",
    "Serial enviado por e-mail com sucesso.": "Serial enviado por correo correctamente.",
    "Enviar serial por e-mail": "Enviar serial por correo",
    "Disponível apenas para vendas no crediário": "Disponible solo para ventas con crédito de tienda",
    "Acompanhe as parcelas do crediário e do cartão de crédito.": "Acompañe las cuotas del crédito de tienda y de la tarjeta de crédito.",
    "As parcelas do crediário e do cartão de crédito aparecerão aqui automaticamente.": "Las cuotas del crédito de tienda y de la tarjeta de crédito aparecerán aquí automáticamente.",
  },
  "zh-CN": {
    "Crediário": "赊账",
    "Cartão": "银行卡",
    "Transferência": "银行转账",
    "Forma de pagamento": "付款方式",
    "As parcelas do crediário ficarão pendentes até a baixa de cada pagamento.": "赊账分期将在每笔付款登记前保持待处理状态。",
    "As parcelas do cartão de crédito serão baixadas automaticamente.": "信用卡分期将自动结清。",
    "Selecione um cliente com e-mail cadastrado para usar o crediário.": "请选择已登记电子邮箱的客户以使用赊账。",
    "O cliente selecionado precisa ter um e-mail válido para receber as cobranças do crediário.": "所选客户需要有效的电子邮箱以接收赊账催款通知。",
    "Selecione o tipo de ativação e o serial antes de enviar.": "发送前请选择激活类型和序列号。",
    "O cliente selecionado não possui e-mail cadastrado.": "所选客户未登记电子邮箱。",
    "Não foi possível enviar o serial por e-mail.": "无法通过电子邮件发送序列号。",
    "O serial selecionado não está disponível para envio.": "所选序列号当前无法发送。",
    "Serial enviado por e-mail com sucesso.": "序列号已成功通过电子邮件发送。",
    "Enviar serial por e-mail": "通过电子邮件发送序列号",
    "Disponível apenas para vendas no crediário": "仅适用于赊账销售",
    "Acompanhe as parcelas do crediário e do cartão de crédito.": "跟踪赊账和信用卡分期。",
    "As parcelas do crediário e do cartão de crédito aparecerão aqui automaticamente.": "赊账和信用卡分期将自动显示在这里。",
  },
};

const TEXTOS_GRAFICOS_CONFIG: Record<Idioma, Dicionario> = {
  pt: {
    "Itens do gráfico": "Itens do gráfico",
    "Ativar nos dashboards": "Ativar nos dashboards",
    "Mostrar rótulos de valor nos pontos/barras": "Mostrar rótulos de valor nos pontos/barras",
    "Mostrar marcadores nos pontos da linha": "Mostrar marcadores nos pontos da linha",
    "Posição da legenda": "Posição da legenda",
    Acima: "Acima",
    Abaixo: "Abaixo",
    Esquerda: "Esquerda",
    Direita: "Direita",
    "Preencher área abaixo da linha": "Preencher área abaixo da linha",
    "Usar gradiente na área preenchida": "Usar gradiente na área preenchida",
    "Empilhar barras": "Empilhar barras",
    "Colunas com bordas arredondadas": "Colunas com bordas arredondadas",
    "Velocidade da onda (ms)": "Velocidade da onda (ms)",
    "Amplitude da onda": "Amplitude da onda",
  },
  en: {
    "Itens do gráfico": "Chart items",
    "Ativar nos dashboards": "Enable on dashboards",
    "Mostrar rótulos de valor nos pontos/barras": "Show value labels on points/bars",
    "Mostrar marcadores nos pontos da linha": "Show markers on line points",
    "Posição da legenda": "Legend position",
    Acima: "Top",
    Abaixo: "Bottom",
    Esquerda: "Left",
    Direita: "Right",
    "Preencher área abaixo da linha": "Fill area below the line",
    "Usar gradiente na área preenchida": "Use gradient in the filled area",
    "Empilhar barras": "Stack bars",
    "Colunas com bordas arredondadas": "Rounded bar corners",
    "Velocidade da onda (ms)": "Wave speed (ms)",
    "Amplitude da onda": "Wave amplitude",
  },
  es: {
    "Itens do gráfico": "Elementos del gráfico",
    "Ativar nos dashboards": "Activar en los paneles",
    "Mostrar rótulos de valor nos pontos/barras": "Mostrar valores en puntos/barras",
    "Mostrar marcadores nos pontos da linha": "Mostrar marcadores en los puntos de la línea",
    "Posição da legenda": "Posición de la leyenda",
    Acima: "Arriba",
    Abaixo: "Abajo",
    Esquerda: "Izquierda",
    Direita: "Derecha",
    "Preencher área abaixo da linha": "Rellenar el área bajo la línea",
    "Usar gradiente na área preenchida": "Usar degradado en el área rellena",
    "Empilhar barras": "Apilar barras",
    "Colunas com bordas arredondadas": "Barras con bordes redondeados",
    "Velocidade da onda (ms)": "Velocidad de la onda (ms)",
    "Amplitude da onda": "Amplitud de la onda",
  },
  "zh-CN": {
    "Itens do gráfico": "图表项目",
    "Ativar nos dashboards": "在仪表板中启用",
    "Mostrar rótulos de valor nos pontos/barras": "显示点/柱上的数值标签",
    "Mostrar marcadores nos pontos da linha": "显示折线点标记",
    "Posição da legenda": "图例位置",
    Acima: "上方",
    Abaixo: "下方",
    Esquerda: "左侧",
    Direita: "右侧",
    "Preencher área abaixo da linha": "填充折线下方区域",
    "Usar gradiente na área preenchida": "在填充区域使用渐变",
    "Empilhar barras": "堆叠柱形",
    "Colunas com bordas arredondadas": "圆角柱形",
    "Velocidade da onda (ms)": "波浪速度（毫秒）",
    "Amplitude da onda": "波浪振幅",
  },
};

const TEXTOS_ANEXOS: Record<Idioma, Dicionario> = {
  pt: {
    Anexos: "Anexos",
    "Selecionar arquivos": "Selecionar arquivos",
    "Anexar arquivos": "Anexar arquivos",
    "Enviando arquivos...": "Enviando arquivos...",
    "Arraste e solte os arquivos aqui ou": "Arraste e solte os arquivos aqui ou",
    "escolha os arquivos": "escolha os arquivos",
    "arquivo(s) anexado(s)": "arquivo(s) anexado(s)",
    "Nenhum arquivo selecionado": "Nenhum arquivo selecionado",
    "máx.": "máx.",
    "Arquivo excede o limite permitido.": "Arquivo excede o limite permitido.",
    "Erro ao anexar.": "Erro ao anexar.",
    "Erro ao remover.": "Erro ao remover.",
    "Erro ao ler o arquivo.": "Erro ao ler o arquivo.",
    "Não foi possível remover todos os anexos.": "Não foi possível remover todos os anexos.",
    "Buscar arquivo...": "Buscar arquivo...",
    "Excluir todos": "Excluir todos",
    "Carregando anexos...": "Carregando anexos...",
    "Nenhum arquivo encontrado.": "Nenhum arquivo encontrado.",
    "Nenhum arquivo importado.": "Nenhum arquivo importado.",
    Baixar: "Baixar",
    Visualizar: "Visualizar",
    Remover: "Remover",
    "Excluir anexos": "Excluir anexos",
    "Excluir anexo": "Excluir anexo",
    Excluir: "Excluir",
    "anexo(s)": "anexo(s)",
    "Esta ação não pode ser desfeita.": "Esta ação não pode ser desfeita.",
    "Excluir o anexo": "Excluir o anexo",
    "Item não encontrado.": "Item não encontrado.",
  },
  en: {
    Anexos: "Attachments",
    "Selecionar arquivos": "Select files",
    "Anexar arquivos": "Attach files",
    "Enviando arquivos...": "Uploading files...",
    "Arraste e solte os arquivos aqui ou": "Drag and drop files here or",
    "escolha os arquivos": "choose files",
    "arquivo(s) anexado(s)": "file(s) attached",
    "Nenhum arquivo selecionado": "No file selected",
    "máx.": "max.",
    "Arquivo excede o limite permitido.": "File exceeds the allowed limit.",
    "Erro ao anexar.": "Error attaching file.",
    "Erro ao remover.": "Error removing file.",
    "Erro ao ler o arquivo.": "Error reading file.",
    "Não foi possível remover todos os anexos.": "Could not remove all attachments.",
    "Buscar arquivo...": "Search file...",
    "Excluir todos": "Delete all",
    "Carregando anexos...": "Loading attachments...",
    "Nenhum arquivo encontrado.": "No file found.",
    "Nenhum arquivo importado.": "No file uploaded.",
    Baixar: "Download",
    Visualizar: "View",
    Remover: "Remove",
    "Excluir anexos": "Delete attachments",
    "Excluir anexo": "Delete attachment",
    Excluir: "Delete",
    "anexo(s)": "attachment(s)",
    "Esta ação não pode ser desfeita.": "This action cannot be undone.",
    "Excluir o anexo": "Delete attachment",
    "Item não encontrado.": "Item not found.",
  },
  es: {
    Anexos: "Adjuntos",
    "Selecionar arquivos": "Seleccionar archivos",
    "Anexar arquivos": "Adjuntar archivos",
    "Enviando arquivos...": "Subiendo archivos...",
    "Arraste e solte os arquivos aqui ou": "Arrastre y suelte los archivos aquí o",
    "escolha os arquivos": "elija los archivos",
    "arquivo(s) anexado(s)": "archivo(s) adjunto(s)",
    "Nenhum arquivo selecionado": "Ningún archivo seleccionado",
    "máx.": "máx.",
    "Arquivo excede o limite permitido.": "El archivo supera el límite permitido.",
    "Erro ao anexar.": "Error al adjuntar el archivo.",
    "Erro ao remover.": "Error al eliminar el archivo.",
    "Erro ao ler o arquivo.": "Error al leer el archivo.",
    "Não foi possível remover todos os anexos.": "No se pudieron eliminar todos los adjuntos.",
    "Buscar arquivo...": "Buscar archivo...",
    "Excluir todos": "Eliminar todos",
    "Carregando anexos...": "Cargando adjuntos...",
    "Nenhum arquivo encontrado.": "No se encontró ningún archivo.",
    "Nenhum arquivo importado.": "No se subió ningún archivo.",
    Baixar: "Descargar",
    Visualizar: "Ver",
    Remover: "Eliminar",
    "Excluir anexos": "Eliminar adjuntos",
    "Excluir anexo": "Eliminar adjunto",
    Excluir: "Eliminar",
    "anexo(s)": "adjunto(s)",
    "Esta ação não pode ser desfeita.": "Esta acción no se puede deshacer.",
    "Excluir o anexo": "Eliminar el adjunto",
    "Item não encontrado.": "Elemento no encontrado.",
  },
  "zh-CN": {
    Anexos: "附件",
    "Selecionar arquivos": "选择文件",
    "Anexar arquivos": "添加附件",
    "Enviando arquivos...": "正在上传文件...",
    "Arraste e solte os arquivos aqui ou": "将文件拖放到此处或",
    "escolha os arquivos": "选择文件",
    "arquivo(s) anexado(s)": "个附件",
    "Nenhum arquivo selecionado": "未选择文件",
    "máx.": "最大",
    "Arquivo excede o limite permitido.": "文件超过允许的大小限制。",
    "Erro ao anexar.": "添加附件时出错。",
    "Erro ao remover.": "删除文件时出错。",
    "Erro ao ler o arquivo.": "读取文件时出错。",
    "Não foi possível remover todos os anexos.": "无法删除所有附件。",
    "Buscar arquivo...": "搜索文件...",
    "Excluir todos": "全部删除",
    "Carregando anexos...": "正在加载附件...",
    "Nenhum arquivo encontrado.": "未找到文件。",
    "Nenhum arquivo importado.": "尚未上传文件。",
    Baixar: "下载",
    Visualizar: "查看",
    Remover: "删除",
    "Excluir anexos": "删除附件",
    "Excluir anexo": "删除附件",
    Excluir: "删除",
    "anexo(s)": "个附件",
    "Esta ação não pode ser desfeita.": "此操作无法撤销。",
    "Excluir o anexo": "删除附件",
    "Item não encontrado.": "未找到项目。",
  },
};

const LINHAS_DASHBOARD_FINANCEIRO_NOVO: Array<[string, string, string, string]> = [
  ["Dashboard Financeiro", "Financial Dashboard", "Panel financiero", "财务仪表盘"],
  ["Acompanhe saldos, receitas, despesas e o resultado do período.", "Track balances, income, expenses, and the period result.", "Consulta saldos, ingresos, gastos y el resultado del período.", "跟踪余额、收入、支出和期间结果。"],
  ["Erro ao carregar o dashboard financeiro.", "Error loading the financial dashboard.", "Error al cargar el panel financiero.", "加载财务仪表盘时出错。"],
  ["Saldo em Conta", "Account Balance", "Saldo en Cuenta", "账户余额"],
  ["Disponível", "Available", "Disponible", "可用"],
  ["Receitas no Período", "Income for the Period", "Ingresos del Período", "本期收入"],
  ["Total recebido", "Total received", "Total recibido", "收款总额"],
  ["Despesas no Período", "Expenses for the Period", "Gastos del Período", "本期支出"],
  ["Total pago", "Total paid", "Total pagado", "付款总额"],
  ["Resultado do Período", "Period Result", "Resultado del Período", "本期结果"],
  ["Lucro líquido", "Net profit", "Beneficio neto", "净利润"],
  ["Prejuízo do período", "Loss for the period", "Pérdida del período", "本期亏损"],
  ["vs. mês anterior", "vs. previous month", "vs. mes anterior", "与上月相比"],
  ["Fluxo de Caixa", "Cash Flow", "Flujo de Caja", "现金流"],
  ["Mensal", "Monthly", "Mensual", "每月"],
  ["Receitas", "Income", "Ingresos", "收入"],
  ["Despesas", "Expenses", "Gastos", "支出"],
  ["Resultado", "Result", "Resultado", "结果"],
  ["Distribuição de Despesas", "Expense Distribution", "Distribución de Gastos", "支出分布"],
  ["Outros", "Other", "Otros", "其他"],
  ["Total", "Total", "Total", "总计"],
  ["Sem despesas pagas no período.", "No expenses paid in the period.", "No hay gastos pagados en el período.", "本期无已支付支出。"],
  ["Ver relatório completo", "View full report", "Ver informe completo", "查看完整报告"],
  ["Contas", "Accounts", "Cuentas", "账户"],
  ["Nenhuma conta bancária cadastrada.", "No bank accounts registered.", "No hay cuentas bancarias registradas.", "尚未登记银行账户。"],
  ["Ver todas as contas", "View all accounts", "Ver todas las cuentas", "查看所有账户"],
  ["Próximos Vencimentos", "Upcoming Due Dates", "Próximos Vencimientos", "即将到期"],
  ["Vencido", "Overdue", "Vencido", "已逾期"],
  ["Vence hoje", "Due today", "Vence hoy", "今日到期"],
  ["A vencer", "Due", "Por vencer", "待到期"],
  ["Pendente", "Pending", "Pendiente", "待处理"],
  ["Nenhum pagamento pendente.", "No pending payments.", "No hay pagos pendientes.", "无待付款项。"],
  ["Ver todos os vencimentos", "View all due dates", "Ver todos los vencimientos", "查看所有到期项"],
  ["Recebimentos em Aberto", "Outstanding Receivables", "Cobros Pendientes", "未收款项"],
  ["A receber", "Receivable", "Por cobrar", "待收款"],
  ["Nenhum recebimento pendente.", "No pending receivables.", "No hay cobros pendientes.", "无待收款项。"],
  ["Ver todos os recebimentos", "View all receivables", "Ver todos los cobros", "查看所有收款"],
  ["Resumo Financeiro", "Financial Summary", "Resumen Financiero", "财务摘要"],
  ["Receitas Totais", "Total Income", "Ingresos Totales", "收入总额"],
  ["(-) Deduções", "(-) Deductions", "(-) Deducciones", "(-) 扣除项"],
  ["Receita Líquida", "Net Income", "Ingresos Netos", "净收入"],
  ["(-) Despesas Totais", "(-) Total Expenses", "(-) Gastos Totales", "(-) 支出总额"],
  ["Resultado Operacional", "Operating Result", "Resultado Operativo", "经营结果"],
  ["(+) Outras Receitas", "(+) Other Income", "(+) Otros Ingresos", "(+) 其他收入"],
  ["(-) Outras Despesas", "(-) Other Expenses", "(-) Otros Gastos", "(-) 其他支出"],
  ["Margem de Lucro", "Profit Margin", "Margen de Beneficio", "利润率"],
  ["Ticket Médio", "Average Order Value", "Ticket Promedio", "平均客单价"],
  ["Ponto de Equilíbrio", "Break-even Point", "Punto de Equilibrio", "盈亏平衡点"],
  ["Rentabilidade", "Profitability", "Rentabilidad", "盈利能力"],
  ["Gerar Relatório", "Generate Report", "Generar Informe", "生成报告"],
];
const TEXTOS_DASHBOARD_FINANCEIRO_NOVO: Record<Idioma, Dicionario> = {
  pt: Object.fromEntries(LINHAS_DASHBOARD_FINANCEIRO_NOVO.map(([pt]) => [pt, pt])),
  en: Object.fromEntries(LINHAS_DASHBOARD_FINANCEIRO_NOVO.map(([pt, en]) => [pt, en])),
  es: Object.fromEntries(LINHAS_DASHBOARD_FINANCEIRO_NOVO.map(([pt, , es]) => [pt, es])),
  "zh-CN": Object.fromEntries(LINHAS_DASHBOARD_FINANCEIRO_NOVO.map(([pt, , , zh]) => [pt, zh])),
};

const LINHAS_COMPRAS_DASHBOARD: Array<[string, string, string, string]> = [
  ["Dashboard de Compras", "Purchasing Dashboard", "Panel de Compras", "采购仪表板"],
  ["Visão geral das compras, fornecedores, pedidos de reposição e desempenho da área de suprimentos.", "Overview of purchases, suppliers, replenishment orders, and procurement performance.", "Resumen de compras, proveedores, pedidos de reposición y rendimiento del área de abastecimiento.", "采购、供应商、补货订单及供应管理绩效概览。"],
  ["Não foi possível carregar o dashboard de compras.", "The purchasing dashboard could not be loaded.", "No se pudo cargar el panel de compras.", "无法加载采购仪表板。"],
  ["Valor Total Comprado", "Total Purchased Value", "Valor Total Comprado", "采购总额"],
  ["Pedidos de Compra", "Purchase Orders", "Pedidos de Compra", "采购订单"],
  ["Ticket Médio de Compra", "Average Purchase Value", "Valor Medio de Compra", "平均采购金额"],
  ["Fornecedores Ativos", "Active Suppliers", "Proveedores Activos", "活跃供应商"],
  ["Compras Recebidas", "Purchases Received", "Compras Recibidas", "已收货采购"],
  ["Pedidos Atrasados", "Overdue Orders", "Pedidos Atrasados", "逾期订单"],
  ["Evolução das Compras", "Purchase Trend", "Evolución de las Compras", "采购趋势"],
  ["Valor comprado", "Purchased amount", "Valor comprado", "采购金额"],
  ["Pedidos", "Orders", "Pedidos", "订单"],
  ["Meta", "Target", "Meta", "目标"],
  ["Outros", "Other", "Otros", "其他"],
  ["Status dos Pedidos de Compra", "Purchase Order Status", "Estado de los Pedidos de Compra", "采购订单状态"],
  ["Pendentes", "Pending", "Pendientes", "待处理"],
  ["Em aprovação", "Under approval", "En aprobación", "审批中"],
  ["Enviados", "Sent", "Enviados", "已发送"],
  ["Recebidos", "Received", "Recibidos", "已收货"],
  ["Atrasados", "Overdue", "Atrasados", "已逾期"],
  ["Pendente", "Pending", "Pendiente", "待处理"],
  ["Enviado", "Sent", "Enviado", "已发送"],
  ["Recebido", "Received", "Recibido", "已收货"],
  ["Atrasado", "Overdue", "Atrasado", "已逾期"],
  ["Atividade do dia", "Daily activity", "Actividad del día", "今日动态"],
  ["Novos pedidos", "New orders", "Nuevos pedidos", "新订单"],
  ["Cotações recebidas", "Quotes received", "Cotizaciones recibidas", "已收报价"],
  ["Aprovações concluídas", "Approvals completed", "Aprobaciones completadas", "已完成审批"],
  ["Entradas lançadas", "Receipts posted", "Entradas registradas", "已登记入库"],
  ["Ver relatório completo", "View full report", "Ver informe completo", "查看完整报告"],
  ["Orçamento por Categoria", "Budget by Category", "Presupuesto por Categoría", "按类别预算"],
  ["Pedidos Recentes", "Recent Orders", "Pedidos Recientes", "近期订单"],
  ["Fornecedor", "Supplier", "Proveedor", "供应商"],
  ["Categoria", "Category", "Categoría", "类别"],
  ["Produto", "Product", "Producto", "商品"],
  ["Valor", "Amount", "Valor", "金额"],
  ["Comprador", "Buyer", "Comprador", "采购员"],
  ["Status", "Status", "Estado", "状态"],
  ["Data", "Date", "Fecha", "日期"],
  ["Ver todos os pedidos", "View all orders", "Ver todos los pedidos", "查看所有订单"],
  ["Top Fornecedores", "Top Suppliers", "Principales Proveedores", "主要供应商"],
  ["Pedidos atendidos", "Orders fulfilled", "Pedidos atendidos", "已履行订单"],
  ["Ver ranking completo", "View full ranking", "Ver clasificación completa", "查看完整排名"],
  ["Desempenho por Canal de Compra", "Purchase Channel Performance", "Rendimiento por Canal de Compra", "采购渠道绩效"],
  ["Prazo médio", "Average lead time", "Plazo medio", "平均周期"],
  ["Resumo de Compras", "Purchasing Summary", "Resumen de Compras", "采购摘要"],
  ["Economia obtida", "Savings achieved", "Ahorro obtenido", "节省金额"],
  ["Pedidos no prazo", "Orders on time", "Pedidos a tiempo", "按时订单"],
  ["Lead time médio", "Average lead time", "Plazo medio", "平均交付周期"],
  ["Dado ainda não informado", "Data not yet provided", "Dato aún no informado", "数据尚未提供"],
  ["Nenhum pedido encontrado.", "No orders found.", "No se encontraron pedidos.", "未找到订单。"],
  ["Nenhum fornecedor encontrado.", "No suppliers found.", "No se encontraron proveedores.", "未找到供应商。"],
  ["Nenhum canal de compra encontrado.", "No purchase channels found.", "No se encontraron canales de compra.", "未找到采购渠道。"],
  ["Não informado", "Not provided", "No informado", "未提供"],
  ["Nova Compra", "New Purchase", "Nueva Compra", "新建采购"],
  ["pedidos", "orders", "pedidos", "个订单"],
];

const TEXTOS_COMPRAS_DASHBOARD: Record<Idioma, Dicionario> = {
  pt: Object.fromEntries(LINHAS_COMPRAS_DASHBOARD.map(([pt]) => [pt, pt])),
  en: Object.fromEntries(LINHAS_COMPRAS_DASHBOARD.map(([pt, en]) => [pt, en])),
  es: Object.fromEntries(LINHAS_COMPRAS_DASHBOARD.map(([pt, , es]) => [pt, es])),
  "zh-CN": Object.fromEntries(LINHAS_COMPRAS_DASHBOARD.map(([pt, , , zh]) => [pt, zh])),
};

const LINHAS_VISAO_GERAL_COMPLETA: Array<[string, string, string, string]> = [
  ["Mapa de Filiais", "Branch Map", "Mapa de sucursales", "分支机构地图"],
  ["Filiais por Estado", "Branches by State", "Sucursales por estado", "按州划分的分支机构"],
  ["Ranking de Filiais por Estado", "Branch Ranking by State", "Ranking de sucursales por estado", "按州分支机构排名"],
  ["Filiais", "Branches", "Sucursales", "分支机构"],
  ["cliente(s)", "customer(s)", "cliente(s)", "位客户"],
  ["filial(is)", "branch(es)", "sucursal(es)", "个分支机构"],
  ["cliente(s) em", "customer(s) across", "cliente(s) en", "位客户，分布在"],
  ["filial(is) em", "branch(es) across", "sucursal(es) en", "个分支机构，分布在"],
  ["estado(s)", "state(s)", "estado(s)", "个州"],
  ["Total cadastrado", "Total registered", "Total registrado", "登记总数"],
];
const TEXTOS_VISAO_GERAL_COMPLETA: Record<Idioma, Dicionario> = {
  pt: Object.fromEntries(LINHAS_VISAO_GERAL_COMPLETA.map(([pt]) => [pt, pt])),
  en: Object.fromEntries(LINHAS_VISAO_GERAL_COMPLETA.map(([pt, en]) => [pt, en])),
  es: Object.fromEntries(LINHAS_VISAO_GERAL_COMPLETA.map(([pt, , es]) => [pt, es])),
  "zh-CN": Object.fromEntries(LINHAS_VISAO_GERAL_COMPLETA.map(([pt, , , zh]) => [pt, zh])),
};
const TEXTOS_ESTOQUE_DASHBOARD: Record<Idioma, Dicionario> = {
  pt: {
    "Dashboard do estoque": "Dashboard do estoque",
    "Dashboard de Estoque": "Dashboard de Estoque",
    "Acompanhe saldos, movimentações, entradas, saídas e os principais indicadores do estoque.":
      "Acompanhe saldos, movimentações, entradas, saídas e os principais indicadores do estoque.",
    "Não foi possível carregar o dashboard do estoque.": "Não foi possível carregar o dashboard do estoque.",
    "Tentar novamente": "Tentar novamente",
    "vs. período anterior": "vs. período anterior",
    "Produtos em Estoque": "Produtos em Estoque",
    "Itens com Estoque Baixo": "Itens com Estoque Baixo",
    "Sem Estoque": "Sem Estoque",
    "Entradas no Período": "Entradas no Período",
    "Saídas no Período": "Saídas no Período",
    "Valor Total em Estoque": "Valor Total em Estoque",
    "Evolução do Estoque": "Evolução do Estoque",
    "Últimos 7 dias": "Últimos 7 dias",
    "Últimos 14 dias": "Últimos 14 dias",
    "Últimos 30 dias": "Últimos 30 dias",
    Entradas: "Entradas",
    Saídas: "Saídas",
    Saldo: "Saldo",
    Itens: "Itens",
    "Carregando...": "Carregando...",
    "Sem movimentações no período.": "Sem movimentações no período.",
    "Distribuição por Categoria": "Distribuição por Categoria",
    "Sem dados por categoria.": "Sem dados por categoria.",
    "Sem categoria": "Sem categoria",
    "Status do Estoque": "Status do Estoque",
    Disponível: "Disponível",
    Reservado: "Reservado",
    "Estoque baixo": "Estoque baixo",
    Bloqueado: "Bloqueado",
    "Sem estoque": "Sem estoque",
    "produtos monitorados": "produtos monitorados",
    "Alertas de Reposição": "Alertas de Reposição",
    "un.": "un.",
    "Reposição urgente": "Reposição urgente",
    "Repor em breve": "Repor em breve",
    "Nenhum alerta de reposição.": "Nenhum alerta de reposição.",
    "Movimentações Recentes": "Movimentações Recentes",
    ID: "ID",
    Produto: "Produto",
    Tipo: "Tipo",
    Quantidade: "Quantidade",
    "Origem/Destino": "Origem/Destino",
    Responsável: "Responsável",
    Horário: "Horário",
    Entrada: "Entrada",
    Ajuste: "Ajuste",
    Saída: "Saída",
    "Nenhuma movimentação recente.": "Nenhuma movimentação recente.",
    "Ver todas as movimentações": "Ver todas as movimentações",
    "Armazéns em Destaque": "Armazéns em Destaque",
    Armazém: "Armazém",
    "Itens armazenados": "Itens armazenados",
    Performance: "Performance",
    itens: "itens",
    "Nenhum armazém com dados.": "Nenhum armazém com dados.",
    "Ver desempenho completo": "Ver desempenho completo",
    "Desempenho por Local": "Desempenho por Local",
    Rupturas: "Rupturas",
    "Nenhum local com movimentações.": "Nenhum local com movimentações.",
    "Resumo de Eficiência": "Resumo de Eficiência",
    "Acuracidade do inventário": "Acuracidade do inventário",
    "Tempo médio de reposição": "Tempo médio de reposição",
    dias: "dias",
    "Giro de estoque": "Giro de estoque",
    "Carregando dashboard do estoque.": "Carregando dashboard do estoque.",
    "Dashboard do estoque atualizado.": "Dashboard do estoque atualizado.",
    "Estoque do fornecedor": "Estoque do fornecedor",
  },
  en: {
    "Dashboard do estoque": "Inventory dashboard",
    "Dashboard de Estoque": "Inventory Dashboard",
    "Acompanhe saldos, movimentações, entradas, saídas e os principais indicadores do estoque.":
      "Track balances, movements, inbound, outbound, and the main inventory indicators.",
    "Não foi possível carregar o dashboard do estoque.": "The inventory dashboard could not be loaded.",
    "Tentar novamente": "Try again",
    "vs. período anterior": "vs. previous period",
    "Produtos em Estoque": "Products in Stock",
    "Itens com Estoque Baixo": "Low-stock Items",
    "Sem Estoque": "Out of Stock",
    "Entradas no Período": "Receipts in Period",
    "Saídas no Período": "Issues in Period",
    "Valor Total em Estoque": "Total Inventory Value",
    "Evolução do Estoque": "Inventory Trend",
    "Últimos 7 dias": "Last 7 days",
    "Últimos 14 dias": "Last 14 days",
    "Últimos 30 dias": "Last 30 days",
    Entradas: "Receipts",
    Saídas: "Issues",
    Saldo: "Balance",
    Itens: "Items",
    "Carregando...": "Loading...",
    "Sem movimentações no período.": "No movements in this period.",
    "Distribuição por Categoria": "Distribution by Category",
    "Sem dados por categoria.": "No category data.",
    "Sem categoria": "Uncategorized",
    "Status do Estoque": "Inventory Status",
    Disponível: "Available",
    Reservado: "Reserved",
    "Estoque baixo": "Low stock",
    Bloqueado: "Blocked",
    "Sem estoque": "Out of stock",
    "produtos monitorados": "monitored products",
    "Alertas de Reposição": "Replenishment Alerts",
    "un.": "units",
    "Reposição urgente": "Urgent replenishment",
    "Repor em breve": "Replenish soon",
    "Nenhum alerta de reposição.": "No replenishment alerts.",
    "Movimentações Recentes": "Recent Movements",
    ID: "ID",
    Produto: "Product",
    Tipo: "Type",
    Quantidade: "Quantity",
    "Origem/Destino": "Source/Destination",
    Responsável: "Owner",
    Horário: "Time",
    Entrada: "Receipt",
    Ajuste: "Adjustment",
    Saída: "Issue",
    "Nenhuma movimentação recente.": "No recent movements.",
    "Ver todas as movimentações": "View all movements",
    "Armazéns em Destaque": "Top Warehouses",
    Armazém: "Warehouse",
    "Itens armazenados": "Stored items",
    Performance: "Performance",
    itens: "items",
    "Nenhum armazém com dados.": "No warehouse data.",
    "Ver desempenho completo": "View full performance",
    "Desempenho por Local": "Performance by Location",
    Rupturas: "Stockouts",
    "Nenhum local com movimentações.": "No locations with movements.",
    "Resumo de Eficiência": "Efficiency Summary",
    "Acuracidade do inventário": "Inventory accuracy",
    "Tempo médio de reposição": "Average replenishment time",
    dias: "days",
    "Giro de estoque": "Inventory turnover",
    "Carregando dashboard do estoque.": "Loading inventory dashboard.",
    "Dashboard do estoque atualizado.": "Inventory dashboard updated.",
    "Estoque do fornecedor": "Supplier inventory",
  },
  es: {
    "Dashboard do estoque": "Panel de inventario",
    "Dashboard de Estoque": "Panel de Inventario",
    "Acompanhe saldos, movimentações, entradas, saídas e os principais indicadores do estoque.":
      "Consulta saldos, movimientos, entradas, salidas y los principales indicadores del inventario.",
    "Não foi possível carregar o dashboard do estoque.": "No se pudo cargar el panel de inventario.",
    "Tentar novamente": "Intentar de nuevo",
    "vs. período anterior": "vs. período anterior",
    "Produtos em Estoque": "Productos en Stock",
    "Itens com Estoque Baixo": "Artículos con Stock Bajo",
    "Sem Estoque": "Sin Stock",
    "Entradas no Período": "Entradas del Período",
    "Saídas no Período": "Salidas del Período",
    "Valor Total em Estoque": "Valor Total del Inventario",
    "Evolução do Estoque": "Evolución del Inventario",
    "Últimos 7 dias": "Últimos 7 días",
    "Últimos 14 dias": "Últimos 14 días",
    "Últimos 30 dias": "Últimos 30 días",
    Entradas: "Entradas",
    Saídas: "Salidas",
    Saldo: "Saldo",
    Itens: "Artículos",
    "Carregando...": "Cargando...",
    "Sem movimentações no período.": "Sin movimientos en el período.",
    "Distribuição por Categoria": "Distribución por Categoría",
    "Sem dados por categoria.": "Sin datos por categoría.",
    "Sem categoria": "Sin categoría",
    "Status do Estoque": "Estado del Inventario",
    Disponível: "Disponible",
    Reservado: "Reservado",
    "Estoque baixo": "Stock bajo",
    Bloqueado: "Bloqueado",
    "Sem estoque": "Sin stock",
    "produtos monitorados": "productos supervisados",
    "Alertas de Reposição": "Alertas de Reposición",
    "un.": "uds.",
    "Reposição urgente": "Reposición urgente",
    "Repor em breve": "Reponer pronto",
    "Nenhum alerta de reposição.": "No hay alertas de reposición.",
    "Movimentações Recentes": "Movimientos Recientes",
    ID: "ID",
    Produto: "Producto",
    Tipo: "Tipo",
    Quantidade: "Cantidad",
    "Origem/Destino": "Origen/Destino",
    Responsável: "Responsable",
    Horário: "Hora",
    Entrada: "Entrada",
    Ajuste: "Ajuste",
    Saída: "Salida",
    "Nenhuma movimentação recente.": "No hay movimientos recientes.",
    "Ver todas as movimentações": "Ver todos los movimientos",
    "Armazéns em Destaque": "Almacenes Destacados",
    Armazém: "Almacén",
    "Itens armazenados": "Artículos almacenados",
    Performance: "Rendimiento",
    itens: "artículos",
    "Nenhum armazém com dados.": "No hay datos de almacenes.",
    "Ver desempenho completo": "Ver rendimiento completo",
    "Desempenho por Local": "Rendimiento por Ubicación",
    Rupturas: "Roturas",
    "Nenhum local com movimentações.": "No hay ubicaciones con movimientos.",
    "Resumo de Eficiência": "Resumen de Eficiencia",
    "Acuracidade do inventário": "Exactitud del inventario",
    "Tempo médio de reposição": "Tiempo medio de reposición",
    dias: "días",
    "Giro de estoque": "Rotación de inventario",
    "Carregando dashboard do estoque.": "Cargando el panel de inventario.",
    "Dashboard do estoque atualizado.": "Panel de inventario actualizado.",
    "Estoque do fornecedor": "Inventario del proveedor",
  },
  "zh-CN": {
    "Dashboard do estoque": "库存仪表板",
    "Dashboard de Estoque": "库存仪表板",
    "Acompanhe saldos, movimentações, entradas, saídas e os principais indicadores do estoque.":
      "跟踪库存余额、流转、入库、出库及主要库存指标。",
    "Não foi possível carregar o dashboard do estoque.": "无法加载库存仪表板。",
    "Tentar novamente": "重试",
    "vs. período anterior": "较上一周期",
    "Produtos em Estoque": "有库存商品",
    "Itens com Estoque Baixo": "低库存商品",
    "Sem Estoque": "缺货",
    "Entradas no Período": "期间入库",
    "Saídas no Período": "期间出库",
    "Valor Total em Estoque": "库存总价值",
    "Evolução do Estoque": "库存趋势",
    "Últimos 7 dias": "最近 7 天",
    "Últimos 14 dias": "最近 14 天",
    "Últimos 30 dias": "最近 30 天",
    Entradas: "入库",
    Saídas: "出库",
    Saldo: "余额",
    Itens: "件",
    "Carregando...": "加载中...",
    "Sem movimentações no período.": "该期间没有库存变动。",
    "Distribuição por Categoria": "按类别分布",
    "Sem dados por categoria.": "没有类别数据。",
    "Sem categoria": "未分类",
    "Status do Estoque": "库存状态",
    Disponível: "可用",
    Reservado: "已预留",
    "Estoque baixo": "低库存",
    Bloqueado: "已锁定",
    "Sem estoque": "缺货",
    "produtos monitorados": "个受监控商品",
    "Alertas de Reposição": "补货提醒",
    "un.": "件",
    "Reposição urgente": "紧急补货",
    "Repor em breve": "尽快补货",
    "Nenhum alerta de reposição.": "没有补货提醒。",
    "Movimentações Recentes": "近期库存变动",
    ID: "编号",
    Produto: "商品",
    Tipo: "类型",
    Quantidade: "数量",
    "Origem/Destino": "来源/目的地",
    Responsável: "负责人",
    Horário: "时间",
    Entrada: "入库",
    Ajuste: "调整",
    Saída: "出库",
    "Nenhuma movimentação recente.": "没有近期库存变动。",
    "Ver todas as movimentações": "查看所有库存变动",
    "Armazéns em Destaque": "重点仓库",
    Armazém: "仓库",
    "Itens armazenados": "存储件数",
    Performance: "绩效",
    itens: "件",
    "Nenhum armazém com dados.": "没有仓库数据。",
    "Ver desempenho completo": "查看完整绩效",
    "Desempenho por Local": "按地点绩效",
    Rupturas: "缺货",
    "Nenhum local com movimentações.": "没有发生库存变动的地点。",
    "Resumo de Eficiência": "效率摘要",
    "Acuracidade do inventário": "盘点准确率",
    "Tempo médio de reposição": "平均补货时间",
    dias: "天",
    "Giro de estoque": "库存周转率",
    "Carregando dashboard do estoque.": "正在加载库存仪表板。",
    "Dashboard do estoque atualizado.": "库存仪表板已更新。",
    "Estoque do fornecedor": "供应商库存",
  },
};

const TEXTOS_PERMISSOES_PERFIL: Record<Idioma, Dicionario> = {
  pt: {
    "Módulos": "Módulos",
    "Telas": "Telas",
    "Permissões liberadas": "Permissões liberadas",
    "Telas bloqueadas": "Telas bloqueadas",
    "sem acesso": "sem acesso",
    "Módulos do sistema": "Módulos do sistema",
    "Todos": "Todos",
    "Todos os módulos": "Todos os módulos",
    "Filtrar telas": "Filtrar telas",
    "Todas": "Todas",
    "Liberadas": "Liberadas",
    "Bloqueadas": "Bloqueadas",
    "Ordenar por": "Ordenar por",
    "Nome": "Nome",
    "Status": "Status",
    "Módulo": "Módulo",
    "Ativar módulo inteiro": "Ativar módulo inteiro",
    "Desativar módulo inteiro": "Desativar módulo inteiro",
    "Ativar todas as telas": "Ativar todas as telas",
    "Desativar todas as telas": "Desativar todas as telas",
    "Limpar permissões": "Limpar permissões",
    "Nenhuma tela encontrada para este filtro.": "Nenhuma tela encontrada para este filtro.",
    "Permissões da tela": "Permissões da tela",
    "Detalhes da permissão": "Detalhes da permissão",
    "Os usuários deste perfil terão acesso às rotinas da tela selecionada conforme as permissões acima.": "Os usuários deste perfil terão acesso às rotinas da tela selecionada conforme as permissões acima.",
    "As alterações serão aplicadas ao salvar.": "As alterações serão aplicadas ao salvar.",
    "Ativar acesso": "Ativar acesso",
    "Visualizar": "Visualizar",
    "Editar": "Editar",
    "Excluir": "Excluir",
    "Imprimir": "Imprimir",
    "Importar": "Importar",
    "Exportar": "Exportar",
    "Permite abrir a tela e consultar os registros.": "Permite abrir a tela e consultar os registros.",
    "Permite criar e alterar registros da tela.": "Permite criar e alterar registros da tela.",
    "Permite excluir registros da tela.": "Permite excluir registros da tela.",
    "Permite imprimir e gerar PDF dos registros.": "Permite imprimir e gerar PDF dos registros.",
    "Permite importar registros por planilha.": "Permite importar registros por planilha.",
    "Permite exportar os dados da tela.": "Permite exportar os dados da tela.",
    "Telas e Permissões de Acesso": "Telas e Permissões de Acesso",
    "Identificação": "Identificação",
    "Janela de Horário de Acesso": "Janela de Horário de Acesso",
    "Descrição": "Descrição",
    "Painel inicial com os indicadores do sistema.": "Painel inicial com os indicadores do sistema.",
    "Dados cadastrais das empresas.": "Dados cadastrais das empresas.",
    "Bancos e contas utilizados.": "Bancos e contas utilizados.",
    "Cadastro da carteira de clientes.": "Cadastro da carteira de clientes.",
    "Cadastro de fornecedores da empresa.": "Cadastro de fornecedores da empresa.",
    "Transportadoras usadas nas entregas.": "Transportadoras usadas nas entregas.",
    "Equipe e dados dos colaboradores.": "Equipe e dados dos colaboradores.",
    "Catálogo de produtos da empresa.": "Catálogo de produtos da empresa.",
    "Catálogo de serviços prestados.": "Catálogo de serviços prestados.",
    "Unidades usadas nos produtos.": "Unidades usadas nos produtos.",
    "Categorias de produtos e estoque.": "Categorias de produtos e estoque.",
    "Locais de armazenamento do estoque.": "Locais de armazenamento do estoque.",
    "Grade de tamanhos dos produtos.": "Grade de tamanhos dos produtos.",
    "Cores da grade de produtos.": "Cores da grade de produtos.",
    "Estrutura de receitas e despesas.": "Estrutura de receitas e despesas.",
    "Maquininhas de cartão e taxas.": "Maquininhas de cartão e taxas.",
    "Códigos de erro do sistema.": "Códigos de erro do sistema.",
    "Controle de números de série.": "Controle de números de série.",
    "Perfis de acesso e permissões.": "Perfis de acesso e permissões.",
    "Logins de acesso dos usuários.": "Logins de acesso dos usuários.",
    "Resumo do módulo de clientes.": "Resumo do módulo de clientes.",
    "Pedidos de insumo dos clientes.": "Pedidos de insumo dos clientes.",
    "Pedidos de suporte dos clientes.": "Pedidos de suporte dos clientes.",
    "Resumo do módulo de vendas.": "Resumo do módulo de vendas.",
    "Registro e consulta de vendas.": "Registro e consulta de vendas.",
    "Pedidos de venda em andamento.": "Pedidos de venda em andamento.",
    "Controle das entregas de vendas.": "Controle das entregas de vendas.",
    "Frente de caixa e vendas rápidas.": "Frente de caixa e vendas rápidas.",
    "Resumo do módulo de compras.": "Resumo do módulo de compras.",
    "Pedidos de compra a fornecedores.": "Pedidos de compra a fornecedores.",
    "Resumo do módulo de estoque.": "Resumo do módulo de estoque.",
    "Saldo atual dos produtos.": "Saldo atual dos produtos.",
    "Entradas e saídas do estoque.": "Entradas e saídas do estoque.",
    "Inventário e ajustes de estoque.": "Inventário e ajustes de estoque.",
    "Resumo do módulo financeiro.": "Resumo do módulo financeiro.",
    "Gestão dos recebíveis da empresa.": "Gestão dos recebíveis da empresa.",
    "Controle de vendas parceladas.": "Controle de vendas parceladas.",
    "Gestão dos pagamentos da empresa.": "Gestão dos pagamentos da empresa.",
    "Conciliação de cartões e recebíveis.": "Conciliação de cartões e recebíveis.",
    "Controle de entradas e saídas.": "Controle de entradas e saídas.",
    "Demonstração do resultado do exercício.": "Demonstração do resultado do exercício.",
    "Resumo do módulo de suporte.": "Resumo do módulo de suporte.",
    "Chamados abertos pelos clientes.": "Chamados abertos pelos clientes.",
    "Quadro visual dos chamados.": "Quadro visual dos chamados.",
    "Agenda de atendimentos e visitas.": "Agenda de atendimentos e visitas.",
    "Conversas com clientes em tempo real.": "Conversas com clientes em tempo real.",
    "Relatórios gerenciais do sistema.": "Relatórios gerenciais do sistema.",
    "Histórico de acessos ao sistema.": "Histórico de acessos ao sistema.",
    "Registro de alterações dos usuários.": "Registro de alterações dos usuários.",
    "Gerencie o acesso ao painel inicial.": "Gerencie o acesso ao painel inicial.",
    "Gerencie acessos do módulo de cadastros.": "Gerencie acessos do módulo de cadastros.",
    "Gerencie acessos do módulo de clientes.": "Gerencie acessos do módulo de clientes.",
    "Gerencie acessos do módulo de vendas.": "Gerencie acessos do módulo de vendas.",
    "Gerencie acessos do módulo de compras.": "Gerencie acessos do módulo de compras.",
    "Gerencie acessos do módulo de estoque.": "Gerencie acessos do módulo de estoque.",
    "Gerencie acessos do módulo financeiro.": "Gerencie acessos do módulo financeiro.",
    "Gerencie acessos do módulo de suporte.": "Gerencie acessos do módulo de suporte.",
    "Gerencie acessos do módulo de relatórios.": "Gerencie acessos do módulo de relatórios.",
    "Gerencie acessos do módulo de auditoria.": "Gerencie acessos do módulo de auditoria.",
    "Gerencie os acessos de todas as telas do sistema.": "Gerencie os acessos de todas as telas do sistema.",
    "Gerencie os acessos deste módulo.": "Gerencie os acessos deste módulo.",
  },
  en: {
    "Módulos": "Modules",
    "Telas": "Screens",
    "Permissões liberadas": "Permissions enabled",
    "Telas bloqueadas": "Blocked screens",
    "sem acesso": "no access",
    "Módulos do sistema": "System modules",
    "Todos": "All",
    "Todos os módulos": "All modules",
    "Filtrar telas": "Filter screens",
    "Todas": "All",
    "Liberadas": "Enabled",
    "Bloqueadas": "Blocked",
    "Ordenar por": "Sort by",
    "Nome": "Name",
    "Status": "Status",
    "Módulo": "Module",
    "Ativar módulo inteiro": "Enable entire module",
    "Desativar módulo inteiro": "Disable entire module",
    "Ativar todas as telas": "Enable all screens",
    "Desativar todas as telas": "Disable all screens",
    "Limpar permissões": "Clear permissions",
    "Nenhuma tela encontrada para este filtro.": "No screens found for this filter.",
    "Permissões da tela": "Screen permissions",
    "Detalhes da permissão": "Permission details",
    "Os usuários deste perfil terão acesso às rotinas da tela selecionada conforme as permissões acima.": "Users with this profile will access the selected screen's features according to the permissions above.",
    "As alterações serão aplicadas ao salvar.": "Changes are applied when you save.",
    "Ativar acesso": "Enable access",
    "Visualizar": "View",
    "Editar": "Edit",
    "Excluir": "Delete",
    "Imprimir": "Print",
    "Importar": "Import",
    "Exportar": "Export",
    "Permite abrir a tela e consultar os registros.": "Allows opening the screen and viewing records.",
    "Permite criar e alterar registros da tela.": "Allows creating and changing records on the screen.",
    "Permite excluir registros da tela.": "Allows deleting records on the screen.",
    "Permite imprimir e gerar PDF dos registros.": "Allows printing and generating PDFs of records.",
    "Permite importar registros por planilha.": "Allows importing records from a spreadsheet.",
    "Permite exportar os dados da tela.": "Allows exporting the screen data.",
    "Telas e Permissões de Acesso": "Screens and Access Permissions",
    "Identificação": "Identification",
    "Janela de Horário de Acesso": "Access Time Window",
    "Descrição": "Description",
    "Painel inicial com os indicadores do sistema.": "Home panel with system indicators.",
    "Dados cadastrais das empresas.": "Company registration data.",
    "Bancos e contas utilizados.": "Banks and accounts in use.",
    "Cadastro da carteira de clientes.": "Customer base registration.",
    "Cadastro de fornecedores da empresa.": "Company supplier registration.",
    "Transportadoras usadas nas entregas.": "Carriers used for deliveries.",
    "Equipe e dados dos colaboradores.": "Team and employee data.",
    "Catálogo de produtos da empresa.": "Company product catalog.",
    "Catálogo de serviços prestados.": "Catalog of services provided.",
    "Unidades usadas nos produtos.": "Units used on products.",
    "Categorias de produtos e estoque.": "Product and inventory categories.",
    "Locais de armazenamento do estoque.": "Inventory storage locations.",
    "Grade de tamanhos dos produtos.": "Product size grid.",
    "Cores da grade de produtos.": "Colors in the product grid.",
    "Estrutura de receitas e despesas.": "Income and expense structure.",
    "Maquininhas de cartão e taxas.": "Card machines and fees.",
    "Códigos de erro do sistema.": "System error codes.",
    "Controle de números de série.": "Serial number control.",
    "Perfis de acesso e permissões.": "Access profiles and permissions.",
    "Logins de acesso dos usuários.": "User access logins.",
    "Resumo do módulo de clientes.": "Customers module summary.",
    "Pedidos de insumo dos clientes.": "Customer supply orders.",
    "Pedidos de suporte dos clientes.": "Customer support requests.",
    "Resumo do módulo de vendas.": "Sales module summary.",
    "Registro e consulta de vendas.": "Sales entry and lookup.",
    "Pedidos de venda em andamento.": "Sales orders in progress.",
    "Controle das entregas de vendas.": "Sales delivery control.",
    "Frente de caixa e vendas rápidas.": "Point of sale and quick sales.",
    "Resumo do módulo de compras.": "Purchasing module summary.",
    "Pedidos de compra a fornecedores.": "Purchase orders to suppliers.",
    "Resumo do módulo de estoque.": "Inventory module summary.",
    "Saldo atual dos produtos.": "Current product balance.",
    "Entradas e saídas do estoque.": "Inventory ins and outs.",
    "Inventário e ajustes de estoque.": "Inventory count and adjustments.",
    "Resumo do módulo financeiro.": "Finance module summary.",
    "Gestão dos recebíveis da empresa.": "Management of company receivables.",
    "Controle de vendas parceladas.": "Installment sales control.",
    "Gestão dos pagamentos da empresa.": "Management of company payables.",
    "Conciliação de cartões e recebíveis.": "Card and receivables reconciliation.",
    "Controle de entradas e saídas.": "Cash in and out control.",
    "Demonstração do resultado do exercício.": "Income statement for the period.",
    "Resumo do módulo de suporte.": "Support module summary.",
    "Chamados abertos pelos clientes.": "Tickets opened by customers.",
    "Quadro visual dos chamados.": "Visual board of tickets.",
    "Agenda de atendimentos e visitas.": "Schedule of services and visits.",
    "Conversas com clientes em tempo real.": "Real-time chat with customers.",
    "Relatórios gerenciais do sistema.": "System management reports.",
    "Histórico de acessos ao sistema.": "System access history.",
    "Registro de alterações dos usuários.": "Log of user changes.",
    "Gerencie o acesso ao painel inicial.": "Manage access to the home panel.",
    "Gerencie acessos do módulo de cadastros.": "Manage access to the registrations module.",
    "Gerencie acessos do módulo de clientes.": "Manage access to the customers module.",
    "Gerencie acessos do módulo de vendas.": "Manage access to the sales module.",
    "Gerencie acessos do módulo de compras.": "Manage access to the purchasing module.",
    "Gerencie acessos do módulo de estoque.": "Manage access to the inventory module.",
    "Gerencie acessos do módulo financeiro.": "Manage access to the finance module.",
    "Gerencie acessos do módulo de suporte.": "Manage access to the support module.",
    "Gerencie acessos do módulo de relatórios.": "Manage access to the reports module.",
    "Gerencie acessos do módulo de auditoria.": "Manage access to the audit module.",
    "Gerencie os acessos de todas as telas do sistema.": "Manage access to every screen in the system.",
    "Gerencie os acessos deste módulo.": "Manage access to this module.",
  },
  es: {
    "Módulos": "Módulos",
    "Telas": "Pantallas",
    "Permissões liberadas": "Permisos habilitados",
    "Telas bloqueadas": "Pantallas bloqueadas",
    "sem acesso": "sin acceso",
    "Módulos do sistema": "Módulos del sistema",
    "Todos": "Todos",
    "Todos os módulos": "Todos los módulos",
    "Filtrar telas": "Filtrar pantallas",
    "Todas": "Todas",
    "Liberadas": "Habilitadas",
    "Bloqueadas": "Bloqueadas",
    "Ordenar por": "Ordenar por",
    "Nome": "Nombre",
    "Status": "Estado",
    "Módulo": "Módulo",
    "Ativar módulo inteiro": "Activar módulo completo",
    "Desativar módulo inteiro": "Desactivar módulo completo",
    "Ativar todas as telas": "Activar todas las pantallas",
    "Desativar todas as telas": "Desactivar todas las pantallas",
    "Limpar permissões": "Limpiar permisos",
    "Nenhuma tela encontrada para este filtro.": "No se encontraron pantallas para este filtro.",
    "Permissões da tela": "Permisos de la pantalla",
    "Detalhes da permissão": "Detalles del permiso",
    "Os usuários deste perfil terão acesso às rotinas da tela selecionada conforme as permissões acima.": "Los usuarios de este perfil accederán a las rutinas de la pantalla seleccionada según los permisos anteriores.",
    "As alterações serão aplicadas ao salvar.": "Los cambios se aplicarán al guardar.",
    "Ativar acesso": "Activar acceso",
    "Visualizar": "Ver",
    "Editar": "Editar",
    "Excluir": "Eliminar",
    "Imprimir": "Imprimir",
    "Importar": "Importar",
    "Exportar": "Exportar",
    "Permite abrir a tela e consultar os registros.": "Permite abrir la pantalla y consultar los registros.",
    "Permite criar e alterar registros da tela.": "Permite crear y modificar registros de la pantalla.",
    "Permite excluir registros da tela.": "Permite eliminar registros de la pantalla.",
    "Permite imprimir e gerar PDF dos registros.": "Permite imprimir y generar PDF de los registros.",
    "Permite importar registros por planilha.": "Permite importar registros desde una planilla.",
    "Permite exportar os dados da tela.": "Permite exportar los datos de la pantalla.",
    "Telas e Permissões de Acesso": "Pantallas y Permisos de Acceso",
    "Identificação": "Identificación",
    "Janela de Horário de Acesso": "Ventana de Horario de Acceso",
    "Descrição": "Descripción",
    "Painel inicial com os indicadores do sistema.": "Panel inicial con los indicadores del sistema.",
    "Dados cadastrais das empresas.": "Datos de registro de las empresas.",
    "Bancos e contas utilizados.": "Bancos y cuentas utilizados.",
    "Cadastro da carteira de clientes.": "Registro de la cartera de clientes.",
    "Cadastro de fornecedores da empresa.": "Registro de proveedores de la empresa.",
    "Transportadoras usadas nas entregas.": "Transportistas usados en las entregas.",
    "Equipe e dados dos colaboradores.": "Equipo y datos de los colaboradores.",
    "Catálogo de produtos da empresa.": "Catálogo de productos de la empresa.",
    "Catálogo de serviços prestados.": "Catálogo de servicios prestados.",
    "Unidades usadas nos produtos.": "Unidades usadas en los productos.",
    "Categorias de produtos e estoque.": "Categorías de productos y stock.",
    "Locais de armazenamento do estoque.": "Lugares de almacenamiento del stock.",
    "Grade de tamanhos dos produtos.": "Grilla de tallas de los productos.",
    "Cores da grade de produtos.": "Colores de la grilla de productos.",
    "Estrutura de receitas e despesas.": "Estructura de ingresos y egresos.",
    "Maquininhas de cartão e taxas.": "Terminales de tarjeta y tasas.",
    "Códigos de erro do sistema.": "Códigos de error del sistema.",
    "Controle de números de série.": "Control de números de serie.",
    "Perfis de acesso e permissões.": "Perfiles de acceso y permisos.",
    "Logins de acesso dos usuários.": "Inicios de sesión de los usuarios.",
    "Resumo do módulo de clientes.": "Resumen del módulo de clientes.",
    "Pedidos de insumo dos clientes.": "Pedidos de insumos de los clientes.",
    "Pedidos de suporte dos clientes.": "Pedidos de soporte de los clientes.",
    "Resumo do módulo de vendas.": "Resumen del módulo de ventas.",
    "Registro e consulta de vendas.": "Registro y consulta de ventas.",
    "Pedidos de venda em andamento.": "Pedidos de venta en curso.",
    "Controle das entregas de vendas.": "Control de las entregas de ventas.",
    "Frente de caixa e vendas rápidas.": "Punto de venta y ventas rápidas.",
    "Resumo do módulo de compras.": "Resumen del módulo de compras.",
    "Pedidos de compra a fornecedores.": "Pedidos de compra a proveedores.",
    "Resumo do módulo de estoque.": "Resumen del módulo de stock.",
    "Saldo atual dos produtos.": "Saldo actual de los productos.",
    "Entradas e saídas do estoque.": "Entradas y salidas del stock.",
    "Inventário e ajustes de estoque.": "Inventario y ajustes de stock.",
    "Resumo do módulo financeiro.": "Resumen del módulo financiero.",
    "Gestão dos recebíveis da empresa.": "Gestión de las cuentas por cobrar.",
    "Controle de vendas parceladas.": "Control de ventas en cuotas.",
    "Gestão dos pagamentos da empresa.": "Gestión de las cuentas por pagar.",
    "Conciliação de cartões e recebíveis.": "Conciliación de tarjetas y cobros.",
    "Controle de entradas e saídas.": "Control de entradas y salidas.",
    "Demonstração do resultado do exercício.": "Estado de resultados del ejercicio.",
    "Resumo do módulo de suporte.": "Resumen del módulo de soporte.",
    "Chamados abertos pelos clientes.": "Tickets abiertos por los clientes.",
    "Quadro visual dos chamados.": "Tablero visual de los tickets.",
    "Agenda de atendimentos e visitas.": "Agenda de atenciones y visitas.",
    "Conversas com clientes em tempo real.": "Conversaciones con clientes en tiempo real.",
    "Relatórios gerenciais do sistema.": "Informes gerenciales del sistema.",
    "Histórico de acessos ao sistema.": "Historial de accesos al sistema.",
    "Registro de alterações dos usuários.": "Registro de cambios de los usuarios.",
    "Gerencie o acesso ao painel inicial.": "Gestione el acceso al panel inicial.",
    "Gerencie acessos do módulo de cadastros.": "Gestione los accesos del módulo de registros.",
    "Gerencie acessos do módulo de clientes.": "Gestione los accesos del módulo de clientes.",
    "Gerencie acessos do módulo de vendas.": "Gestione los accesos del módulo de ventas.",
    "Gerencie acessos do módulo de compras.": "Gestione los accesos del módulo de compras.",
    "Gerencie acessos do módulo de estoque.": "Gestione los accesos del módulo de stock.",
    "Gerencie acessos do módulo financeiro.": "Gestione los accesos del módulo financiero.",
    "Gerencie acessos do módulo de suporte.": "Gestione los accesos del módulo de soporte.",
    "Gerencie acessos do módulo de relatórios.": "Gestione los accesos del módulo de informes.",
    "Gerencie acessos do módulo de auditoria.": "Gestione los accesos del módulo de auditoría.",
    "Gerencie os acessos de todas as telas do sistema.": "Gestione los accesos de todas las pantallas del sistema.",
    "Gerencie os acessos deste módulo.": "Gestione los accesos de este módulo.",
  },
  "zh-CN": {
    "Módulos": "模块",
    "Telas": "页面",
    "Permissões liberadas": "已开通权限",
    "Telas bloqueadas": "已禁用页面",
    "sem acesso": "无访问权限",
    "Módulos do sistema": "系统模块",
    "Todos": "全部",
    "Todos os módulos": "全部模块",
    "Filtrar telas": "筛选页面",
    "Todas": "全部",
    "Liberadas": "已开通",
    "Bloqueadas": "已禁用",
    "Ordenar por": "排序方式",
    "Nome": "名称",
    "Status": "状态",
    "Módulo": "模块",
    "Ativar módulo inteiro": "开通整个模块",
    "Desativar módulo inteiro": "关闭整个模块",
    "Ativar todas as telas": "开通全部页面",
    "Desativar todas as telas": "关闭全部页面",
    "Limpar permissões": "清空权限",
    "Nenhuma tela encontrada para este filtro.": "该筛选条件下未找到页面。",
    "Permissões da tela": "页面权限",
    "Detalhes da permissão": "权限说明",
    "Os usuários deste perfil terão acesso às rotinas da tela selecionada conforme as permissões acima.": "该角色的用户将按以上权限访问所选页面的功能。",
    "As alterações serão aplicadas ao salvar.": "保存后更改才会生效。",
    "Ativar acesso": "启用访问",
    "Visualizar": "查看",
    "Editar": "编辑",
    "Excluir": "删除",
    "Imprimir": "打印",
    "Importar": "导入",
    "Exportar": "导出",
    "Permite abrir a tela e consultar os registros.": "允许打开页面并查看记录。",
    "Permite criar e alterar registros da tela.": "允许新建和修改页面记录。",
    "Permite excluir registros da tela.": "允许删除页面记录。",
    "Permite imprimir e gerar PDF dos registros.": "允许打印记录并生成 PDF。",
    "Permite importar registros por planilha.": "允许通过表格导入记录。",
    "Permite exportar os dados da tela.": "允许导出页面数据。",
    "Telas e Permissões de Acesso": "页面与访问权限",
    "Identificação": "标识信息",
    "Janela de Horário de Acesso": "访问时间段",
    "Descrição": "描述",
    "Painel inicial com os indicadores do sistema.": "展示系统指标的首页面板。",
    "Dados cadastrais das empresas.": "企业的登记资料。",
    "Bancos e contas utilizados.": "使用中的银行和账户。",
    "Cadastro da carteira de clientes.": "客户资料登记。",
    "Cadastro de fornecedores da empresa.": "企业供应商登记。",
    "Transportadoras usadas nas entregas.": "配送使用的承运商。",
    "Equipe e dados dos colaboradores.": "团队与员工资料。",
    "Catálogo de produtos da empresa.": "企业产品目录。",
    "Catálogo de serviços prestados.": "提供的服务目录。",
    "Unidades usadas nos produtos.": "产品使用的计量单位。",
    "Categorias de produtos e estoque.": "产品与库存分类。",
    "Locais de armazenamento do estoque.": "库存存放地点。",
    "Grade de tamanhos dos produtos.": "产品尺码表。",
    "Cores da grade de produtos.": "产品颜色表。",
    "Estrutura de receitas e despesas.": "收入与支出结构。",
    "Maquininhas de cartão e taxas.": "刷卡机与费率。",
    "Códigos de erro do sistema.": "系统错误代码。",
    "Controle de números de série.": "序列号管理。",
    "Perfis de acesso e permissões.": "访问角色与权限。",
    "Logins de acesso dos usuários.": "用户登录账号。",
    "Resumo do módulo de clientes.": "客户模块概览。",
    "Pedidos de insumo dos clientes.": "客户的物料订单。",
    "Pedidos de suporte dos clientes.": "客户的支持请求。",
    "Resumo do módulo de vendas.": "销售模块概览。",
    "Registro e consulta de vendas.": "销售登记与查询。",
    "Pedidos de venda em andamento.": "进行中的销售订单。",
    "Controle das entregas de vendas.": "销售配送管理。",
    "Frente de caixa e vendas rápidas.": "收银台与快速销售。",
    "Resumo do módulo de compras.": "采购模块概览。",
    "Pedidos de compra a fornecedores.": "向供应商下的采购订单。",
    "Resumo do módulo de estoque.": "库存模块概览。",
    "Saldo atual dos produtos.": "产品当前余额。",
    "Entradas e saídas do estoque.": "库存出入库记录。",
    "Inventário e ajustes de estoque.": "库存盘点与调整。",
    "Resumo do módulo financeiro.": "财务模块概览。",
    "Gestão dos recebíveis da empresa.": "企业应收账款管理。",
    "Controle de vendas parceladas.": "分期销售管理。",
    "Gestão dos pagamentos da empresa.": "企业应付账款管理。",
    "Conciliação de cartões e recebíveis.": "银行卡与应收对账。",
    "Controle de entradas e saídas.": "收支明细管理。",
    "Demonstração do resultado do exercício.": "本期利润表。",
    "Resumo do módulo de suporte.": "支持模块概览。",
    "Chamados abertos pelos clientes.": "客户提交的工单。",
    "Quadro visual dos chamados.": "工单看板视图。",
    "Agenda de atendimentos e visitas.": "服务与上门日程。",
    "Conversas com clientes em tempo real.": "与客户实时对话。",
    "Relatórios gerenciais do sistema.": "系统管理报表。",
    "Histórico de acessos ao sistema.": "系统访问历史。",
    "Registro de alterações dos usuários.": "用户操作变更记录。",
    "Gerencie o acesso ao painel inicial.": "管理首页面板的访问权限。",
    "Gerencie acessos do módulo de cadastros.": "管理登记模块的访问权限。",
    "Gerencie acessos do módulo de clientes.": "管理客户模块的访问权限。",
    "Gerencie acessos do módulo de vendas.": "管理销售模块的访问权限。",
    "Gerencie acessos do módulo de compras.": "管理采购模块的访问权限。",
    "Gerencie acessos do módulo de estoque.": "管理库存模块的访问权限。",
    "Gerencie acessos do módulo financeiro.": "管理财务模块的访问权限。",
    "Gerencie acessos do módulo de suporte.": "管理支持模块的访问权限。",
    "Gerencie acessos do módulo de relatórios.": "管理报表模块的访问权限。",
    "Gerencie acessos do módulo de auditoria.": "管理审计模块的访问权限。",
    "Gerencie os acessos de todas as telas do sistema.": "管理系统全部页面的访问权限。",
    "Gerencie os acessos deste módulo.": "管理本模块的访问权限。",
  },
};

const TEXTOS_CENTRAL_NOTIFICACOES: Record<Idioma, Dicionario> = {
  pt: {
    "Notificações": "Notificações",
    "Acompanhe seus alertas em tempo real": "Acompanhe seus alertas em tempo real",
    "Não lidas": "Não lidas",
    "Hoje": "Hoje",
    "Filtrar notificações": "Filtrar notificações",
    "Marcar todas como lidas": "Marcar todas como lidas",
    "Atividades": "Atividades",
    "Novidades": "Novidades",
    "Avisos": "Avisos",
    "Limpar tudo": "Limpar tudo",
    "Pesquisar atividades": "Pesquisar atividades",
    "Pesquisar novidades": "Pesquisar novidades",
    "Pesquisar avisos": "Pesquisar avisos",
  },
  en: {
    "Notificações": "Notifications",
    "Acompanhe seus alertas em tempo real": "Track your alerts in real time",
    "Não lidas": "Unread",
    "Hoje": "Today",
    "Filtrar notificações": "Filter notifications",
    "Marcar todas como lidas": "Mark all as read",
    "Atividades": "Activity",
    "Novidades": "What's new",
    "Avisos": "Alerts",
    "Limpar tudo": "Clear all",
    "Pesquisar atividades": "Search activity",
    "Pesquisar novidades": "Search what's new",
    "Pesquisar avisos": "Search alerts",
  },
  es: {
    "Notificações": "Notificaciones",
    "Acompanhe seus alertas em tempo real": "Siga sus alertas en tiempo real",
    "Não lidas": "No leídas",
    "Hoje": "Hoy",
    "Filtrar notificações": "Filtrar notificaciones",
    "Marcar todas como lidas": "Marcar todas como leídas",
    "Atividades": "Actividades",
    "Novidades": "Novedades",
    "Avisos": "Avisos",
    "Limpar tudo": "Limpiar todo",
    "Pesquisar atividades": "Buscar actividades",
    "Pesquisar novidades": "Buscar novedades",
    "Pesquisar avisos": "Buscar avisos",
  },
  "zh-CN": {
    "Notificações": "通知",
    "Acompanhe seus alertas em tempo real": "实时跟踪您的提醒",
    "Não lidas": "未读",
    "Hoje": "今天",
    "Filtrar notificações": "筛选通知",
    "Marcar todas como lidas": "全部标为已读",
    "Atividades": "动态",
    "Novidades": "新功能",
    "Avisos": "公告",
    "Limpar tudo": "全部清除",
    "Pesquisar atividades": "搜索动态",
    "Pesquisar novidades": "搜索新功能",
    "Pesquisar avisos": "搜索公告",
  },
};

const TEXTOS_CHAT_INTERNO: Record<Idioma, Dicionario> = {
  pt: {
    "Chat interno": "Chat interno",
    "Converse com sua equipe em tempo real": "Converse com sua equipe em tempo real",
    "Pesquisar conversas": "Pesquisar conversas",
    "Pesquisar colegas": "Pesquisar colegas",
    "Filtrar conversas": "Filtrar conversas",
    "Online": "Online",
    "Novo grupo": "Novo grupo",
    "Conversas": "Conversas",
    "Equipe": "Equipe",
    "Arquivadas": "Arquivadas",
    "Arquivar": "Arquivar",
    "Desarquivar": "Desarquivar",
    "Nenhuma conversa por aqui.": "Nenhuma conversa por aqui.",
    "Nenhuma conversa arquivada.": "Nenhuma conversa arquivada.",
    "Nenhum colega encontrado.": "Nenhum colega encontrado.",
    "Sem mensagens ainda": "Sem mensagens ainda",
    "digitando...": "digitando...",
    "Online agora": "Online agora",
    "Offline": "Offline",
    "membros": "membros",
    "Detalhes da conversa": "Detalhes da conversa",
    "Comece a conversa enviando uma mensagem.": "Comece a conversa enviando uma mensagem.",
    "Escreva uma mensagem...": "Escreva uma mensagem...",
    "Emojis": "Emojis",
    "Enviar mensagem": "Enviar mensagem",
    "Arquivo": "Arquivo",
    "Nome do grupo": "Nome do grupo",
    "Ex.: Financeiro": "Ex.: Financeiro",
    "Participantes": "Participantes",
    "Criar grupo": "Criar grupo",
    "Fechar": "Fechar",
    "Ontem": "Ontem",
    "Não foi possível enviar o anexo.": "Não foi possível enviar o anexo.",
    "Não foi possível arquivar a conversa.": "Não foi possível arquivar a conversa.",
    "Não foi possível criar o grupo.": "Não foi possível criar o grupo.",
    "O arquivo excede o tamanho máximo permitido.": "O arquivo excede o tamanho máximo permitido.",
  },
  en: {
    "Chat interno": "Internal chat",
    "Converse com sua equipe em tempo real": "Talk to your team in real time",
    "Pesquisar conversas": "Search conversations",
    "Pesquisar colegas": "Search colleagues",
    "Filtrar conversas": "Filter conversations",
    "Online": "Online",
    "Novo grupo": "New group",
    "Conversas": "Chats",
    "Equipe": "Team",
    "Arquivadas": "Archived",
    "Arquivar": "Archive",
    "Desarquivar": "Unarchive",
    "Nenhuma conversa por aqui.": "No conversations here.",
    "Nenhuma conversa arquivada.": "No archived conversations.",
    "Nenhum colega encontrado.": "No colleagues found.",
    "Sem mensagens ainda": "No messages yet",
    "digitando...": "typing...",
    "Online agora": "Online now",
    "Offline": "Offline",
    "membros": "members",
    "Detalhes da conversa": "Conversation details",
    "Comece a conversa enviando uma mensagem.": "Start the conversation by sending a message.",
    "Escreva uma mensagem...": "Write a message...",
    "Emojis": "Emojis",
    "Enviar mensagem": "Send message",
    "Arquivo": "File",
    "Nome do grupo": "Group name",
    "Ex.: Financeiro": "E.g.: Finance",
    "Participantes": "Members",
    "Criar grupo": "Create group",
    "Fechar": "Close",
    "Ontem": "Yesterday",
    "Não foi possível enviar o anexo.": "Could not send the attachment.",
    "Não foi possível arquivar a conversa.": "Could not archive the conversation.",
    "Não foi possível criar o grupo.": "Could not create the group.",
    "O arquivo excede o tamanho máximo permitido.": "The file exceeds the maximum allowed size.",
  },
  es: {
    "Chat interno": "Chat interno",
    "Converse com sua equipe em tempo real": "Converse con su equipo en tiempo real",
    "Pesquisar conversas": "Buscar conversaciones",
    "Pesquisar colegas": "Buscar colegas",
    "Filtrar conversas": "Filtrar conversaciones",
    "Online": "En línea",
    "Novo grupo": "Nuevo grupo",
    "Conversas": "Conversaciones",
    "Equipe": "Equipo",
    "Arquivadas": "Archivadas",
    "Arquivar": "Archivar",
    "Desarquivar": "Desarchivar",
    "Nenhuma conversa por aqui.": "No hay conversaciones por aquí.",
    "Nenhuma conversa arquivada.": "No hay conversaciones archivadas.",
    "Nenhum colega encontrado.": "No se encontraron colegas.",
    "Sem mensagens ainda": "Aún sin mensajes",
    "digitando...": "escribiendo...",
    "Online agora": "En línea ahora",
    "Offline": "Desconectado",
    "membros": "miembros",
    "Detalhes da conversa": "Detalles de la conversación",
    "Comece a conversa enviando uma mensagem.": "Comience la conversación enviando un mensaje.",
    "Escreva uma mensagem...": "Escriba un mensaje...",
    "Emojis": "Emojis",
    "Enviar mensagem": "Enviar mensaje",
    "Arquivo": "Archivo",
    "Nome do grupo": "Nombre del grupo",
    "Ex.: Financeiro": "Ej.: Finanzas",
    "Participantes": "Participantes",
    "Criar grupo": "Crear grupo",
    "Fechar": "Cerrar",
    "Ontem": "Ayer",
    "Não foi possível enviar o anexo.": "No se pudo enviar el adjunto.",
    "Não foi possível arquivar a conversa.": "No se pudo archivar la conversación.",
    "Não foi possível criar o grupo.": "No se pudo crear el grupo.",
    "O arquivo excede o tamanho máximo permitido.": "El archivo supera el tamaño máximo permitido.",
  },
  "zh-CN": {
    "Chat interno": "内部聊天",
    "Converse com sua equipe em tempo real": "与团队实时沟通",
    "Pesquisar conversas": "搜索会话",
    "Pesquisar colegas": "搜索同事",
    "Filtrar conversas": "筛选会话",
    "Online": "在线",
    "Novo grupo": "新建群组",
    "Conversas": "会话",
    "Equipe": "团队",
    "Arquivadas": "已归档",
    "Arquivar": "归档",
    "Desarquivar": "取消归档",
    "Nenhuma conversa por aqui.": "这里还没有会话。",
    "Nenhuma conversa arquivada.": "没有已归档的会话。",
    "Nenhum colega encontrado.": "未找到同事。",
    "Sem mensagens ainda": "暂无消息",
    "digitando...": "正在输入…",
    "Online agora": "当前在线",
    "Offline": "离线",
    "membros": "位成员",
    "Detalhes da conversa": "会话详情",
    "Comece a conversa enviando uma mensagem.": "发送一条消息，开始对话。",
    "Escreva uma mensagem...": "输入消息…",
    "Emojis": "表情",
    "Enviar mensagem": "发送消息",
    "Arquivo": "文件",
    "Nome do grupo": "群组名称",
    "Ex.: Financeiro": "例如：财务",
    "Participantes": "成员",
    "Criar grupo": "创建群组",
    "Fechar": "关闭",
    "Ontem": "昨天",
    "Não foi possível enviar o anexo.": "无法发送附件。",
    "Não foi possível arquivar a conversa.": "无法归档会话。",
    "Não foi possível criar o grupo.": "无法创建群组。",
    "O arquivo excede o tamanho máximo permitido.": "文件超过允许的最大大小。",
  },
};

const TEXTOS_NAVEGACAO_MODULOS: Record<Idioma, Dicionario> = {
  pt: {
    "Setor Fiscal": "Setor Fiscal",
    "CRM e Marketing": "CRM e Marketing",
    "Recursos Humanos": "Recursos Humanos",
    "Departamento Pessoal": "Departamento Pessoal",
    Produtividade: "Produtividade",
    "Checklists Inteligentes": "Checklists Inteligentes",
    "Workflow Inteligente": "Workflow Inteligente",
    "Documentos com IA": "Documentos com IA",
    "Agenda Digital": "Agenda Digital",
    "Caixa de Sugestão": "Caixa de Sugestão",
  },
  en: {
    "Setor Fiscal": "Tax Department",
    "CRM e Marketing": "CRM and Marketing",
    "Recursos Humanos": "Human Resources",
    "Departamento Pessoal": "Personnel Department",
    Produtividade: "Productivity",
    "Checklists Inteligentes": "Smart Checklists",
    "Workflow Inteligente": "Smart Workflow",
    "Documentos com IA": "AI Documents",
    "Agenda Digital": "Digital Calendar",
    "Caixa de Sugestão": "Suggestion Box",
  },
  es: {
    "Setor Fiscal": "Sector Fiscal",
    "CRM e Marketing": "CRM y Marketing",
    "Recursos Humanos": "Recursos Humanos",
    "Departamento Pessoal": "Departamento de Personal",
    Produtividade: "Productividad",
    "Checklists Inteligentes": "Listas de Verificación Inteligentes",
    "Workflow Inteligente": "Flujo de Trabajo Inteligente",
    "Documentos com IA": "Documentos con IA",
    "Agenda Digital": "Agenda Digital",
    "Caixa de Sugestão": "Buzón de Sugerencias",
  },
  "zh-CN": {
    "Setor Fiscal": "税务部门",
    "CRM e Marketing": "客户关系与营销",
    "Recursos Humanos": "人力资源",
    "Departamento Pessoal": "人事部门",
    Produtividade: "生产力",
    "Checklists Inteligentes": "智能检查清单",
    "Workflow Inteligente": "智能工作流",
    "Documentos com IA": "AI 文档",
    "Agenda Digital": "数字日程",
    "Caixa de Sugestão": "意见箱",
  },
};

const LINHAS_LOJA_MODULOS: Array<[string, string, string, string]> = [
  ["Módulos da plataforma", "Platform modules", "Módulos de la plataforma", "平台模块"],
  ["Defina o nome, o valor mensal e os módulos disponíveis na loja da carteira do cliente.", "Set the name, monthly price, and modules available in the customer's wallet store.", "Define el nombre, el valor mensual y los módulos disponibles en la tienda de la cartera del cliente.", "设置客户钱包商店中可用模块的名称和月费。"],
  ["Nome do módulo", "Module name", "Nombre del módulo", "模块名称"],
  ["Preço mensal (R$)", "Monthly price (R$)", "Precio mensual (R$)", "月费（R$）"],
  ["Salvar módulos", "Save modules", "Guardar módulos", "保存模块"],
  ["Módulos da loja salvos.", "Store modules saved.", "Módulos de la tienda guardados.", "商店模块已保存。"],
  ["Não foi possível carregar os módulos da loja.", "Could not load store modules.", "No se pudieron cargar los módulos de la tienda.", "无法加载商店模块。"],
  ["Não foi possível salvar os módulos da loja.", "Could not save store modules.", "No se pudieron guardar los módulos de la tienda.", "无法保存商店模块。"],
  ["Navegação da carteira", "Wallet navigation", "Navegación de la cartera", "钱包导航"],
  ["Loja de módulos", "Module store", "Tienda de módulos", "模块商店"],
  ["Contrate novos módulos para ampliar os recursos disponíveis na organização.", "Add new modules to expand the features available to the organization.", "Contrata nuevos módulos para ampliar los recursos disponibles en la organización.", "订购新模块以扩展组织可用的功能。"],
  ["A contratação atualiza a assinatura mensal e mantém o plano e os adicionais já ativos.", "The purchase updates the monthly subscription and keeps the current plan and add-ons active.", "La contratación actualiza la suscripción mensual y mantiene activos el plan y los adicionales actuales.", "订购将更新月度订阅，并保留当前套餐和已启用的附加项。"],
  ["Módulo contratado", "Module subscribed", "Módulo contratado", "已订购模块"],
  ["Contratar módulo", "Subscribe to module", "Contratar módulo", "订购模块"],
  ["Nenhum módulo está disponível na loja no momento.", "No modules are currently available in the store.", "No hay módulos disponibles en la tienda en este momento.", "商店中目前没有可用模块。"],
  ["Não foi possível iniciar a contratação do módulo.", "Could not start the module purchase.", "No se pudo iniciar la contratación del módulo.", "无法开始订购模块。"],
];

const TEXTOS_LOJA_MODULOS: Record<Idioma, Dicionario> = {
  pt: Object.fromEntries(LINHAS_LOJA_MODULOS.map(([pt]) => [pt, pt])),
  en: Object.fromEntries(LINHAS_LOJA_MODULOS.map(([pt, en]) => [pt, en])),
  es: Object.fromEntries(LINHAS_LOJA_MODULOS.map(([pt, , es]) => [pt, es])),
  "zh-CN": Object.fromEntries(LINHAS_LOJA_MODULOS.map(([pt, , , zh]) => [pt, zh])),
};

const LINHAS_RELATORIOS_HUB: Array<[string, string, string, string]> = [
  ["Selecionar relatório", "Select report", "Seleccionar informe", "选择报表"],
  ["Nenhum relatório disponível", "No reports available", "No hay informes disponibles", "无可用报表"],
  ["Sem dados disponíveis na plataforma", "No data available on the platform", "Sin datos disponibles en la plataforma", "平台暂无数据"],
  ["Selecione um relatório.", "Select a report.", "Seleccione un informe.", "请选择报表。"],
  ["Selecione um relatório disponível.", "Select an available report.", "Seleccione un informe disponible.", "请选择可用报表。"],
  ["Este relatório ainda não possui dados disponíveis na plataforma.", "This report does not have data available on the platform yet.", "Este informe aún no tiene datos disponibles en la plataforma.", "此报表在平台上暂时没有可用数据。"],
  ["Nenhum dado encontrado para este relatório.", "No data found for this report.", "No se encontraron datos para este informe.", "未找到此报表的数据。"],
  ["Nenhum relatório disponível para os módulos ativos da organização.", "No reports are available for the organization's active modules.", "No hay informes disponibles para los módulos activos de la organización.", "组织已启用的模块暂无可用报表。"],
  ["Dados consolidados com base nos registros atuais da plataforma.", "Consolidated data based on the platform's current records.", "Datos consolidados basados en los registros actuales de la plataforma.", "基于平台当前记录的汇总数据。"],
  ["Títulos vencidos com saldo em aberto.", "Overdue items with an outstanding balance.", "Títulos vencidos con saldo pendiente.", "存在未结余额的逾期项目。"],
  ["Clientes sem compras registradas nos últimos 90 dias.", "Customers with no purchases recorded in the last 90 days.", "Clientes sin compras registradas en los últimos 90 días.", "过去 90 天内无购买记录的客户。"],
  ["Produtos com saldo e sem movimentação nos últimos 90 dias.", "Products with stock and no movement in the last 90 days.", "Productos con existencias y sin movimientos en los últimos 90 días.", "有库存且过去 90 天无变动的商品。"],
  ["SLA calculado com meta de resposta de até 4 horas.", "SLA calculated with a response target of up to 4 hours.", "SLA calculado con un objetivo de respuesta de hasta 4 horas.", "SLA 按 4 小时内响应目标计算。"],
  ["Fluxo de Caixa", "Cash Flow", "Flujo de Caja", "现金流"],
  ["Contas a Receber", "Accounts Receivable", "Cuentas por Cobrar", "应收账款"],
  ["Contas a Pagar", "Accounts Payable", "Cuentas por Pagar", "应付账款"],
  ["Faturamento", "Revenue", "Facturación", "营业收入"],
  ["Recebimentos", "Receipts", "Cobros", "收款"],
  ["Pagamentos", "Payments", "Pagos", "付款"],
  ["Inadimplência", "Delinquency", "Morosidad", "逾期欠款"],
  ["Lucro e Prejuízo", "Profit and Loss", "Ganancias y Pérdidas", "损益"],
  ["DRE", "Income Statement", "Estado de Resultados", "损益表"],
  ["Balancete", "Trial Balance", "Balance de Comprobación", "试算平衡表"],
  ["Conciliação Bancária", "Bank Reconciliation", "Conciliación Bancaria", "银行对账"],
  ["Centro de Custos", "Cost Center", "Centro de Costos", "成本中心"],
  ["Pedidos de Compra", "Purchase Orders", "Pedidos de Compra", "采购订单"],
  ["Cotações", "Quotations", "Cotizaciones", "询价"],
  ["Fornecedores", "Suppliers", "Proveedores", "供应商"],
  ["Produtos Comprados", "Purchased Products", "Productos Comprados", "已采购商品"],
  ["Compras por Período", "Purchases by Period", "Compras por Período", "按期间采购"],
  ["Compras por Fornecedor", "Purchases by Supplier", "Compras por Proveedor", "按供应商采购"],
  ["Compras por Categoria", "Purchases by Category", "Compras por Categoría", "按类别采购"],
  ["Entregas Pendentes", "Pending Deliveries", "Entregas Pendientes", "待交付"],
  ["Histórico de Compras", "Purchase History", "Historial de Compras", "采购历史"],
  ["Ranking de Fornecedores", "Supplier Ranking", "Clasificación de Proveedores", "供应商排名"],
  ["Pedidos de Venda", "Sales Orders", "Pedidos de Venta", "销售订单"],
  ["Vendas por Cliente", "Sales by Customer", "Ventas por Cliente", "按客户销售"],
  ["Vendas por Produto", "Sales by Product", "Ventas por Producto", "按商品销售"],
  ["Vendas por Categoria", "Sales by Category", "Ventas por Categoría", "按类别销售"],
  ["Vendas por Vendedor", "Sales by Salesperson", "Ventas por Vendedor", "按销售员销售"],
  ["Vendas por Período", "Sales by Period", "Ventas por Período", "按期间销售"],
  ["Ticket Médio", "Average Order Value", "Ticket Promedio", "平均客单价"],
  ["Metas de Vendas", "Sales Targets", "Metas de Ventas", "销售目标"],
  ["Produtos Mais Vendidos", "Best-Selling Products", "Productos Más Vendidos", "畅销商品"],
  ["Clientes Inativos", "Inactive Customers", "Clientes Inactivos", "非活跃客户"],
  ["Comissões", "Commissions", "Comisiones", "佣金"],
  ["Posição de Estoque", "Inventory Position", "Posición de Inventario", "库存状况"],
  ["Movimentação de Estoque", "Inventory Movement", "Movimiento de Inventario", "库存变动"],
  ["Entradas", "Entries", "Entradas", "入库"],
  ["Saídas", "Exits", "Salidas", "出库"],
  ["Inventário", "Inventory Count", "Inventario", "盘点"],
  ["Produtos sem Estoque", "Out-of-Stock Products", "Productos sin Stock", "缺货商品"],
  ["Estoque Mínimo", "Minimum Stock", "Stock Mínimo", "最低库存"],
  ["Produtos Parados", "Slow-Moving Products", "Productos sin Movimiento", "滞销商品"],
  ["Giro de Estoque", "Inventory Turnover", "Rotación de Inventario", "库存周转"],
  ["Curva ABC", "ABC Analysis", "Curva ABC", "ABC 分析"],
  ["Valorização do Estoque", "Inventory Valuation", "Valorización del Inventario", "库存估值"],
  ["Tickets Abertos", "Open Tickets", "Tickets Abiertos", "未结工单"],
  ["Tickets Fechados", "Closed Tickets", "Tickets Cerrados", "已关闭工单"],
  ["Tickets Pendentes", "Pending Tickets", "Tickets Pendientes", "待处理工单"],
  ["Tickets por Status", "Tickets by Status", "Tickets por Estado", "按状态统计工单"],
  ["Tickets por Prioridade", "Tickets by Priority", "Tickets por Prioridad", "按优先级统计工单"],
  ["Tickets por Categoria", "Tickets by Category", "Tickets por Categoría", "按类别统计工单"],
  ["Tickets por Atendente", "Tickets by Agent", "Tickets por Agente", "按客服统计工单"],
  ["Tempo Médio de Atendimento", "Average Handling Time", "Tiempo Medio de Atención", "平均处理时长"],
  ["Tempo Médio de Resolução", "Average Resolution Time", "Tiempo Medio de Resolución", "平均解决时长"],
  ["SLA", "SLA", "SLA", "SLA"],
  ["Satisfação do Cliente", "Customer Satisfaction", "Satisfacción del Cliente", "客户满意度"],
  ["Histórico de Atendimentos", "Service History", "Historial de Atenciones", "服务历史"],
  ["Total de entradas", "Total inflows", "Total de entradas", "流入总额"],
  ["Total de saídas", "Total outflows", "Total de salidas", "流出总额"],
  ["Saldo em aberto", "Outstanding balance", "Saldo pendiente", "未结余额"],
  ["Dias em atraso", "Days overdue", "Días de atraso", "逾期天数"],
  ["Data do recebimento", "Receipt date", "Fecha de cobro", "收款日期"],
  ["Data do pagamento", "Payment date", "Fecha de pago", "付款日期"],
  ["Lucro", "Profit", "Ganancia", "利润"],
  ["Prejuízo", "Loss", "Pérdida", "亏损"],
  ["Indicador", "Indicator", "Indicador", "指标"],
  ["Análise vertical", "Vertical analysis", "Análisis vertical", "纵向分析"],
  ["Período anterior", "Previous period", "Período anterior", "上一期间"],
  ["Adquirente", "Acquirer", "Adquirente", "收单机构"],
  ["Conciliados", "Reconciled", "Conciliados", "已对账"],
  ["Divergências", "Discrepancies", "Divergencias", "差异"],
  ["Percentual conciliado", "Reconciled percentage", "Porcentaje conciliado", "对账比例"],
  ["Número", "Number", "Número", "编号"],
  ["Validade", "Validity", "Validez", "有效期"],
  ["Propostas", "Proposals", "Propuestas", "报价方案"],
  ["Valor cotado", "Quoted amount", "Valor cotizado", "报价金额"],
  ["Responsável", "Owner", "Responsable", "负责人"],
  ["Classificação", "Classification", "Clasificación", "分类"],
  ["Quantidade comprada", "Quantity purchased", "Cantidad comprada", "采购数量"],
  ["Quantidade vendida", "Quantity sold", "Cantidad vendida", "销售数量"],
  ["Pedidos", "Orders", "Pedidos", "订单"],
  ["Período", "Period", "Período", "期间"],
  ["Produto principal", "Main product", "Producto principal", "主要商品"],
  ["Prazo de envio", "Shipping deadline", "Plazo de envío", "发货期限"],
  ["Forma de pagamento", "Payment method", "Forma de pago", "付款方式"],
  ["Valor bruto", "Gross amount", "Valor bruto", "总额"],
  ["Vendedor", "Salesperson", "Vendedor", "销售员"],
  ["Sem vendas", "No sales", "Sin ventas", "无销售"],
  ["Última venda", "Last sale", "Última venta", "最近销售"],
  ["Dias sem comprar", "Days without purchasing", "Días sin comprar", "未购买天数"],
  ["Estoque mínimo", "Minimum stock", "Stock mínimo", "最低库存"],
  ["Custo médio", "Average cost", "Costo promedio", "平均成本"],
  ["Valor em estoque", "Inventory value", "Valor en inventario", "库存价值"],
  ["Saldo anterior", "Previous balance", "Saldo anterior", "之前余额"],
  ["Saldo atual", "Current balance", "Saldo actual", "当前余额"],
  ["Tipo de saída", "Exit type", "Tipo de salida", "出库类型"],
  ["Valor de custo", "Cost value", "Valor de costo", "成本价值"],
  ["Divergência total", "Total discrepancy", "Divergencia total", "总差异"],
  ["Finalizado em", "Completed at", "Finalizado el", "完成时间"],
  ["Déficit", "Shortfall", "Déficit", "缺口"],
  ["Sem movimentação", "No movement", "Sin movimiento", "无变动"],
  ["Última movimentação", "Last movement", "Último movimiento", "最近变动"],
  ["Dias parado", "Idle days", "Días sin movimiento", "滞留天数"],
  ["Classe", "Class", "Clase", "等级"],
  ["Participação", "Share", "Participación", "占比"],
  ["Participação acumulada", "Cumulative share", "Participación acumulada", "累计占比"],
  ["Preço de venda", "Sales price", "Precio de venta", "销售价格"],
  ["Valor de venda", "Sales value", "Valor de venta", "销售价值"],
  ["Solicitante", "Requester", "Solicitante", "申请人"],
  ["Atendente", "Agent", "Agente", "客服"],
  ["Abertura", "Opened", "Apertura", "开启时间"],
  ["Percentual", "Percentage", "Porcentaje", "百分比"],
  ["Tickets calculados", "Calculated tickets", "Tickets calculados", "已计算工单"],
  ["Tempo médio (horas)", "Average time (hours)", "Tiempo medio (horas)", "平均时长（小时）"],
  ["Tempo de resposta (horas)", "Response time (hours)", "Tiempo de respuesta (horas)", "响应时长（小时）"],
  ["Dentro do SLA", "Within SLA", "Dentro del SLA", "符合 SLA"],
  ["Fora do SLA", "Outside SLA", "Fuera del SLA", "超出 SLA"],
  ["Etapa anterior", "Previous stage", "Etapa anterior", "上一阶段"],
  ["Etapa atual", "Current stage", "Etapa actual", "当前阶段"],
  // Indicadores e gráficos montados a partir das colunas da própria listagem.
  ["Adicionar Gráficos", "Add charts", "Agregar gráficos", "添加图表"],
  ["Adicionar análise", "Add analysis", "Agregar análisis", "添加分析"],
  ["Análise gráfica", "Chart analysis", "Análisis gráfico", "图表分析"],
  ["Registros", "Records", "Registros", "记录数"],
  ["Itens listados no relatório", "Items listed in the report", "Ítems listados en el informe", "报表中列出的项目"],
  ["Total acumulado", "Accumulated total", "Total acumulado", "累计合计"],
  ["Média dos registros", "Average of records", "Promedio de los registros", "记录平均值"],
  ["Valores distintos", "Distinct values", "Valores distintos", "不同值数量"],
  ["Evolução de", "Evolution of", "Evolución de", "变化趋势："],
  ["Comparativo de", "Comparison of", "Comparativo de", "对比："],
  ["Distribuição por", "Distribution by", "Distribución por", "分布："],
  // Bloco de análise por IA no fim do relatório.
  ["Análise do relatório", "Report analysis", "Análisis del informe", "报表分析"],
  ["Destaques", "Highlights", "Destacados", "亮点"],
  ["Recomendações", "Recommendations", "Recomendaciones", "建议"],
  ["Pontos de atenção", "Points of attention", "Puntos de atención", "注意事项"],
  ["Indicadores considerados", "Indicators considered", "Indicadores considerados", "已考虑的指标"],
  ["Gerando a análise dos dados...", "Generating the data analysis...", "Generando el análisis de los datos...", "正在生成数据分析…"],
  ["Resumo automático dos dados listados acima.", "Automatic summary of the data listed above.", "Resumen automático de los datos listados arriba.", "上方数据的自动摘要。"],
  ["A análise por IA ainda está sendo gerada.", "The AI analysis is still being generated.", "El análisis por IA aún se está generando.", "AI 分析仍在生成中。"],
  ["A análise por IA está indisponível no momento.", "The AI analysis is unavailable at the moment.", "El análisis por IA no está disponible en este momento.", "AI 分析当前不可用。"],
  ["O assistente de IA está desativado nesta plataforma.", "The AI assistant is disabled on this platform.", "El asistente de IA está desactivado en esta plataforma.", "本平台的 AI 助手已停用。"],
  ["A chave da IA ainda não foi configurada no servidor.", "The AI key has not been configured on the server yet.", "La clave de IA aún no se ha configurado en el servidor.", "服务器尚未配置 AI 密钥。"],
  ["A organização está sem créditos de IA para gerar a análise.", "The organization has no AI credits to generate the analysis.", "La organización no tiene créditos de IA para generar el análisis.", "该组织没有可用于生成分析的 AI 额度。"],
  ["Seu perfil não tem permissão para usar a análise por IA.", "Your profile is not allowed to use the AI analysis.", "Su perfil no tiene permiso para usar el análisis por IA.", "您的权限组无法使用 AI 分析。"],
];

const LINHAS_TOUR_SISTEMA: Array<[string, string, string, string]> = [
  ["Este modal permite consultar ou atualizar as informações apresentadas.", "This dialog lets you review or update the information shown.", "Este modal permite consultar o actualizar la información presentada.", "此对话框可用于查看或更新所显示的信息。"],
  ["Preencha os campos conforme o conteúdo solicitado e revise as informações obrigatórias.", "Complete the fields according to the requested content and review the required information.", "Complete los campos según el contenido solicitado y revise la información obligatoria.", "请根据要求填写字段，并检查必填信息。"],
  ["Use a ação principal para confirmar; ao cancelar ou fechar, nenhuma nova alteração será aplicada.", "Use the primary action to confirm; cancelling or closing will not apply any new changes.", "Use la acción principal para confirmar; al cancelar o cerrar no se aplicará ningún cambio nuevo.", "使用主要操作进行确认；取消或关闭不会应用任何新更改。"],
  ["Fechar tour", "Close tour", "Cerrar recorrido", "关闭导览"],
  ["Pular", "Skip", "Omitir", "跳过"],
  ["Começar", "Start", "Comenzar", "开始"],
  ["Voltar", "Back", "Volver", "返回"],
  ["Próximo", "Next", "Siguiente", "下一步"],
  ["Finalizar", "Finish", "Finalizar", "完成"],
  ["Bem-vindo ao Sistema!", "Welcome to the System!", "¡Bienvenido al Sistema!", "欢迎使用系统！"],
  ["Preparamos um tour guiado para apresentar os principais recursos da plataforma.", "We prepared a guided tour to introduce the platform's main features.", "Preparamos un recorrido guiado para presentar las principales funciones de la plataforma.", "我们准备了引导式导览，介绍平台的主要功能。"],
  ["Menu de navegação", "Navigation menu", "Menú de navegación", "导航菜单"],
  ["A sidebar reúne os módulos liberados para o seu perfil. O restante da tela fica desfocado para manter o foco em cada explicação.", "The sidebar contains the modules enabled for your profile. The rest of the screen is blurred to keep the focus on each explanation.", "La barra lateral reúne los módulos habilitados para su perfil. El resto de la pantalla se desenfoca para mantener el foco en cada explicación.", "侧边栏包含您有权访问的模块。其余屏幕会模糊，以便专注于每项说明。"],
  ["Empresa atual", "Current company", "Empresa actual", "当前公司"],
  ["Neste card você confere a empresa ativa e abre o seletor para mudar de empresa.", "This card shows the active company and opens the selector to switch companies.", "En esta tarjeta puede ver la empresa activa y abrir el selector para cambiar de empresa.", "此卡片显示当前公司，并可打开选择器切换公司。"],
  ["Abrir e fechar a sidebar", "Open and close the sidebar", "Abrir y cerrar la barra lateral", "展开和收起侧边栏"],
  ["Use este botão para recolher ou expandir o menu sempre que precisar de mais espaço.", "Use this button to collapse or expand the menu whenever you need more space.", "Use este botón para contraer o expandir el menú cuando necesite más espacio.", "需要更多空间时，可使用此按钮收起或展开菜单。"],
  ["Pessoas conectadas", "People online", "Personas conectadas", "在线人员"],
  ["Os avatares mostram quem está conectado à plataforma neste momento.", "The avatars show who is currently connected to the platform.", "Los avatares muestran quién está conectado a la plataforma en este momento.", "头像显示当前已连接到平台的人员。"],
  ["Pesquisa de menus", "Menu search", "Búsqueda de menús", "菜单搜索"],
  ["Pesquise uma tela pelo nome e acesse rapidamente qualquer módulo disponível.", "Search for a screen by name and quickly open any available module.", "Busque una pantalla por nombre y acceda rápidamente a cualquier módulo disponible.", "按名称搜索页面并快速打开任何可用模块。"],
  ["Filtro de datas", "Date filter", "Filtro de fechas", "日期筛选"],
  ["Defina o intervalo usado pelos indicadores e pelas informações da visão geral.", "Set the date range used by the dashboard indicators and information.", "Defina el intervalo utilizado por los indicadores y la información de la vista general.", "设置概览指标和信息使用的日期范围。"],
  ["Agente de IA", "AI agent", "Agente de IA", "AI 智能体"],
  ["Quando habilitado, o agente de IA oferece suporte e ajuda durante o uso da plataforma.", "When enabled, the AI agent provides support and assistance while using the platform.", "Cuando está habilitado, el agente de IA ofrece soporte y ayuda durante el uso de la plataforma.", "启用后，AI 智能体会在使用平台时提供支持和帮助。"],
  ["Este sino abre a central com atividades, novidades e avisos da plataforma.", "This bell opens the hub with platform activities, updates, and notices.", "Esta campana abre la central con actividades, novedades y avisos de la plataforma.", "此铃铛可打开平台活动、更新和通知中心。"],
  ["Perfil e acessos", "Profile and access", "Perfil y accesos", "个人资料与访问权限"],
  ["Aqui você abre o perfil do login e encontra preferências, idioma, status e acessos pessoais.", "Here you open the signed-in profile and find preferences, language, status, and personal access options.", "Aquí puede abrir el perfil de inicio de sesión y encontrar preferencias, idioma, estado y accesos personales.", "您可以在此打开登录资料，并查看偏好设置、语言、状态和个人访问选项。"],
  ["Menu do perfil", "Profile menu", "Menú del perfil", "个人资料菜单"],
  ["Neste painel você ajusta tema, status e idioma, consulta sua conta e acessa as opções disponíveis para o seu perfil.", "In this panel you can adjust theme, status, and language, view your account, and access the options available to your profile.", "En este panel puede ajustar tema, estado e idioma, consultar su cuenta y acceder a las opciones disponibles para su perfil.", "在此面板中，您可以调整主题、状态和语言，查看账户并访问个人资料可用选项。"],
  ["Central de notificações", "Notification hub", "Central de notificaciones", "通知中心"],
  ["Pesquise alertas, filtre itens não lidos ou do dia, alterne entre atividades, novidades e avisos e marque tudo como lido.", "Search alerts, filter unread or today's items, switch between activities, updates, and notices, and mark everything as read.", "Busque alertas, filtre elementos no leídos o de hoy, alterne entre actividades, novedades y avisos y marque todo como leído.", "搜索提醒，筛选未读或今日项目，在活动、更新和通知间切换，并可全部标记为已读。"],
  ["Intervalo temporal", "Time range", "Intervalo temporal", "时间范围"],
  ["Escolha um período rápido ou personalize as datas no calendário; depois use Filtrar para aplicar ou Limpar para remover o intervalo.", "Choose a quick range or customize dates in the calendar; then use Filter to apply it or Clear to remove the range.", "Elija un período rápido o personalice las fechas en el calendario; luego use Filtrar para aplicarlo o Limpiar para quitar el intervalo.", "选择快捷时间段或在日历中自定义日期，然后使用“筛选”应用或“清除”移除范围。"],
  ["Cadastro de empresas", "Company records", "Registro de empresas", "公司资料"],
  ["Usaremos esta listagem para mostrar o padrão dos cadastros: filtros, pesquisa, ações, seleção, detalhes e paginação.", "We will use this list to show the standard record workflow: filters, search, actions, selection, details, and pagination.", "Usaremos esta lista para mostrar el patrón de los registros: filtros, búsqueda, acciones, selección, detalles y paginación.", "我们将使用此列表展示资料管理的标准流程：筛选、搜索、操作、选择、详情和分页。"],
  ["Filtrar por campo", "Filter by field", "Filtrar por campo", "按字段筛选"],
  ["Escolha o campo usado na pesquisa, como ID, razão social, fantasia, CNPJ, CPF ou cidade, e adicione um período quando necessário.", "Choose the search field, such as ID, legal name, trade name, CNPJ, CPF, or city, and add a date range when needed.", "Elija el campo de búsqueda, como ID, razón social, nombre comercial, CNPJ, CPF o ciudad, y agregue un período cuando sea necesario.", "选择搜索字段，例如 ID、公司法定名称、商号、CNPJ、CPF 或城市，并在需要时添加日期范围。"],
  ["Pesquisa e filtros rápidos", "Search and quick filters", "Búsqueda y filtros rápidos", "搜索与快捷筛选"],
  ["Digite o termo de busca e combine os chips de status e tipo para refinar a listagem imediatamente.", "Enter a search term and combine status and type chips to refine the list immediately.", "Escriba el término de búsqueda y combine los chips de estado y tipo para refinar la lista de inmediato.", "输入搜索词，并组合状态和类型标签以立即缩小列表范围。"],
  ["Controles da listagem", "List controls", "Controles de la lista", "列表控制"],
  ["Atualize os dados e alterne entre as visualizações em lista e grade por estes controles.", "Refresh the data and switch between list and grid views with these controls.", "Actualice los datos y cambie entre las vistas de lista y cuadrícula con estos controles.", "使用这些控件刷新数据并在列表和网格视图之间切换。"],
  ["Ações do cadastro", "Record actions", "Acciones del registro", "资料操作"],
  ["Importar carrega registros por planilha e Nova Empresa abre o formulário de inclusão.", "Import loads records from a spreadsheet, and New Company opens the creation form.", "Importar carga registros desde una hoja de cálculo y Nueva Empresa abre el formulario de alta.", "“导入”从电子表格加载资料，“新建公司”打开新增表单。"],
  ["Importação por planilha", "Spreadsheet import", "Importación por hoja de cálculo", "电子表格导入"],
  ["Arraste um arquivo XLSX ou CSV, baixe o modelo esperado e confirme a importação quando o arquivo estiver pronto.", "Drag an XLSX or CSV file, download the expected template, and confirm the import when the file is ready.", "Arrastre un archivo XLSX o CSV, descargue la plantilla esperada y confirme la importación cuando el archivo esté listo.", "拖入 XLSX 或 CSV 文件，下载所需模板，并在文件准备好后确认导入。"],
  ["Controles de cada registro", "Record controls", "Controles de cada registro", "单条资料控制"],
  ["Em cada linha você pode selecionar o registro, marcar como favorito e usar a ação de edição ou o menu disponível.", "On each row you can select the record, mark it as a favorite, and use the edit action or available menu.", "En cada fila puede seleccionar el registro, marcarlo como favorito y usar la acción de edición o el menú disponible.", "在每一行中，您可以选择资料、标记为收藏，并使用编辑操作或可用菜单。"],
  ["Ações em massa", "Bulk actions", "Acciones masivas", "批量操作"],
  ["Ao selecionar registros, esta barra libera visualizar, duplicar, excluir, exportar, imprimir, consultar anexos e limpar a seleção.", "When records are selected, this bar enables viewing, duplicating, deleting, exporting, printing, checking attachments, and clearing the selection.", "Al seleccionar registros, esta barra permite visualizar, duplicar, eliminar, exportar, imprimir, consultar adjuntos y limpiar la selección.", "选择资料后，此栏可用于查看、复制、删除、导出、打印、查看附件以及清除选择。"],
  ["Detalhes da empresa", "Company details", "Detalles de la empresa", "公司详情"],
  ["O painel lateral organiza todas as informações por seções e oferece exportação e impressão do registro.", "The side panel organizes all information into sections and provides export and print actions for the record.", "El panel lateral organiza toda la información por secciones y permite exportar e imprimir el registro.", "侧面板按部分整理所有信息，并提供资料导出和打印操作。"],
  ["Colunas do relatório", "Report columns", "Columnas del informe", "报表列"],
  ["Antes de imprimir, escolha exatamente quais colunas devem aparecer no relatório ou mantenha a impressão completa.", "Before printing, choose exactly which columns should appear in the report or keep the full printout.", "Antes de imprimir, elija exactamente qué columnas deben aparecer en el informe o mantenga la impresión completa.", "打印前，请准确选择报表中应显示的列，或保留完整打印。"],
  ["Paginação e quantidade de linhas", "Pagination and rows per page", "Paginación y cantidad de filas", "分页与每页行数"],
  ["A listagem começa com 10 itens. Escolha 25, 50 ou 100 por página e navegue pelas páginas para carregar os próximos registros.", "The list starts with 10 items. Choose 25, 50, or 100 per page and navigate through pages to load the next records.", "La lista comienza con 10 elementos. Elija 25, 50 o 100 por página y navegue por las páginas para cargar los siguientes registros.", "列表从 10 项开始。可选择每页 25、50 或 100 项，并通过翻页加载后续资料。"],
];

const TEXTOS_TOUR_SISTEMA: Record<Idioma, Dicionario> = {
  pt: Object.fromEntries(LINHAS_TOUR_SISTEMA.map(([pt]) => [pt, pt])),
  en: Object.fromEntries(LINHAS_TOUR_SISTEMA.map(([pt, en]) => [pt, en])),
  es: Object.fromEntries(LINHAS_TOUR_SISTEMA.map(([pt, , es]) => [pt, es])),
  "zh-CN": Object.fromEntries(LINHAS_TOUR_SISTEMA.map(([pt, , , zh]) => [pt, zh])),
};

const TEXTOS_RELATORIOS_HUB: Record<Idioma, Dicionario> = {
  pt: Object.fromEntries(LINHAS_RELATORIOS_HUB.map(([pt]) => [pt, pt])),
  en: Object.fromEntries(LINHAS_RELATORIOS_HUB.map(([pt, en]) => [pt, en])),
  es: Object.fromEntries(LINHAS_RELATORIOS_HUB.map(([pt, , es]) => [pt, es])),
  "zh-CN": Object.fromEntries(LINHAS_RELATORIOS_HUB.map(([pt, , , zh]) => [pt, zh])),
};

const TEXTOS: Record<Idioma, Dicionario> = {
  pt: {
    ...TEXTOS_RELATORIOS_HUB.pt,
    ...TEXTOS_TOUR_SISTEMA.pt,
    ...TEXTOS_ETAPAS.pt,
    ...TEXTOS_NAVEGACAO_MODULOS.pt,
    ...TEXTOS_LOJA_MODULOS.pt,
    ...TEXTOS_PDV.pt,
    ...TEXTOS_IMPRESSAO_COLUNAS.pt,
    ...TEXTOS_COBRANCA.pt,
    ...TEXTOS_INTEGRACOES_ERP.pt,
    ...TEXTOS_MONITOR_TEF_FISCAL.pt,
    ...TEXTOS_MONITOR_TEF_FISCAL_COMPLEMENTO.pt,
    ...TEXTOS_EMISSAO_FISCAL.pt,
    ...TEXTOS_OPERACAO_ERP.pt,
    ...TEXTOS_OPERACAO_COMPLETA.pt,
    ...TEXTOS_WHATSAPP_QR.pt,
    ...TEXTOS_CONFIG_CHAT_ATENDIMENTO.pt,
    ...TEXTOS_CHECKLIST.pt,
    ...TEXTOS_SEGURANCA.pt,
    ...TEXTOS_AGENTE_IA.pt,
    ...TEXTOS_CADASTRO_LOGIN.pt,
    ...TEXTOS_ERROS_CADASTRO.pt,
    ...TEXTOS_VENDAS_CREDIARIO.pt,
    ...TEXTOS_GRAFICOS_CONFIG.pt,
    ...TEXTOS_ANEXOS.pt,
    ...TEXTOS_COMPRAS_DASHBOARD.pt,
    ...TEXTOS_DASHBOARD_FINANCEIRO_NOVO.pt,
    ...TEXTOS_ESTOQUE_DASHBOARD.pt,
    ...TEXTOS_VISAO_GERAL_COMPLETA.pt,
    ...TEXTOS_DASHBOARD_VENDAS.pt,
    ...TEXTOS_ACESSO_PERMISSAO.pt,
    ...TEXTOS_ESTOQUE_TRANSFERENCIA.pt,
    ...TEXTOS_SUPORTE_TAREFAS.pt,
    ...TEXTOS_CONFIRMACOES_EMPRESA_VENDA.pt,
    ...TEXTOS_COMPRAS_FORNECEDORES.pt,
    ...TEXTOS_PRE_VENDA_PRECOS.pt,
    ...TEXTOS_AJUDA_CADASTROS_BASE.pt,
    ...TEXTOS_AJUDA_CADASTROS_PRINCIPAIS.pt,
    ...TEXTOS_AJUDA_OPERACIONAL_1.pt,
    ...TEXTOS_AJUDA_OPERACIONAL_2.pt,
    ...TEXTOS_FINANCEIRO_DETALHES.pt,
    ...TEXTOS_MARCA_EMPRESA.pt,
    ...TEXTOS_AJUDA_CONFIGURACOES.pt,
    ...TEXTOS_EMAIL_EMPRESAS.pt,
    ...TEXTOS_CREDITOS_OPENAI.pt,
    ...TEXTOS_EMAIL_EMPRESAS_SIMPLIFICADO.pt,
    ...TEXTOS_CHECKLIST_EXECUCAO.pt,
    ...TEXTOS_DASHBOARD_CHECKLIST.pt,
    ...TEXTOS_DASHBOARDS_HOME.pt,
    ...TEXTOS_AREA_CLIENTE.pt,
    ...TEXTOS_COMPONENTES_UI.pt,
    ...TEXTOS_PERMISSOES_PERFIL.pt,
    ...TEXTOS_CENTRAL_NOTIFICACOES.pt,
    ...TEXTOS_CHAT_INTERNO.pt,
    "Seções do cadastro": "Seções do cadastro",
    "App Android": "App Android",
    "App iOS": "App iOS",
    "Logo Agente IA": "Logo Agente IA",
    "Nome Assistente de IA": "Nome Assistente de IA",
    "Gerencie logos, banner de login e favicon.": "Gerencie logos, banner de login e favicon.",
    "Permissões do menu": "Permissões do menu",
    "Selecione um menu": "Selecione um menu",
    "Ative o acesso ao menu para configurar as permissões.": "Ative o acesso ao menu para configurar as permissões.",
    "Selecione um menu para configurar suas permissões.": "Selecione um menu para configurar suas permissões.",
    "Imagem do perfil": "Imagem do perfil",
    "Visualizar imagem do perfil": "Visualizar imagem do perfil",
    "Acessos do login": "Acessos do login",
    Acessos: "Acessos",
    "Acesso à plataforma web": "Acesso à plataforma web",
    "Acesso ao APP": "Acesso ao APP",
    "Exigir autenticação 2FA": "Exigir autenticação 2FA",
    "Usuário sem acesso à plataforma web. Fale com um administrador.": "Usuário sem acesso à plataforma web. Fale com um administrador.",
    "Informe apenas links válidos, começando com http:// ou https://.": "Informe apenas links válidos, começando com http:// ou https://.",
    Dashboard: "Dashboard",
    "Tenha uma visão completa da empresa, acompanhando operações, clientes, estoque, finanças e os principais indicadores do negócio.": "Tenha uma visão completa da empresa, acompanhando operações, clientes, estoque, finanças e os principais indicadores do negócio.",
    "Menu do usuário": "Menu do usuário",
    "Editar perfil": "Editar perfil",
    "Acesso restrito ao dono da plataforma": "Acesso restrito ao dono da plataforma",
    "Acesso restrito ao login com permissão de Configurações": "Acesso restrito ao login com permissão de Configurações",
    "Permitir acesso ao menu Configurações": "Permitir acesso ao menu Configurações",
    "Não foi possível concluir a operação no servidor.": "Não foi possível concluir a operação no servidor.",
    "Informe o nome do agente.": "Informe o nome do agente.",
    "Informe a mensagem inicial.": "Informe a mensagem inicial.",
    "Informe a mensagem de fallback.": "Informe a mensagem de fallback.",
    "Há um campo obrigatório vazio no cadastro.": "Há um campo obrigatório vazio no cadastro.",
    "Há um valor inválido em um dos campos do cadastro.": "Há um valor inválido em um dos campos do cadastro.",
    "Acesso ao menu Configurações": "Acesso ao menu Configurações",
    "Permissão permanente para o login Master principal.": "Permissão permanente para o login Master principal.",
    "Ative para permitir que este login acesse e altere as Configurações.": "Ative para permitir que este login acesse e altere as Configurações.",
    Auditoria: "Auditoria",
    "Log de acesso": "Log de acesso",
    "Auditoria de Usuário": "Auditoria de Usuário",
    "Marcar grupo": "Marcar grupo",
    "Desmarcar grupo": "Desmarcar grupo",
    "Preencha os campos obrigatórios destacados em vermelho.": "Preencha os campos obrigatórios destacados em vermelho.",
    "Selecione a empresa.": "Selecione a empresa.",
    "Informe a data da venda.": "Informe a data da venda.",
    "Selecione um produto.": "Selecione um produto.",
    "Local de estoque": "Local de estoque",
    "+ Adicionar Etapa": "+ Adicionar Etapa",
    "Nenhuma etapa cadastrada": "Nenhuma etapa cadastrada",
    "Selecione o local de estoque.": "Selecione o local de estoque.",
    "Os itens saem do estoque de": "Os itens saem do estoque de",
    "outra empresa": "outra empresa",
    "a venda gera um pedido de compra para essa empresa.": "a venda gera um pedido de compra para essa empresa.",
    "Informe uma quantidade válida.": "Informe uma quantidade válida.",
    "Informe um valor unitário válido.": "Informe um valor unitário válido.",
    "Selecione a forma de pagamento.": "Selecione a forma de pagamento.",
    "Selecione o tipo do cartão.": "Selecione o tipo do cartão.",
    "Informe o primeiro vencimento das parcelas.": "Informe o primeiro vencimento das parcelas.",
    Liberado: "Liberado",
    Bloqueado: "Bloqueado",
    "Seções das configurações": "Seções das configurações",
    "Aparência Geral": "Aparência Geral",
    "Configurações do Mobile": "Configurações do Mobile",
    "Ajustes exclusivos do aplicativo. A identidade visual continua em Aparência Geral.": "Ajustes exclusivos do aplicativo. A identidade visual continua em Aparência Geral.",
    "Tela de Abertura": "Tela de Abertura",
    "Imagem de fundo exibida enquanto o aplicativo carrega. A logo aparece centralizada sobre ela.": "Imagem de fundo exibida enquanto o aplicativo carrega. A logo aparece centralizada sobre ela.",
    "Use uma imagem vertical (proporção de celular), em PNG ou JPG.": "Use uma imagem vertical (proporção de celular), em PNG ou JPG.",
    "Plano de Fundo da Abertura (Splash)": "Plano de Fundo da Abertura (Splash)",
    "Funcionalidades do Sistema": "Funcionalidades do Sistema",
    "Dados, Links e Textos do Sistema": "Dados, Links e Textos do Sistema",
    "Imagens do Sistema": "Imagens do Sistema",
    "Configurações de API": "Configurações de API",
    "Configurações de IA": "Configurações de IA",
    "Ensine o agente como responder e forneça um PDF como base de conhecimento.": "Ensine o agente como responder e forneça um PDF como base de conhecimento.",
    "Instruções do agente de IA": "Instruções do agente de IA",
    "Descreva o tom de voz, as regras e a forma como o agente deve responder.": "Descreva o tom de voz, as regras e a forma como o agente deve responder.",
    "Estas instruções serão usadas como orientação principal nas respostas do agente.": "Estas instruções serão usadas como orientação principal nas respostas do agente.",
    "Base de conhecimento em PDF": "Base de conhecimento em PDF",
    "PDF configurado": "PDF configurado",
    "Nenhum PDF": "Nenhum PDF",
    "Visualizar PDF configurado": "Visualizar PDF configurado",
    "Visualizar PDF da base de conhecimento": "Visualizar PDF da base de conhecimento",
    "Selecionar PDF da base de conhecimento": "Selecionar PDF da base de conhecimento",
    "Remover PDF da base de conhecimento": "Remover PDF da base de conhecimento",
    "O agente poderá usar o conteúdo deste documento para formular respostas.": "O agente poderá usar o conteúdo deste documento para formular respostas.",
    "Moedas e API por Organização": "Moedas e API por Organização",
    "Defina a moeda padrão e a integração de cotações para cada organização.": "Defina a moeda padrão e a integração de cotações para cada organização.",
    "Ativar consulta da API de moedas": "Ativar consulta da API de moedas",
    "Moeda padrão": "Moeda padrão",
    "Moedas para cotação": "Moedas para cotação",
    "Ex.: USD, EUR, GBP": "Ex.: USD, EUR, GBP",
    "URL da API de moedas": "URL da API de moedas",
    "Ex.: https://api.provedor.com": "Ex.: https://api.provedor.com",
    "Chave da API de moedas": "Chave da API de moedas",
    "Cole a chave da API": "Cole a chave da API",
    "API Resend (E-mails)": "API Resend (E-mails)",
    "Cole sua chave API (re_...)": "Cole sua chave API (re_...)",
    "API ChatGPT (Assistente de IA)": "API ChatGPT (Assistente de IA)",
    "Cole sua chave API (sk-...)": "Cole sua chave API (sk-...)",
    "Chave configurada no backend": "Chave configurada no backend",
    "Chave ainda não configurada": "Chave ainda não configurada",
    "Google Client ID inválido": "Google Client ID inválido",
    "Google Client ID inválido. Corrija-o nas configurações antes de tentar novamente.": "Google Client ID inválido. Corrija-o nas configurações antes de tentar novamente.",
    "Login com Google não está configurado.": "Login com Google não está configurado.",
    "Login com Google não está disponível no momento.": "Login com Google não está disponível no momento.",
    "Nenhuma organização disponível para configurar moedas.": "Nenhuma organização disponível para configurar moedas.",
    "Erro ao salvar as configurações de moedas.": "Erro ao salvar as configurações de moedas.",
    "Configurações de moedas inválidas.": "Configurações de moedas inválidas.",
    "Organização inválida na configuração de moedas.": "Organização inválida na configuração de moedas.",
    "PDF atualizado com sucesso.": "PDF atualizado com sucesso.",
    "Erro ao enviar PDF.": "Erro ao enviar PDF.",
    "PDF removido com sucesso.": "PDF removido com sucesso.",
    "Erro ao remover item.": "Erro ao remover item.",
    "Informe uma moeda padrão válida com três letras.": "Informe uma moeda padrão válida com três letras.",
    "Informe as moedas para cotação usando códigos de três letras.": "Informe as moedas para cotação usando códigos de três letras.",
    "Informe uma URL válida para a API de moedas.": "Informe uma URL válida para a API de moedas.",
    "Real brasileiro (BRL)": "Real brasileiro (BRL)",
    "Dólar americano (USD)": "Dólar americano (USD)",
    "Euro (EUR)": "Euro (EUR)",
    "Libra esterlina (GBP)": "Libra esterlina (GBP)",
    "Yuan chinês (CNY)": "Yuan chinês (CNY)",
    "Versões do sistema": "Versões do sistema",
    "Ícone": "Ícone",
    "Data de lançamento": "Data de lançamento",
    "Gerencie a versão atual e o histórico de lançamentos.": "Gerencie a versão atual e o histórico de lançamentos.",
    "Nova versão": "Nova versão",
    "Versão atual": "Versão atual",
    "Salvar versão atual": "Salvar versão atual",
    "Use o formato major.minor.patch, por exemplo 1.0.0.": "Use o formato major.minor.patch, por exemplo 1.0.0.",
    "Editar versão": "Editar versão",
    "Excluir versão": "Excluir versão",
    "Deseja excluir a versão": "Deseja excluir a versão",
    "Imagem da novidade": "Imagem da novidade",
    "Visualizar imagem da versão": "Visualizar imagem da versão",
    "Descreva a novidade ou correção desta versão": "Descreva a novidade ou correção desta versão",
    "Ex.: Novo recurso de IA": "Ex.: Novo recurso de IA",
    "Mudança drástica, quebra compatibilidade.": "Mudança drástica, quebra compatibilidade.",
    "Novo recurso, mantém compatibilidade.": "Novo recurso, mantém compatibilidade.",
    "Correção de bugs e ajustes internos.": "Correção de bugs e ajustes internos.",
    "Informe o título da versão.": "Informe o título da versão.",
    "Informe a versão no formato 1.0.0.": "Informe a versão no formato 1.0.0.",
    "Versão salva com sucesso.": "Versão salva com sucesso.",
    "Versão atualizada com sucesso.": "Versão atualizada com sucesso.",
    "Versão excluída com sucesso.": "Versão excluída com sucesso.",
    "Não foi possível carregar as versões.": "Não foi possível carregar as versões.",
    "Não foi possível salvar a versão.": "Não foi possível salvar a versão.",
    "Não foi possível excluir a versão.": "Não foi possível excluir a versão.",
    "Não foi possível favoritar a versão.": "Não foi possível favoritar a versão.",
    "Iene japonês (JPY)": "Iene japonês (JPY)",
    "Peso argentino (ARS)": "Peso argentino (ARS)",
    "Peso mexicano (MXN)": "Peso mexicano (MXN)",
    "Anexar comprovante de pagamento": "Anexar comprovante de pagamento",
    "Baixar parcela": "Baixar parcela",
    "Recibo de pagamento": "Recibo de pagamento",
    "Recibo nº": "Recibo nº",
    Pagador: "Pagador",
    "Documento do pagador": "Documento do pagador",
    "Venda / Parcela": "Venda / Parcela",
    "Data do recebimento": "Data do recebimento",
    "Dados do pagamento": "Dados do pagamento",
    "Serial vinculado": "Serial vinculado",
    "Assinatura do recebedor": "Assinatura do recebedor",
    "Este recibo confirma a quitação da parcela descrita acima.": "Este recibo confirma a quitação da parcela descrita acima.",
    "Permita pop-ups para imprimir o recibo.": "Permita pop-ups para imprimir o recibo.",
    "Recebemos de {pagador} a importância de {valor}, referente ao pagamento da parcela {parcela} da venda nº {venda}, correspondente a {descricao}.": "Recebemos de {pagador} a importância de {valor}, referente ao pagamento da parcela {parcela} da venda nº {venda}, correspondente a {descricao}.",
    "Status do orçamento": "Status do orçamento",
    "Importar para vendas": "Importar para vendas",
    "Importar orçamento para vendas": "Importar orçamento para vendas",
    "Aprove o orçamento antes de importar": "Aprove o orçamento antes de importar",
    "Apenas orçamentos aprovados podem ser importados para vendas.": "Apenas orçamentos aprovados podem ser importados para vendas.",
    "Este orçamento está vencido e não pode mais ser importado para vendas.": "Este orçamento está vencido e não pode mais ser importado para vendas.",
    "Orçamento vencido não pode ser importado": "Orçamento vencido não pode ser importado",
    "Não foi possível importar o orçamento para vendas.": "Não foi possível importar o orçamento para vendas.",
    "Erro ao importar orçamento para vendas.": "Erro ao importar orçamento para vendas.",
    "Orçamento importado para vendas com sucesso.": "Orçamento importado para vendas com sucesso.",
    "O orçamento não movimenta estoque nem gera lançamentos financeiros.": "O orçamento não movimenta estoque nem gera lançamentos financeiros.",
    "Vendas e orçamentos em um único fluxo, com movimentação automática somente nas vendas.": "Vendas e orçamentos em um único fluxo, com movimentação automática somente nas vendas.",
    "Registrar orçamento": "Registrar orçamento",
    "Novo Orçamento": "Novo Orçamento",
    "Dados do Orçamento": "Dados do Orçamento",
    "Dados do orçamento": "Dados do orçamento",
    "Número do orçamento": "Número do orçamento",
    "Orçamento atualizado com sucesso.": "Orçamento atualizado com sucesso.",
    "Orçamento registrado com sucesso.": "Orçamento registrado com sucesso.",
    "Não foi possível salvar o orçamento.": "Não foi possível salvar o orçamento.",
    "Data do orçamento": "Data do orçamento",
    "Data de validade": "Data de validade",
    "Observações do orçamento...": "Observações do orçamento...",
    "Não foi possível salvar o orçamento para anexar documentos.": "Não foi possível salvar o orçamento para anexar documentos.",
    "Não foi possível preparar o orçamento para anexar documentos.": "Não foi possível preparar o orçamento para anexar documentos.",
    "Orçamento salvo como pendente para manter os documentos disponíveis. Finalize o cadastro para concluir.": "Orçamento salvo como pendente para manter os documentos disponíveis. Finalize o cadastro para concluir.",
    "Erro ao salvar o orçamento.": "Erro ao salvar o orçamento.",
    "Orçamento salvo. Agora você já pode anexar documentos sem fechar a tela.": "Orçamento salvo. Agora você já pode anexar documentos sem fechar a tela.",
    "Nenhuma venda ou orçamento registrado": "Nenhuma venda ou orçamento registrado",
    "Clique em Nova Venda para iniciar o cadastro.": "Clique em Nova Venda para iniciar o cadastro.",
    "Buscar por ID, cliente...": "Buscar por ID, cliente...",
    "Vendas e Orçamentos": "Vendas e Orçamentos",
    "Aprove o orçamento antes de importar para vendas.": "Aprove o orçamento antes de importar para vendas.",
    "Este orçamento não tem itens para importar.": "Este orçamento não tem itens para importar.",
    themeLight: "Tema claro",
    themeDark: "Tema escuro",
    loadingText: "Processando...",
    loginTitle: "Que bom ver você aqui!",
    loginSubtitle: "Entre com suas credenciais",
    emailLabel: "Email",
    emailPlaceholder: "Seu Email",
    passwordLabel: "Senha",
    passwordPlaceholder: "Sua Senha",
    rememberMe: "Ficar conectado",
    forgotPasswordText: "Esqueceu a senha?",
    passwordRecoveryTitle: "Recuperar senha",
    passwordRecoverySubtitle: "Informe seu e-mail para receber as instruções.",
    passwordRecoverySent: "E-mail de recuperação enviado com sucesso.",
    passwordRecoverySendError: "Não foi possível enviar o e-mail de recuperação. Verifique o endereço e tente novamente.",
    passwordRecoveryRateLimit: "Muitas solicitações de recuperação. Aguarde 15 minutos e tente novamente.",
    passwordRecoveryUnavailable: "O envio de e-mails está indisponível no momento. Fale com o administrador do sistema.",
    passwordRecoverySending: "Enviando...", passwordRecoverySend: "Enviar recuperação",
    passwordRecoveryBack: "Voltar para o login", passwordRecoveryNewTitle: "Criar nova senha",
    passwordRecoveryLogoAlt: "Logo da empresa",
    passwordRecoveryNewSubtitle: "Informe e confirme sua nova senha.",
    passwordRecoveryValidating: "Validando link de recuperação...",
    passwordRecoveryChanged: "Senha alterada com sucesso. Você já pode entrar.",
    passwordRecoveryNewPassword: "Nova senha", passwordRecoveryNewPasswordPlaceholder: "Digite a nova senha",
    passwordRecoveryConfirmPassword: "Confirmar senha", passwordRecoveryConfirmPlaceholder: "Repita a nova senha",
    passwordRecoverySaving: "Salvando...", passwordRecoverySave: "Salvar nova senha",
    passwordRecoveryMinLength: "A senha deve ter ao menos 8 caracteres, com letra maiúscula, número e caractere especial.",
    passwordRecoveryMismatch: "As senhas não conferem.",
    passwordRecoveryInvalid: "Este link é inválido ou expirou. Solicite uma nova recuperação.",
    loginBtn: "Entrar",
    loginLoading: "Entrando...",
    noAccountText: "Não possui conta?",
    registerLink: "Cadastrar",
    termsPrefixLogin: "Ao clicar em Entrar, você concorda com os",
    termsLink: "Termos de uso",
    andText: "e",
    privacyLink: "Política de Privacidade (LGPD)",
    socialSeparatorLogin: "Ou use o seu email para continuar",
    followUs: "Siga-nos:",
    "Adicionar item": "Adicionar item",
    "Adicionar grade": "Adicionar grade",
    fillEmailPassword: "Informe e-mail e senha.",
    loginFailed: "Não foi possível entrar.",
    showPassword: "Mostrar senha",
    hidePassword: "Ocultar senha",
    available: "Disponível",
    away: "Ausente",
    busy: "Ocupado",
    offline: "Off-line",
    "Defina seu status atual": "Defina seu status atual",
    "Ativo e disponível para contato": "Ativo e disponível para contato",
    "Estou ausente no momento": "Estou ausente no momento",
    "Em uma atividade intensa": "Em uma atividade intensa",
    "Desconectado ou indisponível": "Desconectado ou indisponível",
    darkMode: "Modo Dark",
    status: "Status",
    language: "Idioma",
    "Selecione o idioma de sua preferência.": "Selecione o idioma de sua preferência.",
    "Português do Brasil": "Português do Brasil",
    Espanhol: "Espanhol",
    Inglês: "Inglês",
    "Chinês Simplificado": "Chinês Simplificado",
    notifications: "Notificações",
    "Preferências de notificações": "Preferências de notificações",
    "notificações ativas": "notificações ativas",
    "Escolha quais notificações deseja receber por e-mail.": "Escolha quais notificações deseja receber por e-mail.",
    "Não foi possível carregar suas preferências de notificações.": "Não foi possível carregar suas preferências de notificações.",
    "Não foi possível salvar suas preferências de notificações.": "Não foi possível salvar suas preferências de notificações.",
    "Carregando preferências...": "Carregando preferências...",
    "Nenhuma notificação está disponível para as telas liberadas no seu perfil.": "Nenhuma notificação está disponível para as telas liberadas no seu perfil.",
    "Receber notificações por e-mail": "Receber notificações por e-mail",
    "Desative para bloquear todos os e-mails de notificações da plataforma.": "Desative para bloquear todos os e-mails de notificações da plataforma.",
    "Novos chamados": "Novos chamados",
    "Receber um e-mail quando um novo chamado chegar.": "Receber um e-mail quando um novo chamado chegar.",
    "Chamados encerrados": "Chamados encerrados",
    "Receber um e-mail quando um chamado for concluído.": "Receber um e-mail quando um chamado for concluído.",
    "Estoque baixo": "Estoque baixo",
    "Avisar quando um produto atingir o estoque mínimo.": "Avisar quando um produto atingir o estoque mínimo.",
    "Novas vendas": "Novas vendas",
    "Avisar quando uma nova venda for concluída.": "Avisar quando uma nova venda for concluída.",
    "Parcelas em atraso": "Parcelas em atraso",
    "Avisar sobre parcelas vencidas que continuam pendentes.": "Avisar sobre parcelas vencidas que continuam pendentes.",
    "Confirmações de pagamento": "Confirmações de pagamento",
    "Avisar quando o pagamento de uma parcela for confirmado.": "Avisar quando o pagamento de uma parcela for confirmado.",
    "Salvando preferências...": "Salvando preferências...",
    "Consulte suas anotações sincronizadas com a plataforma.": "Consulte suas anotações sincronizadas com a plataforma.",
    notes: "Bloco de Notas",
    myAccount: "Minha conta",
    help: "Ajuda",
    accessLog: "Log de acesso",
    settings: "Configurações",
    exit: "Sair",
    user: "Usuário",
    support: "Suporte",
    assistantIa: "Assistente de IA",
    askAnything: "Pergunte-me qualquer coisa...",
  },
  en: {
    ...TEXTOS_RELATORIOS_HUB.en,
    ...TEXTOS_TOUR_SISTEMA.en,
    ...TEXTOS_ETAPAS.en,
    ...TEXTOS_NAVEGACAO_MODULOS.en,
    ...TEXTOS_LOJA_MODULOS.en,
    ...TEXTOS_COBRANCA.en,
    ...TEXTOS_INTEGRACOES_ERP.en,
    ...TEXTOS_MONITOR_TEF_FISCAL.en,
    ...TEXTOS_MONITOR_TEF_FISCAL_COMPLEMENTO.en,
    ...TEXTOS_EMISSAO_FISCAL.en,
    ...TEXTOS_OPERACAO_ERP.en,
    ...TEXTOS_OPERACAO_COMPLETA.en,
    ...TEXTOS_WHATSAPP_QR.en,
    ...TEXTOS_CONFIG_CHAT_ATENDIMENTO.en,
    ...TEXTOS_CHECKLIST.en,
    ...TEXTOS_SEGURANCA.en,
    ...TEXTOS_AGENTE_IA.en,
    ...TEXTOS_CADASTRO_LOGIN.en,
    ...TEXTOS_ERROS_CADASTRO.en,
    ...TEXTOS_VENDAS_CREDIARIO.en,
    ...TEXTOS_GRAFICOS_CONFIG.en,
    ...TEXTOS_ANEXOS.en,
    ...TEXTOS_COMPRAS_DASHBOARD.en,
    ...TEXTOS_DASHBOARD_FINANCEIRO_NOVO.en,
    ...TEXTOS_ESTOQUE_DASHBOARD.en,
    ...TEXTOS_VISAO_GERAL_COMPLETA.en,
    ...TEXTOS_DASHBOARD_VENDAS.en,
    ...TEXTOS_ACESSO_PERMISSAO.en,
    ...TEXTOS_ESTOQUE_TRANSFERENCIA.en,
    ...TEXTOS_SUPORTE_TAREFAS.en,
    ...TEXTOS_CONFIRMACOES_EMPRESA_VENDA.en,
    ...TEXTOS_COMPRAS_FORNECEDORES.en,
    ...TEXTOS_PRE_VENDA_PRECOS.en,
    ...TEXTOS_AJUDA_CADASTROS_BASE.en,
    ...TEXTOS_AJUDA_CADASTROS_PRINCIPAIS.en,
    ...TEXTOS_AJUDA_OPERACIONAL_1.en,
    ...TEXTOS_AJUDA_OPERACIONAL_2.en,
    ...TEXTOS_FINANCEIRO_DETALHES.en,
    ...TEXTOS_MARCA_EMPRESA.en,
    ...TEXTOS_AJUDA_CONFIGURACOES.en,
    ...TEXTOS_EMAIL_EMPRESAS.en,
    ...TEXTOS_CREDITOS_OPENAI.en,
    ...TEXTOS_EMAIL_EMPRESAS_SIMPLIFICADO.en,
    ...TEXTOS_CHECKLIST_EXECUCAO.en,
    ...TEXTOS_DASHBOARD_CHECKLIST.en,
    ...TEXTOS_DASHBOARDS_HOME.en,
    ...TEXTOS_AREA_CLIENTE.en,
    ...TEXTOS_COMPONENTES_UI.en,
    ...TEXTOS_PERMISSOES_PERFIL.en,
    ...TEXTOS_CENTRAL_NOTIFICACOES.en,
    ...TEXTOS_CHAT_INTERNO.en,
    "App Android": "Android app",
    "App iOS": "iOS app",
    "Logo Agente IA": "AI Agent Logo",
    "Nome Assistente de IA": "AI Assistant Name",
    "Gerencie logos, banner de login e favicon.": "Manage logos, the login banner, and the favicon.",
    "Permissões do menu": "Menu permissions",
    "Selecione um menu": "Select a menu",
    "Ative o acesso ao menu para configurar as permissões.": "Enable menu access to configure its permissions.",
    "Selecione um menu para configurar suas permissões.": "Select a menu to configure its permissions.",
    "Imagem do perfil": "Profile image",
    "Visualizar imagem do perfil": "View profile image",
    "Acessos do login": "Login access",
    Acessos: "Access",
    "Acesso à plataforma web": "Web platform access",
    "Acesso ao APP": "App access",
    "Exigir autenticação 2FA": "Require 2FA authentication",
    "Usuário sem acesso à plataforma web. Fale com um administrador.": "This user does not have access to the web platform. Contact an administrator.",
    "Informe apenas links válidos, começando com http:// ou https://.": "Enter only valid links beginning with http:// or https://.",
    Dashboard: "Dashboard",
    "Acesso restrito ao login com permissão de Configurações": "Access restricted to a login with Settings permission",
    "Permitir acesso ao menu Configurações": "Allow access to the Settings menu",
    "Não foi possível concluir a operação no servidor.": "The server could not complete the operation.",
    "Informe o nome do agente.": "Enter the agent name.",
    "Informe a mensagem inicial.": "Enter the initial message.",
    "Informe a mensagem de fallback.": "Enter the fallback message.",
    "Há um campo obrigatório vazio no cadastro.": "A required field is empty.",
    "Há um valor inválido em um dos campos do cadastro.": "One of the form fields contains an invalid value.",
    "Acesso ao menu Configurações": "Settings menu access",
    "Permissão permanente para o login Master principal.": "Permanent permission for the primary Master login.",
    "Ative para permitir que este login acesse e altere as Configurações.": "Enable this to allow the login to access and change Settings.",
    Auditoria: "Audit",
    "Log de acesso": "Access log",
    "Auditoria de Usuário": "User audit",
    "Marcar grupo": "Select group",
    "Desmarcar grupo": "Clear group",
    "Preencha os campos obrigatórios destacados em vermelho.": "Complete the required fields highlighted in red.",
    "Selecione a empresa.": "Select a company.",
    "Informe a data da venda.": "Enter the sale date.",
    "Selecione um produto.": "Select a product.",
    "Local de estoque": "Stock location",
    "+ Adicionar Etapa": "+ Add stage",
    "Nenhuma etapa cadastrada": "No stage registered",
    "Selecione o local de estoque.": "Select the stock location.",
    "Os itens saem do estoque de": "The items come from the stock of",
    "outra empresa": "another company",
    "a venda gera um pedido de compra para essa empresa.": "the sale creates a purchase order for that company.",
    "Informe uma quantidade válida.": "Enter a valid quantity.",
    "Informe um valor unitário válido.": "Enter a valid unit price.",
    "Selecione a forma de pagamento.": "Select a payment method.",
    "Selecione o tipo do cartão.": "Select the card type.",
    "Informe o primeiro vencimento das parcelas.": "Enter the first installment due date.",
    Liberado: "Allowed",
    Bloqueado: "Blocked",
    "Tenha uma visão completa da empresa, acompanhando operações, clientes, estoque, finanças e os principais indicadores do negócio.": "Get a complete view of the company, tracking operations, customers, inventory, finances, and key business indicators.",
    "Seções das configurações": "Settings sections",
    "Aparência Geral": "General Appearance",
    "Configurações do Mobile": "Mobile Settings",
    "Ajustes exclusivos do aplicativo. A identidade visual continua em Aparência Geral.": "App-only settings. Visual identity stays in General Appearance.",
    "Tela de Abertura": "Splash Screen",
    "Imagem de fundo exibida enquanto o aplicativo carrega. A logo aparece centralizada sobre ela.": "Background image shown while the app loads. The logo is centered over it.",
    "Use uma imagem vertical (proporção de celular), em PNG ou JPG.": "Use a portrait image (phone ratio), in PNG or JPG.",
    "Plano de Fundo da Abertura (Splash)": "Splash Background",
    "Funcionalidades do Sistema": "System Features",
    "Dados, Links e Textos do Sistema": "System Data, Links, and Texts",
    "Imagens do Sistema": "System Images",
    "Configurações de API": "API Settings",
    "Configurações de IA": "AI Settings",
    "Ensine o agente como responder e forneça um PDF como base de conhecimento.": "Teach the agent how to respond and provide a PDF as its knowledge base.",
    "Instruções do agente de IA": "AI agent instructions",
    "Descreva o tom de voz, as regras e a forma como o agente deve responder.": "Describe the tone of voice, rules, and how the agent should respond.",
    "Estas instruções serão usadas como orientação principal nas respostas do agente.": "These instructions will be used as the main guidance for the agent's responses.",
    "Base de conhecimento em PDF": "PDF knowledge base",
    "PDF configurado": "PDF configured",
    "Nenhum PDF": "No PDF",
    "Visualizar PDF configurado": "View configured PDF",
    "Visualizar PDF da base de conhecimento": "View knowledge base PDF",
    "Selecionar PDF da base de conhecimento": "Select knowledge base PDF",
    "Remover PDF da base de conhecimento": "Remove knowledge base PDF",
    "O agente poderá usar o conteúdo deste documento para formular respostas.": "The agent can use this document's content to formulate responses.",
    "Moedas e API por Organização": "Currencies and API by Organization",
    "Defina a moeda padrão e a integração de cotações para cada organização.": "Set the default currency and exchange-rate integration for each organization.",
    "Ativar consulta da API de moedas": "Enable currency API lookup",
    "Moeda padrão": "Default currency",
    "Moedas para cotação": "Currencies to quote",
    "Ex.: USD, EUR, GBP": "E.g.: USD, EUR, GBP",
    "URL da API de moedas": "Currency API URL",
    "Ex.: https://api.provedor.com": "E.g.: https://api.provider.com",
    "Chave da API de moedas": "Currency API key",
    "Cole a chave da API": "Paste the API key",
    "API Resend (E-mails)": "Resend API (Email)",
    "Cole sua chave API (re_...)": "Paste your API key (re_...)",
    "API ChatGPT (Assistente de IA)": "ChatGPT API (AI Assistant)",
    "Cole sua chave API (sk-...)": "Paste your API key (sk-...)",
    "Chave configurada no backend": "Key configured on the backend",
    "Chave ainda não configurada": "Key not configured yet",
    "Google Client ID inválido": "Invalid Google Client ID",
    "Google Client ID inválido. Corrija-o nas configurações antes de tentar novamente.": "Invalid Google Client ID. Fix it in settings before trying again.",
    "Login com Google não está configurado.": "Google sign-in is not configured.",
    "Login com Google não está disponível no momento.": "Google sign-in is currently unavailable.",
    "Nenhuma organização disponível para configurar moedas.": "No organization is available for currency settings.",
    "Erro ao salvar as configurações de moedas.": "Could not save the currency settings.",
    "Configurações de moedas inválidas.": "Invalid currency settings.",
    "Organização inválida na configuração de moedas.": "Invalid organization in the currency settings.",
    "PDF atualizado com sucesso.": "PDF updated successfully.",
    "Erro ao enviar PDF.": "Could not upload the PDF.",
    "PDF removido com sucesso.": "PDF removed successfully.",
    "Erro ao remover item.": "Could not remove the item.",
    "Informe uma moeda padrão válida com três letras.": "Enter a valid three-letter default currency.",
    "Informe as moedas para cotação usando códigos de três letras.": "Enter quote currencies using three-letter codes.",
    "Informe uma URL válida para a API de moedas.": "Enter a valid currency API URL.",
    "Real brasileiro (BRL)": "Brazilian real (BRL)",
    "Dólar americano (USD)": "US dollar (USD)",
    "Euro (EUR)": "Euro (EUR)",
    "Libra esterlina (GBP)": "Pound sterling (GBP)",
    "Yuan chinês (CNY)": "Chinese yuan (CNY)",
    "Versões do sistema": "System versions",
    "Ícone": "Icon",
    "Data de lançamento": "Release date",
    "Gerencie a versão atual e o histórico de lançamentos.": "Manage the current version and release history.",
    "Nova versão": "New version",
    "Versão atual": "Current version",
    "Salvar versão atual": "Save current version",
    "Use o formato major.minor.patch, por exemplo 1.0.0.": "Use the major.minor.patch format, for example 1.0.0.",
    "Editar versão": "Edit version",
    "Excluir versão": "Delete version",
    "Deseja excluir a versão": "Do you want to delete version",
    "Imagem da novidade": "Release image",
    "Visualizar imagem da versão": "View version image",
    "Descreva a novidade ou correção desta versão": "Describe the new feature or fix in this version",
    "Ex.: Novo recurso de IA": "Example: New AI feature",
    "Mudança drástica, quebra compatibilidade.": "Major change that breaks compatibility.",
    "Novo recurso, mantém compatibilidade.": "New feature that maintains compatibility.",
    "Correção de bugs e ajustes internos.": "Bug fixes and internal adjustments.",
    "Informe o título da versão.": "Enter the version title.",
    "Informe a versão no formato 1.0.0.": "Enter the version in 1.0.0 format.",
    "Versão salva com sucesso.": "Version saved successfully.",
    "Versão atualizada com sucesso.": "Version updated successfully.",
    "Versão excluída com sucesso.": "Version deleted successfully.",
    "Não foi possível carregar as versões.": "Could not load the versions.",
    "Não foi possível salvar a versão.": "Could not save the version.",
    "Não foi possível excluir a versão.": "Could not delete the version.",
    "Não foi possível favoritar a versão.": "Could not favorite the version.",
    "Iene japonês (JPY)": "Japanese yen (JPY)",
    "Peso argentino (ARS)": "Argentine peso (ARS)",
    "Peso mexicano (MXN)": "Mexican peso (MXN)",
    ...TEXTOS_PDV.en,
    ...TEXTOS_IMPRESSAO_COLUNAS.en,
    "Seções do cadastro": "Registration sections",
    "Anexar comprovante de pagamento": "Attach proof of payment",
    "Baixar parcela": "Settle installment",
    "Recibo de pagamento": "Payment receipt",
    "Recibo nº": "Receipt No.",
    Pagador: "Payer",
    "Documento do pagador": "Payer document",
    "Venda / Parcela": "Sale / Installment",
    "Data do recebimento": "Payment date",
    "Dados do pagamento": "Payment details",
    "Serial vinculado": "Linked serial",
    "Assinatura do recebedor": "Recipient's signature",
    "Este recibo confirma a quitação da parcela descrita acima.": "This receipt confirms settlement of the installment described above.",
    "Permita pop-ups para imprimir o recibo.": "Allow pop-ups to print the receipt.",
    "Recebemos de {pagador} a importância de {valor}, referente ao pagamento da parcela {parcela} da venda nº {venda}, correspondente a {descricao}.": "We acknowledge receipt from {pagador} of {valor}, for payment of installment {parcela} of sale No. {venda}, corresponding to {descricao}.",
    "Status do orçamento": "Quote status",
    "Importar para vendas": "Convert to sale",
    "Importar orçamento para vendas": "Convert quote to sale",
    "Aprove o orçamento antes de importar": "Approve the quote before converting it",
    "Apenas orçamentos aprovados podem ser importados para vendas.": "Only approved quotes can be converted to sales.",
    "Este orçamento está vencido e não pode mais ser importado para vendas.": "This quote has expired and can no longer be converted to a sale.",
    "Orçamento vencido não pode ser importado": "Expired quote cannot be converted",
    "Não foi possível importar o orçamento para vendas.": "The quote could not be converted to a sale.",
    "Erro ao importar orçamento para vendas.": "Error converting quote to sale.",
    "Orçamento importado para vendas com sucesso.": "Quote converted to sale successfully.",
    "O orçamento não movimenta estoque nem gera lançamentos financeiros.": "The quote does not move inventory or create financial entries.",
    "Vendas e orçamentos em um único fluxo, com movimentação automática somente nas vendas.": "Sales and quotes in one workflow, with automatic movements only for sales.",
    "Registrar orçamento": "Save quote",
    "Novo Orçamento": "New Quote",
    "Dados do Orçamento": "Quote Data",
    "Dados do orçamento": "Quote data",
    "Número do orçamento": "Quote number",
    "Orçamento atualizado com sucesso.": "Quote updated successfully.",
    "Orçamento registrado com sucesso.": "Quote saved successfully.",
    "Não foi possível salvar o orçamento.": "The quote could not be saved.",
    "Data do orçamento": "Quote date",
    "Data de validade": "Expiration date",
    "Observações do orçamento...": "Quote notes...",
    "Não foi possível salvar o orçamento para anexar documentos.": "The quote could not be saved to attach documents.",
    "Não foi possível preparar o orçamento para anexar documentos.": "The quote could not be prepared for document attachments.",
    "Orçamento salvo como pendente para manter os documentos disponíveis. Finalize o cadastro para concluir.": "Quote saved as pending to keep documents available. Finish the form to complete it.",
    "Erro ao salvar o orçamento.": "Error saving quote.",
    "Orçamento salvo. Agora você já pode anexar documentos sem fechar a tela.": "Quote saved. You can now attach documents without closing the form.",
    "Nenhuma venda ou orçamento registrado": "No sales or quotes registered",
    "Clique em Nova Venda para iniciar o cadastro.": "Click New Sale to start.",
    "Buscar por ID, cliente...": "Search by ID or customer...",
    "Vendas e Orçamentos": "Sales and Quotes",
    "Aprove o orçamento antes de importar para vendas.": "Approve the quote before converting it to a sale.",
    "Este orçamento não tem itens para importar.": "This quote has no items to convert.",
    themeLight: "Light theme",
    themeDark: "Dark theme",
    loadingText: "Processing...",
    loginTitle: "Great to see you here!",
    loginSubtitle: "Sign in with your credentials",
    emailLabel: "Email",
    emailPlaceholder: "Your Email",
    passwordLabel: "Password",
    passwordPlaceholder: "Your Password",
    rememberMe: "Keep me signed in",
    forgotPasswordText: "Forgot your password?",
    passwordRecoveryTitle: "Recover password",
    passwordRecoverySubtitle: "Enter your email to receive recovery instructions.",
    passwordRecoverySent: "Recovery email sent successfully.",
    passwordRecoverySendError: "We could not send the recovery email. Check the address and try again.",
    passwordRecoveryRateLimit: "Too many recovery requests. Wait 15 minutes and try again.",
    passwordRecoveryUnavailable: "Email sending is unavailable right now. Contact the system administrator.",
    passwordRecoverySending: "Sending...", passwordRecoverySend: "Send recovery email",
    passwordRecoveryBack: "Back to sign in", passwordRecoveryNewTitle: "Create a new password",
    passwordRecoveryLogoAlt: "Company logo",
    passwordRecoveryNewSubtitle: "Enter and confirm your new password.",
    passwordRecoveryValidating: "Validating recovery link...",
    passwordRecoveryChanged: "Password changed successfully. You can now sign in.",
    passwordRecoveryNewPassword: "New password", passwordRecoveryNewPasswordPlaceholder: "Enter the new password",
    passwordRecoveryConfirmPassword: "Confirm password", passwordRecoveryConfirmPlaceholder: "Repeat the new password",
    passwordRecoverySaving: "Saving...", passwordRecoverySave: "Save new password",
    passwordRecoveryMinLength: "The password must be at least 8 characters long, with an uppercase letter, a number, and a special character.",
    passwordRecoveryMismatch: "The passwords do not match.",
    passwordRecoveryInvalid: "This link is invalid or has expired. Request a new recovery email.",
    loginBtn: "Sign in",
    loginLoading: "Signing in...",
    noAccountText: "Don't have an account?",
    registerLink: "Register",
    termsPrefixLogin: "By clicking Sign in, you agree to the",
    termsLink: "Terms of Use",
    andText: "and",
    privacyLink: "Privacy Policy (LGPD)",
    socialSeparatorLogin: "Or use your email to continue",
    followUs: "Follow us:",
    "Adicionar item": "Add item",
    "Adicionar grade": "Add grid",
    fillEmailPassword: "Enter your email and password.",
    loginFailed: "Unable to sign in.",
    showPassword: "Show password",
    hidePassword: "Hide password",
    available: "Available",
    away: "Away",
    busy: "Busy",
    offline: "Offline",
    "Defina seu status atual": "Set your current status",
    "Ativo e disponível para contato": "Active and available to talk",
    "Estou ausente no momento": "I'm away right now",
    "Em uma atividade intensa": "Busy with an intensive task",
    "Desconectado ou indisponível": "Disconnected or unavailable",
    darkMode: "Dark Mode",
    status: "Status",
    language: "Language",
    "Selecione o idioma de sua preferência.": "Select your preferred language.",
    "Português do Brasil": "Brazilian Portuguese",
    Espanhol: "Spanish",
    Inglês: "English",
    "Chinês Simplificado": "Simplified Chinese",
    notifications: "Notifications",
    "Preferências de notificações": "Notification preferences",
    "notificações ativas": "active notifications",
    "Escolha quais notificações deseja receber por e-mail.": "Choose which notifications you want to receive by email.",
    "Não foi possível carregar suas preferências de notificações.": "Your notification preferences could not be loaded.",
    "Não foi possível salvar suas preferências de notificações.": "Your notification preferences could not be saved.",
    "Carregando preferências...": "Loading preferences...",
    "Nenhuma notificação está disponível para as telas liberadas no seu perfil.": "No notifications are available for the screens enabled in your profile.",
    "Receber notificações por e-mail": "Receive email notifications",
    "Desative para bloquear todos os e-mails de notificações da plataforma.": "Turn this off to block all platform notification emails.",
    "Novos chamados": "New tickets",
    "Receber um e-mail quando um novo chamado chegar.": "Receive an email when a new ticket arrives.",
    "Chamados encerrados": "Closed tickets",
    "Receber um e-mail quando um chamado for concluído.": "Receive an email when a ticket is completed.",
    "Estoque baixo": "Low stock",
    "Avisar quando um produto atingir o estoque mínimo.": "Notify me when a product reaches its minimum stock.",
    "Novas vendas": "New sales",
    "Avisar quando uma nova venda for concluída.": "Notify me when a new sale is completed.",
    "Parcelas em atraso": "Overdue installments",
    "Avisar sobre parcelas vencidas que continuam pendentes.": "Notify me about overdue installments that are still pending.",
    "Confirmações de pagamento": "Payment confirmations",
    "Avisar quando o pagamento de uma parcela for confirmado.": "Notify me when an installment payment is confirmed.",
    "Salvando preferências...": "Saving preferences...",
    "Consulte suas anotações sincronizadas com a plataforma.": "Review your notes synchronized with the platform.",
    notes: "Notes",
    myAccount: "My account",
    help: "Help",
    accessLog: "Access log",
    settings: "Settings",
    exit: "Sign out",
    user: "User",
    support: "Support",
    assistantIa: "AI Assistant",
    askAnything: "Ask me anything...",
  },
  es: {
    ...TEXTOS_RELATORIOS_HUB.es,
    ...TEXTOS_TOUR_SISTEMA.es,
    ...TEXTOS_ETAPAS.es,
    ...TEXTOS_NAVEGACAO_MODULOS.es,
    ...TEXTOS_LOJA_MODULOS.es,
    ...TEXTOS_COBRANCA.es,
    ...TEXTOS_INTEGRACOES_ERP.es,
    ...TEXTOS_MONITOR_TEF_FISCAL.es,
    ...TEXTOS_MONITOR_TEF_FISCAL_COMPLEMENTO.es,
    ...TEXTOS_EMISSAO_FISCAL.es,
    ...TEXTOS_OPERACAO_ERP.es,
    ...TEXTOS_OPERACAO_COMPLETA.es,
    ...TEXTOS_WHATSAPP_QR.es,
    ...TEXTOS_CONFIG_CHAT_ATENDIMENTO.es,
    ...TEXTOS_CHECKLIST.es,
    ...TEXTOS_SEGURANCA.es,
    ...TEXTOS_AGENTE_IA.es,
    ...TEXTOS_CADASTRO_LOGIN.es,
    ...TEXTOS_ERROS_CADASTRO.es,
    ...TEXTOS_VENDAS_CREDIARIO.es,
    ...TEXTOS_GRAFICOS_CONFIG.es,
    ...TEXTOS_ANEXOS.es,
    ...TEXTOS_COMPRAS_DASHBOARD.es,
    ...TEXTOS_DASHBOARD_FINANCEIRO_NOVO.es,
    ...TEXTOS_ESTOQUE_DASHBOARD.es,
    ...TEXTOS_VISAO_GERAL_COMPLETA.es,
    ...TEXTOS_DASHBOARD_VENDAS.es,
    ...TEXTOS_ACESSO_PERMISSAO.es,
    ...TEXTOS_ESTOQUE_TRANSFERENCIA.es,
    ...TEXTOS_SUPORTE_TAREFAS.es,
    ...TEXTOS_CONFIRMACOES_EMPRESA_VENDA.es,
    ...TEXTOS_COMPRAS_FORNECEDORES.es,
    ...TEXTOS_PRE_VENDA_PRECOS.es,
    ...TEXTOS_AJUDA_CADASTROS_BASE.es,
    ...TEXTOS_AJUDA_CADASTROS_PRINCIPAIS.es,
    ...TEXTOS_AJUDA_OPERACIONAL_1.es,
    ...TEXTOS_AJUDA_OPERACIONAL_2.es,
    ...TEXTOS_FINANCEIRO_DETALHES.es,
    ...TEXTOS_MARCA_EMPRESA.es,
    ...TEXTOS_AJUDA_CONFIGURACOES.es,
    ...TEXTOS_EMAIL_EMPRESAS.es,
    ...TEXTOS_CREDITOS_OPENAI.es,
    ...TEXTOS_EMAIL_EMPRESAS_SIMPLIFICADO.es,
    ...TEXTOS_CHECKLIST_EXECUCAO.es,
    ...TEXTOS_DASHBOARD_CHECKLIST.es,
    ...TEXTOS_DASHBOARDS_HOME.es,
    ...TEXTOS_AREA_CLIENTE.es,
    ...TEXTOS_COMPONENTES_UI.es,
    ...TEXTOS_PERMISSOES_PERFIL.es,
    ...TEXTOS_CENTRAL_NOTIFICACOES.es,
    ...TEXTOS_CHAT_INTERNO.es,
    "App Android": "Aplicación Android",
    "App iOS": "Aplicación iOS",
    "Logo Agente IA": "Logo del agente de IA",
    "Nome Assistente de IA": "Nombre del asistente de IA",
    "Gerencie logos, banner de login e favicon.": "Administre los logos, el banner de inicio de sesión y el favicon.",
    "Permissões do menu": "Permisos del menú",
    "Selecione um menu": "Seleccione un menú",
    "Ative o acesso ao menu para configurar as permissões.": "Active el acceso al menú para configurar sus permisos.",
    "Selecione um menu para configurar suas permissões.": "Seleccione un menú para configurar sus permisos.",
    "Imagem do perfil": "Imagen del perfil",
    "Visualizar imagem do perfil": "Ver imagen del perfil",
    "Acessos do login": "Accesos del login",
    Acessos: "Accesos",
    "Acesso à plataforma web": "Acceso a la plataforma web",
    "Acesso ao APP": "Acceso a la aplicación",
    "Exigir autenticação 2FA": "Exigir autenticación 2FA",
    "Usuário sem acesso à plataforma web. Fale com um administrador.": "Este usuario no tiene acceso a la plataforma web. Hable con un administrador.",
    "Informe apenas links válidos, começando com http:// ou https://.": "Ingrese solo enlaces válidos que comiencen con http:// o https://.",
    Dashboard: "Panel de control",
    "Acesso restrito ao login com permissão de Configurações": "Acceso restringido al login con permiso de Configuración",
    "Permitir acesso ao menu Configurações": "Permitir acceso al menú Configuración",
    "Não foi possível concluir a operação no servidor.": "El servidor no pudo completar la operación.",
    "Informe o nome do agente.": "Introduzca el nombre del agente.",
    "Informe a mensagem inicial.": "Introduzca el mensaje inicial.",
    "Informe a mensagem de fallback.": "Introduzca el mensaje alternativo.",
    "Há um campo obrigatório vazio no cadastro.": "Hay un campo obligatorio vacío.",
    "Há um valor inválido em um dos campos do cadastro.": "Uno de los campos del formulario contiene un valor no válido.",
    "Acesso ao menu Configurações": "Acceso al menú Configuración",
    "Permissão permanente para o login Master principal.": "Permiso permanente para el login Master principal.",
    "Ative para permitir que este login acesse e altere as Configurações.": "Actívelo para permitir que este login acceda y modifique la Configuración.",
    Auditoria: "Auditoría",
    "Log de acesso": "Registro de acceso",
    "Auditoria de Usuário": "Auditoría de usuario",
    "Marcar grupo": "Marcar grupo",
    "Desmarcar grupo": "Desmarcar grupo",
    "Preencha os campos obrigatórios destacados em vermelho.": "Complete los campos obligatorios resaltados en rojo.",
    "Selecione a empresa.": "Seleccione una empresa.",
    "Informe a data da venda.": "Introduzca la fecha de la venta.",
    "Selecione um produto.": "Seleccione un producto.",
    "Local de estoque": "Ubicación de stock",
    "+ Adicionar Etapa": "+ Añadir etapa",
    "Nenhuma etapa cadastrada": "Ninguna etapa registrada",
    "Selecione o local de estoque.": "Seleccione la ubicación de stock.",
    "Os itens saem do estoque de": "Los artículos salen del stock de",
    "outra empresa": "otra empresa",
    "a venda gera um pedido de compra para essa empresa.": "la venta genera un pedido de compra para esa empresa.",
    "Informe uma quantidade válida.": "Introduzca una cantidad válida.",
    "Informe um valor unitário válido.": "Introduzca un precio unitario válido.",
    "Selecione a forma de pagamento.": "Seleccione una forma de pago.",
    "Selecione o tipo do cartão.": "Seleccione el tipo de tarjeta.",
    "Informe o primeiro vencimento das parcelas.": "Introduzca el primer vencimiento de las cuotas.",
    Liberado: "Permitido",
    Bloqueado: "Bloqueado",
    "Tenha uma visão completa da empresa, acompanhando operações, clientes, estoque, finanças e os principais indicadores do negócio.": "Obtenga una visión completa de la empresa y supervise las operaciones, los clientes, el inventario, las finanzas y los principales indicadores del negocio.",
    "Seções das configurações": "Secciones de configuración",
    "Aparência Geral": "Apariencia general",
    "Configurações do Mobile": "Configuración del móvil",
    "Ajustes exclusivos do aplicativo. A identidade visual continua em Aparência Geral.": "Ajustes exclusivos de la aplicación. La identidad visual sigue en Apariencia general.",
    "Tela de Abertura": "Pantalla de inicio",
    "Imagem de fundo exibida enquanto o aplicativo carrega. A logo aparece centralizada sobre ela.": "Imagen de fondo mostrada mientras la aplicación carga. El logotipo aparece centrado sobre ella.",
    "Use uma imagem vertical (proporção de celular), em PNG ou JPG.": "Usa una imagen vertical (proporción de móvil), en PNG o JPG.",
    "Plano de Fundo da Abertura (Splash)": "Fondo de la pantalla de inicio",
    "Funcionalidades do Sistema": "Funcionalidades del sistema",
    "Dados, Links e Textos do Sistema": "Datos, enlaces y textos del sistema",
    "Imagens do Sistema": "Imágenes del sistema",
    "Configurações de API": "Configuración de API",
    "Configurações de IA": "Configuración de IA",
    "Ensine o agente como responder e forneça um PDF como base de conhecimento.": "Enseñe al agente cómo responder y proporcione un PDF como base de conocimiento.",
    "Instruções do agente de IA": "Instrucciones del agente de IA",
    "Descreva o tom de voz, as regras e a forma como o agente deve responder.": "Describa el tono de voz, las reglas y cómo debe responder el agente.",
    "Estas instruções serão usadas como orientação principal nas respostas do agente.": "Estas instrucciones serán la guía principal para las respuestas del agente.",
    "Base de conhecimento em PDF": "Base de conocimiento en PDF",
    "PDF configurado": "PDF configurado",
    "Nenhum PDF": "Ningún PDF",
    "Visualizar PDF configurado": "Ver PDF configurado",
    "Visualizar PDF da base de conhecimento": "Ver PDF de la base de conocimiento",
    "Selecionar PDF da base de conhecimento": "Seleccionar PDF de la base de conocimiento",
    "Remover PDF da base de conhecimento": "Eliminar PDF de la base de conocimiento",
    "O agente poderá usar o conteúdo deste documento para formular respostas.": "El agente podrá usar el contenido de este documento para formular respuestas.",
    "Moedas e API por Organização": "Monedas y API por organización",
    "Defina a moeda padrão e a integração de cotações para cada organização.": "Defina la moneda predeterminada y la integración de cotizaciones para cada organización.",
    "Ativar consulta da API de moedas": "Activar consulta de la API de monedas",
    "Moeda padrão": "Moneda predeterminada",
    "Moedas para cotação": "Monedas para cotización",
    "Ex.: USD, EUR, GBP": "Ej.: USD, EUR, GBP",
    "URL da API de moedas": "URL de la API de monedas",
    "Ex.: https://api.provedor.com": "Ej.: https://api.proveedor.com",
    "Chave da API de moedas": "Clave de la API de monedas",
    "Cole a chave da API": "Pegue la clave de la API",
    "API Resend (E-mails)": "API de Resend (Correos)",
    "Cole sua chave API (re_...)": "Pegue su clave API (re_...)",
    "API ChatGPT (Assistente de IA)": "API de ChatGPT (Asistente de IA)",
    "Cole sua chave API (sk-...)": "Pegue su clave API (sk-...)",
    "Chave configurada no backend": "Clave configurada en el backend",
    "Chave ainda não configurada": "Clave aún no configurada",
    "Google Client ID inválido": "Google Client ID no válido",
    "Google Client ID inválido. Corrija-o nas configurações antes de tentar novamente.": "El Google Client ID no es válido. Corríjalo en la configuración antes de intentarlo de nuevo.",
    "Login com Google não está configurado.": "El inicio de sesión con Google no está configurado.",
    "Login com Google não está disponível no momento.": "El inicio de sesión con Google no está disponible en este momento.",
    "Nenhuma organização disponível para configurar moedas.": "No hay organizaciones disponibles para configurar monedas.",
    "Erro ao salvar as configurações de moedas.": "No se pudo guardar la configuración de monedas.",
    "Configurações de moedas inválidas.": "Configuración de monedas no válida.",
    "Organização inválida na configuração de moedas.": "Organización no válida en la configuración de monedas.",
    "PDF atualizado com sucesso.": "PDF actualizado correctamente.",
    "Erro ao enviar PDF.": "No se pudo cargar el PDF.",
    "PDF removido com sucesso.": "PDF eliminado correctamente.",
    "Erro ao remover item.": "No se pudo eliminar el elemento.",
    "Informe uma moeda padrão válida com três letras.": "Ingrese una moneda predeterminada válida de tres letras.",
    "Informe as moedas para cotação usando códigos de três letras.": "Ingrese las monedas de cotización con códigos de tres letras.",
    "Informe uma URL válida para a API de moedas.": "Ingrese una URL válida para la API de monedas.",
    "Real brasileiro (BRL)": "Real brasileño (BRL)",
    "Dólar americano (USD)": "Dólar estadounidense (USD)",
    "Euro (EUR)": "Euro (EUR)",
    "Libra esterlina (GBP)": "Libra esterlina (GBP)",
    "Yuan chinês (CNY)": "Yuan chino (CNY)",
    "Versões do sistema": "Versiones del sistema",
    "Ícone": "Icono",
    "Data de lançamento": "Fecha de lanzamiento",
    "Gerencie a versão atual e o histórico de lançamentos.": "Administre la versión actual y el historial de lanzamientos.",
    "Nova versão": "Nueva versión",
    "Versão atual": "Versión actual",
    "Salvar versão atual": "Guardar versión actual",
    "Use o formato major.minor.patch, por exemplo 1.0.0.": "Use el formato major.minor.patch, por ejemplo 1.0.0.",
    "Editar versão": "Editar versión",
    "Excluir versão": "Eliminar versión",
    "Deseja excluir a versão": "¿Desea eliminar la versión",
    "Imagem da novidade": "Imagen de la novedad",
    "Visualizar imagem da versão": "Ver imagen de la versión",
    "Descreva a novidade ou correção desta versão": "Describa la novedad o corrección de esta versión",
    "Ex.: Novo recurso de IA": "Ej.: Nueva función de IA",
    "Mudança drástica, quebra compatibilidade.": "Cambio importante que rompe la compatibilidad.",
    "Novo recurso, mantém compatibilidade.": "Nueva función que mantiene la compatibilidad.",
    "Correção de bugs e ajustes internos.": "Corrección de errores y ajustes internos.",
    "Informe o título da versão.": "Ingrese el título de la versión.",
    "Informe a versão no formato 1.0.0.": "Ingrese la versión con el formato 1.0.0.",
    "Versão salva com sucesso.": "Versión guardada correctamente.",
    "Versão atualizada com sucesso.": "Versión actualizada correctamente.",
    "Versão excluída com sucesso.": "Versión eliminada correctamente.",
    "Não foi possível carregar as versões.": "No se pudieron cargar las versiones.",
    "Não foi possível salvar a versão.": "No se pudo guardar la versión.",
    "Não foi possível excluir a versão.": "No se pudo eliminar la versión.",
    "Não foi possível favoritar a versão.": "No se pudo marcar la versión como favorita.",
    "Iene japonês (JPY)": "Yen japonés (JPY)",
    "Peso argentino (ARS)": "Peso argentino (ARS)",
    "Peso mexicano (MXN)": "Peso mexicano (MXN)",
    ...TEXTOS_PDV.es,
    ...TEXTOS_IMPRESSAO_COLUNAS.es,
    "Seções do cadastro": "Secciones del registro",
    "Anexar comprovante de pagamento": "Adjuntar comprobante de pago",
    "Baixar parcela": "Liquidar cuota",
    "Recibo de pagamento": "Recibo de pago",
    "Recibo nº": "Recibo n.º",
    Pagador: "Pagador",
    "Documento do pagador": "Documento del pagador",
    "Venda / Parcela": "Venta / Cuota",
    "Data do recebimento": "Fecha de recepción",
    "Dados do pagamento": "Datos del pago",
    "Serial vinculado": "Serie vinculada",
    "Assinatura do recebedor": "Firma del receptor",
    "Este recibo confirma a quitação da parcela descrita acima.": "Este recibo confirma la liquidación de la cuota descrita anteriormente.",
    "Permita pop-ups para imprimir o recibo.": "Permita las ventanas emergentes para imprimir el recibo.",
    "Recebemos de {pagador} a importância de {valor}, referente ao pagamento da parcela {parcela} da venda nº {venda}, correspondente a {descricao}.": "Hemos recibido de {pagador} el importe de {valor}, correspondiente al pago de la cuota {parcela} de la venta n.º {venda}, por concepto de {descricao}.",
    "Status do orçamento": "Estado del presupuesto",
    "Importar para vendas": "Convertir en venta",
    "Importar orçamento para vendas": "Convertir presupuesto en venta",
    "Aprove o orçamento antes de importar": "Apruebe el presupuesto antes de convertirlo",
    "Apenas orçamentos aprovados podem ser importados para vendas.": "Solo los presupuestos aprobados pueden convertirse en ventas.",
    "Este orçamento está vencido e não pode mais ser importado para vendas.": "Este presupuesto ha vencido y ya no puede convertirse en venta.",
    "Orçamento vencido não pode ser importado": "Presupuesto vencido no puede convertirse",
    "Não foi possível importar o orçamento para vendas.": "No se pudo convertir el presupuesto en venta.",
    "Erro ao importar orçamento para vendas.": "Error al convertir el presupuesto en venta.",
    "Orçamento importado para vendas com sucesso.": "Presupuesto convertido en venta correctamente.",
    "O orçamento não movimenta estoque nem gera lançamentos financeiros.": "El presupuesto no mueve inventario ni genera asientos financieros.",
    "Vendas e orçamentos em um único fluxo, com movimentação automática somente nas vendas.": "Ventas y presupuestos en un único flujo, con movimientos automáticos solo para las ventas.",
    "Registrar orçamento": "Guardar presupuesto",
    "Novo Orçamento": "Nuevo Presupuesto",
    "Dados do Orçamento": "Datos del Presupuesto",
    "Dados do orçamento": "Datos del presupuesto",
    "Número do orçamento": "Número del presupuesto",
    "Orçamento atualizado com sucesso.": "Presupuesto actualizado correctamente.",
    "Orçamento registrado com sucesso.": "Presupuesto guardado correctamente.",
    "Não foi possível salvar o orçamento.": "No se pudo guardar el presupuesto.",
    "Data do orçamento": "Fecha del presupuesto",
    "Data de validade": "Fecha de validez",
    "Observações do orçamento...": "Observaciones del presupuesto...",
    "Não foi possível salvar o orçamento para anexar documentos.": "No se pudo guardar el presupuesto para adjuntar documentos.",
    "Não foi possível preparar o orçamento para anexar documentos.": "No se pudo preparar el presupuesto para adjuntar documentos.",
    "Orçamento salvo como pendente para manter os documentos disponíveis. Finalize o cadastro para concluir.": "Presupuesto guardado como pendiente para mantener los documentos disponibles. Finalice el formulario para completarlo.",
    "Erro ao salvar o orçamento.": "Error al guardar el presupuesto.",
    "Orçamento salvo. Agora você já pode anexar documentos sem fechar a tela.": "Presupuesto guardado. Ahora puede adjuntar documentos sin cerrar el formulario.",
    "Nenhuma venda ou orçamento registrado": "No hay ventas ni presupuestos registrados",
    "Clique em Nova Venda para iniciar o cadastro.": "Haga clic en Nueva Venta para comenzar.",
    "Buscar por ID, cliente...": "Buscar por ID o cliente...",
    "Vendas e Orçamentos": "Ventas y Presupuestos",
    "Aprove o orçamento antes de importar para vendas.": "Apruebe el presupuesto antes de convertirlo en venta.",
    "Este orçamento não tem itens para importar.": "Este presupuesto no tiene elementos para convertir.",
    themeLight: "Tema claro",
    themeDark: "Tema oscuro",
    loadingText: "Procesando...",
    loginTitle: "¡Qué bueno verte por aquí!",
    loginSubtitle: "Ingresa con tus credenciales",
    emailLabel: "Correo",
    emailPlaceholder: "Tu correo",
    passwordLabel: "Contrase\u00F1a",
    passwordPlaceholder: "Tu contrase\u00F1a",
    rememberMe: "Mantener sesión iniciada",
    forgotPasswordText: "\u00BFOlvidaste tu contrase\u00F1a?",
    passwordRecoveryTitle: "Recuperar contraseña",
    passwordRecoverySubtitle: "Ingresa tu correo para recibir las instrucciones.",
    passwordRecoverySent: "Correo de recuperación enviado correctamente.",
    passwordRecoverySendError: "No se pudo enviar el correo de recuperación. Verifica la dirección e inténtalo de nuevo.",
    passwordRecoveryRateLimit: "Demasiadas solicitudes de recuperación. Espera 15 minutos e inténtalo de nuevo.",
    passwordRecoveryUnavailable: "El envío de correos no está disponible en este momento. Contacta al administrador del sistema.",
    passwordRecoverySending: "Enviando...", passwordRecoverySend: "Enviar recuperación",
    passwordRecoveryBack: "Volver al inicio de sesión", passwordRecoveryNewTitle: "Crear nueva contraseña",
    passwordRecoveryLogoAlt: "Logo de la empresa",
    passwordRecoveryNewSubtitle: "Ingresa y confirma tu nueva contraseña.",
    passwordRecoveryValidating: "Validando enlace de recuperación...",
    passwordRecoveryChanged: "Contraseña actualizada correctamente. Ya puedes iniciar sesión.",
    passwordRecoveryNewPassword: "Nueva contraseña", passwordRecoveryNewPasswordPlaceholder: "Ingresa la nueva contraseña",
    passwordRecoveryConfirmPassword: "Confirmar contraseña", passwordRecoveryConfirmPlaceholder: "Repite la nueva contraseña",
    passwordRecoverySaving: "Guardando...", passwordRecoverySave: "Guardar nueva contraseña",
    passwordRecoveryMinLength: "La contraseña debe tener al menos 8 caracteres, con mayúscula, número y carácter especial.",
    passwordRecoveryMismatch: "Las contraseñas no coinciden.",
    passwordRecoveryInvalid: "Este enlace no es válido o ha vencido. Solicita una nueva recuperación.",
    loginBtn: "Entrar",
    loginLoading: "Entrando...",
    noAccountText: "¿No tienes cuenta?",
    registerLink: "Registrarse",
    termsPrefixLogin: "Al hacer clic en Entrar, aceptas los",
    termsLink: "Términos de uso",
    andText: "y la",
    privacyLink: "Política de Privacidad (LGPD)",
    socialSeparatorLogin: "O usa tu correo para continuar",
    followUs: "Síguenos:",
    "Adicionar item": "Agregar artículo",
    "Adicionar grade": "Agregar cuadrícula",
    fillEmailPassword: "Ingresa correo y contrase\u00F1a.",
    loginFailed: "No fue posible iniciar sesión.",
    showPassword: "Mostrar contrase\u00F1a",
    hidePassword: "Ocultar contrase\u00F1a",
    available: "Disponible",
    away: "Ausente",
    busy: "Ocupado",
    offline: "Desconectado",
    "Defina seu status atual": "Defina su estado actual",
    "Ativo e disponível para contato": "Activo y disponible para contacto",
    "Estou ausente no momento": "Estoy ausente en este momento",
    "Em uma atividade intensa": "En una actividad intensa",
    "Desconectado ou indisponível": "Desconectado o no disponible",
    darkMode: "Modo oscuro",
    status: "Estado",
    language: "Idioma",
    "Selecione o idioma de sua preferência.": "Selecciona el idioma de tu preferencia.",
    "Português do Brasil": "Portugués de Brasil",
    Espanhol: "Español",
    Inglês: "Inglés",
    "Chinês Simplificado": "Chino simplificado",
    notifications: "Notificaciones",
    "Preferências de notificações": "Preferencias de notificaciones",
    "notificações ativas": "notificaciones activas",
    "Escolha quais notificações deseja receber por e-mail.": "Elige qué notificaciones deseas recibir por correo electrónico.",
    "Não foi possível carregar suas preferências de notificações.": "No se pudieron cargar tus preferencias de notificaciones.",
    "Não foi possível salvar suas preferências de notificações.": "No se pudieron guardar tus preferencias de notificaciones.",
    "Carregando preferências...": "Cargando preferencias...",
    "Nenhuma notificação está disponível para as telas liberadas no seu perfil.": "No hay notificaciones disponibles para las pantallas habilitadas en tu perfil.",
    "Receber notificações por e-mail": "Recibir notificaciones por correo",
    "Desative para bloquear todos os e-mails de notificações da plataforma.": "Desactiva esta opción para bloquear todos los correos de notificaciones de la plataforma.",
    "Novos chamados": "Nuevos tickets",
    "Receber um e-mail quando um novo chamado chegar.": "Recibir un correo cuando llegue un nuevo ticket.",
    "Chamados encerrados": "Tickets cerrados",
    "Receber um e-mail quando um chamado for concluído.": "Recibir un correo cuando se complete un ticket.",
    "Estoque baixo": "Stock bajo",
    "Avisar quando um produto atingir o estoque mínimo.": "Avisar cuando un producto alcance el stock mínimo.",
    "Novas vendas": "Nuevas ventas",
    "Avisar quando uma nova venda for concluída.": "Avisar cuando se complete una nueva venta.",
    "Parcelas em atraso": "Cuotas vencidas",
    "Avisar sobre parcelas vencidas que continuam pendentes.": "Avisar sobre cuotas vencidas que continúan pendientes.",
    "Confirmações de pagamento": "Confirmaciones de pago",
    "Avisar quando o pagamento de uma parcela for confirmado.": "Avisar cuando se confirme el pago de una cuota.",
    "Salvando preferências...": "Guardando preferencias...",
    "Consulte suas anotações sincronizadas com a plataforma.": "Consulta tus notas sincronizadas con la plataforma.",
    notes: "Bloc de notas",
    myAccount: "Mi cuenta",
    help: "Ayuda",
    accessLog: "Registro de acceso",
    settings: "Configuración",
    exit: "Salir",
    user: "Usuario",
    support: "Soporte",
    assistantIa: "Asistente de IA",
    askAnything: "Pregúntame lo que quieras...",
  },
  "zh-CN": {
    ...TEXTOS_RELATORIOS_HUB["zh-CN"],
    ...TEXTOS_TOUR_SISTEMA["zh-CN"],
    ...TEXTOS_ETAPAS["zh-CN"],
    ...TEXTOS_NAVEGACAO_MODULOS["zh-CN"],
    ...TEXTOS_LOJA_MODULOS["zh-CN"],
    ...TEXTOS_COBRANCA["zh-CN"],
    ...TEXTOS_INTEGRACOES_ERP["zh-CN"],
    ...TEXTOS_MONITOR_TEF_FISCAL["zh-CN"],
    ...TEXTOS_MONITOR_TEF_FISCAL_COMPLEMENTO["zh-CN"],
    ...TEXTOS_EMISSAO_FISCAL["zh-CN"],
    ...TEXTOS_OPERACAO_ERP["zh-CN"],
    ...TEXTOS_OPERACAO_COMPLETA["zh-CN"],
    ...TEXTOS_WHATSAPP_QR["zh-CN"],
    ...TEXTOS_CONFIG_CHAT_ATENDIMENTO["zh-CN"],
    ...TEXTOS_CHECKLIST["zh-CN"],
    ...TEXTOS_SEGURANCA["zh-CN"],
    ...TEXTOS_AGENTE_IA["zh-CN"],
    ...TEXTOS_CADASTRO_LOGIN["zh-CN"],
    ...TEXTOS_ERROS_CADASTRO["zh-CN"],
    ...TEXTOS_VENDAS_CREDIARIO["zh-CN"],
    ...TEXTOS_GRAFICOS_CONFIG["zh-CN"],
    ...TEXTOS_ANEXOS["zh-CN"],
    ...TEXTOS_COMPRAS_DASHBOARD["zh-CN"],
    ...TEXTOS_DASHBOARD_FINANCEIRO_NOVO["zh-CN"],
    ...TEXTOS_ESTOQUE_DASHBOARD["zh-CN"],
    ...TEXTOS_VISAO_GERAL_COMPLETA["zh-CN"],
    ...TEXTOS_DASHBOARD_VENDAS["zh-CN"],
    ...TEXTOS_ACESSO_PERMISSAO["zh-CN"],
    ...TEXTOS_ESTOQUE_TRANSFERENCIA["zh-CN"],
    ...TEXTOS_SUPORTE_TAREFAS["zh-CN"],
    ...TEXTOS_CONFIRMACOES_EMPRESA_VENDA["zh-CN"],
    ...TEXTOS_COMPRAS_FORNECEDORES["zh-CN"],
    ...TEXTOS_PRE_VENDA_PRECOS["zh-CN"],
    ...TEXTOS_AJUDA_CADASTROS_BASE["zh-CN"],
    ...TEXTOS_AJUDA_CADASTROS_PRINCIPAIS["zh-CN"],
    ...TEXTOS_AJUDA_OPERACIONAL_1["zh-CN"],
    ...TEXTOS_AJUDA_OPERACIONAL_2["zh-CN"],
    ...TEXTOS_FINANCEIRO_DETALHES["zh-CN"],
    ...TEXTOS_MARCA_EMPRESA["zh-CN"],
    ...TEXTOS_AJUDA_CONFIGURACOES["zh-CN"],
    ...TEXTOS_EMAIL_EMPRESAS["zh-CN"],
    ...TEXTOS_CREDITOS_OPENAI["zh-CN"],
    ...TEXTOS_EMAIL_EMPRESAS_SIMPLIFICADO["zh-CN"],
    ...TEXTOS_CHECKLIST_EXECUCAO["zh-CN"],
    ...TEXTOS_DASHBOARD_CHECKLIST["zh-CN"],
    ...TEXTOS_DASHBOARDS_HOME["zh-CN"],
    ...TEXTOS_AREA_CLIENTE["zh-CN"],
    ...TEXTOS_COMPONENTES_UI["zh-CN"],
    ...TEXTOS_PERMISSOES_PERFIL["zh-CN"],
    ...TEXTOS_CENTRAL_NOTIFICACOES["zh-CN"],
    ...TEXTOS_CHAT_INTERNO["zh-CN"],
    "App Android": "Android 应用",
    "App iOS": "iOS 应用",
    "Logo Agente IA": "AI 智能体徽标",
    "Nome Assistente de IA": "AI 助手名称",
    "Gerencie logos, banner de login e favicon.": "管理徽标、登录横幅和网站图标。",
    "Permissões do menu": "菜单权限",
    "Selecione um menu": "请选择菜单",
    "Ative o acesso ao menu para configurar as permissões.": "请先启用菜单访问权限，再配置其权限。",
    "Selecione um menu para configurar suas permissões.": "请选择一个菜单以配置其权限。",
    "Imagem do perfil": "个人资料图片",
    "Visualizar imagem do perfil": "查看个人资料图片",
    "Acessos do login": "登录访问权限",
    Acessos: "访问权限",
    "Acesso à plataforma web": "Web 平台访问权限",
    "Acesso ao APP": "应用程序访问权限",
    "Exigir autenticação 2FA": "要求双重身份验证",
    "Usuário sem acesso à plataforma web. Fale com um administrador.": "该用户无权访问 Web 平台。请联系管理员。",
    "Informe apenas links válidos, começando com http:// ou https://.": "仅输入以 http:// 或 https:// 开头的有效链接。",
    "Acesso restrito ao login com permissão de Configurações": "仅限具有设置权限的登录账号访问",
    "Permitir acesso ao menu Configurações": "允许访问设置菜单",
    "Não foi possível concluir a operação no servidor.": "服务器无法完成该操作。",
    "Informe o nome do agente.": "请输入代理名称。",
    "Informe a mensagem inicial.": "请输入初始消息。",
    "Informe a mensagem de fallback.": "请输入备用消息。",
    "Há um campo obrigatório vazio no cadastro.": "有一个必填字段为空。",
    "Há um valor inválido em um dos campos do cadastro.": "表单中的某个字段包含无效值。",
    "Acesso ao menu Configurações": "设置菜单访问权限",
    "Permissão permanente para o login Master principal.": "主 Master 登录账号拥有永久权限。",
    "Ative para permitir que este login acesse e altere as Configurações.": "启用后，此登录账号可以访问和更改设置。",
    Auditoria: "审计",
    "Log de acesso": "访问日志",
    "Auditoria de Usuário": "用户审计",
    "Marcar grupo": "选择分组",
    "Desmarcar grupo": "取消选择分组",
    "Preencha os campos obrigatórios destacados em vermelho.": "请填写以红色标出的必填字段。",
    "Selecione a empresa.": "请选择公司。",
    "Informe a data da venda.": "请输入销售日期。",
    "Selecione um produto.": "请选择产品。",
    "Local de estoque": "库存地点",
    "+ Adicionar Etapa": "+ 添加阶段",
    "Nenhuma etapa cadastrada": "尚未登记阶段",
    "Selecione o local de estoque.": "请选择库存地点。",
    "Os itens saem do estoque de": "商品出自以下公司的库存：",
    "outra empresa": "其他公司",
    "a venda gera um pedido de compra para essa empresa.": "该销售会向这家公司生成一张采购订单。",
    "Informe uma quantidade válida.": "请输入有效数量。",
    "Informe um valor unitário válido.": "请输入有效单价。",
    "Selecione a forma de pagamento.": "请选择付款方式。",
    "Selecione o tipo do cartão.": "请选择银行卡类型。",
    "Informe o primeiro vencimento das parcelas.": "请输入首期分期付款到期日。",
    Liberado: "已允许",
    Bloqueado: "已阻止",
    Dashboard: "仪表板",
    "Tenha uma visão completa da empresa, acompanhando operações, clientes, estoque, finanças e os principais indicadores do negócio.": "全面了解公司运营，掌握业务、客户、库存、财务和关键经营指标。",
    "Seções das configurações": "设置分区",
    "Aparência Geral": "常规外观",
    "Configurações do Mobile": "移动端设置",
    "Ajustes exclusivos do aplicativo. A identidade visual continua em Aparência Geral.": "仅限应用的设置。视觉标识仍在“常规外观”中配置。",
    "Tela de Abertura": "启动画面",
    "Imagem de fundo exibida enquanto o aplicativo carrega. A logo aparece centralizada sobre ela.": "应用加载时显示的背景图片，标识居中显示在其上方。",
    "Use uma imagem vertical (proporção de celular), em PNG ou JPG.": "请使用竖版图片（手机比例），格式为 PNG 或 JPG。",
    "Plano de Fundo da Abertura (Splash)": "启动画面背景",
    "Funcionalidades do Sistema": "系统功能",
    "Dados, Links e Textos do Sistema": "系统数据、链接和文本",
    "Imagens do Sistema": "系统图片",
    "Configurações de API": "API 设置",
    "Configurações de IA": "AI 设置",
    "Ensine o agente como responder e forneça um PDF como base de conhecimento.": "指导智能助手如何回答，并提供 PDF 作为知识库。",
    "Instruções do agente de IA": "AI 助手说明",
    "Descreva o tom de voz, as regras e a forma como o agente deve responder.": "说明语气、规则以及智能助手应如何回答。",
    "Estas instruções serão usadas como orientação principal nas respostas do agente.": "这些说明将作为智能助手回答时的主要指导。",
    "Base de conhecimento em PDF": "PDF 知识库",
    "PDF configurado": "PDF 已配置",
    "Nenhum PDF": "无 PDF",
    "Visualizar PDF configurado": "查看已配置的 PDF",
    "Visualizar PDF da base de conhecimento": "查看知识库 PDF",
    "Selecionar PDF da base de conhecimento": "选择知识库 PDF",
    "Remover PDF da base de conhecimento": "删除知识库 PDF",
    "O agente poderá usar o conteúdo deste documento para formular respostas.": "智能助手可使用此文档内容来组织回答。",
    "Moedas e API por Organização": "按组织配置货币和 API",
    "Defina a moeda padrão e a integração de cotações para cada organização.": "为每个组织设置默认货币和汇率集成。",
    "Ativar consulta da API de moedas": "启用货币 API 查询",
    "Moeda padrão": "默认货币",
    "Moedas para cotação": "报价货币",
    "Ex.: USD, EUR, GBP": "例如：USD、EUR、GBP",
    "URL da API de moedas": "货币 API URL",
    "Ex.: https://api.provedor.com": "例如：https://api.provider.com",
    "Chave da API de moedas": "货币 API 密钥",
    "Cole a chave da API": "粘贴 API 密钥",
    "API Resend (E-mails)": "Resend API（电子邮件）",
    "Cole sua chave API (re_...)": "粘贴您的 API 密钥（re_...）",
    "API ChatGPT (Assistente de IA)": "ChatGPT API（AI 助手）",
    "Cole sua chave API (sk-...)": "粘贴您的 API 密钥（sk-...）",
    "Chave configurada no backend": "后端已配置密钥",
    "Chave ainda não configurada": "尚未配置密钥",
    "Google Client ID inválido": "Google Client ID 无效",
    "Google Client ID inválido. Corrija-o nas configurações antes de tentar novamente.": "Google Client ID 无效。请在设置中更正后重试。",
    "Login com Google não está configurado.": "尚未配置 Google 登录。",
    "Login com Google não está disponível no momento.": "Google 登录当前不可用。",
    "Nenhuma organização disponível para configurar moedas.": "没有可配置货币的组织。",
    "Erro ao salvar as configurações de moedas.": "无法保存货币设置。",
    "Configurações de moedas inválidas.": "货币设置无效。",
    "Organização inválida na configuração de moedas.": "货币设置中的组织无效。",
    "PDF atualizado com sucesso.": "PDF 更新成功。",
    "Erro ao enviar PDF.": "无法上传 PDF。",
    "PDF removido com sucesso.": "PDF 删除成功。",
    "Erro ao remover item.": "无法删除该项目。",
    "Informe uma moeda padrão válida com três letras.": "请输入有效的三字母默认货币代码。",
    "Informe as moedas para cotação usando códigos de três letras.": "请使用三字母代码输入报价货币。",
    "Informe uma URL válida para a API de moedas.": "请输入有效的货币 API URL。",
    "Real brasileiro (BRL)": "巴西雷亚尔 (BRL)",
    "Dólar americano (USD)": "美元 (USD)",
    "Euro (EUR)": "欧元 (EUR)",
    "Libra esterlina (GBP)": "英镑 (GBP)",
    "Yuan chinês (CNY)": "人民币 (CNY)",
    "Versões do sistema": "系统版本",
    "Ícone": "图标",
    "Data de lançamento": "发布日期",
    "Gerencie a versão atual e o histórico de lançamentos.": "管理当前版本和发布历史。",
    "Nova versão": "新建版本",
    "Versão atual": "当前版本",
    "Salvar versão atual": "保存当前版本",
    "Use o formato major.minor.patch, por exemplo 1.0.0.": "请使用 major.minor.patch 格式，例如 1.0.0。",
    "Editar versão": "编辑版本",
    "Excluir versão": "删除版本",
    "Deseja excluir a versão": "是否删除版本",
    "Imagem da novidade": "版本图片",
    "Visualizar imagem da versão": "查看版本图片",
    "Descreva a novidade ou correção desta versão": "描述此版本的新功能或修复内容",
    "Ex.: Novo recurso de IA": "例如：新的 AI 功能",
    "Mudança drástica, quebra compatibilidade.": "重大变更，会破坏兼容性。",
    "Novo recurso, mantém compatibilidade.": "新增功能，保持兼容性。",
    "Correção de bugs e ajustes internos.": "错误修复和内部调整。",
    "Informe o título da versão.": "请输入版本标题。",
    "Informe a versão no formato 1.0.0.": "请按 1.0.0 格式输入版本。",
    "Versão salva com sucesso.": "版本保存成功。",
    "Versão atualizada com sucesso.": "版本更新成功。",
    "Versão excluída com sucesso.": "版本删除成功。",
    "Não foi possível carregar as versões.": "无法加载版本。",
    "Não foi possível salvar a versão.": "无法保存版本。",
    "Não foi possível excluir a versão.": "无法删除版本。",
    "Não foi possível favoritar a versão.": "无法收藏该版本。",
    "Iene japonês (JPY)": "日元 (JPY)",
    "Peso argentino (ARS)": "阿根廷比索 (ARS)",
    "Peso mexicano (MXN)": "墨西哥比索 (MXN)",
    ...TEXTOS_PDV["zh-CN"],
    ...TEXTOS_IMPRESSAO_COLUNAS["zh-CN"],
    "Seções do cadastro": "登记部分",
    "Anexar comprovante de pagamento": "附加付款凭证",
    "Baixar parcela": "结清分期",
    "Recibo de pagamento": "付款收据",
    "Recibo nº": "收据编号",
    Pagador: "付款人",
    "Documento do pagador": "付款人证件",
    "Venda / Parcela": "销售 / 分期",
    "Data do recebimento": "收款日期",
    "Dados do pagamento": "付款信息",
    "Serial vinculado": "关联序列号",
    "Assinatura do recebedor": "收款人签名",
    "Este recibo confirma a quitação da parcela descrita acima.": "本收据确认上述分期款项已结清。",
    "Permita pop-ups para imprimir o recibo.": "请允许弹出窗口以打印收据。",
    "Recebemos de {pagador} a importância de {valor}, referente ao pagamento da parcela {parcela} da venda nº {venda}, correspondente a {descricao}.": "兹收到 {pagador} 支付的 {valor}，用于支付销售单号 {venda} 的第 {parcela} 期款项，对应内容为 {descricao}。",
    "Status do orçamento": "报价状态",
    "Importar para vendas": "转换为销售",
    "Importar orçamento para vendas": "将报价转换为销售",
    "Aprove o orçamento antes de importar": "请先批准报价再进行转换",
    "Apenas orçamentos aprovados podem ser importados para vendas.": "只有已批准的报价才能转换为销售。",
    "Este orçamento está vencido e não pode mais ser importado para vendas.": "该报价已过期，无法再转换为销售。",
    "Orçamento vencido não pode ser importado": "已过期的报价无法转换",
    "Não foi possível importar o orçamento para vendas.": "无法将报价转换为销售。",
    "Erro ao importar orçamento para vendas.": "将报价转换为销售时出错。",
    "Orçamento importado para vendas com sucesso.": "报价已成功转换为销售。",
    "O orçamento não movimenta estoque nem gera lançamentos financeiros.": "报价不会变动库存，也不会生成财务记录。",
    "Vendas e orçamentos em um único fluxo, com movimentação automática somente nas vendas.": "销售与报价统一管理，仅销售会自动产生库存和财务变动。",
    "Registrar orçamento": "保存报价",
    "Novo Orçamento": "新建报价",
    "Dados do Orçamento": "报价数据",
    "Dados do orçamento": "报价数据",
    "Número do orçamento": "报价编号",
    "Orçamento atualizado com sucesso.": "报价更新成功。",
    "Orçamento registrado com sucesso.": "报价保存成功。",
    "Não foi possível salvar o orçamento.": "无法保存报价。",
    "Data do orçamento": "报价日期",
    "Data de validade": "有效期至",
    "Observações do orçamento...": "报价备注...",
    "Não foi possível salvar o orçamento para anexar documentos.": "无法保存报价以附加文档。",
    "Não foi possível preparar o orçamento para anexar documentos.": "无法准备报价以附加文档。",
    "Orçamento salvo como pendente para manter os documentos disponíveis. Finalize o cadastro para concluir.": "报价已保存为待处理，以保留文档。请完成表单。",
    "Erro ao salvar o orçamento.": "保存报价时出错。",
    "Orçamento salvo. Agora você já pode anexar documentos sem fechar a tela.": "报价已保存。现在无需关闭表单即可附加文档。",
    "Nenhuma venda ou orçamento registrado": "尚未登记销售或报价",
    "Clique em Nova Venda para iniciar o cadastro.": "点击新建销售开始登记。",
    "Buscar por ID, cliente...": "按 ID 或客户搜索...",
    "Vendas e Orçamentos": "销售与报价",
    "Aprove o orçamento antes de importar para vendas.": "请先批准报价再转换为销售。",
    "Este orçamento não tem itens para importar.": "此报价没有可转换的项目。",
    themeLight: "浅色主题",
    themeDark: "深色主题",
    loadingText: "处理中...",
    loginTitle: "很高兴见到您！",
    loginSubtitle: "请输入您的登录凭据",
    emailLabel: "邮箱",
    emailPlaceholder: "您的邮箱",
    passwordLabel: "密码",
    passwordPlaceholder: "您的密码",
    rememberMe: "保持登录",
    forgotPasswordText: "忘记密码？",
    passwordRecoveryTitle: "找回密码",
    passwordRecoverySubtitle: "请输入您的邮箱以接收找回密码说明。",
    passwordRecoverySent: "找回密码邮件已成功发送。",
    passwordRecoverySendError: "无法发送找回密码邮件。请检查邮箱地址后重试。",
    passwordRecoveryRateLimit: "找回密码请求过多。请等待 15 分钟后重试。",
    passwordRecoveryUnavailable: "邮件发送服务当前不可用。请联系系统管理员。",
    passwordRecoverySending: "正在发送...", passwordRecoverySend: "发送找回邮件",
    passwordRecoveryBack: "返回登录", passwordRecoveryNewTitle: "创建新密码",
    passwordRecoveryLogoAlt: "公司徽标",
    passwordRecoveryNewSubtitle: "请输入并确认您的新密码。",
    passwordRecoveryValidating: "正在验证找回密码链接...",
    passwordRecoveryChanged: "密码已成功修改。您现在可以登录。",
    passwordRecoveryNewPassword: "新密码", passwordRecoveryNewPasswordPlaceholder: "请输入新密码",
    passwordRecoveryConfirmPassword: "确认密码", passwordRecoveryConfirmPlaceholder: "请再次输入新密码",
    passwordRecoverySaving: "正在保存...", passwordRecoverySave: "保存新密码",
    passwordRecoveryMinLength: "密码必须至少包含 8 个字符，包括大写字母、数字和特殊字符。",
    passwordRecoveryMismatch: "两次输入的密码不一致。",
    passwordRecoveryInvalid: "此链接无效或已过期。请重新申请找回密码。",
    loginBtn: "登录",
    loginLoading: "正在登录...",
    noAccountText: "还没有账号？",
    registerLink: "注册",
    termsPrefixLogin: "点击登录即表示您同意",
    termsLink: "使用条款",
    andText: "和",
    privacyLink: "隐私政策 (LGPD)",
    socialSeparatorLogin: "或使用您的邮箱继续",
    followUs: "关注我们：",
    "Adicionar item": "添加项目",
    "Adicionar grade": "添加规格",
    fillEmailPassword: "请输入邮箱和密码。",
    loginFailed: "登录失败。",
    showPassword: "显示密码",
    hidePassword: "隐藏密码",
    available: "在线",
    away: "离开",
    busy: "忙碌",
    offline: "离线",
    "Defina seu status atual": "设置您的当前状态",
    "Ativo e disponível para contato": "在线且可联系",
    "Estou ausente no momento": "我暂时离开",
    "Em uma atividade intensa": "正在处理繁忙事务",
    "Desconectado ou indisponível": "已断开或不可用",
    darkMode: "深色模式",
    status: "状态",
    language: "\u8BED\u8A00",
    "Selecione o idioma de sua preferência.": "请选择您的首选语言。",
    "Português do Brasil": "巴西葡萄牙语",
    Espanhol: "西班牙语",
    Inglês: "英语",
    "Chinês Simplificado": "简体中文",
    notifications: "通知",
    "Preferências de notificações": "通知偏好设置",
    "notificações ativas": "已开启的通知",
    "Escolha quais notificações deseja receber por e-mail.": "选择您希望通过电子邮件接收的通知。",
    "Não foi possível carregar suas preferências de notificações.": "无法加载您的通知偏好设置。",
    "Não foi possível salvar suas preferências de notificações.": "无法保存您的通知偏好设置。",
    "Carregando preferências...": "正在加载偏好设置...",
    "Nenhuma notificação está disponível para as telas liberadas no seu perfil.": "您的个人资料所启用的页面暂无可用通知。",
    "Receber notificações por e-mail": "接收电子邮件通知",
    "Desative para bloquear todos os e-mails de notificações da plataforma.": "关闭此选项可阻止平台发送所有通知邮件。",
    "Novos chamados": "新工单",
    "Receber um e-mail quando um novo chamado chegar.": "新工单到达时接收电子邮件。",
    "Chamados encerrados": "已关闭工单",
    "Receber um e-mail quando um chamado for concluído.": "工单完成时接收电子邮件。",
    "Estoque baixo": "库存不足",
    "Avisar quando um produto atingir o estoque mínimo.": "产品达到最低库存时发出通知。",
    "Novas vendas": "新销售",
    "Avisar quando uma nova venda for concluída.": "新销售完成时发出通知。",
    "Parcelas em atraso": "逾期分期款",
    "Avisar sobre parcelas vencidas que continuam pendentes.": "通知仍未支付的逾期分期款。",
    "Confirmações de pagamento": "付款确认",
    "Avisar quando o pagamento de uma parcela for confirmado.": "分期付款确认时发出通知。",
    "Salvando preferências...": "正在保存偏好设置...",
    "Consulte suas anotações sincronizadas com a plataforma.": "查看与平台同步的笔记。",
    notes: "便签",
    myAccount: "我的账户",
    help: "帮助",
    accessLog: "访问日志",
    settings: "设置",
    exit: "退出",
    user: "用户",
    support: "支持",
    assistantIa: "AI 助手",
    askAnything: "随时向我提问...",
  },
};

export const OPCOES_IDIOMA: Array<{ code: Idioma; title: string }> = [
  { code: "pt", title: "Português" },
  { code: "es", title: "Espa\u00F1ol" },
  { code: "en", title: "English" },
  { code: "zh-CN", title: "\u4E2D\u6587" },
];

const TEXTOS_SECOES_MODAL: Record<Idioma, Dicionario> = {
  pt: {
    Dados: "Dados",
    Observação: "Observação",
    Observações: "Observações",
    Observacoes: "Observações",
    Imagem: "Imagem",
    Imagens: "Imagens",
    Identificação: "Identificação",
    Documentos: "Documentos",
    Contato: "Contato",
    Endereço: "Endereço",
    "Dados Bancários": "Dados Bancários",
    "Dados bancários": "Dados bancários",
    Complementos: "Complementos",
    Anexos: "Anexos",
    "Datas e Remuneração": "Datas e Remuneração",
    Responsável: "Responsável",
    "Contato Responsável": "Contato Responsável",
    "Dados Bancários e Pagamento": "Dados Bancários e Pagamento",
    "Imagem do perfil": "Imagem do perfil",
    Vínculo: "Vínculo",
    "Credenciais de Acesso": "Credenciais de Acesso",
    "Acessos do login": "Acessos do login",
    "Empresas que o usuário acessa": "Empresas que o usuário acessa",
    "Telas e Permissões de Acesso": "Telas e Permissões de Acesso",
    "Janela de Horário de Acesso": "Janela de Horário de Acesso",
    Descrição: "Descrição",
    Classificação: "Classificação",
    Preços: "Preços",
    "Ficha Técnica": "Ficha Técnica",
    "Atributos Comerciais": "Atributos Comerciais",
    Descrições: "Descrições",
    "Variações (Grade de Produtos)": "Variações (Grade de Produtos)",
    Valores: "Valores",
    Equipamentos: "Equipamentos",
    "Titular e PIX": "Titular e PIX",
    "Descrição e solução": "Descrição e solução",
    "Chave de ativação": "Chave de ativação",
    "Dados do Lançamento": "Dados do Lançamento",
    Tags: "Tags",
    "Dados da Compra": "Dados da Compra",
    Itens: "Itens",
    Grade: "Grade",
    Contagem: "Contagem",
    "Pedido do cliente": "Pedido do cliente",
    "Registro da etapa": "Registro da etapa",
    "Resposta do suporte": "Resposta do suporte",
    "Dados do Chamado": "Dados do Chamado",
    "Pedido em": "Pedido em",
    "Sem descrição informada.": "Sem descrição informada.",
    "Ainda sem resposta registrada.": "Ainda sem resposta registrada.",
    "Visualizar login de suporte": "Visualizar login de suporte",
    Nível: "Nível",
    "Dados da Abertura": "Dados da Abertura",
    Pedido: "Pedido",
    Confirmação: "Confirmação",
    "Comprovante de entrega": "Comprovante de entrega",
    "Resumo do Caixa": "Resumo do Caixa",
    "Confirmação do Fechamento": "Confirmação do Fechamento",
    Movimentação: "Movimentação",
    "Resumo da venda": "Resumo da venda",
    Parcelas: "Parcelas",
    Situação: "Situação",
    "Baixa da parcela": "Baixa da parcela",
    "Documento da parcela": "Documento da parcela",
    Cliente: "Cliente",
    "Itens do pedido": "Itens do pedido",
    Finalização: "Finalização",
    "Dados do Orçamento": "Dados do Orçamento",
    "Dados da Venda": "Dados da Venda",
    "Forma de pagamento": "Forma de pagamento",
    Entrega: "Entrega",
    Cadastros: "Cadastros",
    Comercial: "Comercial",
    Estoque: "Estoque",
    Finanças: "Finanças",
    Tickets: "Tickets",
    Plataforma: "Plataforma",
    "Caminho da página": "Caminho da página",
    "Progresso do cadastro": "Progresso do cadastro",
    Seção: "Seção",
    de: "de",
    preenchido: "preenchido",
    "Atalhos das seções": "Atalhos das seções",
    "Ir para a seção": "Ir para a seção",
    "Seus dados estão seguros conosco.": "Seus dados estão seguros conosco.",
    "Seções do cadastro": "Seções do cadastro",
    Obrigatório: "Obrigatório",
    Válido: "Válido",
    WhatsApp: "WhatsApp",
    "Formato: CPF 000.000.000-00 ou CNPJ 00.000.000/0000-00": "Formato: CPF 000.000.000-00 ou CNPJ 00.000.000/0000-00",
    "Formato: 000.000.000-00": "Formato: 000.000.000-00",
    "Formato: 00.000.000/0000-00": "Formato: 00.000.000/0000-00",
    "Formato: 00000-000": "Formato: 00000-000",
    "Formato: dia/mês/ano": "Formato: dia/mês/ano",
    "Informe o código e/ou descrição do CNAE principal.": "Informe o código e/ou descrição do CNAE principal.",
  },
  en: {
    Dados: "Details",
    Observação: "Notes",
    Observações: "Notes",
    Observacoes: "Notes",
    Imagem: "Image",
    Imagens: "Images",
    Identificação: "Identification",
    Documentos: "Documents",
    Contato: "Contact",
    Endereço: "Address",
    "Dados Bancários": "Bank details",
    "Dados bancários": "Bank details",
    Complementos: "Additional details",
    Anexos: "Attachments",
    "Datas e Remuneração": "Dates and compensation",
    Responsável: "Owner",
    "Contato Responsável": "Primary contact",
    "Dados Bancários e Pagamento": "Banking and payment",
    "Imagem do perfil": "Profile image",
    Vínculo: "Association",
    "Credenciais de Acesso": "Access credentials",
    "Acessos do login": "Login access",
    "Empresas que o usuário acessa": "Companies the user can access",
    "Telas e Permissões de Acesso": "Screens and access permissions",
    "Janela de Horário de Acesso": "Access time window",
    Descrição: "Description",
    Classificação: "Classification",
    Preços: "Prices",
    "Ficha Técnica": "Technical specifications",
    "Atributos Comerciais": "Commercial attributes",
    Descrições: "Descriptions",
    "Variações (Grade de Produtos)": "Variations (product matrix)",
    Valores: "Values",
    Equipamentos: "Equipment",
    "Titular e PIX": "Account holder and PIX",
    "Descrição e solução": "Description and solution",
    "Chave de ativação": "Activation key",
    "Dados do Lançamento": "Transaction details",
    Tags: "Tags",
    "Dados da Compra": "Purchase details",
    Itens: "Items",
    Grade: "Product matrix",
    Contagem: "Count",
    "Pedido do cliente": "Customer request",
    "Registro da etapa": "Stage record",
    "Resposta do suporte": "Support response",
    "Dados do Chamado": "Ticket details",
    "Pedido em": "Requested on",
    "Sem descrição informada.": "No description provided.",
    "Ainda sem resposta registrada.": "No support response has been recorded yet.",
    "Visualizar login de suporte": "View support login",
    Nível: "Level",
    "Dados da Abertura": "Opening details",
    Pedido: "Order",
    Confirmação: "Confirmation",
    "Comprovante de entrega": "Proof of delivery",
    "Resumo do Caixa": "Register summary",
    "Confirmação do Fechamento": "Closing confirmation",
    Movimentação: "Movement",
    "Resumo da venda": "Sale summary",
    Parcelas: "Installments",
    Situação: "Status",
    "Baixa da parcela": "Installment settlement",
    "Documento da parcela": "Installment document",
    Cliente: "Customer",
    "Itens do pedido": "Order items",
    Finalização: "Completion",
    "Dados do Orçamento": "Quote details",
    "Dados da Venda": "Sale details",
    "Forma de pagamento": "Payment method",
    Entrega: "Delivery",
    Cadastros: "Registrations",
    Comercial: "Commercial",
    Estoque: "Inventory",
    Finanças: "Finance",
    Tickets: "Tickets",
    Plataforma: "Platform",
    "Caminho da página": "Page path",
    "Progresso do cadastro": "Registration progress",
    Seção: "Section",
    de: "of",
    preenchido: "complete",
    "Atalhos das seções": "Section shortcuts",
    "Ir para a seção": "Go to section",
    "Seus dados estão seguros conosco.": "Your data is safe with us.",
    "Seções do cadastro": "Registration sections",
    Obrigatório: "Required",
    Válido: "Valid",
    WhatsApp: "WhatsApp",
    "Formato: CPF 000.000.000-00 ou CNPJ 00.000.000/0000-00": "Format: CPF 000.000.000-00 or CNPJ 00.000.000/0000-00",
    "Formato: 000.000.000-00": "Format: 000.000.000-00",
    "Formato: 00.000.000/0000-00": "Format: 00.000.000/0000-00",
    "Formato: 00000-000": "Format: 00000-000",
    "Formato: dia/mês/ano": "Format: day/month/year",
    "Informe o código e/ou descrição do CNAE principal.": "Enter the primary CNAE code and/or description.",
  },
  es: {
    Dados: "Datos",
    Observação: "Observación",
    Observações: "Observaciones",
    Observacoes: "Observaciones",
    Imagem: "Imagen",
    Imagens: "Imágenes",
    Identificação: "Identificación",
    Documentos: "Documentos",
    Contato: "Contacto",
    Endereço: "Dirección",
    "Dados Bancários": "Datos bancarios",
    "Dados bancários": "Datos bancarios",
    Complementos: "Complementos",
    Anexos: "Adjuntos",
    "Datas e Remuneração": "Fechas y remuneración",
    Responsável: "Responsable",
    "Contato Responsável": "Contacto responsable",
    "Dados Bancários e Pagamento": "Datos bancarios y pago",
    "Imagem do perfil": "Imagen del perfil",
    Vínculo: "Vínculo",
    "Credenciais de Acesso": "Credenciales de acceso",
    "Acessos do login": "Accesos del usuario",
    "Empresas que o usuário acessa": "Empresas a las que accede el usuario",
    "Telas e Permissões de Acesso": "Pantallas y permisos de acceso",
    "Janela de Horário de Acesso": "Horario de acceso",
    Descrição: "Descripción",
    Classificação: "Clasificación",
    Preços: "Precios",
    "Ficha Técnica": "Ficha técnica",
    "Atributos Comerciais": "Atributos comerciales",
    Descrições: "Descripciones",
    "Variações (Grade de Produtos)": "Variaciones (matriz de productos)",
    Valores: "Valores",
    Equipamentos: "Equipos",
    "Titular e PIX": "Titular y PIX",
    "Descrição e solução": "Descripción y solución",
    "Chave de ativação": "Clave de activación",
    "Dados do Lançamento": "Datos del movimiento",
    Tags: "Etiquetas",
    "Dados da Compra": "Datos de la compra",
    Itens: "Ítems",
    Grade: "Matriz de productos",
    Contagem: "Conteo",
    "Pedido do cliente": "Solicitud del cliente",
    "Registro da etapa": "Registro de la etapa",
    "Resposta do suporte": "Respuesta de soporte",
    "Dados do Chamado": "Datos del ticket",
    "Pedido em": "Solicitado el",
    "Sem descrição informada.": "No se proporcionó ninguna descripción.",
    "Ainda sem resposta registrada.": "Aún no se ha registrado ninguna respuesta.",
    "Visualizar login de suporte": "Ver login de soporte",
    Nível: "Nivel",
    "Dados da Abertura": "Datos de apertura",
    Pedido: "Pedido",
    Confirmação: "Confirmación",
    "Comprovante de entrega": "Comprobante de entrega",
    "Resumo do Caixa": "Resumen de caja",
    "Confirmação do Fechamento": "Confirmación del cierre",
    Movimentação: "Movimiento",
    "Resumo da venda": "Resumen de la venta",
    Parcelas: "Cuotas",
    Situação: "Situación",
    "Baixa da parcela": "Cobro de la cuota",
    "Documento da parcela": "Documento de la cuota",
    Cliente: "Cliente",
    "Itens do pedido": "Ítems del pedido",
    Finalização: "Finalización",
    "Dados do Orçamento": "Datos del presupuesto",
    "Dados da Venda": "Datos de la venta",
    "Forma de pagamento": "Forma de pago",
    Entrega: "Entrega",
    Cadastros: "Registros",
    Comercial: "Comercial",
    Estoque: "Inventario",
    Finanças: "Finanzas",
    Tickets: "Tickets",
    Plataforma: "Plataforma",
    "Caminho da página": "Ruta de la página",
    "Progresso do cadastro": "Progreso del registro",
    Seção: "Sección",
    de: "de",
    preenchido: "completado",
    "Atalhos das seções": "Accesos directos a las secciones",
    "Ir para a seção": "Ir a la sección",
    "Seus dados estão seguros conosco.": "Tus datos están seguros con nosotros.",
    "Seções do cadastro": "Secciones del registro",
    Obrigatório: "Obligatorio",
    Válido: "Válido",
    WhatsApp: "WhatsApp",
    "Formato: CPF 000.000.000-00 ou CNPJ 00.000.000/0000-00": "Formato: CPF 000.000.000-00 o CNPJ 00.000.000/0000-00",
    "Formato: 000.000.000-00": "Formato: 000.000.000-00",
    "Formato: 00.000.000/0000-00": "Formato: 00.000.000/0000-00",
    "Formato: 00000-000": "Formato: 00000-000",
    "Formato: dia/mês/ano": "Formato: día/mes/año",
    "Informe o código e/ou descrição do CNAE principal.": "Ingrese el código y/o la descripción del CNAE principal.",
  },
  "zh-CN": {
    Dados: "数据",
    Observação: "备注",
    Observações: "备注",
    Observacoes: "备注",
    Imagem: "图片",
    Imagens: "图片",
    Identificação: "基本信息",
    Documentos: "文档",
    Contato: "联系方式",
    Endereço: "地址",
    "Dados Bancários": "银行信息",
    "Dados bancários": "银行信息",
    Complementos: "补充信息",
    Anexos: "附件",
    "Datas e Remuneração": "日期与薪酬",
    Responsável: "负责人",
    "Contato Responsável": "主要联系人",
    "Dados Bancários e Pagamento": "银行与付款信息",
    "Imagem do perfil": "头像",
    Vínculo: "关联",
    "Credenciais de Acesso": "访问凭证",
    "Acessos do login": "登录访问权限",
    "Empresas que o usuário acessa": "用户可访问的公司",
    "Telas e Permissões de Acesso": "页面与访问权限",
    "Janela de Horário de Acesso": "访问时间范围",
    Descrição: "描述",
    Classificação: "分类",
    Preços: "价格",
    "Ficha Técnica": "技术规格",
    "Atributos Comerciais": "商业属性",
    Descrições: "描述",
    "Variações (Grade de Produtos)": "变体（产品矩阵）",
    Valores: "数值",
    Equipamentos: "设备",
    "Titular e PIX": "账户持有人与 PIX",
    "Descrição e solução": "描述与解决方案",
    "Chave de ativação": "激活密钥",
    "Dados do Lançamento": "交易信息",
    Tags: "标签",
    "Dados da Compra": "采购信息",
    Itens: "项目",
    Grade: "产品矩阵",
    Contagem: "盘点",
    "Pedido do cliente": "客户请求",
    "Registro da etapa": "阶段记录",
    "Resposta do suporte": "支持回复",
    "Dados do Chamado": "工单信息",
    "Pedido em": "请求时间",
    "Sem descrição informada.": "未提供描述。",
    "Ainda sem resposta registrada.": "尚未记录支持回复。",
    "Visualizar login de suporte": "查看支持登录账号",
    Nível: "级别",
    "Dados da Abertura": "开班信息",
    Pedido: "订单",
    Confirmação: "确认",
    "Comprovante de entrega": "交付凭证",
    "Resumo do Caixa": "收银台摘要",
    "Confirmação do Fechamento": "关账确认",
    Movimentação: "流水",
    "Resumo da venda": "销售摘要",
    Parcelas: "分期",
    Situação: "状态",
    "Baixa da parcela": "分期收款",
    "Documento da parcela": "分期文档",
    Cliente: "客户",
    "Itens do pedido": "订单项目",
    Finalização: "完成",
    "Dados do Orçamento": "报价信息",
    "Dados da Venda": "销售信息",
    "Forma de pagamento": "付款方式",
    Entrega: "交付",
    Cadastros: "登记",
    Comercial: "商务",
    Estoque: "库存",
    Finanças: "财务",
    Tickets: "工单",
    Plataforma: "平台",
    "Caminho da página": "页面路径",
    "Progresso do cadastro": "登记进度",
    Seção: "步骤",
    de: "/",
    preenchido: "已完成",
    "Atalhos das seções": "分区快捷导航",
    "Ir para a seção": "前往分区",
    "Seus dados estão seguros conosco.": "您的数据在我们这里是安全的。",
    "Seções do cadastro": "登记部分",
    Obrigatório: "必填",
    Válido: "有效",
    WhatsApp: "WhatsApp",
    "Formato: CPF 000.000.000-00 ou CNPJ 00.000.000/0000-00": "格式：CPF 000.000.000-00 或 CNPJ 00.000.000/0000-00",
    "Formato: 000.000.000-00": "格式：000.000.000-00",
    "Formato: 00.000.000/0000-00": "格式：00.000.000/0000-00",
    "Formato: 00000-000": "格式：00000-000",
    "Formato: dia/mês/ano": "格式：日/月/年",
    "Informe o código e/ou descrição do CNAE principal.": "请输入主要 CNAE 代码和/或说明。",
  },
};

const TEXTOS_LISTAGEM_COMPACTA: Record<Idioma, Dicionario> = {
  pt: {
    Total: "Total",
    registros: "registros",
    "do total": "do total",
    "Resumo da listagem": "Resumo da listagem",
    Ativos: "Ativos",
    Inativos: "Inativos",
    "Pessoa Física": "Pessoa Física",
    "Pessoa Jurídica": "Pessoa Jurídica",
    Favoritos: "Favoritos",
    Web: "Web",
    Mobile: "Mobile",
    "Mais Tags": "Mais Tags",
    "Filtre a listagem pelas tags do cadastro": "Filtre a listagem pelas tags do cadastro",
    "Nenhuma tag configurada nesta tela.": "Nenhuma tag configurada nesta tela.",
    "Limpar seleção": "Limpar seleção",
    Disponibilidade: "Disponibilidade",
    "Em Estoque": "Em Estoque",
    "Sob Encomenda": "Sob Encomenda",
    "Limpar filtro": "Limpar filtro",
    "Termos e Condições de uso": "Termos e Condições de uso",
    "Aceitar": "Aceitar",
    "Rejeitar": "Rejeitar",
    "Ao clicar em": "Ao clicar em",
    "você concorda com os": "você concorda com os",
    "Termos de Uso": "Termos de Uso",
    "Política de Privacidade": "Política de Privacidade",
    "Política de Cookies": "Política de Cookies",
    "Política de Cancelamento": "Política de Cancelamento",
    "Para reforçar o compromisso com nossos usuários e garantir o controle com relação ao uso de dados pessoais, em decorrência da Lei Geral de Proteção de Dados (nº 13.709/2018), estamos disponibilizando os Termos e Políticas de Privacidade para os usuários da nossa plataforma.": "Para reforçar o compromisso com nossos usuários e garantir o controle com relação ao uso de dados pessoais, em decorrência da Lei Geral de Proteção de Dados (nº 13.709/2018), estamos disponibilizando os Termos e Políticas de Privacidade para os usuários da nossa plataforma.",
    "Para garantir a continuidade do uso de nossos serviços, por favor, revise essas informações e confirme estar de acordo com nossos termos.": "Para garantir a continuidade do uso de nossos serviços, por favor, revise essas informações e confirme estar de acordo com nossos termos.",
    "Não foi possível registrar seu aceite. Tente novamente.": "Não foi possível registrar seu aceite. Tente novamente.",
    "Como deseja receber": "Como deseja receber",
    "Enviamos o link de redefinição para o e-mail informado.": "Enviamos o link de redefinição para o e-mail informado.",
    "Enviamos o link de redefinição para o telefone cadastrado nesta conta.": "Enviamos o link de redefinição para o telefone cadastrado nesta conta.",
    "Nenhuma sessão do WhatsApp está conectada.": "Nenhuma sessão do WhatsApp está conectada.",
    "A recuperação por WhatsApp está desativada.": "A recuperação por WhatsApp está desativada.",
    "Envio": "Envio",
    "Ambiente": "Ambiente",
    "Sandbox (número de teste)": "Sandbox (número de teste)",
    "Produção (destinatário real)": "Produção (destinatário real)",
    "Identificação do remetente": "Identificação do remetente",
    "Número de teste": "Número de teste",
    "Opcional": "Opcional",
    "Cada 160 caracteres consomem 1 crédito; a partir daí, mais 1 a cada 153.": "Cada 160 caracteres consomem 1 crédito; a partir daí, mais 1 a cada 153.",
    "Chave da API": "Chave da API",
    "Chave configurada": "Chave configurada",
    "Nova chave (auth-key)": "Nova chave (auth-key)",
    "Salvar chave": "Salvar chave",
    "Teste de envio": "Teste de envio",
    "Número do destinatário": "Número do destinatário",
    "Não foi possível salvar a chave da API.": "Não foi possível salvar a chave da API.",
    "Pacotes de créditos": "Pacotes de créditos",
    "Defina o preço e libere os pacotes que as organizações podem comprar em Minha conta.": "Defina o preço e libere os pacotes que as organizações podem comprar em Minha conta.",
    "Preço (R$)": "Preço (R$)",
    "Disponível": "Disponível",
    "créditos": "créditos",
    "Saldo por organização": "Saldo por organização",
    "Créditos disponíveis": "Créditos disponíveis",
    "Créditos usados no mês": "Créditos usados no mês",
    "Enviados no mês": "Enviados no mês",
    "Créditos usados": "Créditos usados",
    "Salvar configurações": "Salvar configurações",
    "Nenhuma organização cadastrada.": "Nenhuma organização cadastrada.",
    "Saldo de": "Saldo de",
    "Comprar": "Comprar",
    "moedas de e-mail": "moedas de e-mail",
    "Compra de moedas de e-mail": "Compra de moedas de e-mail",
    "Não foi possível iniciar a compra de moedas.": "Não foi possível iniciar a compra de moedas.",
    "Verificação anti-bot": "Verificação anti-bot",
    "Proteção do login": "Proteção do login",
    "Exigir verificação no login": "Exigir verificação no login",
    "Chave do site (site key)": "Chave do site (site key)",
    "Chave secreta": "Chave secreta",
    "Nova chave secreta": "Nova chave secreta",
    "Chave secreta do Turnstile salva.": "Chave secreta do Turnstile salva.",
    "Configuração do Turnstile salva.": "Configuração do Turnstile salva.",
    "Não foi possível salvar a chave secreta.": "Não foi possível salvar a chave secreta.",
    "Não foi possível carregar a configuração do Turnstile.": "Não foi possível carregar a configuração do Turnstile.",
    "Não foi possível salvar a configuração do Turnstile.": "Não foi possível salvar a configuração do Turnstile.",
    "Conclua a verificação de segurança para entrar.": "Conclua a verificação de segurança para entrar.",
    "Não foi possível carregar a verificação de segurança. Recarregue a página ou verifique a conexão.": "Não foi possível carregar a verificação de segurança. Recarregue a página ou verifique a conexão.",
    "Não foi possível concluir a verificação anti-bot.": "Não foi possível concluir a verificação anti-bot.",
    "A verificação está ativa: o login só é aceito com o desafio concluído.": "A verificação está ativa: o login só é aceito com o desafio concluído.",
    "Falta a chave do site ou a chave secreta — enquanto isso o login segue sem verificação.": "Falta a chave do site ou a chave secreta — enquanto isso o login segue sem verificação.",
    "A verificação está desligada e o login não exibe o desafio.": "A verificação está desligada e o login não exibe o desafio.",
    "A chave secreta fica cifrada no servidor e nunca é enviada ao navegador.": "A chave secreta fica cifrada no servidor e nunca é enviada ao navegador.",
    "Nenhuma chave secreta configurada. Copie a Secret Key do widget na Cloudflare.": "Nenhuma chave secreta configurada. Copie a Secret Key do widget na Cloudflare.",
    "O widget aparece na tela de login e o token é conferido no servidor a cada tentativa. Crie o widget em Turnstile no painel da Cloudflare, adicione os domínios da plataforma e cole aqui as duas chaves.": "O widget aparece na tela de login e o token é conferido no servidor a cada tentativa. Crie o widget em Turnstile no painel da Cloudflare, adicione os domínios da plataforma e cole aqui as duas chaves.",
    "Não foi possível salvar os pacotes.": "Não foi possível salvar os pacotes.",
    "Pacotes salvos.": "Pacotes salvos.",
    "Salvar pacotes": "Salvar pacotes",
    "Pacotes de créditos de e-mail": "Pacotes de créditos de e-mail",
    "Defina o preço e libere os pacotes de disparos de marketing que as organizações podem comprar em Minha conta.": "Defina o preço e libere os pacotes de disparos de marketing que as organizações podem comprar em Minha conta.",
    "API CONCILIADOR": "API CONCILIADOR",
    "Log de Acesso": "Log de Acesso",
    "Chat Interno": "Chat Interno",
    "Erro ao carregar o DRE.": "Erro ao carregar o DRE.",
    "Receita operacional bruta": "Receita operacional bruta",
    "(-) Deduções": "(-) Deduções",
    "(=) Receita operacional líquida": "(=) Receita operacional líquida",
    "(-) CMV": "(-) CMV",
    "(=) Lucro bruto": "(=) Lucro bruto",
    "(-) Despesas operacionais": "(-) Despesas operacionais",
    "(=) Resultado operacional": "(=) Resultado operacional",
    "(+/-) Resultado financeiro": "(+/-) Resultado financeiro",
    "(=) EBITDA": "(=) EBITDA",
    "(-) Despesas financeiras": "(-) Despesas financeiras",
    "(=) Lucro antes dos impostos": "(=) Lucro antes dos impostos",
    "(-) Imposto sobre renda": "(-) Imposto sobre renda",
    "(=) Lucro líquido": "(=) Lucro líquido",
    "Faturamento bruto": "Faturamento bruto",
    "Ticket médio": "Ticket médio",
    "Margem de contribuição": "Margem de contribuição",
    "Ponto de equilíbrio": "Ponto de equilíbrio",
    "EBITDA": "EBITDA",
    "Lucratividade": "Lucratividade",
    "Indicadores de desempenho": "Indicadores de desempenho",
    "Dados da Organização": "Dados da Organização",
    "Empresa atual": "Empresa atual",
    "Empresas vinculadas": "Empresas vinculadas",
    "Módulos ativos": "Módulos ativos",
    "Data de criação": "Data de criação",
    "Gerenciar organização": "Gerenciar organização",
    "Ver empresas": "Ver empresas",
    "Atingimento": "Atingimento",
    "Gauge semicircular": "Gauge semicircular",
    "Felicidade do cliente (NPS)": "Felicidade do cliente (NPS)",
    "Use os controles abaixo apenas para testar a aparência destes gráficos. Eles não alteram os dashboards.": "Use os controles abaixo apenas para testar a aparência destes gráficos. Eles não alteram os dashboards.",
    "Todas ativas": "Todas ativas",
    "ativas": "ativas",
    "este mês": "este mês",
    "em uso": "em uso",
    "novos este mês": "novos este mês",
    "Resultado consolidado do mês": "Resultado consolidado do mês",
    "vs mês anterior": "vs mês anterior",
    "Vendas registradas no mês": "Vendas registradas no mês",
    "Novo registro cadastrado": "Novo registro cadastrado",
    "Atualização cadastral": "Atualização cadastral",
    "Registro excluído": "Registro excluído",
    "Empresa alterada": "Empresa alterada",
    "Organização alterada": "Organização alterada",
    "Entrada no sistema": "Entrada no sistema",
    "Saída do sistema": "Saída do sistema",
    "Ação realizada": "Ação realizada",
    "por": "por",
    "Referência anterior": "Referência anterior",
    "Realizado": "Realizado",
    "Período analisado": "Período analisado",
    "Os indicadores e demonstrativos abaixo respeitam o intervalo selecionado.": "Os indicadores e demonstrativos abaixo respeitam o intervalo selecionado.",
    "Data inicial": "Data inicial",
    "Data final": "Data final",
    "Análise visual mensal": "Análise visual mensal",
    "Abertura acumulada, resultado operacional e saldo final de cada mês.": "Abertura acumulada, resultado operacional e saldo final de cada mês.",
    "Bom": "Bom",
    "Crítico": "Crítico",
    "Atenção": "Atenção",
    "Abertura acumulada": "Abertura acumulada",
    "Resultado operacional": "Resultado operacional",
    "Saldo final": "Saldo final",
    "Sem dados no período.": "Sem dados no período.",
    "Resumo financeiro mensal": "Resumo financeiro mensal",
    "Demonstrativo consolidado por mês no formato do DRE.": "Demonstrativo consolidado por mês no formato do DRE.",
    "Receita, Custos e Lucro Líquido (12 meses)": "Receita, Custos e Lucro Líquido (12 meses)",
    "Evolução mensal dos principais indicadores da DRE.": "Evolução mensal dos principais indicadores da DRE.",
    "Receita Bruta": "Receita Bruta",
    "Custos": "Custos",
    "Lucro Líquido": "Lucro Líquido",
    "Receita Bruta x Despesas Operacionais": "Receita Bruta x Despesas Operacionais",
    "Comparativo mensal entre receita e despesas.": "Comparativo mensal entre receita e despesas.",
    "Despesas Operacionais": "Despesas Operacionais",
    "Composição das Despesas Operacionais": "Composição das Despesas Operacionais",
    "Sem despesas no período.": "Sem despesas no período.",
    "Estrutura do DRE (Waterfall)": "Estrutura do DRE (Waterfall)",
    "Formação do resultado, da receita bruta ao lucro líquido.": "Formação do resultado, da receita bruta ao lucro líquido.",
    "Relatórios Financeiros": "Relatórios Financeiros",
    "Relatórios de Vendas": "Relatórios de Vendas",
    "Relatórios de Compras": "Relatórios de Compras",
    "Relatórios de Tickets": "Relatórios de Tickets",
    "Relatórios de Estoque": "Relatórios de Estoque",
    "Selecione e gere os relatórios disponíveis para este módulo.": "Selecione e gere os relatórios disponíveis para este módulo.",
    "Selecione um relatório, gere a visualização e imprima no padrão da plataforma.": "Selecione um relatório, gere a visualização e imprima no padrão da plataforma.",
    "Pesquisar relatório": "Pesquisar relatório",
    "Financeiro Consolidado": "Financeiro Consolidado",
    "Vendas por Status": "Vendas por Status",
    "Compras por Status": "Compras por Status",
    "Suporte por Etapa": "Suporte por Etapa",
    "Estoque por Saldo": "Estoque por Saldo",
    "Seu perfil não tem acesso a este módulo.": "Seu perfil não tem acesso a este módulo.",
    "Relatório de Vendas": "Relatório de Vendas",
    "Resumo por status das vendas registradas.": "Resumo por status das vendas registradas.",
    "Relatório de Compras": "Relatório de Compras",
    "Resumo por status dos pedidos de compra.": "Resumo por status dos pedidos de compra.",
    "Relatório de Tickets": "Relatório de Tickets",
    "Chamados agrupados por etapa de atendimento.": "Chamados agrupados por etapa de atendimento.",
    "Relatório de Estoque": "Relatório de Estoque",
    "Produtos com maior saldo atual na base.": "Produtos com maior saldo atual na base.",
    "Relatório Financeiro Consolidado": "Relatório Financeiro Consolidado",
    "Receitas, despesas e resultado agrupados por categoria.": "Receitas, despesas e resultado agrupados por categoria.",
    "Erro ao gerar o relatório.": "Erro ao gerar o relatório.",
    "Escolha um relatório acima e clique em gerar para montar a prévia.": "Escolha um relatório acima e clique em gerar para montar a prévia.",
    "Relatórios específicos do módulo de vendas.": "Relatórios específicos do módulo de vendas.",
    "Relatórios específicos do módulo de compras.": "Relatórios específicos do módulo de compras.",
    "Relatórios específicos do módulo de estoque.": "Relatórios específicos do módulo de estoque.",
    "Relatórios específicos do módulo financeiro.": "Relatórios específicos do módulo financeiro.",
    "Relatórios específicos do módulo de tickets.": "Relatórios específicos do módulo de tickets.",
    "Visualizar apenas o Estoque": "Visualizar apenas o Estoque",
    "Adicionar todos os produtos": "Adicionar todos os produtos",
    "Este login possui acesso somente para visualizar o saldo do estoque.": "Este login possui acesso somente para visualizar o saldo do estoque.",
    "Ajuda": "Ajuda",
    "Histórico de compras de moedas": "Histórico de compras de moedas",
    "Buscar por descrição, status, valor ou data": "Buscar por descrição, status, valor ou data",
    "Baixar comprovante": "Baixar comprovante",
    "O comprovante fica disponível após a confirmação do pagamento.": "O comprovante fica disponível após a confirmação do pagamento.",
    "Nenhuma compra encontrada para a busca.": "Nenhuma compra encontrada para a busca.",
    "Nenhuma compra de moedas registrada até o momento.": "Nenhuma compra de moedas registrada até o momento.",
    "Consulte pagamentos confirmados, pendentes e vencidos do plano.": "Consulte pagamentos confirmados, pendentes e vencidos do plano.",
    "moedas": "moedas",
    "item(s)": "item(s)",
    selecionados: "selecionados",
    favoritos: "favoritos",
    "Menu principal": "Menu principal",
  },
  en: {
    Total: "Total",
    registros: "records",
    "do total": "of total",
    "Resumo da listagem": "List summary",
    Ativos: "Active",
    Inativos: "Inactive",
    "Pessoa Física": "Individual",
    "Pessoa Jurídica": "Company",
    Favoritos: "Favorites",
    Web: "Web",
    Mobile: "Mobile",
    "Mais Tags": "More Tags",
    "Filtre a listagem pelas tags do cadastro": "Filter the list by registration tags",
    "Nenhuma tag configurada nesta tela.": "No tags are configured for this screen.",
    "Limpar seleção": "Clear selection",
    Disponibilidade: "Availability",
    "Em Estoque": "In Stock",
    "Sob Encomenda": "Made to Order",
    "Limpar filtro": "Clear filter",
    "Termos e Condições de uso": "Terms and Conditions of use",
    "Aceitar": "Accept",
    "Rejeitar": "Decline",
    "Ao clicar em": "By clicking",
    "você concorda com os": "you agree to the",
    "Termos de Uso": "Terms of Use",
    "Política de Privacidade": "Privacy Policy",
    "Política de Cookies": "Cookie Policy",
    "Política de Cancelamento": "Cancellation Policy",
    "Para reforçar o compromisso com nossos usuários e garantir o controle com relação ao uso de dados pessoais, em decorrência da Lei Geral de Proteção de Dados (nº 13.709/2018), estamos disponibilizando os Termos e Políticas de Privacidade para os usuários da nossa plataforma.": "To reinforce our commitment to users and ensure control over the use of personal data under the Brazilian General Data Protection Law (No. 13,709/2018), we are making the Terms and Privacy Policies available to the users of our platform.",
    "Para garantir a continuidade do uso de nossos serviços, por favor, revise essas informações e confirme estar de acordo com nossos termos.": "To keep using our services, please review this information and confirm that you agree with our terms.",
    "Não foi possível registrar seu aceite. Tente novamente.": "We could not record your acceptance. Try again.",
    "Como deseja receber": "How do you want to receive it",
    "Enviamos o link de redefinição para o e-mail informado.": "We send the reset link to the email you enter.",
    "Enviamos o link de redefinição para o telefone cadastrado nesta conta.": "We send the reset link to the phone registered on this account.",
    "Nenhuma sessão do WhatsApp está conectada.": "No WhatsApp session is connected.",
    "A recuperação por WhatsApp está desativada.": "WhatsApp recovery is turned off.",
    "Envio": "Sending",
    "Ambiente": "Environment",
    "Sandbox (número de teste)": "Sandbox (test number)",
    "Produção (destinatário real)": "Production (real recipient)",
    "Identificação do remetente": "Sender identifier",
    "Número de teste": "Test number",
    "Opcional": "Optional",
    "Cada 160 caracteres consomem 1 crédito; a partir daí, mais 1 a cada 153.": "Every 160 characters use 1 credit; after that, 1 more every 153.",
    "Chave da API": "API key",
    "Chave configurada": "Key configured",
    "Nova chave (auth-key)": "New key (auth-key)",
    "Salvar chave": "Save key",
    "Teste de envio": "Send test",
    "Número do destinatário": "Recipient number",
    "Não foi possível salvar a chave da API.": "We could not save the API key.",
    "Pacotes de créditos": "Credit packages",
    "Defina o preço e libere os pacotes que as organizações podem comprar em Minha conta.": "Set the price and release the packages organizations can buy in My account.",
    "Preço (R$)": "Price (R$)",
    "Disponível": "Available",
    "créditos": "credits",
    "Saldo por organização": "Balance by organization",
    "Créditos disponíveis": "Credits available",
    "Créditos usados no mês": "Credits used this month",
    "Enviados no mês": "Sent this month",
    "Créditos usados": "Credits used",
    "Salvar configurações": "Save settings",
    "Nenhuma organização cadastrada.": "No organization registered.",
    "Saldo de": "Balance of",
    "Comprar": "Buy",
    "moedas de e-mail": "email credits",
    "Compra de moedas de e-mail": "Email credit purchase",
    "Não foi possível iniciar a compra de moedas.": "We could not start the credit purchase.",
    "Verificação anti-bot": "Anti-bot verification",
    "Proteção do login": "Login protection",
    "Exigir verificação no login": "Require verification at login",
    "Chave do site (site key)": "Site key",
    "Chave secreta": "Secret key",
    "Nova chave secreta": "New secret key",
    "Chave secreta do Turnstile salva.": "Turnstile secret key saved.",
    "Configuração do Turnstile salva.": "Turnstile settings saved.",
    "Não foi possível salvar a chave secreta.": "We could not save the secret key.",
    "Não foi possível carregar a configuração do Turnstile.": "We could not load the Turnstile settings.",
    "Não foi possível salvar a configuração do Turnstile.": "We could not save the Turnstile settings.",
    "Conclua a verificação de segurança para entrar.": "Complete the security check to sign in.",
    "Não foi possível carregar a verificação de segurança. Recarregue a página ou verifique a conexão.": "We could not load the security check. Reload the page or check your connection.",
    "Não foi possível concluir a verificação anti-bot.": "We could not complete the anti-bot verification.",
    "A verificação está ativa: o login só é aceito com o desafio concluído.": "Verification is on: sign-in is only accepted with the challenge completed.",
    "Falta a chave do site ou a chave secreta — enquanto isso o login segue sem verificação.": "The site key or secret key is missing — until then, sign-in works without verification.",
    "A verificação está desligada e o login não exibe o desafio.": "Verification is off and the login does not show the challenge.",
    "A chave secreta fica cifrada no servidor e nunca é enviada ao navegador.": "The secret key is encrypted on the server and never sent to the browser.",
    "Nenhuma chave secreta configurada. Copie a Secret Key do widget na Cloudflare.": "No secret key configured. Copy the Secret Key from the widget in Cloudflare.",
    "O widget aparece na tela de login e o token é conferido no servidor a cada tentativa. Crie o widget em Turnstile no painel da Cloudflare, adicione os domínios da plataforma e cole aqui as duas chaves.": "The widget appears on the login screen and the token is checked on the server at every attempt. Create the widget under Turnstile in the Cloudflare dashboard, add the platform domains and paste both keys here.",
    "Não foi possível salvar os pacotes.": "We could not save the packages.",
    "Pacotes salvos.": "Packages saved.",
    "Salvar pacotes": "Save packages",
    "Pacotes de créditos de e-mail": "Email credit packages",
    "Defina o preço e libere os pacotes de disparos de marketing que as organizações podem comprar em Minha conta.": "Set the price and release the marketing campaign packages organizations can buy in My account.",
    "API CONCILIADOR": "CONCILIATOR API",
    "Log de Acesso": "Access log",
    "Chat Interno": "Internal chat",
    "Erro ao carregar o DRE.": "Could not load the income statement.",
    "Receita operacional bruta": "Gross operating revenue",
    "(-) Deduções": "(-) Deductions",
    "(=) Receita operacional líquida": "(=) Net operating revenue",
    "(-) CMV": "(-) Cost of goods sold",
    "(=) Lucro bruto": "(=) Gross profit",
    "(-) Despesas operacionais": "(-) Operating expenses",
    "(=) Resultado operacional": "(=) Operating result",
    "(+/-) Resultado financeiro": "(+/-) Financial result",
    "(=) EBITDA": "(=) EBITDA",
    "(-) Despesas financeiras": "(-) Financial expenses",
    "(=) Lucro antes dos impostos": "(=) Profit before taxes",
    "(-) Imposto sobre renda": "(-) Income tax",
    "(=) Lucro líquido": "(=) Net profit",
    "Faturamento bruto": "Gross revenue",
    "Ticket médio": "Average ticket",
    "Margem de contribuição": "Contribution margin",
    "Ponto de equilíbrio": "Break-even point",
    "EBITDA": "EBITDA",
    "Lucratividade": "Profitability",
    "Indicadores de desempenho": "Performance indicators",
    "Dados da Organização": "Organization Details",
    "Empresa atual": "Current company",
    "Empresas vinculadas": "Linked companies",
    "Módulos ativos": "Active modules",
    "Data de criação": "Creation date",
    "Gerenciar organização": "Manage organization",
    "Ver empresas": "View companies",
    "Atingimento": "Achievement",
    "Gauge semicircular": "Semicircular gauge",
    "Felicidade do cliente (NPS)": "Customer happiness (NPS)",
    "Use os controles abaixo apenas para testar a aparência destes gráficos. Eles não alteram os dashboards.": "Use the controls below only to test the appearance of these charts. They do not change the dashboards.",
    "Todas ativas": "All active",
    "ativas": "active",
    "este mês": "this month",
    "em uso": "in use",
    "novos este mês": "new this month",
    "Resultado consolidado do mês": "Consolidated monthly result",
    "vs mês anterior": "vs previous month",
    "Vendas registradas no mês": "Sales recorded this month",
    "Novo registro cadastrado": "New record created",
    "Atualização cadastral": "Record updated",
    "Registro excluído": "Record deleted",
    "Empresa alterada": "Company changed",
    "Organização alterada": "Organization changed",
    "Entrada no sistema": "Signed in",
    "Saída do sistema": "Signed out",
    "Ação realizada": "Action performed",
    "por": "by",
    "Referência anterior": "Previous reference",
    "Realizado": "Actual",
    "Período analisado": "Analyzed period",
    "Os indicadores e demonstrativos abaixo respeitam o intervalo selecionado.": "The indicators and statements below follow the selected date range.",
    "Data inicial": "Start date",
    "Data final": "End date",
    "Análise visual mensal": "Monthly visual analysis",
    "Abertura acumulada, resultado operacional e saldo final de cada mês.": "Opening balance, operating result, and closing balance for each month.",
    "Bom": "Good",
    "Crítico": "Critical",
    "Atenção": "Attention",
    "Abertura acumulada": "Opening balance",
    "Resultado operacional": "Operating result",
    "Saldo final": "Closing balance",
    "Sem dados no período.": "No data for this period.",
    "Resumo financeiro mensal": "Monthly financial summary",
    "Demonstrativo consolidado por mês no formato do DRE.": "Consolidated monthly statement in income statement format.",
    "Receita, Custos e Lucro Líquido (12 meses)": "Revenue, Costs, and Net Profit (12 months)",
    "Evolução mensal dos principais indicadores da DRE.": "Monthly trend of the main income statement indicators.",
    "Receita Bruta": "Gross Revenue",
    "Custos": "Costs",
    "Lucro Líquido": "Net Profit",
    "Receita Bruta x Despesas Operacionais": "Gross Revenue vs. Operating Expenses",
    "Comparativo mensal entre receita e despesas.": "Monthly comparison between revenue and expenses.",
    "Despesas Operacionais": "Operating Expenses",
    "Composição das Despesas Operacionais": "Operating Expense Breakdown",
    "Sem despesas no período.": "No expenses for this period.",
    "Estrutura do DRE (Waterfall)": "Income Statement Structure (Waterfall)",
    "Formação do resultado, da receita bruta ao lucro líquido.": "Result composition, from gross revenue to net profit.",
    "Relatórios Financeiros": "Financial Reports",
    "Relatórios de Vendas": "Sales Reports",
    "Relatórios de Compras": "Purchasing Reports",
    "Relatórios de Tickets": "Ticket Reports",
    "Relatórios de Estoque": "Inventory Reports",
    "Selecione e gere os relatórios disponíveis para este módulo.": "Select and generate the reports available for this module.",
    "Selecione um relatório, gere a visualização e imprima no padrão da plataforma.": "Select a report, generate the preview, and print it using the platform standard.",
    "Pesquisar relatório": "Search reports",
    "Financeiro Consolidado": "Consolidated Financials",
    "Vendas por Status": "Sales by Status",
    "Compras por Status": "Purchases by Status",
    "Suporte por Etapa": "Support by Stage",
    "Estoque por Saldo": "Inventory by Balance",
    "Seu perfil não tem acesso a este módulo.": "Your profile does not have access to this module.",
    "Relatório de Vendas": "Sales Report",
    "Resumo por status das vendas registradas.": "Summary of recorded sales by status.",
    "Relatório de Compras": "Purchasing Report",
    "Resumo por status dos pedidos de compra.": "Summary of purchase orders by status.",
    "Relatório de Tickets": "Ticket Report",
    "Chamados agrupados por etapa de atendimento.": "Tickets grouped by service stage.",
    "Relatório de Estoque": "Inventory Report",
    "Produtos com maior saldo atual na base.": "Products with the highest current inventory balance.",
    "Relatório Financeiro Consolidado": "Consolidated Financial Report",
    "Receitas, despesas e resultado agrupados por categoria.": "Revenue, expenses, and results grouped by category.",
    "Erro ao gerar o relatório.": "Could not generate the report.",
    "Escolha um relatório acima e clique em gerar para montar a prévia.": "Choose a report above and click Generate to build the preview.",
    "Relatórios específicos do módulo de vendas.": "Reports specific to the sales module.",
    "Relatórios específicos do módulo de compras.": "Reports specific to the purchasing module.",
    "Relatórios específicos do módulo de estoque.": "Reports specific to the inventory module.",
    "Relatórios específicos do módulo financeiro.": "Reports specific to the financial module.",
    "Relatórios específicos do módulo de tickets.": "Reports specific to the ticket module.",
    "Visualizar apenas o Estoque": "View inventory only",
    "Adicionar todos os produtos": "Add all products",
    "Este login possui acesso somente para visualizar o saldo do estoque.": "This login can only view the current inventory balance.",
    "Ajuda": "Help",
    "Histórico de compras de moedas": "Credit purchase history",
    "Buscar por descrição, status, valor ou data": "Search by description, status, amount or date",
    "Baixar comprovante": "Download receipt",
    "O comprovante fica disponível após a confirmação do pagamento.": "The receipt becomes available once the payment is confirmed.",
    "Nenhuma compra encontrada para a busca.": "No purchase found for this search.",
    "Nenhuma compra de moedas registrada até o momento.": "No credit purchase recorded yet.",
    "Consulte pagamentos confirmados, pendentes e vencidos do plano.": "Check confirmed, pending and overdue plan payments.",
    "moedas": "credits",
    "item(s)": "item(s)",
    selecionados: "selected",
    favoritos: "favorites",
    "Menu principal": "Main menu",
  },
  es: {
    Total: "Total",
    registros: "registros",
    "do total": "del total",
    "Resumo da listagem": "Resumen del listado",
    Ativos: "Activos",
    Inativos: "Inactivos",
    "Pessoa Física": "Persona física",
    "Pessoa Jurídica": "Persona jurídica",
    Favoritos: "Favoritos",
    Web: "Web",
    Mobile: "Móvil",
    "Mais Tags": "Más etiquetas",
    "Filtre a listagem pelas tags do cadastro": "Filtre la lista por las etiquetas del registro",
    "Nenhuma tag configurada nesta tela.": "No hay etiquetas configuradas para esta pantalla.",
    "Limpar seleção": "Limpiar selección",
    Disponibilidade: "Disponibilidad",
    "Em Estoque": "En Stock",
    "Sob Encomenda": "Bajo Pedido",
    "Limpar filtro": "Limpiar filtro",
    "Termos e Condições de uso": "Términos y Condiciones de uso",
    "Aceitar": "Aceptar",
    "Rejeitar": "Rechazar",
    "Ao clicar em": "Al hacer clic en",
    "você concorda com os": "aceptas los",
    "Termos de Uso": "Términos de Uso",
    "Política de Privacidade": "Política de Privacidad",
    "Política de Cookies": "Política de Cookies",
    "Política de Cancelamento": "Política de Cancelación",
    "Para reforçar o compromisso com nossos usuários e garantir o controle com relação ao uso de dados pessoais, em decorrência da Lei Geral de Proteção de Dados (nº 13.709/2018), estamos disponibilizando os Termos e Políticas de Privacidade para os usuários da nossa plataforma.": "Para reforzar el compromiso con nuestros usuarios y garantizar el control sobre el uso de datos personales, conforme a la Ley General de Protección de Datos (nº 13.709/2018), ponemos a disposición los Términos y Políticas de Privacidad para los usuarios de nuestra plataforma.",
    "Para garantir a continuidade do uso de nossos serviços, por favor, revise essas informações e confirme estar de acordo com nossos termos.": "Para garantizar la continuidad del uso de nuestros servicios, revisa esta información y confirma que estás de acuerdo con nuestros términos.",
    "Não foi possível registrar seu aceite. Tente novamente.": "No se pudo registrar tu aceptación. Inténtalo de nuevo.",
    "Como deseja receber": "Cómo deseas recibirlo",
    "Enviamos o link de redefinição para o e-mail informado.": "Enviamos el enlace de restablecimiento al correo indicado.",
    "Enviamos o link de redefinição para o telefone cadastrado nesta conta.": "Enviamos el enlace de restablecimiento al teléfono registrado en esta cuenta.",
    "Nenhuma sessão do WhatsApp está conectada.": "Ninguna sesión de WhatsApp está conectada.",
    "A recuperação por WhatsApp está desativada.": "La recuperación por WhatsApp está desactivada.",
    "Envio": "Envío",
    "Ambiente": "Entorno",
    "Sandbox (número de teste)": "Sandbox (número de prueba)",
    "Produção (destinatário real)": "Producción (destinatario real)",
    "Identificação do remetente": "Identificación del remitente",
    "Número de teste": "Número de prueba",
    "Opcional": "Opcional",
    "Cada 160 caracteres consomem 1 crédito; a partir daí, mais 1 a cada 153.": "Cada 160 caracteres consumen 1 crédito; a partir de ahí, 1 más cada 153.",
    "Chave da API": "Clave de la API",
    "Chave configurada": "Clave configurada",
    "Nova chave (auth-key)": "Nueva clave (auth-key)",
    "Salvar chave": "Guardar clave",
    "Teste de envio": "Prueba de envío",
    "Número do destinatário": "Número del destinatario",
    "Não foi possível salvar a chave da API.": "No se pudo guardar la clave de la API.",
    "Pacotes de créditos": "Paquetes de créditos",
    "Defina o preço e libere os pacotes que as organizações podem comprar em Minha conta.": "Define el precio y habilita los paquetes que las organizaciones pueden comprar en Mi cuenta.",
    "Preço (R$)": "Precio (R$)",
    "Disponível": "Disponible",
    "créditos": "créditos",
    "Saldo por organização": "Saldo por organización",
    "Créditos disponíveis": "Créditos disponibles",
    "Créditos usados no mês": "Créditos usados en el mes",
    "Enviados no mês": "Enviados en el mes",
    "Créditos usados": "Créditos usados",
    "Salvar configurações": "Guardar configuraciones",
    "Nenhuma organização cadastrada.": "Ninguna organización registrada.",
    "Saldo de": "Saldo de",
    "Comprar": "Comprar",
    "moedas de e-mail": "créditos de correo",
    "Compra de moedas de e-mail": "Compra de créditos de correo",
    "Não foi possível iniciar a compra de moedas.": "No se pudo iniciar la compra de créditos.",
    "Verificação anti-bot": "Verificación anti-bot",
    "Proteção do login": "Protección del inicio de sesión",
    "Exigir verificação no login": "Exigir verificación al iniciar sesión",
    "Chave do site (site key)": "Clave del sitio (site key)",
    "Chave secreta": "Clave secreta",
    "Nova chave secreta": "Nueva clave secreta",
    "Chave secreta do Turnstile salva.": "Clave secreta de Turnstile guardada.",
    "Configuração do Turnstile salva.": "Configuración de Turnstile guardada.",
    "Não foi possível salvar a chave secreta.": "No se pudo guardar la clave secreta.",
    "Não foi possível carregar a configuração do Turnstile.": "No se pudo cargar la configuración de Turnstile.",
    "Não foi possível salvar a configuração do Turnstile.": "No se pudo guardar la configuración de Turnstile.",
    "Conclua a verificação de segurança para entrar.": "Completa la verificación de seguridad para entrar.",
    "Não foi possível carregar a verificação de segurança. Recarregue a página ou verifique a conexão.": "No se pudo cargar la verificación de seguridad. Recarga la página o revisa la conexión.",
    "Não foi possível concluir a verificação anti-bot.": "No se pudo completar la verificación anti-bot.",
    "A verificação está ativa: o login só é aceito com o desafio concluído.": "La verificación está activa: el inicio de sesión solo se acepta con el desafío completado.",
    "Falta a chave do site ou a chave secreta — enquanto isso o login segue sem verificação.": "Falta la clave del sitio o la clave secreta: mientras tanto, el inicio de sesión sigue sin verificación.",
    "A verificação está desligada e o login não exibe o desafio.": "La verificación está desactivada y el inicio de sesión no muestra el desafío.",
    "A chave secreta fica cifrada no servidor e nunca é enviada ao navegador.": "La clave secreta se cifra en el servidor y nunca se envía al navegador.",
    "Nenhuma chave secreta configurada. Copie a Secret Key do widget na Cloudflare.": "Ninguna clave secreta configurada. Copia la Secret Key del widget en Cloudflare.",
    "O widget aparece na tela de login e o token é conferido no servidor a cada tentativa. Crie o widget em Turnstile no painel da Cloudflare, adicione os domínios da plataforma e cole aqui as duas chaves.": "El widget aparece en la pantalla de inicio de sesión y el token se verifica en el servidor en cada intento. Crea el widget en Turnstile en el panel de Cloudflare, agrega los dominios de la plataforma y pega aquí ambas claves.",
    "Não foi possível salvar os pacotes.": "No se pudieron guardar los paquetes.",
    "Pacotes salvos.": "Paquetes guardados.",
    "Salvar pacotes": "Guardar paquetes",
    "Pacotes de créditos de e-mail": "Paquetes de créditos de correo",
    "Defina o preço e libere os pacotes de disparos de marketing que as organizações podem comprar em Minha conta.": "Define el precio y habilita los paquetes de envíos de marketing que las organizaciones pueden comprar en Mi cuenta.",
    "API CONCILIADOR": "API CONCILIADOR",
    "Log de Acesso": "Registro de acceso",
    "Chat Interno": "Chat interno",
    "Erro ao carregar o DRE.": "No se pudo cargar el estado de resultados.",
    "Receita operacional bruta": "Ingresos operativos brutos",
    "(-) Deduções": "(-) Deducciones",
    "(=) Receita operacional líquida": "(=) Ingresos operativos netos",
    "(-) CMV": "(-) Costo de ventas",
    "(=) Lucro bruto": "(=) Utilidad bruta",
    "(-) Despesas operacionais": "(-) Gastos operativos",
    "(=) Resultado operacional": "(=) Resultado operativo",
    "(+/-) Resultado financeiro": "(+/-) Resultado financiero",
    "(=) EBITDA": "(=) EBITDA",
    "(-) Despesas financeiras": "(-) Gastos financieros",
    "(=) Lucro antes dos impostos": "(=) Utilidad antes de impuestos",
    "(-) Imposto sobre renda": "(-) Impuesto sobre la renta",
    "(=) Lucro líquido": "(=) Utilidad neta",
    "Faturamento bruto": "Facturación bruta",
    "Ticket médio": "Ticket promedio",
    "Margem de contribuição": "Margen de contribución",
    "Ponto de equilíbrio": "Punto de equilibrio",
    "EBITDA": "EBITDA",
    "Lucratividade": "Rentabilidad",
    "Indicadores de desempenho": "Indicadores de rendimiento",
    "Dados da Organização": "Datos de la Organización",
    "Empresa atual": "Empresa actual",
    "Empresas vinculadas": "Empresas vinculadas",
    "Módulos ativos": "Módulos activos",
    "Data de criação": "Fecha de creación",
    "Gerenciar organização": "Gestionar organización",
    "Ver empresas": "Ver empresas",
    "Atingimento": "Cumplimiento",
    "Gauge semicircular": "Medidor semicircular",
    "Felicidade do cliente (NPS)": "Felicidad del cliente (NPS)",
    "Use os controles abaixo apenas para testar a aparência destes gráficos. Eles não alteram os dashboards.": "Use los controles siguientes solo para probar la apariencia de estos gráficos. No modifican los paneles.",
    "Todas ativas": "Todas activas",
    "ativas": "activas",
    "este mês": "este mes",
    "em uso": "en uso",
    "novos este mês": "nuevos este mes",
    "Resultado consolidado do mês": "Resultado consolidado del mes",
    "vs mês anterior": "vs mes anterior",
    "Vendas registradas no mês": "Ventas registradas en el mes",
    "Novo registro cadastrado": "Nuevo registro creado",
    "Atualização cadastral": "Registro actualizado",
    "Registro excluído": "Registro eliminado",
    "Empresa alterada": "Empresa modificada",
    "Organização alterada": "Organización modificada",
    "Entrada no sistema": "Inicio de sesión",
    "Saída do sistema": "Cierre de sesión",
    "Ação realizada": "Acción realizada",
    "por": "por",
    "Referência anterior": "Referencia anterior",
    "Realizado": "Realizado",
    "Período analisado": "Período analizado",
    "Os indicadores e demonstrativos abaixo respeitam o intervalo selecionado.": "Los indicadores y estados siguientes respetan el intervalo seleccionado.",
    "Data inicial": "Fecha inicial",
    "Data final": "Fecha final",
    "Análise visual mensal": "Análisis visual mensual",
    "Abertura acumulada, resultado operacional e saldo final de cada mês.": "Saldo inicial, resultado operativo y saldo final de cada mes.",
    "Bom": "Bueno",
    "Crítico": "Crítico",
    "Atenção": "Atención",
    "Abertura acumulada": "Saldo inicial",
    "Resultado operacional": "Resultado operativo",
    "Saldo final": "Saldo final",
    "Sem dados no período.": "No hay datos para el período.",
    "Resumo financeiro mensal": "Resumen financiero mensual",
    "Demonstrativo consolidado por mês no formato do DRE.": "Estado consolidado por mes en formato de resultados.",
    "Receita, Custos e Lucro Líquido (12 meses)": "Ingresos, Costos y Utilidad Neta (12 meses)",
    "Evolução mensal dos principais indicadores da DRE.": "Evolución mensual de los principales indicadores del estado de resultados.",
    "Receita Bruta": "Ingresos Brutos",
    "Custos": "Costos",
    "Lucro Líquido": "Utilidad Neta",
    "Receita Bruta x Despesas Operacionais": "Ingresos Brutos vs. Gastos Operativos",
    "Comparativo mensal entre receita e despesas.": "Comparación mensual entre ingresos y gastos.",
    "Despesas Operacionais": "Gastos Operativos",
    "Composição das Despesas Operacionais": "Composición de los Gastos Operativos",
    "Sem despesas no período.": "No hay gastos para el período.",
    "Estrutura do DRE (Waterfall)": "Estructura del Estado de Resultados (Cascada)",
    "Formação do resultado, da receita bruta ao lucro líquido.": "Formación del resultado, desde los ingresos brutos hasta la utilidad neta.",
    "Relatórios Financeiros": "Informes Financieros",
    "Relatórios de Vendas": "Informes de Ventas",
    "Relatórios de Compras": "Informes de Compras",
    "Relatórios de Tickets": "Informes de Tickets",
    "Relatórios de Estoque": "Informes de Inventario",
    "Selecione e gere os relatórios disponíveis para este módulo.": "Selecciona y genera los informes disponibles para este módulo.",
    "Selecione um relatório, gere a visualização e imprima no padrão da plataforma.": "Selecciona un informe, genera la vista previa e imprímelo con el formato de la plataforma.",
    "Pesquisar relatório": "Buscar informe",
    "Financeiro Consolidado": "Finanzas Consolidadas",
    "Vendas por Status": "Ventas por Estado",
    "Compras por Status": "Compras por Estado",
    "Suporte por Etapa": "Soporte por Etapa",
    "Estoque por Saldo": "Inventario por Saldo",
    "Seu perfil não tem acesso a este módulo.": "Tu perfil no tiene acceso a este módulo.",
    "Relatório de Vendas": "Informe de Ventas",
    "Resumo por status das vendas registradas.": "Resumen por estado de las ventas registradas.",
    "Relatório de Compras": "Informe de Compras",
    "Resumo por status dos pedidos de compra.": "Resumen por estado de las órdenes de compra.",
    "Relatório de Tickets": "Informe de Tickets",
    "Chamados agrupados por etapa de atendimento.": "Tickets agrupados por etapa de atención.",
    "Relatório de Estoque": "Informe de Inventario",
    "Produtos com maior saldo atual na base.": "Productos con mayor saldo actual de inventario.",
    "Relatório Financeiro Consolidado": "Informe Financiero Consolidado",
    "Receitas, despesas e resultado agrupados por categoria.": "Ingresos, gastos y resultados agrupados por categoría.",
    "Erro ao gerar o relatório.": "No se pudo generar el informe.",
    "Escolha um relatório acima e clique em gerar para montar a prévia.": "Elige un informe arriba y haz clic en Generar para crear la vista previa.",
    "Relatórios específicos do módulo de vendas.": "Informes específicos del módulo de ventas.",
    "Relatórios específicos do módulo de compras.": "Informes específicos del módulo de compras.",
    "Relatórios específicos do módulo de estoque.": "Informes específicos del módulo de inventario.",
    "Relatórios específicos do módulo financeiro.": "Informes específicos del módulo financiero.",
    "Relatórios específicos do módulo de tickets.": "Informes específicos del módulo de tickets.",
    "Visualizar apenas o Estoque": "Visualizar solo el inventario",
    "Adicionar todos os produtos": "Agregar todos los productos",
    "Este login possui acesso somente para visualizar o saldo do estoque.": "Este usuario solo puede ver el saldo actual del inventario.",
    "Ajuda": "Ayuda",
    "Histórico de compras de moedas": "Historial de compras de créditos",
    "Buscar por descrição, status, valor ou data": "Buscar por descripción, estado, importe o fecha",
    "Baixar comprovante": "Descargar comprobante",
    "O comprovante fica disponível após a confirmação do pagamento.": "El comprobante estará disponible tras la confirmación del pago.",
    "Nenhuma compra encontrada para a busca.": "No se encontró ninguna compra para esta búsqueda.",
    "Nenhuma compra de moedas registrada até o momento.": "Ninguna compra de créditos registrada hasta ahora.",
    "Consulte pagamentos confirmados, pendentes e vencidos do plano.": "Consulta los pagos confirmados, pendientes y vencidos del plan.",
    "moedas": "créditos",
    "item(s)": "elemento(s)",
    selecionados: "seleccionados",
    favoritos: "favoritos",
    "Menu principal": "Menú principal",
  },
  "zh-CN": {
    Total: "总计",
    registros: "条记录",
    "do total": "占总数",
    "Resumo da listagem": "列表摘要",
    Ativos: "启用",
    Inativos: "停用",
    "Pessoa Física": "个人",
    "Pessoa Jurídica": "企业",
    Favoritos: "收藏",
    Web: "网页端",
    Mobile: "移动端",
    "Mais Tags": "更多标签",
    "Filtre a listagem pelas tags do cadastro": "按登记标签筛选列表",
    "Nenhuma tag configurada nesta tela.": "此页面尚未配置标签。",
    "Limpar seleção": "清除选择",
    Disponibilidade: "可用性",
    "Em Estoque": "有库存",
    "Sob Encomenda": "按订单生产",
    "Limpar filtro": "清除筛选",
    "Termos e Condições de uso": "使用条款和条件",
    "Aceitar": "接受",
    "Rejeitar": "拒绝",
    "Ao clicar em": "点击",
    "você concorda com os": "即表示您同意",
    "Termos de Uso": "使用条款",
    "Política de Privacidade": "隐私政策",
    "Política de Cookies": "Cookie 政策",
    "Política de Cancelamento": "取消政策",
    "Para reforçar o compromisso com nossos usuários e garantir o controle com relação ao uso de dados pessoais, em decorrência da Lei Geral de Proteção de Dados (nº 13.709/2018), estamos disponibilizando os Termos e Políticas de Privacidade para os usuários da nossa plataforma.": "为加强对用户的承诺并确保依据《通用数据保护法》（第13.709/2018号）对个人数据使用的管控，我们向平台用户提供条款与隐私政策。",
    "Para garantir a continuidade do uso de nossos serviços, por favor, revise essas informações e confirme estar de acordo com nossos termos.": "为确保继续使用我们的服务，请查看这些信息并确认您同意我们的条款。",
    "Não foi possível registrar seu aceite. Tente novamente.": "无法记录您的接受。请重试。",
    "Como deseja receber": "希望通过何种方式接收",
    "Enviamos o link de redefinição para o e-mail informado.": "我们会将重置链接发送到您填写的邮箱。",
    "Enviamos o link de redefinição para o telefone cadastrado nesta conta.": "我们会将重置链接发送到该账户登记的手机。",
    "Nenhuma sessão do WhatsApp está conectada.": "没有已连接的 WhatsApp 会话。",
    "A recuperação por WhatsApp está desativada.": "WhatsApp 找回功能已关闭。",
    "Envio": "发送",
    "Ambiente": "环境",
    "Sandbox (número de teste)": "沙盒（测试号码）",
    "Produção (destinatário real)": "生产（真实收件人）",
    "Identificação do remetente": "发送方标识",
    "Número de teste": "测试号码",
    "Opcional": "可选",
    "Cada 160 caracteres consomem 1 crédito; a partir daí, mais 1 a cada 153.": "每 160 个字符消耗 1 个额度；之后每 153 个字符再消耗 1 个。",
    "Chave da API": "API 密钥",
    "Chave configurada": "已配置密钥",
    "Nova chave (auth-key)": "新密钥（auth-key）",
    "Salvar chave": "保存密钥",
    "Teste de envio": "发送测试",
    "Número do destinatário": "收件人号码",
    "Não foi possível salvar a chave da API.": "无法保存 API 密钥。",
    "Pacotes de créditos": "额度套餐",
    "Defina o preço e libere os pacotes que as organizações podem comprar em Minha conta.": "设置价格并开放组织可在“我的账户”中购买的套餐。",
    "Preço (R$)": "价格（R$）",
    "Disponível": "可用",
    "créditos": "额度",
    "Saldo por organização": "各组织余额",
    "Créditos disponíveis": "可用额度",
    "Créditos usados no mês": "本月已用额度",
    "Enviados no mês": "本月发送量",
    "Créditos usados": "已用额度",
    "Salvar configurações": "保存设置",
    "Nenhuma organização cadastrada.": "没有已注册的组织。",
    "Saldo de": "余额：",
    "Comprar": "购买",
    "moedas de e-mail": "邮件额度",
    "Compra de moedas de e-mail": "购买邮件额度",
    "Não foi possível iniciar a compra de moedas.": "无法开始购买额度。",
    "Verificação anti-bot": "反机器人验证",
    "Proteção do login": "登录保护",
    "Exigir verificação no login": "登录时要求验证",
    "Chave do site (site key)": "站点密钥（site key）",
    "Chave secreta": "密钥",
    "Nova chave secreta": "新密钥",
    "Chave secreta do Turnstile salva.": "Turnstile 密钥已保存。",
    "Configuração do Turnstile salva.": "Turnstile 设置已保存。",
    "Não foi possível salvar a chave secreta.": "无法保存密钥。",
    "Não foi possível carregar a configuração do Turnstile.": "无法加载 Turnstile 设置。",
    "Não foi possível salvar a configuração do Turnstile.": "无法保存 Turnstile 设置。",
    "Conclua a verificação de segurança para entrar.": "请完成安全验证后登录。",
    "Não foi possível carregar a verificação de segurança. Recarregue a página ou verifique a conexão.": "无法加载安全验证。请刷新页面或检查网络连接。",
    "Não foi possível concluir a verificação anti-bot.": "无法完成反机器人验证。",
    "A verificação está ativa: o login só é aceito com o desafio concluído.": "验证已启用：仅在完成挑战后才接受登录。",
    "Falta a chave do site ou a chave secreta — enquanto isso o login segue sem verificação.": "缺少站点密钥或密钥——在此期间登录不做验证。",
    "A verificação está desligada e o login não exibe o desafio.": "验证已关闭，登录不显示挑战。",
    "A chave secreta fica cifrada no servidor e nunca é enviada ao navegador.": "密钥在服务器端加密存储，绝不发送到浏览器。",
    "Nenhuma chave secreta configurada. Copie a Secret Key do widget na Cloudflare.": "尚未配置密钥。请从 Cloudflare 的小组件中复制 Secret Key。",
    "O widget aparece na tela de login e o token é conferido no servidor a cada tentativa. Crie o widget em Turnstile no painel da Cloudflare, adicione os domínios da plataforma e cole aqui as duas chaves.": "小组件显示在登录页面，每次尝试都会在服务器端校验令牌。请在 Cloudflare 面板的 Turnstile 中创建小组件，添加平台域名，并在此粘贴两个密钥。",
    "Não foi possível salvar os pacotes.": "无法保存套餐。",
    "Pacotes salvos.": "套餐已保存。",
    "Salvar pacotes": "保存套餐",
    "Pacotes de créditos de e-mail": "邮件额度套餐",
    "Defina o preço e libere os pacotes de disparos de marketing que as organizações podem comprar em Minha conta.": "设置价格并开放组织可在“我的账户”中购买的营销发送套餐。",
    "API CONCILIADOR": "对账 API",
    "Log de Acesso": "访问日志",
    "Chat Interno": "内部聊天",
    "Erro ao carregar o DRE.": "无法加载损益表。",
    "Receita operacional bruta": "营业总收入",
    "(-) Deduções": "(-) 扣减项",
    "(=) Receita operacional líquida": "(=) 营业净收入",
    "(-) CMV": "(-) 销售成本",
    "(=) Lucro bruto": "(=) 毛利润",
    "(-) Despesas operacionais": "(-) 营业费用",
    "(=) Resultado operacional": "(=) 营业利润",
    "(+/-) Resultado financeiro": "(+/-) 财务损益",
    "(=) EBITDA": "(=) EBITDA",
    "(-) Despesas financeiras": "(-) 财务费用",
    "(=) Lucro antes dos impostos": "(=) 税前利润",
    "(-) Imposto sobre renda": "(-) 所得税",
    "(=) Lucro líquido": "(=) 净利润",
    "Faturamento bruto": "总营业额",
    "Ticket médio": "平均客单价",
    "Margem de contribuição": "贡献毛利率",
    "Ponto de equilíbrio": "盈亏平衡点",
    "EBITDA": "EBITDA",
    "Lucratividade": "盈利能力",
    "Indicadores de desempenho": "绩效指标",
    "Dados da Organização": "组织信息",
    "Empresa atual": "当前公司",
    "Empresas vinculadas": "关联公司",
    "Módulos ativos": "已启用模块",
    "Data de criação": "创建日期",
    "Gerenciar organização": "管理组织",
    "Ver empresas": "查看公司",
    "Atingimento": "达成率",
    "Gauge semicircular": "半圆仪表盘",
    "Felicidade do cliente (NPS)": "客户满意度（NPS）",
    "Use os controles abaixo apenas para testar a aparência destes gráficos. Eles não alteram os dashboards.": "以下控件仅用于测试这些图表的外观，不会更改仪表板。",
    "Todas ativas": "全部启用",
    "ativas": "启用",
    "este mês": "本月",
    "em uso": "使用中",
    "novos este mês": "本月新增",
    "Resultado consolidado do mês": "本月综合结果",
    "vs mês anterior": "较上月",
    "Vendas registradas no mês": "本月已登记销售额",
    "Novo registro cadastrado": "新建记录",
    "Atualização cadastral": "记录已更新",
    "Registro excluído": "记录已删除",
    "Empresa alterada": "公司已切换",
    "Organização alterada": "组织已切换",
    "Entrada no sistema": "登录系统",
    "Saída do sistema": "退出系统",
    "Ação realizada": "已执行操作",
    "por": "操作人",
    "Referência anterior": "上期参考",
    "Realizado": "实际值",
    "Período analisado": "分析期间",
    "Os indicadores e demonstrativos abaixo respeitam o intervalo selecionado.": "以下指标和报表均按所选日期范围计算。",
    "Data inicial": "开始日期",
    "Data final": "结束日期",
    "Análise visual mensal": "月度可视化分析",
    "Abertura acumulada, resultado operacional e saldo final de cada mês.": "各月期初余额、营业利润和期末余额。",
    "Bom": "良好",
    "Crítico": "严重",
    "Atenção": "注意",
    "Abertura acumulada": "期初余额",
    "Resultado operacional": "营业利润",
    "Saldo final": "期末余额",
    "Sem dados no período.": "该期间暂无数据。",
    "Resumo financeiro mensal": "月度财务摘要",
    "Demonstrativo consolidado por mês no formato do DRE.": "按月汇总的损益表。",
    "Receita, Custos e Lucro Líquido (12 meses)": "收入、成本和净利润（12个月）",
    "Evolução mensal dos principais indicadores da DRE.": "损益表主要指标的月度趋势。",
    "Receita Bruta": "总收入",
    "Custos": "成本",
    "Lucro Líquido": "净利润",
    "Receita Bruta x Despesas Operacionais": "总收入与营业费用",
    "Comparativo mensal entre receita e despesas.": "收入与费用的月度对比。",
    "Despesas Operacionais": "营业费用",
    "Composição das Despesas Operacionais": "营业费用构成",
    "Sem despesas no período.": "该期间暂无费用。",
    "Estrutura do DRE (Waterfall)": "损益表结构（瀑布图）",
    "Formação do resultado, da receita bruta ao lucro líquido.": "从总收入到净利润的结果构成。",
    "Relatórios Financeiros": "财务报表",
    "Relatórios de Vendas": "销售报表",
    "Relatórios de Compras": "采购报表",
    "Relatórios de Tickets": "工单报表",
    "Relatórios de Estoque": "库存报表",
    "Selecione e gere os relatórios disponíveis para este módulo.": "选择并生成此模块可用的报表。",
    "Selecione um relatório, gere a visualização e imprima no padrão da plataforma.": "选择报表、生成预览并按平台标准打印。",
    "Pesquisar relatório": "搜索报表",
    "Financeiro Consolidado": "合并财务",
    "Vendas por Status": "按状态统计销售",
    "Compras por Status": "按状态统计采购",
    "Suporte por Etapa": "按阶段统计支持",
    "Estoque por Saldo": "按余额统计库存",
    "Seu perfil não tem acesso a este módulo.": "您的角色无权访问此模块。",
    "Relatório de Vendas": "销售报表",
    "Resumo por status das vendas registradas.": "按状态汇总已登记的销售。",
    "Relatório de Compras": "采购报表",
    "Resumo por status dos pedidos de compra.": "按状态汇总采购订单。",
    "Relatório de Tickets": "工单报表",
    "Chamados agrupados por etapa de atendimento.": "按服务阶段分组的工单。",
    "Relatório de Estoque": "库存报表",
    "Produtos com maior saldo atual na base.": "当前库存余额最高的产品。",
    "Relatório Financeiro Consolidado": "合并财务报表",
    "Receitas, despesas e resultado agrupados por categoria.": "按类别分组的收入、费用和结果。",
    "Erro ao gerar o relatório.": "无法生成报表。",
    "Escolha um relatório acima e clique em gerar para montar a prévia.": "请选择上方的报表并点击生成以创建预览。",
    "Relatórios específicos do módulo de vendas.": "销售模块专用报表。",
    "Relatórios específicos do módulo de compras.": "采购模块专用报表。",
    "Relatórios específicos do módulo de estoque.": "库存模块专用报表。",
    "Relatórios específicos do módulo financeiro.": "财务模块专用报表。",
    "Relatórios específicos do módulo de tickets.": "工单模块专用报表。",
    "Visualizar apenas o Estoque": "仅查看库存",
    "Adicionar todos os produtos": "添加所有产品",
    "Este login possui acesso somente para visualizar o saldo do estoque.": "此登录账号只能查看当前库存余额。",
    "Ajuda": "帮助",
    "Histórico de compras de moedas": "额度购买记录",
    "Buscar por descrição, status, valor ou data": "按描述、状态、金额或日期搜索",
    "Baixar comprovante": "下载凭证",
    "O comprovante fica disponível após a confirmação do pagamento.": "付款确认后即可下载凭证。",
    "Nenhuma compra encontrada para a busca.": "未找到符合搜索条件的购买记录。",
    "Nenhuma compra de moedas registrada até o momento.": "目前尚无额度购买记录。",
    "Consulte pagamentos confirmados, pendentes e vencidos do plano.": "查看方案的已确认、待付和逾期付款。",
    "moedas": "额度",
    "item(s)": "项",
    selecionados: "已选择",
    favoritos: "收藏",
    "Menu principal": "主菜单",
  },
};

const ATRIBUTOS_TRADUZIVEIS = ["title", "aria-label", "placeholder", "alt", "data-tooltip"] as const;
type AtributoTraduzivel = (typeof ATRIBUTOS_TRADUZIVEIS)[number];
interface RegistroTraducao {
  original: string;
  traduzido: string;
}

const textosTraduzidos = new WeakMap<Text, RegistroTraducao>();
const atributosTraduzidos = new WeakMap<Element, Partial<Record<AtributoTraduzivel, RegistroTraducao>>>();
const SELETOR_TEXTO_IGNORADO =
  'script, style, noscript, textarea, input, code, pre, [contenteditable="true"], [data-i18n-skip]';
const SELETOR_DADOS = 'tbody, [data-i18n-db]';

function chaveBusca(valor: string): string {
  return normalizarTextoPtBr(valor).trim().replace(/\s+/g, " ").toLocaleLowerCase("pt-BR");
}

function criarIndiceIdioma(idioma: Idioma): Map<string, string> {
  const indice = new Map<string, string>();

  Object.entries(TEXTOS.pt).forEach(([chave, textoPt]) => {
    const traduzido = TEXTOS[idioma][chave] || textoPt;
    indice.set(chaveBusca(chave), traduzido);
    indice.set(chaveBusca(textoPt), traduzido);
  });

  Object.entries(FRASES_COMUNS[idioma]).forEach(([chave, texto]) => {
    indice.set(chaveBusca(chave), texto);
  });

  Object.entries(TEXTOS_SECOES_MODAL[idioma]).forEach(([chave, texto]) => {
    indice.set(chaveBusca(chave), texto);
  });

  Object.entries(TEXTOS_LISTAGEM_COMPACTA[idioma]).forEach(([chave, texto]) => {
    indice.set(chaveBusca(chave), texto);
  });

  Object.entries(TEXTOS_BADGES[idioma]).forEach(([chave, texto]) => {
    indice.set(chaveBusca(chave), texto);
    // Enums fixos como CRITICA chegam sem acentos; o valor persistido permanece o mesmo.
    indice.set(chaveBusca(chave.normalize("NFD").replace(/[\u0300-\u036f]/g, "")), texto);
  });

  // Completa a Revenda sem traduzir nomes, códigos ou descrições cadastradas nas tabelas.
  Object.entries(TEXTOS_REVENDA[idioma]).forEach(([chave, texto]) => {
    indice.set(chaveBusca(chave), texto);
  });
  Object.entries(TEXTOS_RELATORIOS[idioma]).forEach(([chave, texto]) => {
    indice.set(chaveBusca(chave), texto);
  });

  return indice;
}

const INDICES_TRADUCAO: Record<Idioma, Map<string, string>> = {
  pt: criarIndiceIdioma("pt"),
  en: criarIndiceIdioma("en"),
  es: criarIndiceIdioma("es"),
  "zh-CN": criarIndiceIdioma("zh-CN"),
};

function aplicarCaixaDoOriginal(original: string, traduzido: string, idioma: Idioma): string {
  const letrasOriginais = original.replace(/[^\p{L}]/gu, "");
  if (!letrasOriginais || idioma === "zh-CN") return traduzido;
  if (letrasOriginais === letrasOriginais.toLocaleUpperCase("pt-BR")) {
    return traduzido.toLocaleUpperCase(idioma === "pt" ? "pt-BR" : idioma);
  }
  return traduzido;
}

function traduzirFraseDinamica(texto: string, idioma: Idioma): string | null {
  const relatorio = traduzirFraseRelatorio(texto, idioma, (parte) => INDICES_TRADUCAO[idioma].get(chaveBusca(parte)) || parte);
  if (relatorio) return relatorio;
  // Uma frase estática conhecida tem precedência sobre modelos como "Código {numero}".
  const revenda = INDICES_TRADUCAO[idioma].has(chaveBusca(texto)) ? null : traduzirFraseRevenda(texto, idioma);
  if (revenda) return revenda;

  const atrasoBadge = texto.match(/^(\d+)\s+dias?\s+de atraso$/i);
  if (atrasoBadge) {
    const dias = atrasoBadge[1];
    if (idioma === "en") return `${dias} ${dias === "1" ? "day" : "days"} overdue`;
    if (idioma === "es") return `${dias} ${dias === "1" ? "día" : "días"} de retraso`;
    if (idioma === "zh-CN") return `逾期${dias}天`;
  }
  const traduzirParte = (valor: string) => {
    const parte = normalizarTextoPtBr(valor).trim();
    return INDICES_TRADUCAO[idioma].get(chaveBusca(parte)) || parte;
  };

  const valorMuitoLongo = texto.match(/^O valor informado no campo\s+(.+?)\s+excede o tamanho permitido$/i);
  if (valorMuitoLongo) {
    const campo = traduzirParte(valorMuitoLongo[1]);
    if (idioma === "en") return `The value entered in the ${campo} field exceeds the allowed length`;
    if (idioma === "es") return `El valor introducido en el campo ${campo} supera la longitud permitida`;
    if (idioma === "zh-CN") return `${campo}字段中的值超过允许的长度`;
  }

  const valorForaDaRegra = texto.match(/^O valor informado no campo\s+(.+?)\s+n[aã]o atende [àa]s regras do cadastro$/i);
  if (valorForaDaRegra) {
    const campo = traduzirParte(valorForaDaRegra[1]);
    if (idioma === "en") return `The value entered in the ${campo} field does not meet the record rules`;
    if (idioma === "es") return `El valor introducido en el campo ${campo} no cumple las reglas del registro`;
    if (idioma === "zh-CN") return `${campo}字段中的值不符合记录规则`;
  }

  const campoVazio = texto.match(/^O campo\s+(.+?)\s+está vazio no cadastro$/i);
  if (campoVazio) {
    const campo = traduzirParte(campoVazio[1]);
    if (idioma === "en") return `The ${campo} field is empty`;
    if (idioma === "es") return `El campo ${campo} está vacío`;
    if (idioma === "zh-CN") return `${campo}字段为空`;
  }

  const valorInvalido = texto.match(/^O valor informado no campo\s+(.+?)\s+é inválido$/i);
  if (valorInvalido) {
    const campo = traduzirParte(valorInvalido[1]);
    if (idioma === "en") return `The value entered in the ${campo} field is invalid`;
    if (idioma === "es") return `El valor introducido en el campo ${campo} no es válido`;
    if (idioma === "zh-CN") return `${campo}字段中的值无效`;
  }

  const campoIndisponivel = texto.match(/^O campo\s+(.+?)\s+não está disponível no cadastro$/i);
  if (campoIndisponivel) {
    const campo = traduzirParte(campoIndisponivel[1]);
    if (idioma === "en") return `The ${campo} field is not available in this record`;
    if (idioma === "es") return `El campo ${campo} no está disponible en este registro`;
    if (idioma === "zh-CN") return `此记录中没有${campo}字段`;
  }

  const estoqueNaoCadastrado = texto.match(/^Estoque não cadastrado para "(.+?)"\. Cadastre uma entrada de estoque antes de concluir a venda\.$/i);
  if (estoqueNaoCadastrado) {
    const produto = estoqueNaoCadastrado[1];
    if (idioma === "en") return `No stock is registered for "${produto}". Register a stock entry before completing the sale.`;
    if (idioma === "es") return `No hay stock registrado para "${produto}". Registre una entrada de stock antes de completar la venta.`;
    if (idioma === "zh-CN") return `“${produto}”尚未登记库存。请先登记入库再完成销售。`;
  }

  const estoqueInsuficiente = texto.match(/^Estoque insuficiente para "(.+?)"\. Necessário: (.+?)\. Disponível: (.+?)\.$/i);
  if (estoqueInsuficiente) {
    const [, produto, necessario, disponivel] = estoqueInsuficiente;
    if (idioma === "en") return `Insufficient stock for "${produto}". Required: ${necessario}. Available: ${disponivel}.`;
    if (idioma === "es") return `Stock insuficiente para "${produto}". Necesario: ${necessario}. Disponible: ${disponivel}.`;
    if (idioma === "zh-CN") return `“${produto}”库存不足。需要：${necessario}。可用：${disponivel}。`;
  }

  const comUnidade = texto.match(/^(.+?)\s*\(([^)]+)\)$/);
  if (comUnidade) {
    const [, rotulo, unidade] = comUnidade;
    const traduzido = traduzirParte(rotulo);
    return `${traduzido} (${unidade})`;
  }

  if (/^Selecione a empresa primeiro$/i.test(texto)) {
    if (idioma === "en") return "Select the company first";
    if (idioma === "es") return "Seleccione la empresa primero";
    if (idioma === "zh-CN") return "请先选择公司";
  }

  const selecione = texto.match(/^Selecione\s+(?:o|a)?\s*(.+?)(\.\.\.)?$/i);
  if (selecione) {
    const entidade = traduzirParte(selecione[1]).toLocaleLowerCase(idioma === "pt" ? "pt-BR" : idioma);
    if (idioma === "en") return `Select ${entidade}${selecione[2] ? "..." : ""}`;
    if (idioma === "es") return `Seleccione ${entidade}${selecione[2] ? "..." : ""}`;
    if (idioma === "zh-CN") return `选择${entidade}${selecione[2] ? "..." : ""}`;
  }

  const semNestaEmpresa = texto.match(/^Sem\s+(.+?)\s+nesta empresa$/i);
  if (semNestaEmpresa) {
    const entidade = traduzirParte(semNestaEmpresa[1]).toLocaleLowerCase(idioma === "pt" ? "pt-BR" : idioma);
    if (idioma === "en") return `No ${entidade} in this company`;
    if (idioma === "es") return `Sin ${entidade} en esta empresa`;
    if (idioma === "zh-CN") return `该公司没有${entidade}`;
  }

  const tituloAcao = texto.match(/^(Novo|Nova|Editar|Excluir|Importar|Visualizar|Duplicar)\s+(.+)$/i);
  if (tituloAcao) {
    const [, acao, entidadeOriginal] = tituloAcao;
    const entidade = traduzirParte(entidadeOriginal);
    const acaoNormalizada = chaveBusca(acao);
    if (idioma === "en") {
      const mapa: Record<string, string> = {
        novo: "New",
        nova: "New",
        editar: "Edit",
        excluir: "Delete",
        importar: "Import",
        visualizar: "View",
        duplicar: "Duplicate",
      };
      return `${mapa[acaoNormalizada] || acao} ${entidade}`;
    }
    if (idioma === "es") {
      const mapa: Record<string, string> = {
        novo: "Nuevo",
        nova: "Nueva",
        editar: "Editar",
        excluir: "Eliminar",
        importar: "Importar",
        visualizar: "Ver",
        duplicar: "Duplicar",
      };
      return `${mapa[acaoNormalizada] || acao} ${entidade}`;
    }
    if (idioma === "zh-CN") {
      const mapa: Record<string, string> = {
        novo: "新建",
        nova: "新建",
        editar: "编辑",
        excluir: "删除",
        importar: "导入",
        visualizar: "查看",
        duplicar: "复制",
      };
      return `${mapa[acaoNormalizada] || acao}${entidade}`;
    }
  }

  const dadosEntidade = texto.match(/^Dados\s+(?:do|da|de)\s+(.+)$/i);
  if (dadosEntidade) {
    const entidade = traduzirParte(dadosEntidade[1]);
    if (idioma === "en") return `${entidade} data`;
    if (idioma === "es") return `Datos de ${entidade}`;
    if (idioma === "zh-CN") return `${entidade}数据`;
  }

  const nenhum = texto.match(/^Nenhum(a)?\s+(.+?)\s+(encontrado|encontrada|registrado|registrada)$/i);
  if (nenhum) {
    const entidade = traduzirParte(nenhum[2]).toLocaleLowerCase(idioma === "pt" ? "pt-BR" : idioma);
    const status = chaveBusca(nenhum[3]).startsWith("registr") ? "registered" : "found";
    if (idioma === "en") return `No ${entidade} ${status}`;
    if (idioma === "es") return `Ningún registro de ${entidade}`;
    if (idioma === "zh-CN") return `未找到${entidade}`;
  }

  const sem = texto.match(/^Sem\s+(.+)$/i);
  if (sem) {
    const entidade = traduzirParte(sem[1]).toLocaleLowerCase(idioma === "pt" ? "pt-BR" : idioma);
    if (idioma === "en") return `No ${entidade}`;
    if (idioma === "es") return `Sin ${entidade}`;
    if (idioma === "zh-CN") return `无${entidade}`;
  }

  const carregandoAlgo = texto.match(/^Carregando\s+(.+?)(\.\.\.|…)$/i);
  if (carregandoAlgo) {
    const entidade = traduzirParte(carregandoAlgo[1]).toLocaleLowerCase(idioma === "pt" ? "pt-BR" : idioma);
    if (idioma === "en") return `Loading ${entidade}...`;
    if (idioma === "es") return `Cargando ${entidade}...`;
    if (idioma === "zh-CN") return `正在加载${entidade}...`;
  }

  const clientesEstado = texto.match(/^(\d+)\s+cliente\(s\)\s+em\s+(\d+)\s+estado\(s\)$/i);
  if (clientesEstado) {
    const [, clientes, estados] = clientesEstado;
    if (idioma === "en") return `${clientes} customer(s) in ${estados} state(s)`;
    if (idioma === "es") return `${clientes} cliente(s) en ${estados} estado(s)`;
    if (idioma === "zh-CN") return `${clientes} 个客户，分布在 ${estados} 个州`;
  }

  const resultado = texto.match(/^Resultado:\s*(.+)$/i);
  if (resultado) {
    const [, valor] = resultado;
    if (idioma === "en") return `Result: ${valor}`;
    if (idioma === "es") return `Resultado: ${valor}`;
    if (idioma === "zh-CN") return `结果: ${valor}`;
  }

  // Contadores do painel de Telas e Permissoes de Acesso (Perfil de Usuario).
  const telasLiberadas = texto.match(/^(\d+)\s+de\s+(\d+)\s+telas\s+liberadas$/i);
  if (telasLiberadas) {
    const [, liberadas, total] = telasLiberadas;
    if (idioma === "en") return `${liberadas} of ${total} screens enabled`;
    if (idioma === "es") return `${liberadas} de ${total} pantallas habilitadas`;
    if (idioma === "zh-CN") return `已开通 ${liberadas}/${total} 个页面`;
  }

  const mostrandoTelas = texto.match(/^Mostrando\s+(\d+)\s+de\s+(\d+)\s+telas$/i);
  if (mostrandoTelas) {
    const [, exibidas, total] = mostrandoTelas;
    if (idioma === "en") return `Showing ${exibidas} of ${total} screens`;
    if (idioma === "es") return `Mostrando ${exibidas} de ${total} pantallas`;
    if (idioma === "zh-CN") return `显示 ${exibidas}/${total} 个页面`;
  }

  const comAcesso = texto.match(/^(\d+)\s+com\s+acesso$/i);
  if (comAcesso) {
    const [, quantidade] = comAcesso;
    if (idioma === "en") return `${quantidade} with access`;
    if (idioma === "es") return `${quantidade} con acceso`;
    if (idioma === "zh-CN") return `${quantidade} 个已授权`;
  }

  const percentualLiberadas = texto.match(/^(\d+)%\s+liberadas$/i);
  if (percentualLiberadas) {
    const [, percentual] = percentualLiberadas;
    if (idioma === "en") return `${percentual}% enabled`;
    if (idioma === "es") return `${percentual}% habilitadas`;
    if (idioma === "zh-CN") return `已开通 ${percentual}%`;
  }

  const contagemDeTotal = texto.match(/^(\d+)\s+de\s+(\d+)$/);
  if (contagemDeTotal) {
    const [, parte, total] = contagemDeTotal;
    if (idioma === "en") return `${parte} of ${total}`;
    if (idioma === "es") return `${parte} de ${total}`;
    if (idioma === "zh-CN") return `${parte} / ${total}`;
  }

  return null;
}

function buscarTraducao(texto: string, idioma: Idioma): string | null {
  const textoLimpo = normalizarTextoPtBr(texto).trim().replace(/\s+/g, " ");
  if (!textoLimpo) return null;

  const pontuacaoFinal = textoLimpo.match(/[.,:;!?。！？：]$/)?.[0] ?? "";
  const temObrigatorio = /\*$/.test(textoLimpo);
  const temMais = /^\+\s*/.test(textoLimpo);
  const base = textoLimpo
    .replace(/^\+\s*/, "")
    .replace(/\s*\*$/, "")
    .replace(/[.,:;!?。！？：]$/, "")
    .trim();

  const dinamica = traduzirFraseDinamica(base, idioma) || traduzirFraseDinamica(textoLimpo, idioma);
  if (dinamica) return dinamica;

  const indice = INDICES_TRADUCAO[idioma];
  const traduzido = indice.get(chaveBusca(textoLimpo)) || indice.get(chaveBusca(base));
  if (!traduzido) return null;

  const comCaixa = aplicarCaixaDoOriginal(base, traduzido, idioma);
  const prefixoMais = temMais ? "+ " : "";
  const sufixoObrigatorio = temObrigatorio ? " *" : "";
  const sufixoPontuacao = pontuacaoFinal && !/[.,:;!?。！？：]$/.test(comCaixa) ? pontuacaoFinal : "";
  return `${prefixoMais}${comCaixa}${sufixoObrigatorio}${sufixoPontuacao}`;
}

export function traduzirTexto(texto: string, idioma: Idioma): string {
  const original = String(texto ?? "");
  const prefixo = original.match(/^\s*/)?.[0] ?? "";
  const sufixo = original.match(/\s*$/)?.[0] ?? "";
  const miolo = original.slice(prefixo.length, original.length - sufixo.length);
  const traduzido = idioma === "pt" ? normalizarTextoPtBr(miolo) : buscarTraducao(miolo, idioma);
  return `${prefixo}${traduzido || normalizarTextoPtBr(miolo)}${sufixo}`;
}

function deveIgnorarElemento(elemento: Element): boolean {
  if (elemento.closest(SELETOR_TEXTO_IGNORADO)) return true;
  // Rótulos fixos podem optar pela tradução dentro das tabelas. Dados aninhados continuam protegidos.
  const limite = elemento.closest(`${SELETOR_DADOS}, [data-i18n-ui]`);
  return Boolean(limite?.matches(SELETOR_DADOS));
}

function deveIgnorarTexto(no: Text): boolean {
  const pai = no.parentElement;
  return !pai || deveIgnorarElemento(pai);
}

function traduzirNoTexto(no: Text, idioma: Idioma) {
  if (deveIgnorarTexto(no)) return;
  const atual = no.nodeValue ?? "";
  const registro = textosTraduzidos.get(no);
  const original = registro && atual === registro.traduzido ? registro.original : atual;
  const traduzido = traduzirTexto(original, idioma);
  textosTraduzidos.set(no, { original, traduzido });
  if (traduzido !== atual) no.nodeValue = traduzido;
}

function traduzirAtributos(elemento: Element, idioma: Idioma) {
  if (deveIgnorarElemento(elemento)) return;

  ATRIBUTOS_TRADUZIVEIS.forEach((atributo) => {
    const atual = elemento.getAttribute(atributo);
    if (!atual) return;

    const registros = atributosTraduzidos.get(elemento) ?? {};
    const registro = registros[atributo];
    const original = registro && atual === registro.traduzido ? registro.original : atual;
    const traduzido = traduzirTexto(original, idioma);
    registros[atributo] = { original, traduzido };
    atributosTraduzidos.set(elemento, registros);

    if (traduzido !== atual) elemento.setAttribute(atributo, traduzido);
  });
}

export function aplicarTraducaoDocumento(idioma: Idioma, documento?: Document) {
  const alvo = documento ?? (typeof document !== "undefined" ? document : null);
  if (!alvo?.body) return;

  const walker = alvo.createTreeWalker(documento ? alvo.documentElement : alvo.body, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let atual: Node | null = walker.currentNode;

  while (atual) {
    if (atual.nodeType === Node.TEXT_NODE) {
      traduzirNoTexto(atual as Text, idioma);
    } else if (atual.nodeType === Node.ELEMENT_NODE) {
      traduzirAtributos(atual as Element, idioma);
    }
    atual = walker.nextNode();
  }
}

interface IdiomaContextValue {
  idioma: Idioma;
  setIdioma: (idioma: Idioma) => void;
  t: (chave: ChaveTexto) => string;
  traduzir: (texto: string) => string;
}

const IdiomaContext = createContext<IdiomaContextValue | undefined>(undefined);

function lerIdiomaInicial(): Idioma {
  const salvo = String(localStorage.getItem("appLanguage") || "").trim();
  if (salvo === "pt" || salvo === "en" || salvo === "es" || salvo === "zh-CN") return salvo;
  return "pt";
}

export function IdiomaProvider({ children }: { children: ReactNode }) {
  const [idioma, setIdiomaState] = useState<Idioma>(lerIdiomaInicial);

  useEffect(() => {
    localStorage.setItem("appLanguage", idioma);
    document.documentElement.lang = idioma === "pt" ? "pt-BR" : idioma;

    let timer: number | undefined;
    const aplicar = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => aplicarTraducaoDocumento(idioma), 20);
    };

    aplicar();

    if (!document.body) return;
    const observer = new MutationObserver(aplicar);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...ATRIBUTOS_TRADUZIVEIS],
    });

    return () => {
      if (timer) window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [idioma]);

  const value = useMemo<IdiomaContextValue>(() => ({
    idioma,
    setIdioma: setIdiomaState,
    t: (chave) => traduzirTexto(TEXTOS[idioma][chave] || TEXTOS.pt[chave] || chave, idioma),
    traduzir: (texto) => traduzirTexto(texto, idioma),
  }), [idioma]);

  return <IdiomaContext.Provider value={value}>{children}</IdiomaContext.Provider>;
}

export function useIdioma(): IdiomaContextValue {
  const ctx = useContext(IdiomaContext);
  if (!ctx) throw new Error("useIdioma deve ser usado dentro de <IdiomaProvider>.");
  return ctx;
}
